import { useState, useCallback, useRef, useMemo, useEffect, Fragment } from "react";
import * as XLSX from "xlsx";
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.mjs?url";
import { load, save, backupWeekly, flushPendingWrites, reloadIfRemoteChanged, recoverFromBackup, loadLivePrices, saveLivePrices, loadHistPrices, saveHistPrices } from "./storage.js";

// pdf.js needs a worker URL configured before any document loads. Vite resolves
// the `?url` import to a final asset URL at build time so the worker file is
// served alongside the main bundle.
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// NOTE: Original artifact used window.storage. Replaced with Supabase-backed load/save.
// Original artifact called https://api.anthropic.com/v1/messages directly.
// In production, those calls go to /api/anthropic-proxy (Vercel function) which adds the API key.
const API_ENDPOINT = "/api/anthropic-proxy";

// ════════════════════════════════════════════════════════════════
//  CONSTANTS
// ════════════════════════════════════════════════════════════════
const IDX_BROKERS = ["Stockbit","Mirae Asset","Semesta","Semesta Margin","Semesta Regular","OSK","AM Capital","BNI Sekuritas","Indo Premier","Mandiri","Henan Putihrai","MNC Sekuritas"];
const US_BROKERS  = ["Interactive Brokers","Schwab","Fidelity","TD Ameritrade","Robinhood"];
const ALL_BROKERS = [...IDX_BROKERS, ...US_BROKERS];
const INVESTORS   = ["Robert","Yanuar","Elizabeth","Tomy","Franky","Reya","Edwin"];
const IDX_BUY_PCT = 0.0015, IDX_SELL_PCT = 0.0025, STAMP = 10000;
const US_FLAT_FEE = 1.5066;

// Per-broker default commission (Setup → Broker Fees), stored in settings.brokerFees as
// { [broker]: { buy, sell } }. IDX brokers: PERCENT of gross (e.g. 0.15). US brokers: flat
// USD per trade. Blank/missing → the global defaults above. Only used for NEW trades.
const brokerFeeDefault = (mkt, type) =>
  mkt === "IDX" ? (type === "BUY" ? IDX_BUY_PCT : IDX_SELL_PCT) * 100 : US_FLAT_FEE;
const brokerFeeRate = (brokerFees, brk, mkt, type) => {
  const v = brokerFees?.[brk]?.[type === "BUY" ? "buy" : "sell"];
  return v === "" || v == null || isNaN(+v) ? brokerFeeDefault(mkt, type) : +v;
};
const calcAutoFee = (brokerFees, { mkt, brk, type, gross }) => {
  const rate = brokerFeeRate(brokerFees, brk, mkt, type);
  return mkt === "IDX" ? Math.round(gross * rate / 100) : rate;
};
// Bea meterai rule: Rp 10,000 per ACCOUNT (investor+broker) per DAY, but ONLY if that
// account's total transaction value that day (buys + sells, gross) reaches this threshold.
// IPO trades are exempt and excluded from the total. US/IBKR has no meterai.
const STAMP_THRESHOLD = 10_000_000;

const stampGroupKey = (t) => `${t.inv}||${t.brk}||${t.date}`;
const stampTradeGross = (t) => {
  const shares = t.mkt === "IDX" ? (+t.qty || 0) * 100 : (+t.qty || 0);
  return shares * (+t.px || 0);
};
const isIpoTrade = (t) =>
  t.isIpo === true || (typeof t.note === "string" && t.note.trim().toUpperCase().startsWith("IPO"));
// PDF-imported days carry the broker's ACTUAL charged stamp — never recompute those.
const isPdfTrade = (t) => (typeof t.id === "string" && t.id.startsWith("pdf")) || t._stampApplied > 0;

// Recompute the Rp 10,000 stamp bearer for the given (investor, broker, date) group keys,
// per the threshold rule. Idempotent: `_stampDay` marks whether STAMP is already baked into
// a trade's `fee`, so we never double-add or double-remove. Returns new buys/sells arrays.
function reconcileStampGroups(buys, sells, keys) {
  const keySet = new Set((keys || []).filter(Boolean));
  if (keySet.size === 0) return { buys, sells };
  const nb = buys.map((t) => ({ ...t }));
  const ns = sells.map((t) => ({ ...t }));
  const all = [...nb, ...ns];  // same object refs as nb/ns → mutations propagate
  for (const key of keySet) {
    const group = all.filter((t) => stampGroupKey(t) === key);
    if (group.length === 0) continue;
    // Only IDX groups get meterai; leave PDF-sourced days exactly as the broker charged them.
    if (!group.every((t) => t.mkt === "IDX") || group.some(isPdfTrade)) continue;
    const nonIpo = group.filter((t) => !isIpoTrade(t));
    const dayTotal = nonIpo.reduce((s, t) => s + stampTradeGross(t), 0);
    const shouldStamp = nonIpo.length > 0 && dayTotal >= STAMP_THRESHOLD;
    const bearers = group.filter((t) => t._stampDay > 0);
    if (shouldStamp && bearers.length === 0) {
      const bearer = nonIpo
        .slice()
        .sort((a, b) => String(a.id).localeCompare(String(b.id)))[0];
      bearer.fee = (+bearer.fee || 0) + STAMP;
      bearer._stampDay = STAMP;
    } else if (shouldStamp && bearers.length > 1) {
      bearers.slice(1).forEach((t) => { t.fee = (+t.fee || 0) - (t._stampDay || STAMP); delete t._stampDay; });
    } else if (!shouldStamp) {
      bearers.forEach((t) => { t.fee = (+t.fee || 0) - (t._stampDay || STAMP); delete t._stampDay; });
    }
  }
  return { buys: nb, sells: ns };
}

// ════════════════════════════════════════════════════════════════
//  PDF TRADE-CONFIRMATION PARSERS (LOCAL, NO API)
// ════════════════════════════════════════════════════════════════
// pdf.js extracts text from broker PDFs in the browser. Per-broker regex
// then parses the trade rows. All processing happens locally — no upload,
// no API call, no cost. Adding a new broker is a matter of adding one
// detection keyword + one parser function below.
//
// Returned shape (matches what the previous Claude API call returned):
//   { market, broker, date, currency, investor, account_no,
//     expected_settlement, total_stamp_duty, trades: [
//       { type, stock, qty, shares, price, gross, fee, is_ipo }
//     ] }

const parseNum = (s) => {
  if (s == null || s === "" || s === "-") return 0;
  const cleaned = String(s).replace(/,/g, "").replace(/^\(/, "-").replace(/\)$/, "");
  const n = +cleaned;
  return isNaN(n) ? 0 : n;
};
const parseInt0 = (s) => Math.trunc(parseNum(s));

// Extract plain text from a PDF's bytes using pdf.js. Each page's text items
// are concatenated in reading order with newlines between rows that visibly
// differ in y-position. This mimics what pdfplumber does on the Python side
// closely enough for our regexes to match.
async function extractPdfText(uint8) {
  const doc = await pdfjsLib.getDocument({ data: uint8 }).promise;
  let allText = "";
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    // Group text items by approximate y position to recreate visible lines.
    const lines = {};
    for (const item of content.items) {
      // transform[5] is the y coordinate. Round to integer to bucket items
      // that visually share a row but have minute float differences.
      const y = Math.round(item.transform[5]);
      if (!lines[y]) lines[y] = [];
      lines[y].push({ x: item.transform[4], str: item.str });
    }
    // Sort lines top-down (PDF y increases upward, so descending y = top-down)
    const ys = Object.keys(lines).map(Number).sort((a, b) => b - a);
    for (const y of ys) {
      const rowItems = lines[y].sort((a, b) => a.x - b.x);
      allText += rowItems.map(i => i.str).join(" ").replace(/\s+/g, " ").trim() + "\n";
    }
    allText += "\n";
  }
  await doc.destroy();
  return allText;
}

function detectBroker(text) {
  const t = text.toUpperCase();
  if (t.includes("MIRAE ASSET SEKURITAS")) return "Mirae Asset";
  if (t.includes("STOCKBIT SEKURITAS"))    return "Stockbit";
  if (t.includes("SEMESTA INDOVEST"))      return "Semesta";
  if (t.includes("RHB SEKURITAS"))         return "OSK";
  if (t.includes("BNI SEKURITAS"))         return "BNI Sekuritas";
  if (t.includes("MANDIRI SEKURITAS"))     return "Mandiri";
  if (t.includes("MNC SEKURITAS"))         return "MNC Sekuritas";
  if (t.includes("INDO PREMIER") || t.includes("IPOT")) return "Indo Premier";
  if (t.includes("HENAN PUTIHRAI"))        return "Henan Putihrai";
  if (t.includes("AM CAPITAL"))            return "AM Capital";
  return null;
}

const MONTH_ABBR = { Jan:"01", Feb:"02", Mar:"03", Apr:"04", May:"05", Jun:"06", Jul:"07", Aug:"08", Sep:"09", Oct:"10", Nov:"11", Dec:"12" };

// ─────────────────────────── MIRAE ASSET ────────────────────────────
// Section headers: "BUY INVOICE" / "SELL INVOICE"
// Trade row: TICKER [board codes] price qty gross brokerFee levy WHT VAT PPh23 net
// Example: "BUMI RG D 260 40,000 10,400,000 10,000 4,502 0 1,098 0 10,415,600"
function parseMirae(text) {
  const out = { market: "IDX", broker: "Mirae Asset", date: null, currency: "IDR",
                investor: null, account_no: null, expected_settlement: null,
                total_stamp_duty: 0, trades: [] };

  const dm = text.match(/Trade Date\s+(\d{2})\/(\d{2})\/(\d{4})/);
  if (dm) out.date = `${dm[3]}-${dm[2]}-${dm[1]}`;

  const im = text.match(/(\d{6,8})\s*-\s*([A-Z][A-Z\s.,]+?)\s+Sales\s*:/);
  if (im) {
    out.account_no = im[1];
    out.investor = im[2].trim().split(/\s+/)[0];
    out.investor = out.investor[0] + out.investor.slice(1).toLowerCase();
  }

  const gm = text.match(/Grand\s+Tot\s*al\s+IDR\s*\(?(-?[\d,]+)\)?/i);
  if (gm) {
    const v = parseInt0(gm[1]);
    // Parens or leading minus → user owes (negative settlement)
    const negative = /\(\s*[\d,]+\s*\)/.test(gm[0]) || gm[0].includes("(-");
    out.expected_settlement = negative ? -Math.abs(v) : v;
  }
  // Stamp duty (Meterai) — appears once in the footer of every Mirae confirmation.
  // pdf.js often splits "Meterai" into "Met erai" and similarly mangles the number
  // (e.g. "10, 000" with a space after the comma). Tolerate both with [\d,\s]+.
  const sm = text.match(/Met\s*erai\s+([\d,\s]+?)(?:\s|$|\n)/i);
  if (sm) {
    const cleaned = sm[1].replace(/\s+/g, "");  // "10, 000" → "10,000"
    out.total_stamp_duty = parseInt0(cleaned);
  }
  // Final fallback: every Mirae confirmation has Rp 10,000 stamp duty by Indonesian law.
  // If we got 0 or didn't match, default to 10000 (safer than dropping it silently).
  if (!out.total_stamp_duty || out.total_stamp_duty < 1000) out.total_stamp_duty = 10000;

  // Walk lines tracking BUY/SELL section
  let currentType = null;
  for (let raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    if (/BUY\s+INVOICE/i.test(line))  { currentType = "BUY";  continue; }
    if (/SELL\s+INVOICE/i.test(line)) { currentType = "SELL"; continue; }
    if (!currentType) continue;
    if (/^total\b/i.test(line)) continue;
    if (/^product\b/i.test(line) && /price/i.test(line)) continue;

    // Match trade: TICKER (3-5 letters) + optional board codes (1-3 letter words) + 9 numeric columns
    const m = line.match(/^([A-Z]{3,5})(?:\s+[A-Z]{1,3})*\s+([\d,]+(?:\.\d+)?)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)$/);
    if (m) {
      const [, ticker, price, qtySh, gross, comm, levy, wht, vat, pph, net] = m;
      const shares = parseInt0(qtySh);
      const lots = Math.trunc(shares / 100);
      const fee = parseInt0(comm) + parseInt0(levy) + parseInt0(vat) + parseInt0(wht) + parseInt0(pph);
      out.trades.push({
        type: currentType, stock: ticker, qty: lots, shares,
        price: parseNum(price), gross: parseInt0(gross), fee, net: parseInt0(net),
        is_ipo: false,
      });
    }
  }
  return out;
}

// ─────────────────────────── STOCKBIT ───────────────────────────────
// Trade row: refNo Board TICKER ShareName ... lot qty.00 price.00 buy_amt.00 sell_amt.00
// Example: "0484805 RG INET Sinergi Inti Andalan Prima Tbk. 300 30,000.00 414.00 12,420,000.00 0.00"
// Per-trade fee is computed by allocating the total fee (Total Cost − Total Value)
// proportional to that trade's gross amount.
function parseStockbit(text) {
  const out = { market: "IDX", broker: "Stockbit", date: null, currency: "IDR",
                investor: null, account_no: null, expected_settlement: null,
                total_stamp_duty: 0, trades: [] };

  const dm = text.match(/Transaction Date\s+(\d{2})\/(\d{2})\/(\d{4})/);
  if (dm) out.date = `${dm[3]}-${dm[2]}-${dm[1]}`;

  const im = text.match(/To\s+(\d{6,8})\s+([A-Z][A-Z\s,.]+?)\s*$/m)
          || text.match(/To\s+(\d{6,8})\s+([A-Z][A-Z\s,.]+?)\s+(?:Address|Settlement|Transaction)/);
  if (im) {
    out.account_no = im[1];
    out.investor = im[2].trim().split(/\s+/)[0];
    out.investor = out.investor[0] + out.investor.slice(1).toLowerCase();
  }

  // Pull the totals row to know how to allocate fees
  let buyTotalValue = 0, sellTotalValue = 0, buyTotalCost = 0, sellTotalCost = 0;
  const tv = text.match(/Total Value\s+([\d,.]+)\s+([\d,.]+)/);
  if (tv) { buyTotalValue = parseNum(tv[1]); sellTotalValue = parseNum(tv[2]); }
  const tc = text.match(/Total Cost\s+([\d,.]+)\s+([\d,.]+)/);
  if (tc) { buyTotalCost = parseNum(tc[1]); sellTotalCost = parseNum(tc[2]); }

  // Stamp duty: Stockbit shows it as "Stamp Duty <buy> <sell>" (typically 10,000 buy, 0 sell).
  // We MUST capture this and EXCLUDE it from per-row fee allocation, since handlePDF
  // adds total_stamp_duty back to the last trade's fee. Otherwise stamp gets counted twice.
  let stampBuy = 0, stampSell = 0;
  const sd = text.match(/Stamp Duty\s+([\d,.]+)\s+([\d,.]+)/);
  if (sd) { stampBuy = parseNum(sd[1]); stampSell = parseNum(sd[2]); }
  out.total_stamp_duty = Math.round(stampBuy + stampSell);

  // Per-side total fees, excluding stamp (handlePDF adds it separately to the last trade).
  // Total Cost for buys = Total Value + commissions + VAT + IDX fee + VAT levy + stamp duty.
  // Subtract stamp here so the per-trade allocated fee covers only the percentage components.
  const totalBuyFee  = buyTotalCost  - buyTotalValue  - stampBuy;
  // For sells: Total Value − Total Cost = total fees on sell side, also includes stamp (usually 0).
  const totalSellFee = sellTotalValue - sellTotalCost - stampSell;

  const pd = text.match(/Payment due to us IDR\s+([\d,.]+)\s+([\d,.]+)/);
  if (pd) out.expected_settlement = -(parseNum(pd[1])) + parseNum(pd[2]);

  for (let raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;

    // refNo (6-8 digits), Board (RG/TN/NG/UH/etc, 2-3 letters), TICKER (2-5 letters), share name, lot, qty.dd, price.dd, buy.dd, sell.dd
    const m = line.match(/^(\d{6,8})\s+([A-Z]{2,3})\s+([A-Z]{2,5})\s+.+?\s+([\d,]+)\s+([\d,]+\.\d{2})\s+([\d,]+\.\d{2})\s+([\d,]+\.\d{2})\s+([\d,]+\.\d{2})$/);
    if (m) {
      const [, , , ticker, lot, qtySh, price, buyAmt, sellAmt] = m;
      const buyVal  = parseNum(buyAmt);
      const sellVal = parseNum(sellAmt);
      let type, gross, fee = 0;
      if (buyVal > 0) {
        type = "BUY"; gross = buyVal;
        if (buyTotalValue > 0) fee = Math.round(totalBuyFee * (gross / buyTotalValue));
      } else if (sellVal > 0) {
        type = "SELL"; gross = sellVal;
        if (sellTotalValue > 0) fee = Math.round(totalSellFee * (gross / sellTotalValue));
      } else continue;

      out.trades.push({
        type, stock: ticker,
        qty: parseInt0(lot),
        shares: parseInt0(qtySh),
        price: parseNum(price),
        gross: Math.round(gross),
        fee,
        is_ipo: false,
      });
    }
  }
  return out;
}

// ─────────────────────────── SEMESTA ────────────────────────────────
// Trade row: contractNo TICKER - DESCRIPTION board qty(shares) price gross commPct comm levy vat tax stamp net
// Example: "09051354 AGRO - PT BANK RAKYAT INDON RG 100,000 186 18,600,000 0.123% 22,906 8,054 2,520 0 10,000 18,643,480"
function parseSemesta(text) {
  const out = { market: "IDX", broker: "Semesta", date: null, currency: "IDR",
                investor: null, account_no: null, expected_settlement: null,
                total_stamp_duty: 0, trades: [] };

  const dm = text.match(/Trade Date\s*:\s*(\d{2})-([A-Za-z]{3})-(\d{4})/);
  if (dm) out.date = `${dm[3]}-${MONTH_ABBR[dm[2]] || "01"}-${dm[1]}`;

  const im = text.match(/To\s*:\s*(\w+)\s*-\s*([A-Z][A-Z.,\s]+?)\s+Trade Date/);
  if (im) {
    out.account_no = im[1];
    const fullName = im[2].trim();
    out.investor = fullName.split(/[.\s]+/)[0];
    out.investor = out.investor[0] + out.investor.slice(1).toLowerCase();
  }

  const gm = text.match(/Total Net Amount\s*:\s*\(?(-?[\d,]+)\)?/);
  if (gm) {
    const v = parseInt0(gm[1]);
    const negative = /\(\s*[\d,]+\s*\)/.test(gm[0]);
    out.expected_settlement = negative ? -Math.abs(v) : v;
  }
  // Stamp duty: Semesta puts a "Total Amount" grand-total row on the last page that
  // sums all per-row stamp duty. Format: "Total Amount <comm> <levy> <vat> <tax> <stamp>".
  // The 5 numeric tokens after "Total Amount" are the column totals; the 5th = stamp.
  const ta = text.match(/Total Amount\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)/);
  if (ta) out.total_stamp_duty = parseInt0(ta[5]);
  // Fallback: scan visible per-row stamps (some PDFs may differ in layout)
  if (!out.total_stamp_duty) {
    // pdf.js sometimes detaches the totals numerics from the "Total Buy :" label;
    // check both the labeled and unlabeled forms for resilience.
    const tb = text.match(/Total Buy\s*:\s*[\d,]+\s+[\d,]+\s+[\d,]+\s+[\d,]+\s+([\d,]+)\b/);
    if (tb) out.total_stamp_duty += parseInt0(tb[1]);
    const ts = text.match(/Total Sell\s*:\s*[\d,]+\s+[\d,]+\s+[\d,]+\s+[\d,]+\s+([\d,]+)\b/);
    if (ts) out.total_stamp_duty += parseInt0(ts[1]);
  }

  let currentType = null;
  for (let raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const lower = line.toLowerCase();
    if (lower === "buy"  || lower.startsWith("buy "))  { currentType = "BUY";  continue; }
    if (lower === "sell" || lower.startsWith("sell ")) { currentType = "SELL"; continue; }
    if (lower.startsWith("total buy"))  { currentType = null; continue; }
    if (lower.startsWith("total sell")) { currentType = null; continue; }
    if (!currentType) continue;

    const m = line.match(/^(\d{8})\s+([A-Z]{2,5})\s+-\s+.+?\s+([A-Z]{2,3})\s+([\d,]+)\s+([\d,]+(?:\.\d+)?)\s+([\d,]+)\s+[\d.]+%\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)\s+([\d,]+)$/);
    if (m) {
      const [, , ticker, , qtySh, price, gross, comm, levy, vat, tax, stamp, net] = m;
      const shares = parseInt0(qtySh);
      const lots = Math.trunc(shares / 100);
      const fee = parseInt0(comm) + parseInt0(levy) + parseInt0(vat) + parseInt0(tax);  // exclude stamp; reported separately
      out.trades.push({
        type: currentType, stock: ticker, qty: lots, shares,
        price: parseNum(price), gross: parseInt0(gross), fee,
        net: parseInt0(net), is_ipo: false,
      });
    }
  }
  return out;
}

const PDF_PARSERS = {
  "Mirae Asset": parseMirae,
  "Stockbit":    parseStockbit,
  "Semesta":     parseSemesta,
};

// Top-level entry point: takes a base64 PDF string, returns the same shape
// the Claude API used to return. Throws on unsupported broker / parse failure.
async function parsePdfLocally(b64) {
  // base64 → Uint8Array
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  const text = await extractPdfText(bytes);
  const broker = detectBroker(text);
  if (!broker) {
    throw new Error("Couldn't identify broker from PDF. Supported: Mirae Asset, Stockbit, Semesta. (Header keywords not found.)");
  }
  const parser = PDF_PARSERS[broker];
  if (!parser) {
    throw new Error(`PDF parser for "${broker}" not yet implemented. Currently supported: Mirae Asset, Stockbit, Semesta.`);
  }
  const result = parser(text);
  if (!result.trades || result.trades.length === 0) {
    throw new Error(`Detected as ${broker} but no trades extracted. PDF format may have changed — please report.`);
  }
  return result;
}

// ── TICKER OWNERSHIP CONVENTION ──────────────────────────────────
// Some tickers in the user's data are tagged with suffixes indicating
// shared ownership:
//   "BCAP.RF"   → "RF" = shared 50/50 with a friend; user owns 50%
//   "BEEF.RUDI" → ".RUDI" = belongs to Rudi 100%; not the user's holding
//
// `getOwnership(stk)` returns { multiplier, baseTicker, label }:
//   multiplier — apply to qty/cost/value/P&L (1.0 for normal, 0.5 for .RF, 0 for .RUDI)
//   baseTicker — the underlying ticker without the suffix (used for Yahoo lookup)
//   label      — short tag for UI display (empty string for normal tickers)
//
// Adding new suffixes: just extend the SUFFIX_MAP below.
const SUFFIX_MAP = {
  RF:   { multiplier: 0.5, label: "50%" },
  RUDI: { multiplier: 0,   label: "RUDI" },
};
function getOwnership(stk) {
  if (!stk) return { multiplier: 1, baseTicker: stk, label: "" };
  const dot = stk.indexOf(".");
  if (dot < 0) return { multiplier: 1, baseTicker: stk, label: "" };
  const suffix = stk.slice(dot + 1).toUpperCase();
  const rule = SUFFIX_MAP[suffix];
  if (!rule) return { multiplier: 1, baseTicker: stk, label: "" };
  return { multiplier: rule.multiplier, baseTicker: stk.slice(0, dot), label: rule.label };
}

// Indices available for performance comparison.
// `key` is the field name stored on each `monthly` row (legacy: "ihsg", "sp500"
// already exist; new ones are namespaced).
// `mkt` indicates which market the index belongs to — used for default selection
// when filtering by market in the perf tab.
const INDEX_REGISTRY = [
  { key: "ihsg",   label: "IHSG",        long: "Jakarta Composite Index",     mkt: "IDX", color: "#60a5fa", yahoo: "^JKSE" },
  { key: "lq45",   label: "LQ45",        long: "LQ45 Index",                  mkt: "IDX", color: "#3b82f6", yahoo: "^JKLQ45" },
  { key: "idx30",  label: "IDX30",       long: "IDX30 Index",                 mkt: "IDX", color: "#1d4ed8", yahoo: "IDX30.JK" },
  { key: "sp500",  label: "S&P 500",     long: "S&P 500 Index",               mkt: "US",  color: "#fbbf24", yahoo: "^GSPC" },
  { key: "ndx",    label: "NASDAQ-100",  long: "NASDAQ-100",                  mkt: "US",  color: "#f59e0b", yahoo: "^NDX" },
  { key: "dji",    label: "Dow Jones",   long: "Dow Jones Industrial",        mkt: "US",  color: "#d97706", yahoo: "^DJI" },
  { key: "n225",   label: "NIKKEI 225",  long: "NIKKEI 225",                  mkt: "JP",  color: "#a78bfa", yahoo: "^N225" },
];

// ════════════════════════════════════════════════════════════════
//  SEED DATA  (from saham.xlsx)
// ════════════════════════════════════════════════════════════════

// ────────────────────────────────────────────────────────────────
//  Pre-IPO / Private Investments
// ────────────────────────────────────────────────────────────────
// Each deal tracks: company, currency, holder (custodian), principal,
// SPV/management fees, carry on profit %, optional FX rate (if funded
// in a different currency), and a chronological list of sub-events
// (refunds, partial exits, full exits, additional capital, notes).
//
// Currency semantics: a deal's `currency` is the underlying investment
// currency — all economics (principal, carry, SPV fee) compute in this
// currency. For deals funded with a different currency (e.g. wiring IDR
// to fund a USD-denominated investment), `initialTransferIDR` and
// `initialFxRate` record the actual IDR cash that left your account.
const INIT_PREIPO = [
  {id:"preipo_seed_IDR_1", company:"Alto Pharmacy", currency:"USD", holder:"Raymond", status:"active", principal:15000.0, spvFeePct:0, spvFeeFlat:0, carryPct:0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2021-12-15", initialTransfer:15000.0, initialTransferIDR:215250000.0, initialFxRate:14350, note:"Alto Pharmacy - Raymond(US$15,000 @Rp 14,350)", currentValuation:null, postMoneyAtEntry:null, events:[{id:"evt_fee",date:"2021-12-16",type:"fee_payment",amount:147.91,note:"1% transfer fee to Yanuar (Rp 2,122,500 @ Rp14,350)"}]},
  {id:"preipo_seed_IDR_2", company:"JoinedApp", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:0, spvFeeFlat:0, carryPct:0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2022-02-22", initialTransfer:2000.0, initialTransferIDR:28732000.0, initialFxRate:14366, note:"JoinedApp - Raymond (US$2,000 @Rp14,366)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_IDR_3", company:"Gapai", currency:"IDR", holder:"Raymond", status:"active", principal:20000000, spvFeePct:0, spvFeeFlat:0, carryPct:0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2023-02-24", initialTransfer:20000000.0, initialTransferIDR:null, initialFxRate:null, note:"Gapai - Raymond (Rp 20Jt, no Fee, no carry)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_IDR_4", company:"Brewbird", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:0, spvFeeFlat:0, carryPct:0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2023-05-30", initialTransfer:2000.0, initialTransferIDR:30000000.0, initialFxRate:15000, note:"Brewbird - Raymond (US$2000 @Rp15,0000)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_IDR_5", company:"Arrowster", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:0, spvFeeFlat:0, carryPct:0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2024-03-08", initialTransfer:2000.0, initialTransferIDR:31214000.0, initialFxRate:15607, note:"Arrowster - Raymond (US$2000 @Rp15,607)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_IDR_7", company:"Retro-Bio", currency:"USD", holder:"Raymond", status:"active", principal:1000.0, spvFeePct:10, spvFeeFlat:0, carryPct:20.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2026-01-20", initialTransfer:1100.0, initialTransferIDR:18663700.0, initialFxRate:16967, note:"Retro-Bio - Raymond (US$1000, SPV $100, Carry 20% @Rp16,967)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_1", company:"CANN", currency:"USD", holder:"Raymond", status:"active", principal:5000.0, spvFeePct:2, spvFeeFlat:0, carryPct:10.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2022-06-28", initialTransfer:5000.0, initialTransferIDR:null, initialFxRate:null, note:"CANN - Raymond (US$5,000 from Comm OZ, include 2% comm, Carry 10%)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_2", company:"PIPE", currency:"USD", holder:"Raymond", status:"active", principal:9600.0, spvFeePct:5, spvFeeFlat:0, carryPct:0.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2022-07-12", initialTransfer:10080.0, initialTransferIDR:null, initialFxRate:null, note:"PIPE - Raymond (US$9,600 + $480 fee from Comm OZ, Carry 0%)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_3", company:"Workstream", currency:"USD", holder:"Raymond", status:"active", principal:9600.0, spvFeePct:2, spvFeeFlat:0, carryPct:20.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2022-08-22", initialTransfer:9600.0, initialTransferIDR:null, initialFxRate:null, note:"Workstream - Raymond (US$9,600 incl 2% fee from Comm Oz, Carry 20%)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_4", company:"Kasa Living", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:2, spvFeeFlat:0, carryPct:0.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2022-11-23", initialTransfer:2040.0, initialTransferIDR:null, initialFxRate:null, note:"Kasa Living - Raymond (US$2,000 + 2% Comm, Carry 0%)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_5", company:"OneNav", currency:"USD", holder:"Raymond", status:"active", principal:10000.0, spvFeePct:2, spvFeeFlat:0, carryPct:20.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2023-03-14", initialTransfer:10000.0, initialTransferIDR:null, initialFxRate:null, note:"OneNav - Raymond (US$10,000 incl 2% fee from Comm Oz, Carry 20%)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_6", company:"Machina Labs", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:2, spvFeeFlat:0, carryPct:0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2023-08-08", initialTransfer:2040.0, initialTransferIDR:null, initialFxRate:null, note:"Machina Labs - Raymond (US$2,000 + 2% comm oz)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_7", company:"OpenAI", currency:"USD", holder:"Raymond", status:"cancelled", principal:2000.0, spvFeePct:0.7, spvFeeFlat:0, carryPct:9.5, mgmtFeePct:1.0, mgmtFeeYears:10, mgmtFeePaidYears:0, initialDate:"2024-04-16", initialTransfer:2000.0, initialTransferIDR:null, initialFxRate:null, note:"OpenAI - Raymond (US$2,000, Carry 9.5%, inclusive SPV Fee 0.7%, Management Fee 1% annualy for 10 years, paid out over 2 years)", currentValuation:null, postMoneyAtEntry:null, events:[{id:"evt_0",date:"2024-07-24",type:"refund",amount:2000.0,note:"Return Transfer (Cancelled)"}]},
  {id:"preipo_seed_USD_8", company:"Anthropic", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:0.3, spvFeeFlat:0, carryPct:20.0, mgmtFeePct:0.4, mgmtFeeYears:10, mgmtFeePaidYears:1, initialDate:"2024-04-16", initialTransfer:2000.0, initialTransferIDR:null, initialFxRate:null, note:"Anthropic - Raymond (US$2,000 + Carry 20%, Inclusive SPV Fee 0.3%, Management fee 0.4% annually for 10 years, paid out over 1 year). Entry $20B (per deal docs), current $965B (Series H May 2026).", currentValuation:965000000000, postMoneyAtEntry:20000000000, events:[]},
  {id:"preipo_seed_USD_9", company:"Pendulum Global", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:5, spvFeeFlat:0, carryPct:10.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2024-06-07", initialTransfer:2100.0, initialTransferIDR:null, initialFxRate:null, note:"Pendulum Global - Raymond (US$2,000 +5% Comm, Carry 10%) - Comm Inclusive, kelebihan transfer", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_10", company:"Groq", currency:"USD", holder:"Raymond", status:"partial_exit", principal:2000.0, spvFeePct:10, spvFeeFlat:0, carryPct:15.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2024-06-16", initialTransfer:1905.6, initialTransferIDR:null, initialFxRate:null, note:"Groq - Raymond (US$2000 + 10% Comm inclusive, Carry 15%). Entry ~$2.8B (Aug 2024), current $6.9B (Sep 2025).", currentValuation:6900000000, postMoneyAtEntry:2800000000, events:[{id:"evt_0",date:"2026-03-03",type:"partial_exit",amount:4520.0,note:"Return Transfer 1 (partial)"}]},
  {id:"preipo_seed_USD_11", company:"Upcodes", currency:"USD", holder:"Raymond", status:"active", principal:1000.0, spvFeePct:5, spvFeeFlat:0, carryPct:20.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2024-08-24", initialTransfer:950.0, initialTransferIDR:null, initialFxRate:null, note:"Upcodes - Raymond (US$1000 - 5% Fee, Carry 20%)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_13", company:"Anduril (v1)", currency:"USD", holder:"Raymond", status:"cancelled", principal:2000.0, spvFeePct:7.5, spvFeeFlat:0, carryPct:17.5, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2024-12-19", initialTransfer:2000.0, initialTransferIDR:null, initialFxRate:null, note:"Anduril - Raymond (US$2000 - 7.5% SPV Fee, Carry 17.5%) - Cancelled", currentValuation:null, postMoneyAtEntry:null, events:[{id:"evt_0",date:"2025-02-04",type:"refund",amount:2000.0,note:"Return Transfer - Cancel"}]},
  {id:"preipo_seed_USD_14", company:"Anduril (v2)", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:14.5, spvFeeFlat:0, carryPct:17.5, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2025-02-23", initialTransfer:2000.0, initialTransferIDR:null, initialFxRate:null, note:"Anduril - Raymond ($2000 - 14.5% Fee, carry 17.5%). Entry ~$14B (Aug 2024 Series F era), current $61B (Series H May 2026).", currentValuation:61000000000, postMoneyAtEntry:14000000000, events:[]},
  {id:"preipo_seed_USD_15", company:"FIZZ", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:2.5, spvFeeFlat:0, carryPct:20.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2025-05-06", initialTransfer:2000.0, initialTransferIDR:null, initialFxRate:null, note:"FIZZ - Raymond ($2000 - fee 2.5% deducted from fund, carry 20%, post money valuation $60M)", currentValuation:null, postMoneyAtEntry:60000000, events:[]},
  {id:"preipo_seed_USD_16", company:"Erebor Bank", currency:"USD", holder:"Raymond", status:"failed_allocation", principal:1000.0, spvFeePct:2, spvFeeFlat:0, carryPct:20, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2025-07-17", initialTransfer:1020.0, initialTransferIDR:null, initialFxRate:null, note:"Erebor Bank - Raymond ($1000 - 2% Spv Fee, Carry on Profit 20%) - Fail to get, fund moved to Cambium", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_17", company:"Polymarket", currency:"USD", holder:"Raymond", status:"cancelled", principal:1000.0, spvFeePct:10.0, spvFeeFlat:0, carryPct:20.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2025-07-17", initialTransfer:1100.0, initialTransferIDR:null, initialFxRate:null, note:"Polymarket - Raymond ($1000 - Spv Fee 10%, Carry on profit 20%)", currentValuation:null, postMoneyAtEntry:null, events:[{id:"evt_0",date:"2025-08-22",type:"refund",amount:1100.0,note:"Return Transfer - cancelled application"}]},
  {id:"preipo_seed_USD_18", company:"Honest Repair", currency:"USD", holder:"Raymond", status:"failed_allocation", principal:500.0, spvFeePct:0, spvFeeFlat:0, carryPct:0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2025-08-31", initialTransfer:500.0, initialTransferIDR:null, initialFxRate:null, note:"Honest Repair ($500, Direct cap table, no fee, no carry) - Cancelled, moved to Jetzero", currentValuation:null, postMoneyAtEntry:null, events:[{id:"evt_0",date:"2025-09-23",type:"refund",amount:500.0,note:"Moved Fund to Jetzero"}]},
  {id:"preipo_seed_USD_19", company:"Jetzero", currency:"USD", holder:"Raymond", status:"active", principal:1000.0, spvFeePct:10, spvFeeFlat:0, carryPct:20.0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2025-09-23", initialTransfer:1100.0, initialTransferIDR:null, initialFxRate:null, note:"Jetzero ($1000, SPV 10%, Carry on profit 20%) - funded $500 fresh + $500 from Honest Repair", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_20", company:"Cambium", currency:"USD", holder:"Raymond", status:"active", principal:1000.0, spvFeePct:2, spvFeeFlat:0, carryPct:0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2025-11-17", initialTransfer:1020.0, initialTransferIDR:null, initialFxRate:null, note:"Cambium - Raymond ($1000 - 2% Spv fee) - funded from Erebor Bank", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_21", company:"Axiom Math", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:10, spvFeeFlat:0, carryPct:20, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2026-02-12", initialTransfer:2200.0, initialTransferIDR:null, initialFxRate:null, note:"Axiom Math - Raymond ($2000 - 10% Spv fee, Carry on Profit 20%). Entry ~$1.6B (Series A Feb/Mar 2026, Menlo-led); seed was $300M Oct 2025 — verify which round your allocation came from.", currentValuation:1600000000, postMoneyAtEntry:1600000000, events:[]},
  {id:"preipo_seed_USD_22", company:"Piris Labs", currency:"USD", holder:"Raymond", status:"active", principal:1000.0, spvFeePct:5, spvFeeFlat:0, carryPct:20, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2026-04-14", initialTransfer:1050.0, initialTransferIDR:null, initialFxRate:null, note:"Piris Labs - Raymond ($1000 - 5% Spv fee, Carry on Profit 20%)", currentValuation:null, postMoneyAtEntry:null, events:[{id:"evt_0",date:"2026-05-15",type:"refund",amount:11.02,note:"Refund coz oversubscribed, reduce investment"}]},
  {id:"preipo_seed_USD_23", company:"Starcloud", currency:"USD", holder:"Raymond", status:"active", principal:500.0, spvFeePct:10, spvFeeFlat:0, carryPct:20, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2026-05-19", initialTransfer:550.0, initialTransferIDR:null, initialFxRate:null, note:"Starcloud - Raymond ($500 - 10% Spv fee, Carry on Profit 20%). Entry ~$1.1B (Series A Mar 2026), current $1.1B (no newer round yet).", currentValuation:1100000000, postMoneyAtEntry:1100000000, events:[]},
  {id:"preipo_seed_USD_24", company:"Carbon", currency:"USD", holder:"Victor", status:"active", principal:10000.0, spvFeePct:0, spvFeeFlat:0, carryPct:0, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2026-05-21", initialTransfer:10000.0, initialTransferIDR:null, initialFxRate:17687, note:"Carbon - under Victor's name (Raymond's brother)", currentValuation:null, postMoneyAtEntry:null, events:[]},
  {id:"preipo_seed_USD_25", company:"Volantis", currency:"USD", holder:"Raymond", status:"active", principal:2000.0, spvFeePct:5, spvFeeFlat:0, carryPct:20, mgmtFeePct:0, mgmtFeeYears:0, mgmtFeePaidYears:0, initialDate:"2026-05-28", initialTransfer:2100.0, initialTransferIDR:null, initialFxRate:null, note:"Volantis - Raymond ($2000, SPV Fee 5%, Carry 20%)", currentValuation:null, postMoneyAtEntry:null, events:[]},
];

// Pre-IPO computation helpers. Pure functions over a single deal.
const preipoStatusOptions = ["active", "exited", "partial_exit", "cancelled", "failed_allocation"];
const preipoStatusLabels = {
  active: "Active",
  exited: "Fully Exited",
  partial_exit: "Partial Exit",
  cancelled: "Cancelled",
  failed_allocation: "Failed Allocation",
};
const preipoStatusColors = {
  active: "#60a5fa",
  exited: "#34d399",
  partial_exit: "#a78bfa",
  cancelled: "#6b7280",
  failed_allocation: "#fbbf24",
};
// Cost basis = initial transfer − refunds. The actual cash you have at risk.
const dealCostBasis = (d) => {
  // Cancelled or failed allocations: the money was returned, so nothing is at risk.
  // Cost basis is zero regardless of what was transferred.
  if (d.status === "cancelled" || d.status === "failed_allocation") return 0;
  const refunds = (d.events || []).filter(e => e.type === "refund").reduce((s, e) => s + (+e.amount || 0), 0);
  const additional = (d.events || []).filter(e => e.type === "additional_capital").reduce((s, e) => s + (+e.amount || 0), 0);
  const fees = (d.events || []).filter(e => e.type === "fee_payment").reduce((s, e) => s + (+e.amount || 0), 0);
  return Math.max(0, (+d.initialTransfer || 0) + additional + fees - refunds);
};
// Total exit proceeds (partial_exit + exit events). These are NET amounts the
// user actually received (carry, SPV, and management fees already deducted by
// the SPV operator before the money hits the user's account).
const dealProceeds = (d) => (d.events || []).filter(e => e.type === "partial_exit" || e.type === "exit").reduce((s, e) => s + (+e.amount || 0), 0);
// Carry that the SPV operator WOULD have taken on the gross gain — informational
// only, since user-entered proceeds are already net of it. Helps the user see
// roughly how much carry was skimmed. Computed on gross gain vs initial transfer.
const dealCarryInfo = (d) => {
  const proceeds = dealProceeds(d);
  const principal = (+d.initialTransfer || 0);
  // Gross-up: if proceeds are net of carry, implied gross gain = netGain / (1 - carry%).
  // But we don't know gross for certain, so show carry as an estimate on the net gain.
  const netGain = proceeds - principal;
  if (netGain <= 0) return 0;
  const c = (+d.carryPct || 0) / 100;
  if (c <= 0 || c >= 1) return 0;
  // implied gross gain G where netGain = G − G*c  → G = netGain / (1 − c); carry = G*c
  const grossGain = netGain / (1 - c);
  return grossGain * c;
};
// Management fee accrued so far (annual % × years × initial transfer) — informational.
const dealMgmtFeeAccrued = (d) => {
  const years = +d.mgmtFeeYears || 0;
  if (years === 0) return 0;
  const paid = Math.min(+d.mgmtFeePaidYears || 0, years);
  return paid * ((+d.mgmtFeePct || 0) / 100) * (+d.initialTransfer || 0);
};
// Net P&L = proceeds − cost basis. Proceeds are ALREADY NET of carry/SPV/mgmt,
// so we do NOT subtract them again. This is the user's actual realized gain/loss.
const dealNetPnl = (d) => dealProceeds(d) - dealCostBasis(d);
// Unrealized P&L based on currentValuation (manual input). Compares the implied
// current value of the user's stake to cost basis. Returns null if no valuation set.
const dealUnrealized = (d) => {
  if (d.currentValuation == null || d.postMoneyAtEntry == null) return null;
  const myShare = (+d.initialTransfer || 0) / (+d.postMoneyAtEntry || 1);
  const currentValue = myShare * (+d.currentValuation || 0);
  const gain = currentValue - dealCostBasis(d);
  // Carry that would be owed at exit on this unrealized gain (informational)
  const carryOwed = gain > 0 ? gain * ((+d.carryPct || 0) / 100) : 0;
  return { currentValue, gain, carryOwed, netGain: gain - carryOwed, myShare,
           valuationMultiple: d.postMoneyAtEntry > 0 ? (+d.currentValuation || 0) / (+d.postMoneyAtEntry) : null };
};

const INIT_BUYS = [
  // Rob - Stockbit 2026
  {id:"b1",  mkt:"IDX", inv:"Robert",    brk:"Stockbit",            date:"2026-02-03", stk:"BUMI", qty:100, px:264,    fee:3960},
  {id:"b2",  mkt:"IDX", inv:"Robert",    brk:"Stockbit",            date:"2026-02-04", stk:"BUMI", qty:100, px:246,    fee:3690},
  {id:"b3",  mkt:"IDX", inv:"Robert",    brk:"Stockbit",            date:"2026-02-11", stk:"CDIA", qty:50,  px:1210,   fee:9075},
  {id:"b4",  mkt:"IDX", inv:"Robert",    brk:"Stockbit",            date:"2026-02-13", stk:"CDIA", qty:50,  px:1140,   fee:8550},
  {id:"b5",  mkt:"IDX", inv:"Robert",    brk:"Stockbit",            date:"2026-02-23", stk:"INET", qty:200, px:414,    fee:12420},
  {id:"b6",  mkt:"IDX", inv:"Robert",    brk:"Stockbit",            date:"2026-03-25", stk:"BBNI", qty:25,  px:4050,   fee:15187.5},
  // IBKR Robert - MGX
  {id:"b7",  mkt:"US",  inv:"Robert",    brk:"Interactive Brokers", date:"2025-11-21", stk:"MGX",  qty:300, px:1.56,   fee:1.5066},
  {id:"b8",  mkt:"US",  inv:"Robert",    brk:"Interactive Brokers", date:"2025-11-21", stk:"MGX",  qty:300, px:1.52,   fee:1.5066},
  {id:"b9",  mkt:"US",  inv:"Robert",    brk:"Interactive Brokers", date:"2025-11-25", stk:"MGX",  qty:600, px:1.62,   fee:3.0132},
  {id:"b10", mkt:"US",  inv:"Robert",    brk:"Interactive Brokers", date:"2026-03-17", stk:"MGX",  qty:300, px:1.45,   fee:1.5},
  // IBKR Elizabeth
  {id:"b11", mkt:"US",  inv:"Elizabeth", brk:"Interactive Brokers", date:"2026-03-23", stk:"VOOG", qty:1,   px:422.00, fee:1},
  {id:"b12", mkt:"US",  inv:"Elizabeth", brk:"Interactive Brokers", date:"2026-03-25", stk:"VOO",  qty:3,   px:605.00, fee:1},
  {id:"b13", mkt:"US",  inv:"Elizabeth", brk:"Interactive Brokers", date:"2026-03-26", stk:"VOOG", qty:4,   px:411.41, fee:1},
  {id:"b14", mkt:"US",  inv:"Elizabeth", brk:"Interactive Brokers", date:"2026-03-26", stk:"VOO",  qty:4,   px:595.00, fee:1},
];

const INIT_SELLS = [
  // INET partial sell same day
  {id:"s1", mkt:"IDX", inv:"Robert",    brk:"Stockbit",            date:"2026-02-23", stk:"INET", qty:100, px:422,  fee:10550},
  // MGX partial sell FIFO from b7
  {id:"s2", mkt:"US",  inv:"Robert",    brk:"Interactive Brokers", date:"2026-03-09", stk:"MGX",  qty:300, px:1.80, fee:1.56},
];

const INIT_MONTHLY = [
  {id:"m1", period:"2022-03", ihsg:7071.44, sp500:null, brk:"Semesta",     bal:1118072000, fd:0},
  {id:"m2", period:"2022-03", ihsg:7071.44, sp500:null, brk:"Mirae Asset", bal:621617321, fd:0},
  {id:"m3", period:"2022-04", ihsg:7228.91, sp500:null, brk:"Semesta",     bal:990508000, fd:44042923},
  {id:"m4", period:"2022-04", ihsg:7228.91, sp500:null, brk:"Mirae Asset", bal:531039949, fd:8000000},
  {id:"m5", period:"2022-05", ihsg:7148.9, sp500:null, brk:"Semesta",     bal:786572000, fd:-108772750},
  {id:"m6", period:"2022-05", ihsg:7148.9, sp500:null, brk:"Mirae Asset", bal:485793548, fd:7000000},
  {id:"m7", period:"2022-06", ihsg:6911.58, sp500:null, brk:"Semesta",     bal:721323201, fd:0},
  {id:"m8", period:"2022-06", ihsg:6911.58, sp500:null, brk:"Mirae Asset", bal:259679000, fd:-125000000},
  {id:"m9", period:"2022-07", ihsg:6951.12, sp500:null, brk:"Semesta",     bal:798037028, fd:0},
  {id:"m10", period:"2022-07", ihsg:6951.12, sp500:null, brk:"Mirae Asset", bal:285372634, fd:0},
  {id:"m11", period:"2022-08", ihsg:7178.59, sp500:null, brk:"Semesta",     bal:767256599, fd:0},
  {id:"m12", period:"2022-08", ihsg:7178.59, sp500:null, brk:"Mirae Asset", bal:282292834, fd:13000000},
  {id:"m13", period:"2022-09", ihsg:7040.8, sp500:null, brk:"Semesta",     bal:444136902, fd:-255200000},
  {id:"m14", period:"2022-09", ihsg:7040.8, sp500:null, brk:"Mirae Asset", bal:246855609, fd:0},
  {id:"m15", period:"2022-10", ihsg:7098.89, sp500:null, brk:"Semesta",     bal:496014301, fd:63000000},
  {id:"m16", period:"2022-10", ihsg:7098.89, sp500:null, brk:"Mirae Asset", bal:275441984, fd:43500000},
  {id:"m17", period:"2022-11", ihsg:7081.31, sp500:null, brk:"Semesta",     bal:382340000, fd:-95000000},
  {id:"m18", period:"2022-11", ihsg:7081.31, sp500:null, brk:"Mirae Asset", bal:246871974, fd:-30000000},
  {id:"m19", period:"2022-12", ihsg:6850.62, sp500:null, brk:"Semesta",     bal:336499500, fd:8500000},
  {id:"m20", period:"2022-12", ihsg:6850.62, sp500:null, brk:"Mirae Asset", bal:211761509, fd:0},
  {id:"m21", period:"2023-01", ihsg:6839.34, sp500:null, brk:"Semesta",     bal:323694000, fd:1000000},
  {id:"m22", period:"2023-01", ihsg:6839.34, sp500:null, brk:"Mirae Asset", bal:292584520, fd:115000000},
  {id:"m23", period:"2023-02", ihsg:6843.24, sp500:null, brk:"Semesta",     bal:351606500, fd:36500000},
  {id:"m24", period:"2023-02", ihsg:6843.24, sp500:null, brk:"Mirae Asset", bal:201897364, fd:-110000000},
  {id:"m25", period:"2023-03", ihsg:6805.28, sp500:null, brk:"Semesta",     bal:318390451, fd:0},
  {id:"m26", period:"2023-03", ihsg:6805.28, sp500:null, brk:"Mirae Asset", bal:180778800, fd:17000000},
  {id:"m27", period:"2023-04", ihsg:6915.72, sp500:null, brk:"Semesta",     bal:339826206, fd:32210000},
  {id:"m28", period:"2023-04", ihsg:6915.72, sp500:null, brk:"Mirae Asset", bal:186187629, fd:-7000000},
  {id:"m29", period:"2023-05", ihsg:6633.26, sp500:null, brk:"Semesta",     bal:337874376, fd:37000000},
  {id:"m30", period:"2023-05", ihsg:6633.26, sp500:null, brk:"Mirae Asset", bal:170528801, fd:-12500000},
  {id:"m31", period:"2023-06", ihsg:6661.88, sp500:null, brk:"Semesta",     bal:348302084, fd:0},
  {id:"m32", period:"2023-06", ihsg:6661.88, sp500:null, brk:"Mirae Asset", bal:182518483, fd:0},
  {id:"m33", period:"2023-07", ihsg:6931.36, sp500:null, brk:"Semesta",     bal:324816684, fd:-40000000},
  {id:"m34", period:"2023-07", ihsg:6931.36, sp500:null, brk:"Mirae Asset", bal:193115582, fd:0},
  {id:"m35", period:"2023-08", ihsg:6953.26, sp500:null, brk:"Semesta",     bal:345151184, fd:20000000},
  {id:"m36", period:"2023-08", ihsg:6953.26, sp500:null, brk:"Mirae Asset", bal:193328633, fd:0},
  {id:"m37", period:"2023-09", ihsg:6939.89, sp500:null, brk:"Semesta",     bal:345179810, fd:0},
  {id:"m38", period:"2023-09", ihsg:6939.89, sp500:null, brk:"Mirae Asset", bal:188576659, fd:0},
  {id:"m39", period:"2023-10", ihsg:6752.21, sp500:null, brk:"Semesta",     bal:223480520, fd:-85000000},
  {id:"m40", period:"2023-10", ihsg:6752.21, sp500:null, brk:"Mirae Asset", bal:167498544, fd:-35000000},
  {id:"m41", period:"2023-11", ihsg:7080.74, sp500:null, brk:"Semesta",     bal:236621520, fd:0},
  {id:"m42", period:"2023-11", ihsg:7080.74, sp500:null, brk:"Mirae Asset", bal:177653844, fd:0},
  {id:"m43", period:"2023-12", ihsg:7272.8, sp500:null, brk:"Semesta",     bal:241277720, fd:0},
  {id:"m44", period:"2023-12", ihsg:7272.8, sp500:null, brk:"Mirae Asset", bal:174054382, fd:0},
  {id:"m45", period:"2024-01", ihsg:7238.79, sp500:null, brk:"Semesta",     bal:225582920, fd:0},
  {id:"m46", period:"2024-01", ihsg:7238.79, sp500:null, brk:"Mirae Asset", bal:171206281, fd:0},
  {id:"m47", period:"2024-02", ihsg:7316.11, sp500:null, brk:"Semesta",     bal:216034920, fd:0},
  {id:"m48", period:"2024-02", ihsg:7316.11, sp500:null, brk:"Mirae Asset", bal:203546104, fd:33000000},
  {id:"m49", period:"2024-03", ihsg:7288.81, sp500:null, brk:"Semesta",     bal:225574755, fd:10000000},
  {id:"m50", period:"2024-03", ihsg:7288.81, sp500:null, brk:"Mirae Asset", bal:244390369, fd:10000000},
  {id:"m51", period:"2024-04", ihsg:7234.2, sp500:null, brk:"Semesta",     bal:242527195, fd:26500000},
  {id:"m52", period:"2024-04", ihsg:7234.2, sp500:null, brk:"Mirae Asset", bal:237359707, fd:35500000},
  {id:"m53", period:"2024-05", ihsg:6970.74, sp500:null, brk:"Semesta",     bal:317504522, fd:92500000},
  {id:"m54", period:"2024-05", ihsg:6970.74, sp500:null, brk:"Mirae Asset", bal:218218374, fd:-2000000},
  {id:"m55", period:"2024-06", ihsg:7063.58, sp500:null, brk:"Semesta",     bal:393867735, fd:67000000},
  {id:"m56", period:"2024-06", ihsg:7063.58, sp500:null, brk:"Mirae Asset", bal:213402447, fd:0},
  {id:"m57", period:"2024-07", ihsg:7255.76, sp500:null, brk:"Semesta",     bal:417850035, fd:6000000},
  {id:"m58", period:"2024-07", ihsg:7255.76, sp500:null, brk:"Mirae Asset", bal:250863443, fd:0},
  {id:"m59", period:"2024-08", ihsg:7670.73, sp500:null, brk:"Semesta",     bal:617463854, fd:57750000},
  {id:"m60", period:"2024-08", ihsg:7670.73, sp500:null, brk:"Mirae Asset", bal:338103161, fd:10000000},
  {id:"m61", period:"2024-09", ihsg:7527.93, sp500:null, brk:"Semesta",     bal:532554141, fd:0},
  {id:"m62", period:"2024-09", ihsg:7527.93, sp500:null, brk:"Mirae Asset", bal:304479047, fd:9500000},
  {id:"m63", period:"2024-10", ihsg:7574.02, sp500:null, brk:"Semesta",     bal:547354692, fd:46850000},
  {id:"m64", period:"2024-10", ihsg:7574.02, sp500:null, brk:"Mirae Asset", bal:325355702, fd:21000000},
  {id:"m65", period:"2024-11", ihsg:7114.27, sp500:null, brk:"Semesta",     bal:562146979, fd:42600000},
  {id:"m66", period:"2024-11", ihsg:7114.27, sp500:null, brk:"Mirae Asset", bal:324780470, fd:28000000},
  {id:"m67", period:"2024-12", ihsg:7079.9, sp500:null, brk:"Semesta",     bal:569898862, fd:42050000},
  {id:"m68", period:"2024-12", ihsg:7079.9, sp500:null, brk:"Mirae Asset", bal:306161072, fd:-6000000},
  {id:"m69", period:"2025-01", ihsg:7109.2, sp500:null, brk:"Semesta",     bal:597511406, fd:9288000},
  {id:"m70", period:"2025-01", ihsg:7109.2, sp500:null, brk:"Mirae Asset", bal:427949383, fd:113000000},
  {id:"m71", period:"2025-02", ihsg:6270.6, sp500:null, brk:"Semesta",     bal:677118270, fd:176775000},
  {id:"m72", period:"2025-02", ihsg:6270.6, sp500:null, brk:"Mirae Asset", bal:437750805, fd:59000000},
  {id:"m73", period:"2025-03", ihsg:6510.62, sp500:null, brk:"Semesta",     bal:792495895, fd:37970000},
  {id:"m74", period:"2025-03", ihsg:6510.62, sp500:null, brk:"Mirae Asset", bal:362940817, fd:-72000000},
  {id:"m75", period:"2025-04", ihsg:6766.8, sp500:null, brk:"Semesta",     bal:891986535, fd:37500000},
  {id:"m76", period:"2025-04", ihsg:6766.8, sp500:null, brk:"Mirae Asset", bal:471864060, fd:60000000},
  {id:"m77", period:"2025-05", ihsg:7175.82, sp500:null, brk:"Semesta",     bal:941538235, fd:0},
  {id:"m78", period:"2025-05", ihsg:7175.82, sp500:null, brk:"Mirae Asset", bal:489378973, fd:36000000},
  {id:"m79", period:"2025-06", ihsg:6927.68, sp500:null, brk:"Semesta",     bal:874335260, fd:38500000},
  {id:"m80", period:"2025-06", ihsg:6927.68, sp500:null, brk:"Mirae Asset", bal:465258836, fd:36000000},
  {id:"m81", period:"2025-07", ihsg:7484.34, sp500:null, brk:"Semesta",     bal:942651021, fd:90000000},
  {id:"m82", period:"2025-07", ihsg:7484.34, sp500:null, brk:"Mirae Asset", bal:480910226, fd:-20500000},
  {id:"m83", period:"2025-08", ihsg:7830.49, sp500:null, brk:"Semesta",     bal:1016730021, fd:0},
  {id:"m84", period:"2025-08", ihsg:7830.49, sp500:null, brk:"Mirae Asset", bal:536675901, fd:14000000},
  {id:"m85", period:"2025-09", ihsg:8061.62, sp500:null, brk:"Semesta",     bal:1050845746, fd:73000000},
  {id:"m86", period:"2025-09", ihsg:8061.62, sp500:null, brk:"Mirae Asset", bal:464403452, fd:-61000000},
  {id:"m87", period:"2025-10", ihsg:8163.88, sp500:null, brk:"Semesta",     bal:1089239346, fd:0},
  {id:"m88", period:"2025-10", ihsg:8163.88, sp500:null, brk:"Mirae Asset", bal:462826991, fd:0},
  {id:"m89", period:"2025-11", ihsg:8508.71, sp500:null, brk:"Semesta",     bal:1052751946, fd:0},
  {id:"m90", period:"2025-11", ihsg:8508.71, sp500:null, brk:"Mirae Asset", bal:460897402, fd:0},
  {id:"m91", period:"2025-12", ihsg:8646.93, sp500:null, brk:"Semesta",     bal:1048439526, fd:0},
  {id:"m92", period:"2025-12", ihsg:8646.93, sp500:null, brk:"Mirae Asset", bal:481949547, fd:14000000},
  {id:"m93", period:"2026-01", ihsg:8329.6, sp500:null, brk:"Semesta",     bal:1052305290, fd:59000000},
  {id:"m94", period:"2026-01", ihsg:8329.6, sp500:null, brk:"Mirae Asset", bal:462983827, fd:0},
  {id:"m95", period:"2026-01", ihsg:8329.6, sp500:null, brk:"Stockbit",    bal:25000000, fd:30000000},
  {id:"m96", period:"2026-02", ihsg:8235.49, sp500:null, brk:"Semesta",     bal:1043614397, fd:-39000000},
  {id:"m97", period:"2026-02", ihsg:8235.49, sp500:null, brk:"Mirae Asset", bal:426322641, fd:-43000000},
  {id:"m98", period:"2026-02", ihsg:8235.49, sp500:null, brk:"Stockbit",    bal:38105577, fd:15000000},
  {id:"m99", period:"2026-03", ihsg:7048.22, sp500:null, brk:"Semesta",     bal:980169572, fd:66500000},
  {id:"m100", period:"2026-03", ihsg:7048.22, sp500:null, brk:"Mirae Asset", bal:321205306, fd:-63000000},
  {id:"m101", period:"2026-03", ihsg:7048.22, sp500:null, brk:"Stockbit",    bal:36840586, fd:6000000},
  {id:"m102", period:"2026-04", ihsg:6956.80, sp500:null, brk:"_index",     bal:null, fd:null},
];

// ════════════════════════════════════════════════════════════════
//  FIFO ENGINE
//  Per investor+broker+stock queue; lot = IDX lot (100 shares) or US share
// ════════════════════════════════════════════════════════════════

// Apply stock splits to buys/sells. Trades BEFORE a split.date for the same stk+mkt
// get qty multiplied by ratio and price divided by ratio (total cost preserved).
// For a forward 2:1 split, ratio = 2 (doubles shares, halves price).
// For a reverse 1:10 split, ratio = 0.1 (one-tenth shares, ten-times price).
// Fees are NOT adjusted because they were real cash amounts paid in the past.
// If multiple splits exist for same stock, they compound in chronological order.
function applySplits(trades, splits) {
  if (!splits?.length) return trades;
  const sortedSplits = [...splits].sort((a, b) => a.date.localeCompare(b.date));
  return trades.map(t => {
    // Find all splits after this trade for same stock+market
    const applicable = sortedSplits.filter(s =>
      s.stk === t.stk && s.mkt === t.mkt && s.date > t.date
    );
    if (!applicable.length) return t;
    // Compound the ratios
    const cumRatio = applicable.reduce((r, s) => r * (s.ratio || 1), 1);
    if (cumRatio === 1) return t;
    return {
      ...t,
      qty: t.qty * cumRatio,
      px:  t.px  / cumRatio,
      _splitAdjusted: true,
      _cumRatio: cumRatio,
      _originalQty: t.qty,
      _originalPx:  t.px,
    };
  });
}

function runFIFO(buys, sells) {
  // ── Hybrid engine ──────────────────────────────────────────────
  // Trades can be either:
  //   (a) PAIRED — buy has pairedSellId, sell has pairedBuyId.
  //       The sell is locked to its paired buy. FIFO never touches either.
  //       Typical for day-trades or quick swings imported from Excel rows
  //       where the same row has both a buy date and a sell date.
  //   (b) UNPAIRED — no pairing. Buys accumulate in a FIFO queue;
  //       sells consume from the queue in chronological order.
  //
  // This matches Excel's per-row accounting for quick trades while still
  // using FIFO cost basis for long holds sold across multiple tranches.

  const results = [];

  // Pass 1: Handle PAIRED trades first — sell gets its cost basis from its paired buy.
  // Build a lookup so sell can find its buy by id.
  const buyById = {};
  buys.forEach(b => { buyById[b.id] = b; });

  const pairedSellBuyIds = new Set();  // buys that have been paired → skip in FIFO
  const pairedSells      = new Set();  // sells that have been paired → skip in FIFO

  sells.forEach(sell => {
    if (!sell.pairedBuyId) return;
    const buy = buyById[sell.pairedBuyId];
    if (!buy) {
      // Paired buy missing (maybe deleted). Fall back to FIFO for this sell.
      return;
    }
    pairedSellBuyIds.add(buy.id);
    pairedSells.add(sell.id);

    // Compute P&L from this specific buy alone — Excel-style row matching.
    const sellShares = sell.mkt === "IDX" ? sell.qty * 100 : sell.qty;
    const buyShares  = sell.mkt === "IDX" ? buy.qty  * 100 : buy.qty;
    const grossRevenue   = sellShares * sell.px;
    const netRevenue     = grossRevenue - (sell.fee || 0);
    const buyCost        = buyShares * buy.px + (buy.fee || 0);
    // For partial pairs (sold qty < bought qty), pro-rate cost basis by qty sold
    const costBasisUsed  = buyCost * (sellShares / buyShares);
    const profit = netRevenue - costBasisUsed;
    const pct    = costBasisUsed > 0 ? profit / costBasisUsed * 100 : 0;
    const days   = Math.round((new Date(sell.date) - new Date(buy.date)) / 86400000);

    results.push({
      ...sell,
      sellShares,
      grossRevenue,
      netRevenue,
      totalCostBasis: costBasisUsed,
      profit, pct, days,
      matched: [{ buyId: buy.id, buyDate: buy.date, buyPx: buy.px, used: sell.qty, costBasis: costBasisUsed }],
      _paired: true,
    });
  });

  // Pass 2: FIFO for the remaining (unpaired) buys + sells.
  // Any buy used in pass 1 is marked consumed (so it doesn't also show as open).
  const queues = {};
  const unpairedBuys = buys.filter(b => !pairedSellBuyIds.has(b.id));
  const sortedBuys   = [...unpairedBuys].sort((a, b) => a.date.localeCompare(b.date));
  sortedBuys.forEach(b => {
    const k = `${b.inv}::${b.brk}::${b.stk}`;
    if (!queues[k]) queues[k] = [];
    const shares = b.mkt === "IDX" ? b.qty * 100 : b.qty;
    const totalCost = shares * b.px + (b.fee || 0);
    const costPerShare = totalCost / shares;
    queues[k].push({ ...b, shares, totalCost, costPerShare, remaining: b.qty });
  });

  const unpairedSells = sells.filter(s => !pairedSells.has(s.id));
  const sellsSorted   = [...unpairedSells].sort((a, b) => a.date.localeCompare(b.date));

  sellsSorted.forEach(sell => {
    const k = `${sell.inv}::${sell.brk}::${sell.stk}`;
    const q = queues[k] || [];
    const sellShares = sell.mkt === "IDX" ? sell.qty * 100 : sell.qty;
    const grossRevenue = sellShares * sell.px;
    const netRevenue = grossRevenue - (sell.fee || 0);

    let lotsLeft = sell.qty;
    let totalCostBasis = 0;
    const matched = [];

    for (let i = 0; i < q.length && lotsLeft > 0; i++) {
      if (q[i].remaining <= 0) continue;
      const used = Math.min(q[i].remaining, lotsLeft);
      const usedShares = sell.mkt === "IDX" ? used * 100 : used;
      const costBasis = usedShares * q[i].costPerShare;
      totalCostBasis += costBasis;
      matched.push({
        buyId: q[i].id, buyDate: q[i].date,
        buyPx: q[i].px, used, costBasis,
      });
      q[i].remaining -= used;
      lotsLeft -= used;
    }

    const profit = netRevenue - totalCostBasis;
    const pct = totalCostBasis > 0 ? profit / totalCostBasis * 100 : 0;
    const days = matched[0] ? Math.round((new Date(sell.date) - new Date(matched[0].buyDate)) / 86400000) : 0;

    results.push({ ...sell, sellShares, grossRevenue, netRevenue, totalCostBasis, profit, pct, days, matched });
  });

  // Sort combined results by sell date for consistent ordering
  results.sort((a, b) => a.date.localeCompare(b.date));

  // Open lots remaining (only from FIFO queue — paired buys are fully consumed by their paired sells)
  const open = [];
  Object.values(queues).forEach(q => {
    q.forEach(lot => {
      if (lot.remaining > 0) {
        const units = lot.mkt === "IDX" ? lot.remaining * 100 : lot.remaining;
        const costPerUnit = lot.costPerShare;
        const totalCost = units * costPerUnit;
        open.push({ ...lot, qty: lot.remaining, units, costPerUnit, totalCost });
      }
    });
  });

  return { results, open };
}

// ── Broker-to-broker stock transfers ─────────────────────────────
// A transfer {id, mkt, inv, stk, qty, date, fromBrk, toBrk, fee?, note?} moves shares
// between two brokers of the SAME investor. It's expanded into two synthetic legs:
//   out-leg: a "sell" at fromBrk priced at the FIFO cost of the lots it consumes
//   in-leg:  a "buy"  at toBrk at that same cost per share, zero fee
// so realized P&L is exactly zero and cost basis carries over. Legs are flagged `_xfer`
// ("out" | "in"): consumers must NOT move cash for them (only `_xferFee`, which rides on the
// IN-leg because the RECEIVING broker charges the transfer — confirmed by owner 2026-09-25),
// and must hide out-legs from realized-P&L lists. Legs are never stored — transfers are.
// Returns issues[] for transfers that exceed the lots available at the source.
function expandTransfers(buys, sells, transfers) {
  const legBuys = [], legSells = [], issues = [];
  if (!transfers?.length) return { legBuys, legSells, issues };
  const sorted = [...transfers].sort((a, b) => a.date.localeCompare(b.date) || String(a.id).localeCompare(String(b.id)));
  for (const t of sorted) {
    const out = {
      id: `xo_${t.id}`, mkt: t.mkt, inv: t.inv, brk: t.fromBrk, date: t.date, stk: t.stk,
      qty: +t.qty, px: 0, fee: 0, _xfer: "out", _xferId: t.id,
    };
    // Resolve which lots the out-leg consumes, given every earlier transfer's legs.
    const { results } = runFIFO([...buys, ...legBuys], [...sells, ...legSells, out]);
    const r = results.find(x => x.id === out.id);
    const usedQty = r ? r.matched.reduce((s, m) => s + m.used, 0) : 0;
    const usedShares = t.mkt === "IDX" ? usedQty * 100 : usedQty;
    const costPer = usedShares > 0 ? r.totalCostBasis / usedShares : 0;
    if (usedQty < +t.qty - 1e-9) issues.push({ id: t.id, available: usedQty });
    out.px = costPer;
    legSells.push(out);
    legBuys.push({
      id: `xi_${t.id}`, mkt: t.mkt, inv: t.inv, brk: t.toBrk, date: t.date, stk: t.stk,
      qty: +t.qty, px: costPer, fee: 0, _xfer: "in", _xferId: t.id, _xferFee: +t.fee || 0, note: `Transfer from ${t.fromBrk}`,
    });
  }
  return { legBuys, legSells, issues };
}

// Value a transfer leg for performance flow accounting: month-end close for the
// transfer month when known, else cost. A transfer is a flow OUT of the source broker
// and INTO the destination; across all brokers the two cancel to zero.
const xferLegValue = (leg, histPrices) => {
  const shares = leg.mkt === "IDX" ? leg.qty * 100 : leg.qty;
  const close = histPrices?.[leg.stk]?.[leg.date.slice(0, 7)];
  return shares * (close != null ? close : leg.px);
};

// ════════════════════════════════════════════════════════════════
//  HELPERS
// ════════════════════════════════════════════════════════════════
const R = (n, d = 0) => (typeof n === "number" && !isNaN(n)) ? +n.toFixed(d) : n;
const idr = n => {
  if (n == null || isNaN(n)) return "—";
  const abs = Math.abs(n);
  const s = abs >= 1e9 ? `${R(abs / 1e9, 2)}M` : abs >= 1e6 ? `${R(abs / 1e6, 1)}jt` : Math.round(abs).toLocaleString("id-ID");
  return `${n < 0 ? "-" : ""}Rp${s}`;
};
// Exact IDR — no compression to jt/M — for precision-sensitive displays like equity/balance
const idrExact = n => n == null || isNaN(n) ? "—" : `${n < 0 ? "-" : ""}Rp${Math.round(Math.abs(n)).toLocaleString("id-ID")}`;
const usd = n => n == null || isNaN(n) ? "—" : `${n < 0 ? "-" : ""}$${Math.abs(n).toFixed(2)}`;
const pct = n => n == null || isNaN(n) ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
const dt  = d => d ? new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
const gc  = n => n == null ? "#4b5563" : n >= 0 ? "#34d399" : "#f87171";
const money = (n, mkt) => mkt === "IDX" ? idr(n) : usd(n);
// Exact variant of money — no rounding/compression. Use for balance/equity where precision matters.
const moneyExact = (n, mkt) => mkt === "IDX" ? idrExact(n) : usd(n);

// Get today's date in YYYY-MM-DD format using LOCAL timezone (not UTC).
// Critical for users in positive timezones (Jakarta UTC+7) where toISOString()
// can return yesterday's date between 00:00 and 07:00 local time.
const todayLocal = () => {
  const d = new Date();
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
// Get N days ago as YYYY-MM-DD in LOCAL time.
const daysAgoLocal = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// ─── Sort helpers ─────────────────────────────────────────────
// sortRows(rows, sortKey, sortDir, getters) — sortKey is a string from getters map
// getters: { colName: (row) => sortable value }
function sortRows(rows, sortKey, sortDir, getters) {
  if (!sortKey || !getters[sortKey]) return rows;
  const getter = getters[sortKey];
  const mul = sortDir === "desc" ? -1 : 1;
  const arr = [...rows];
  arr.sort((a, b) => {
    const va = getter(a);
    const vb = getter(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;  // nulls last regardless of direction
    if (vb == null) return -1;
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul;
    return String(va).localeCompare(String(vb)) * mul;
  });
  return arr;
}

// ════════════════════════════════════════════════════════════════
//  STORAGE — imported from storage.js (Supabase-backed)
// ════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════
//  MAIN APP
// ════════════════════════════════════════════════════════════════
export default function App({ account, onLogout }) {
  // Responsive: detect mobile viewport (tracked on resize). Used throughout the UI
  // to stack grids, shrink paddings, and render the sidebar as a slide-out drawer.
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Start EMPTY, never with the seed. If the cloud read fails (e.g. a corrupted/truncated
  // file), the app must NOT silently display the built-in sample data (INIT_BUYS ends
  // 2026-03-26) — that looks exactly like "my data reset to March". hydrateFromCloud fills
  // these from the cloud; on failure we show an explicit load-error banner instead.
  const [buys,    setBuys]    = useState([]);
  const [sells,   setSells]   = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [live,    setLive]    = useState({});
  // Historical monthly close prices per ticker. Stored as { TICKER: { "YYYY-MM": close, ... } }.
  // Populated by fetchHistoricalPrices (Yahoo Finance) on demand. Cached in Dropbox so
  // subsequent loads are instant; only newer months need refetching.
  const [histPrices, setHistPrices] = useState({});
  const [aiText,  setAiText]  = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  // Equity-vs-index performance settings
  const [perfFreq, setPerfFreq] = useState("monthly");      // "monthly" | "quarterly" | "yearly"
  const [perfMkt,  setPerfMkt]  = useState("ALL");          // "ALL" | "IDX" | "US"
  const [perfInv,  setPerfInv]  = useState("ALL");          // "ALL" | <investor name>
  const [perfBrk,  setPerfBrk]  = useState("ALL");          // "ALL" | <broker name>
  const [perfIndex, setPerfIndex] = useState(["ihsg"]);     // array of index keys to compare against (multiselect)
  const [perfDateFrom, setPerfDateFrom] = useState("");     // YYYY-MM (inclusive)
  const [perfDateTo, setPerfDateTo] = useState("");         // YYYY-MM (inclusive)
  const [perfTableSort, setPerfTableSort] = useState({ key: "period", dir: "desc" });
  const [monthlyDetailSort, setMonthlyDetailSort] = useState({ key: "period", dir: "desc" });
  // Per-broker breakdown is heavy to compute and render — opt in
  const [showBrokerTable, setShowBrokerTable] = useState(false);
  // Cash balances per investor::broker key
  const [cash,    setCash]    = useState({});     // { "Robert::Stockbit": 25000000, ... }
  const [adjustments, setAdjustments] = useState([]); // [{id, date, inv, brk, type:"deposit|withdraw|adjust", amount, note}]
  const [splits, setSplits] = useState([]); // [{id, stk, mkt, date, ratio, type:"forward|reverse", note}]
  const [transfers, setTransfers] = useState([]); // [{id, mkt, inv, stk, qty, date, fromBrk, toBrk, fee, note}] — see expandTransfers
  const [brokerFees, setBrokerFees] = useState({}); // { [broker]: { buy, sell } } — see brokerFeeRate
  const [applyStamp, setApplyStamp] = useState(false); // OFF by default — user offsets via starting balance

  // Pre-IPO / private investments. Separate from public-market trades; pure tracker.
  // Starts empty (not the seed) for the same reason as buys/sells above.
  const [preipo, setPreipo] = useState([]);
  // Detail modal for a single deal; null = closed. When set, holds the deal's id.
  const [preipoDetailId, setPreipoDetailId] = useState(null);
  // Form state for add-deal & edit-deal forms (shared shape).
  const emptyDealForm = () => ({
    id: null,             // null = creating new; non-null = editing existing
    company: "", currency: "USD", holder: "Raymond", status: "active",
    principal: "", spvFeePct: "", spvFeeFlat: "", carryPct: "",
    mgmtFeePct: "", mgmtFeeYears: "", mgmtFeePaidYears: "",
    initialDate: "", initialTransfer: "", initialTransferIDR: "", initialFxRate: "",
    note: "", currentValuation: "", postMoneyAtEntry: "",
  });
  const [dealForm, setDealForm] = useState(emptyDealForm());
  const [dealFormOpen, setDealFormOpen] = useState(false);  // controls visibility of the add/edit form
  // Form state for adding a sub-event (refund / partial exit / etc) to a deal
  const emptyEventForm = () => ({ type: "partial_exit", date: todayLocal(), amount: "", note: "" });
  const [eventForm, setEventForm] = useState(emptyEventForm());
  // Filter state for Pre-IPO tab list
  const [preipoFilter, setPreipoFilter] = useState({ status: "all", currency: "all", holder: "all" });
  const [preipoSort, setPreipoSort] = useState({ key: "initialDate", dir: "desc" });
  const [preipoSearch, setPreipoSearch] = useState("");

  const [section, setSection] = useState("stocks");  // "stocks" | "preipo"
  const [tab,     setTab]     = useState("dash");
  const [loading, setLoading] = useState({ live: false, ai: false, pdf: false });
  const [flash,   setFlash]   = useState("");
  const [pdfRes,  setPdfRes]  = useState(null);
  const fileRef = useRef();

  // Form states
  const [bf, setBf] = useState({ type:"BUY", mkt:"IDX", inv:"Robert", brk:"Stockbit", date:"", stk:"", qty:"", px:"", fee:"", note:"", isIpo: false });
  // Batch trade entry — rapid row-by-row form. User adds rows with stock/qty/price/type;
  // date and fee rates are shared across all rows (set once, applies to all).
  const [batchMode, setBatchMode] = useState(false);
  const emptyBatchRow = () => ({ id: `r_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, type:"BUY", stk:"", qty:"", px:"", isIpo: false });
  const [batchRows, setBatchRows] = useState([emptyBatchRow()]);
  // Shared fields applied to ALL batch rows. Buy/sell fee % default to IDX standard rates;
  // user can override once and it applies to every row in the batch.
  const [batchShared, setBatchShared] = useState({
    date: todayLocal(),
    buyFeePct:  (IDX_BUY_PCT  * 100).toFixed(2),  // "0.15" — percentage form for display
    sellFeePct: (IDX_SELL_PCT * 100).toFixed(2),  // "0.25"
  });
  const sbs = (k, v) => setBatchShared(p => ({ ...p, [k]: v }));
  const [mf, setMf] = useState({ period:"", ihsg:"", sp500:"", brk:"Semesta", bal:"", fd:"" });
  const sb = (k, v) => setBf(p => ({ ...p, [k]: v }));
  const sm = (k, v) => setMf(p => ({ ...p, [k]: v }));

  // Sort state per table — { key: "columnName", dir: "asc"|"desc" }
  const [openSort, setOpenSort]   = useState({ key: "absUnreal", dir: "desc" });
  const [histSort, setHistSort]   = useState({ key: "date",  dir: "desc" });
  const [aggSort,  setAggSort]    = useState({ key: "absPL", dir: "desc" });
  const [buysSort, setBuysSort]   = useState({ key: "date",  dir: "desc" });
  const [floatSort, setFloatSort] = useState({ key: "absUnreal", dir: "desc" });
  // Helper: create a click handler that toggles sort direction
  const makeSortToggle = (state, setState) => (col) => {
    setState(p => p.key === col ? { key: col, dir: p.dir === "desc" ? "asc" : "desc" } : { key: col, dir: "desc" });
  };

  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);  // cloud read failed (corrupt/syncing) — show banner, never the seed

  // Read every key from cloud storage and push it into React state.
  // Used both on mount AND when we detect another device/tab wrote newer data
  // (focus re-sync + write-conflict recovery), so a stale tab can never overwrite
  // newer cloud data with its own out-of-date snapshot.
  const hydrateFromCloud = useCallback(async () => {
    const [b, s, m, l, a, c, adj, sp, st, ar, hp, pi, xf, bfees] = await Promise.all([
      load("tt:buys",    []),
      load("tt:sells",   []),
      load("tt:monthly", []),
      loadLivePrices(),          // live prices come from their OWN file now, not data.json
      load("tt:ai",      ""),
      load("tt:cash",    {}),
      load("tt:adj",     []),
      load("tt:splits",  []),
      load("tt:stamp",   false),
      load("tt:autoref", true),
      loadHistPrices(),          // month-end closes: their OWN file (hist.json), not data.json
      load("tt:preipo",  []),
      load("tt:transfers", []),
      load("tt:brokerFees", {}),
    ]);
    setBuys(b); setSells(s); setMonthly(m); setLive(l); setAiText(a);
    setCash(c); setAdjustments(adj); setSplits(sp); setApplyStamp(st); setAutoRefresh(ar);
    setTransfers(xf); setBrokerFees(bfees);
    setHistPrices(hp);
    // Pre-IPO: cloud (data.json) is the source of truth. We also keep a localStorage
    // mirror as a passive backup. Only fall back to the local copy if the cloud copy
    // came back as the untouched seed AND a local copy exists with real edits — this
    // covers the brief window right after first deploy before the cloud copy is written.
    let preipoToUse = pi;
    try {
      const cloudIsSeed = !pi || JSON.stringify(pi) === JSON.stringify(INIT_PREIPO);
      if (cloudIsSeed) {
        const localRaw = typeof window !== "undefined" ? window.localStorage.getItem("tt:preipo:local") : null;
        if (localRaw) {
          const localCopy = JSON.parse(localRaw);
          if (Array.isArray(localCopy) && localCopy.length > 0 && JSON.stringify(localCopy) !== JSON.stringify(INIT_PREIPO)) {
            preipoToUse = localCopy;
            console.log("[tt] Cloud preipo was seed; using newer localStorage copy");
          }
        }
      }
    } catch (e) { console.warn("[tt] preipo localStorage read failed:", e.message); }
    setPreipo(preipoToUse);
  }, []);

  // Load from storage on mount. Retry on failure — a corrupt/truncated read is often a
  // transient Dropbox sync state, and we must NEVER fall through to showing the seed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (let attempt = 1; !cancelled; attempt++) {
        try {
          await hydrateFromCloud();
          if (cancelled) return;
          setLoadError(false);
          break;  // success
        } catch (err) {
          console.error(`[tt] Load failed (attempt ${attempt}) — NOT enabling saves, keeping data EMPTY (not seed):`, err);
          setLoadError(true);   // show banner; saves stay disabled (isLoaded false)
          // After a few failed attempts it's not just a transient sync — the cloud file is
          // corrupt. Self-heal: restore the newest good backup via the API, then retry.
          if (attempt >= 3) {
            try {
              const n = await recoverFromBackup();
              if (n > 0 && !cancelled) { flash2(`✓ Data dipulihkan otomatis dari cadangan (${n} transaksi)`); continue; }
            } catch (e) { console.warn("[tt] self-heal failed:", e.message); }
          }
          // Back off and retry (5s, capped). The file may just be mid-sync.
          await new Promise(r => setTimeout(r, Math.min(5000, 1500 * attempt)));
        }
      }
      if (cancelled) return;
      // CRITICAL: only enable saves AFTER load completes successfully.
      // Otherwise a save with initial (empty) state would overwrite real Dropbox data.
      setIsLoaded(true);

      // Run daily auto-backup (skips if already done today).
      // This way, if the app or a bug ever corrupts data, user can restore from /backups/YYYY-MM-DD.json.
      try {
        const backupRes = await backupWeekly();
        if (backupRes?.ok) console.log("[tt] Auto-backup saved");
        else if (backupRes?.skipped) console.log(`[tt] Auto-backup skipped: ${backupRes.reason}`);
      } catch (e) {
        console.warn("[tt] Auto-backup failed:", e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [hydrateFromCloud]);

  // ── Multi-device / multi-tab safety ──────────────────────────────
  // Root cause of "my data reset itself": a tab loads data once and then never
  // re-reads, so when another device writes newer data, the stale tab silently
  // overwrites it with its own old snapshot on the next save. Guard against it:
  //   • on tab focus / becoming visible → pull fresh data if the cloud rev changed
  //   • on tab hide → flush pending debounced writes so nothing is lost on close
  //   • on a write-conflict (storage emits "tt:remote-changed") → re-hydrate state
  useEffect(() => {
    if (!isLoaded) return;
    let busy = false;
    const resync = async (force) => {
      if (busy) return;
      busy = true;
      try {
        const res = force ? { changed: true } : await reloadIfRemoteChanged();
        if (res.changed) {
          await hydrateFromCloud();
          flash2("↻ Data diperbarui dari perangkat/tab lain");
        }
      } catch (e) {
        console.warn("[tt] re-sync failed:", e.message);
      } finally {
        busy = false;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") resync(false);
      else flushPendingWrites().catch(() => {});  // tab hidden/closing → persist pending edits
    };
    const onFocus = () => resync(false);
    const onRemoteChanged = () => resync(true);   // conflict already reloaded the cache
    const onWriteError = () => flash2("⚠ Penyimpanan gagal (file tak utuh) — data aman di perangkat ini, coba lagi/segarkan");
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("tt:remote-changed", onRemoteChanged);
    window.addEventListener("tt:write-error", onWriteError);
    // Belt-and-suspenders: a tab left visible/foreground for a long time never fires
    // focus/visibilitychange, so it would only re-sync reactively (on a write-conflict)
    // and could drift stale enough to lose an edit first. Poll the cloud rev every 60s
    // while visible — it's just a cheap get_metadata call when nothing changed.
    const poll = setInterval(() => {
      if (document.visibilityState === "visible") resync(false);
    }, 60 * 1000);
    return () => {
      clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("tt:remote-changed", onRemoteChanged);
      window.removeEventListener("tt:write-error", onWriteError);
    };
  }, [isLoaded, hydrateFromCloud]);

  // Persist on change — BUT only after initial load completes,
  // to prevent the "save initial state on mount" race condition that
  // overwrites real data with INIT_BUYS/INIT_SELLS demo data.
  useEffect(() => { if (isLoaded) save("tt:buys",    buys);    }, [buys, isLoaded]);
  useEffect(() => { if (isLoaded) save("tt:sells",   sells);   }, [sells, isLoaded]);
  useEffect(() => { if (isLoaded) save("tt:monthly", monthly); }, [monthly, isLoaded]);
  // Live prices persist to their OWN file (live.json) — a price write NEVER touches the
  // trade file. This removes the frequent, unattended overnight write that was truncating.
  useEffect(() => { if (isLoaded) saveLivePrices(live); }, [live, isLoaded]);
  useEffect(() => { if (isLoaded) save("tt:ai",      aiText);  }, [aiText, isLoaded]);
  useEffect(() => { if (isLoaded) save("tt:cash",    cash);    }, [cash, isLoaded]);
  useEffect(() => { if (isLoaded) save("tt:adj",     adjustments); }, [adjustments, isLoaded]);
  useEffect(() => { if (isLoaded) save("tt:splits",  splits);      }, [splits, isLoaded]);
  useEffect(() => { if (isLoaded) save("tt:transfers", transfers); }, [transfers, isLoaded]);
  useEffect(() => { if (isLoaded) save("tt:brokerFees", brokerFees); }, [brokerFees, isLoaded]);
  useEffect(() => { if (isLoaded) save("tt:stamp",   applyStamp);  }, [applyStamp, isLoaded]);
  useEffect(() => { if (isLoaded) save("tt:autoref", autoRefresh); }, [autoRefresh, isLoaded]);
  useEffect(() => {
    if (!isLoaded) return;
    save("tt:preipo", preipo);
    // Mirror to localStorage so edits survive even if the cloud layer drops the key.
    try { if (typeof window !== "undefined") window.localStorage.setItem("tt:preipo:local", JSON.stringify(preipo)); }
    catch (e) { console.warn("[tt] preipo localStorage write failed:", e.message); }
  }, [preipo, isLoaded]);
  useEffect(() => { if (isLoaded) saveHistPrices(histPrices); }, [histPrices, isLoaded]);

  // ── FIFO ────────────────────────────────────────────────────
  // NOTE: stamp duty is applied automatically per (broker, date) at trade-entry
  // time — see saveTrade / saveBatch / handlePDF. The toggle below is retained
  // in state for backward compatibility with older Dropbox backups but no longer
  // affects behavior; the auto-per-day logic always runs.
  // Historical trades keep their original fees — user offsets discrepancy via starting cash balance.
  // After FIFO runs, we apply the ownership multiplier (.RF = 50%, .RUDI = 0%) to
  // realized profit and underlying values so downstream P&L is correctly sized.
  // Transfer legs on RAW trades — consumed by the month-by-month walkers below
  // (broker balance table, equity curve, per-broker curves), which work on raw qty.
  const xferRaw = useMemo(() => expandTransfers(buys, sells, transfers), [buys, sells, transfers]);

  const { results: closed, open: openPos, xferIssues } = useMemo(() => {
    const adjBuys  = applySplits(buys, splits);
    const adjSells = applySplits(sells, splits);
    // Transfers expand on split-adjusted data so leg costs match the FIFO run below.
    const xf = expandTransfers(adjBuys, adjSells, applySplits(transfers, splits));
    const raw = runFIFO([...adjBuys, ...xf.legBuys], [...adjSells, ...xf.legSells]);
    // Apply ownership to closed trades — halve profit/revenue/cost for .RF, drop .RUDI
    const adjustedClosed = raw.results
      .filter(r => !r._xfer)   // transfer out-legs are not realized trades
      .map(r => {
        const own = getOwnership(r.stk);
        if (own.multiplier === 0) return null;
        if (own.multiplier === 1) return r;
        return {
          ...r,
          ownership: own,
          profit: r.profit * own.multiplier,
          revenue: (r.revenue || 0) * own.multiplier,
          costBasis: (r.costBasis || 0) * own.multiplier,
          // qty stays nominal in display; the ownership label will be shown next to it
        };
      })
      .filter(Boolean);
    return { results: adjustedClosed, open: raw.open, xferIssues: xf.issues };
  }, [buys, sells, splits, transfers]);

  // ── Cash balances per account ────────────────────────────────
  const cashByAccount = useMemo(() => {
    const result = {};
    Object.entries(cash).forEach(([key, v]) => {
      result[key] = { starting: v || 0, bought: 0, sold: 0, adjusted: 0, current: v || 0 };
    });
    buys.forEach(b => {
      const k = `${b.inv}::${b.brk}`;
      const shares = b.mkt === "IDX" ? b.qty * 100 : b.qty;
      const cost = shares * b.px + b.fee;
      if (!result[k]) result[k] = { starting: 0, bought: 0, sold: 0, adjusted: 0, current: 0 };
      result[k].bought += cost;
      result[k].current -= cost;
    });
    sells.forEach(s => {
      const k = `${s.inv}::${s.brk}`;
      const shares = s.mkt === "IDX" ? s.qty * 100 : s.qty;
      const revenue = shares * s.px - s.fee;
      if (!result[k]) result[k] = { starting: 0, bought: 0, sold: 0, adjusted: 0, current: 0 };
      result[k].sold += revenue;
      result[k].current += revenue;
    });
    adjustments.forEach(a => {
      const k = `${a.inv}::${a.brk}`;
      if (!result[k]) result[k] = { starting: 0, bought: 0, sold: 0, adjusted: 0, current: 0 };
      result[k].adjusted += a.amount;
      result[k].current  += a.amount;
    });
    // Stock transfers move no cash — only the transfer charge, billed by the RECEIVING broker.
    transfers.forEach(t => {
      const fee = +t.fee || 0;
      if (!fee) return;
      const k = `${t.inv}::${t.toBrk}`;
      if (!result[k]) result[k] = { starting: 0, bought: 0, sold: 0, adjusted: 0, current: 0 };
      result[k].adjusted -= fee;
      result[k].current  -= fee;
    });
    return result;
  }, [cash, buys, sells, adjustments, transfers]);

  const allAccounts = useMemo(() => {
    const set = new Set();
    buys.forEach(b => set.add(`${b.inv}::${b.brk}::${b.mkt}`));
    sells.forEach(s => set.add(`${s.inv}::${s.brk}::${s.mkt}`));
    transfers.forEach(t => set.add(`${t.inv}::${t.toBrk}::${t.mkt}`));
    Object.keys(cash).forEach(k => {
      const [inv, brk] = k.split("::");
      const mkt = [...buys, ...sells].find(t => t.inv === inv && t.brk === brk)?.mkt || (US_BROKERS.includes(brk) ? "US" : "IDX");
      set.add(`${inv}::${brk}::${mkt}`);
    });
    adjustments.forEach(a => {
      const mkt = [...buys, ...sells].find(t => t.inv === a.inv && t.brk === a.brk)?.mkt || (US_BROKERS.includes(a.brk) ? "US" : "IDX");
      set.add(`${a.inv}::${a.brk}::${mkt}`);
    });
    return [...set].map(k => {
      const [inv, brk, mkt] = k.split("::");
      return { inv, brk, mkt, key: `${inv}::${brk}` };
    });
  }, [buys, sells, cash, adjustments, transfers]);

  // Open positions enriched with live price
  // openEnriched — augments each open position with live price, current value,
  // unrealized P&L. ALSO applies the ownership multiplier here (.RF = 50%, .RUDI = 0%)
  // so all downstream consumers (equity card, Floating P&L, Performance tab) get
  // correctly-sized values without needing to know about the suffix convention.
  // The price (lp) is the FULL share price — but units, totalCost, curVal, unreal
  // all reflect ONLY the user's share.
  const openEnriched = useMemo(() => openPos
    .map(p => {
      const own = getOwnership(p.stk);
      // Skip positions that aren't yours at all (.RUDI). They won't appear anywhere.
      if (own.multiplier === 0) return null;
      const lp = live.prices?.[p.stk];
      // Apply multiplier to qty/cost. Price stays at full market value per share.
      const adjUnits     = p.units * own.multiplier;
      const adjTotalCost = p.totalCost * own.multiplier;
      const curVal = lp ? adjUnits * lp : null;
      const unreal = curVal != null ? curVal - adjTotalCost : null;
      const unrealPct = unreal != null && adjTotalCost ? unreal / adjTotalCost * 100 : null;
      return {
        ...p,
        units: adjUnits,
        totalCost: adjTotalCost,
        ownership: own,
        lp, curVal, unreal, unrealPct,
      };
    })
    .filter(Boolean),
  [openPos, live]);

  // ── Monthly performance ───────────────────────────────────────
  const perfRows = useMemo(() => {
    // Manual month-end records, indexed by period → { ihsg, sp500, accounts:{brk:{bal,fd}} }
    const byPeriod = {};
    monthly.forEach(m => {
      if (!byPeriod[m.period]) byPeriod[m.period] = { ihsg: m.ihsg, sp500: m.sp500, accounts: {} };
      else { if (m.ihsg != null) byPeriod[m.period].ihsg = m.ihsg; if (m.sp500 != null) byPeriod[m.period].sp500 = m.sp500; }
      if (m.brk && m.brk !== "_index") byPeriod[m.period].accounts[m.brk] = { bal: m.bal, fd: m.fd };
    });

    // Build the full period range: from the earliest data point (manual record OR
    // trade) through the current month, so the table keeps extending automatically
    // as new trades come in — no need to record a balance for the month to appear.
    const validPeriod = (p) => typeof p === "string" && /^\d{4}-\d{2}$/.test(p);
    const validDate = (d) => typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d) && !isNaN(new Date(d).getTime());
    const FLOOR = "2015-01";  // refuse to generate rows before this — guards against junk dates
    const allDates = [
      ...monthly.map(m => m.period).filter(validPeriod),
      ...buys.filter(b => validDate(b.date)).map(b => b.date.slice(0, 7)),
      ...sells.filter(s => validDate(s.date)).map(s => s.date.slice(0, 7)),
    ].filter(p => p >= FLOOR).sort();
    if (allDates.length === 0) return [];
    let firstPeriod = allDates[0];
    if (firstPeriod < FLOOR) firstPeriod = FLOOR;
    const nowPeriod = new Date().toISOString().slice(0, 7);
    let lastPeriod = allDates[allDates.length - 1];
    if (nowPeriod > lastPeriod) lastPeriod = nowPeriod;
    // Hard safety cap: never generate more than 240 months (20 yr) of rows.
    const periods = [];
    {
      let [y, m] = firstPeriod.split("-").map(Number);
      const [ey, em] = lastPeriod.split("-").map(Number);
      let guard = 0;
      while ((y < ey || (y === ey && m <= em)) && guard++ < 240) {
        periods.push(`${y}-${String(m).padStart(2, "0")}`);
        m++; if (m > 12) { m = 1; y++; }
      }
    }

    // Which brokers to show: any broker that appears in manual records OR in trades.
    const allBuysX  = [...buys, ...xferRaw.legBuys];    // + transfer legs (no cash)
    const allSellsX = [...sells, ...xferRaw.legSells];
    const tradeBrokers = [...new Set([...allBuysX, ...allSellsX].map(t => t.brk).filter(Boolean))];
    const manualBrokers = [...new Set(monthly.map(m => m.brk).filter(b => b && b !== "_index"))];
    const brokerSet = [...new Set([...manualBrokers, ...tradeBrokers])];

    // Pre-sort trades AND adjustments per broker for the running auto-compute.
    const buysByBrk = {}, sellsByBrk = {}, adjByBrk = {};
    brokerSet.forEach(bk => {
      buysByBrk[bk]  = allBuysX.filter(b => b.brk === bk).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      sellsByBrk[bk] = allSellsX.filter(s => s.brk === bk).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      adjByBrk[bk]   = adjustments.filter(a => a.brk === bk).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    });
    // Running state per broker for auto-compute (cash + lots), advanced month by month.
    // Cash keys are "Investor::Broker" — sum across all investors for each broker
    // since this table is per-broker, not per-investor.
    const cashByBroker = {};
    Object.entries(cash || {}).forEach(([key, amount]) => {
      const brk = key.includes("::") ? key.split("::")[1] : key;
      cashByBroker[brk] = (cashByBroker[brk] || 0) + (+amount || 0);
    });
    // A broker's cash is "anchored" if it has a starting balance OR any adjustment
    // (deposit/withdraw/reconcile). Without an anchor the running cash is just
    // (0 − all buys), which is meaningless and goes negative — in that case we show
    // market value of holdings only, not a bogus cash-inclusive balance.
    const anchoredBrokers = new Set([
      ...Object.keys(cashByBroker),
      ...adjustments.map(a => a.brk).filter(Boolean),
    ]);
    const runState = {};
    brokerSet.forEach(bk => { runState[bk] = { cash: cashByBroker[bk] || 0, lots: {}, bi: 0, si: 0, ai: 0 }; });

    // Valuation for a ticker in a period. Priority:
    //  1. live price (current month only)
    //  2. that period's historical month-end close
    //  3. the most recent historical close BEFORE this period (carry forward)
    //  4. null → caller falls back to cost basis (last resort, flagged)
    // Carry-forward (3) matters because cost basis overstates stocks that have
    // fallen — using the last real market price keeps balances realistic.
    const sortedHistMonths = {};  // cache of sorted month keys per ticker
    const valueAt = (stk, shares, period, isCurrent) => {
      if (isCurrent && live.prices?.[stk] != null) return shares * live.prices[stk];
      const hp = histPrices[stk];
      if (hp) {
        if (hp[period] != null) return shares * hp[period];
        // carry forward: find the latest month <= period
        if (!sortedHistMonths[stk]) sortedHistMonths[stk] = Object.keys(hp).sort();
        const months = sortedHistMonths[stk];
        let best = null;
        for (const mo of months) { if (mo <= period) best = mo; else break; }
        if (best != null && hp[best] != null) return shares * hp[best];
        // no prior close, but if live exists use it as a rough proxy
        if (live.prices?.[stk] != null) return shares * live.prices[stk];
      } else if (live.prices?.[stk] != null) {
        return shares * live.prices[stk];
      }
      return null; // signal "no price" → cost basis fallback
    };

    const rows = periods.map((p, idx) => {
      const isCurrent = idx === periods.length - 1;
      const endDate = `${p}-31`; // string compare upper bound for the month
      const manual = byPeriod[p];
      const accounts = {};
      let pricedAll = true;

      brokerSet.forEach(bk => {
        const st = runState[bk];
        let fd = 0; // net deposits/withdrawals this period (from adjustments)
        // Advance buys up through this month-end.
        while (st.bi < buysByBrk[bk].length && buysByBrk[bk][st.bi].date <= endDate) {
          const b = buysByBrk[bk][st.bi++];
          const sh = b.mkt === "IDX" ? b.qty * 100 : b.qty;
          if (b._xfer) { fd += xferLegValue(b, histPrices); st.cash -= b._xferFee || 0; }  // transfer in: flow + the charge
          else st.cash -= sh * b.px + (b.fee || 0);
          if (!st.lots[b.stk]) st.lots[b.stk] = [];
          st.lots[b.stk].push({ shares: sh, costPer: b.px + (b.fee || 0) / sh });
        }
        // Advance sells.
        while (st.si < sellsByBrk[bk].length && sellsByBrk[bk][st.si].date <= endDate) {
          const s = sellsByBrk[bk][st.si++];
          let sh = s.mkt === "IDX" ? s.qty * 100 : s.qty;
          if (s._xfer) fd -= xferLegValue(s, histPrices);   // transfer out: flow only, no cash
          else st.cash += sh * s.px - (s.fee || 0);
          const lots = st.lots[s.stk] || [];
          while (sh > 0 && lots.length) {
            const lot = lots[0];
            const take = Math.min(sh, lot.shares);
            lot.shares -= take; sh -= take;
            if (lot.shares <= 0) lots.shift();
          }
        }
        // Advance adjustments (deposits / withdrawals) — THIS is the cash the
        // brokerage account is funded with; without it cash goes wrongly negative.
        while (st.ai < adjByBrk[bk].length && adjByBrk[bk][st.ai].date <= endDate) {
          const a = adjByBrk[bk][st.ai++];
          const v = +a.amount || 0;
          if (a.type === "withdraw") { st.cash -= v; fd -= v; }
          else                        { st.cash += v; fd += v; }  // deposit / adjust
        }

        // Prefer the manual balance for this broker+period when present.
        const manualAcc = manual?.accounts?.[bk];
        if (manualAcc && manualAcc.bal != null) {
          accounts[bk] = { bal: manualAcc.bal, fd: manualAcc.fd, source: "manual" };
        } else {
          // Auto-compute: cash + market value of open lots (ownership-adjusted).
          let mv = 0, costBasis = 0;
          for (const stk in st.lots) {
            const own = getOwnership(stk);
            if (own.multiplier === 0) continue;  // .RUDI — not the user's holding
            let sh = 0, cb = 0;
            for (const l of st.lots[stk]) { sh += l.shares; cb += l.shares * l.costPer; }
            sh *= own.multiplier; cb *= own.multiplier;
            if (sh <= 0) continue;
            costBasis += cb;
            const v = valueAt(stk, sh, p, isCurrent);
            if (v == null) { mv += cb; pricedAll = false; } else { mv += v; }
          }
          // If cash isn't anchored, st.cash is just −(buys) and meaningless. Show
          // market value of holdings only, and flag it so the cell can mark it.
          const cashReliable = anchoredBrokers.has(bk);
          // For the CURRENT month, prefer the trusted cash figure (same math as the
          // Setup·Cash page: starting − buys + sells + adjust across all investors of
          // this broker). This guarantees the latest month's cash matches Setup·Cash.
          let cashComponent = st.cash;
          if (isCurrent) {
            let trusted = 0, found = false;
            Object.entries(cashByAccount).forEach(([key, c]) => {
              const b2 = key.includes("::") ? key.split("::")[1] : key;
              if (b2 === bk) { trusted += c.current || 0; found = true; }
            });
            if (found) cashComponent = trusted;
          }
          accounts[bk] = {
            bal: cashReliable ? cashComponent + mv : mv,
            cash: cashReliable ? cashComponent : null,
            mv,
            fd, source: "auto", pricedAll, cashReliable,
          };
        }
      });

      return {
        period: p,
        ihsg: manual?.ihsg ?? (isCurrent ? (live.prices?.["__ihsg"] ?? null) : null),
        sp500: manual?.sp500 ?? (isCurrent ? (live.prices?.["__sp500"] ?? null) : null),
        accounts,
      };
    });

    // Compute period-over-period % growth (uses prior row's values).
    return rows.map((row, i) => {
      const prev = i > 0 ? rows[i - 1] : null;
      const ihsgG  = prev?.ihsg  && row.ihsg  ? (row.ihsg  - prev.ihsg)  / prev.ihsg  * 100 : null;
      const sp500G = prev?.sp500 && row.sp500 ? (row.sp500 - prev.sp500) / prev.sp500 * 100 : null;
      const acctG = {};
      Object.entries(row.accounts).forEach(([brk, { bal, fd }]) => {
        const pb = prev?.accounts?.[brk];
        if (pb && pb.bal) acctG[brk] = (bal - pb.bal - (fd || 0)) / pb.bal * 100;
      });
      return { ...row, ihsgG, sp500G, acctG };
    });
  }, [monthly, buys, sells, cash, live, histPrices, adjustments, cashByAccount, xferRaw]);

  const allBrokers = useMemo(() => {
    const tradeBrokers = [...new Set([...buys, ...sells].map(t => t.brk).filter(Boolean))];
    const manualBrokers = [...new Set(monthly.map(m => m.brk).filter(b => b && b !== "_index"))];
    return [...new Set([...manualBrokers, ...tradeBrokers])];
  }, [monthly, buys, sells]);

  // ── Auto equity curve (period-by-period) ─────────────────────────────
  // Computes equity at the END of each period (monthly/quarterly/yearly) from
  // trades + cash + adjustments. Uses live prices for the most recent period
  // (where available) and cost-basis for older periods (since we don't store
  // historical prices). Net flows (deposits/withdrawals) are tracked separately
  // so we can compute Time-Weighted Return:
  //   TWR_period = (end_equity − start_equity − net_flows_during_period) / start_equity
  // TWR strips out the distortion from adding/removing capital, so it cleanly
  // measures portfolio performance comparable to an index.
  const equityCurve = useMemo(() => {
    // Skip the entire heavy compute when not viewing the Performance tab.
    // Even though useMemo only runs on dep changes, the deps include things
    // that change frequently (live, histPrices) — and with ~2000+ trades the
    // compute is expensive enough to be noticeable.
    if (tab !== "perf") return { points: [], allPoints: [] };
    // Helper: format a date into a period key per chosen frequency.
    // Monthly  → "2026-04"
    // Quarterly→ "2026-Q2"
    // Yearly   → "2026"
    const periodKey = (dateStr) => {
      const d = new Date(dateStr);
      const y = d.getUTCFullYear();
      const m = d.getUTCMonth() + 1;  // 1..12
      if (perfFreq === "yearly") return String(y);
      if (perfFreq === "quarterly") return `${y}-Q${Math.ceil(m / 3)}`;
      return `${y}-${String(m).padStart(2, "0")}`;
    };
    // Helper: end-of-period date (last day of the period) — for snapshot timestamping
    const periodEnd = (key) => {
      if (perfFreq === "yearly") return `${key}-12-31`;
      if (perfFreq === "quarterly") {
        const [y, q] = key.split("-Q");
        const lastMonth = +q * 3;
        const lastDay = new Date(Date.UTC(+y, lastMonth, 0)).getUTCDate();
        return `${y}-${String(lastMonth).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      }
      const [y, m] = key.split("-");
      const lastDay = new Date(Date.UTC(+y, +m, 0)).getUTCDate();
      return `${y}-${m}-${String(lastDay).padStart(2, "0")}`;
    };

    // Filter by market, investor, and broker.
    // - buys/sells/adjustments have `mkt`, `inv`, `brk` fields directly.
    // - cash is a flat object {"investor::broker": amount}; we derive market from the broker name
    //   and split the key for inv/brk filtering.
    const matches = (x) => {
      if (perfMkt !== "ALL" && x.mkt !== perfMkt) return false;
      if (perfInv !== "ALL" && x.inv !== perfInv) return false;
      if (perfBrk !== "ALL" && x.brk !== perfBrk) return false;
      return true;
    };
    const fBuys  = [...buys, ...xferRaw.legBuys].filter(matches);
    const fSells = [...sells, ...xferRaw.legSells].filter(matches);
    const fAdj   = adjustments.filter(matches);
    // Convert cash object → array of {key, inv, brk, amount, mkt} so we can iterate and filter.
    const cashEntries = Object.entries(cash || {}).map(([key, amount]) => {
      const [inv, broker] = key.split("::");
      const mkt = US_BROKERS.includes(broker) ? "US" : "IDX";
      return { key, amount: +amount || 0, mkt, inv, brk: broker };
    });
    const fCash = cashEntries.filter(matches);
    if (fBuys.length === 0 && fSells.length === 0) return { points: [], indexPoints: [] };

    // Find earliest activity date and latest, build set of period keys.
    // Only consider events with valid ISO-format dates (YYYY-MM-DD) — bad/missing
    // dates from corrupted imports would otherwise poison earliestDate (e.g. a
    // single event with date=0 or "" would make earliestDate="" → periodKey("")
    // → 1970/1900, generating 1500+ junk periods).
    const isValidDate = (d) => typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d) && !isNaN(new Date(d).getTime());
    const allEvents = [...fBuys, ...fSells, ...fAdj].filter(e => isValidDate(e.date));
    let earliestDate = allEvents.reduce((min, e) => !min || e.date < min ? e.date : min, "");
    if (!earliestDate) return { points: [], indexPoints: [] };
    // Defensive clamp: even with valid-date filtering, refuse to generate periods
    // earlier than 2015. Prevents UI explosions if a stray trade has date "2010-01-01"
    // or similar pre-portfolio test data.
    const FLOOR_DATE = "2015-01-01";
    if (earliestDate < FLOOR_DATE) earliestDate = FLOOR_DATE;
    const today = new Date().toISOString().slice(0, 10);
    const startKey = periodKey(earliestDate);
    // End at the later of: today, or the latest period that has any data (trades,
    // sells, adjustments, or manual monthly entries). This guarantees the most
    // recent activity always shows even if the system clock is behind, and that
    // manual balances entered for a future-ish month aren't truncated.
    const dataDates = [
      ...allEvents.map(e => e.date),
      ...monthly.filter(m => m.period).map(m => `${m.period}-28`),
    ].filter(isValidDate);
    let endAnchor = today;
    for (const d of dataDates) { if (d > endAnchor) endAnchor = d; }
    const endKey = periodKey(endAnchor);

    // Generate all period keys from start to end
    const allKeys = [];
    if (perfFreq === "yearly") {
      const sY = +startKey, eY = +endKey;
      for (let y = sY; y <= eY; y++) allKeys.push(String(y));
    } else if (perfFreq === "quarterly") {
      const [sY, sQ] = startKey.split("-Q").map(Number);
      const [eY, eQ] = endKey.split("-Q").map(Number);
      for (let y = sY; y <= eY; y++) {
        const qStart = y === sY ? sQ : 1;
        const qEnd = y === eY ? eQ : 4;
        for (let q = qStart; q <= qEnd; q++) allKeys.push(`${y}-Q${q}`);
      }
    } else {
      const [sY, sM] = startKey.split("-").map(Number);
      const [eY, eM] = endKey.split("-").map(Number);
      let y = sY, m = sM;
      while (y < eY || (y === eY && m <= eM)) {
        allKeys.push(`${y}-${String(m).padStart(2, "0")}`);
        m++;
        if (m > 12) { m = 1; y++; }
      }
    }

    // For each period END, compute equity = cash_at_end_of_period + stock_value.
    //
    // CASH RECONSTRUCTION (forward-walk):
    // The user's `cash` map represents STARTING cash per account (initial deposit).
    // The current balance is computed by: starting + (sells received) − (buys paid) + (adjustments).
    // To get cash at the end of period N, we apply only the trades/adjustments
    // that happened on or BEFORE the end of period N to the starting cash:
    //   cash_at_period_N = starting_cash
    //                    + (deposits up to N)
    //                    − (withdrawals up to N)
    //                    + (sells received up to N)
    //                    − (buys paid up to N)
    const startingCashTotal = fCash.reduce((s, c) => s + (+c.amount || 0), 0);

    // PERFORMANCE: incremental period-by-period state walk.
    // Rather than re-filtering all trades for each period (which was O(periods × trades)
    // and slow with 2000+ trades), we sort trades + adjustments once by date, then walk
    // through them in chronological order, advancing through the periods array.
    // For each period boundary, we snapshot the current cumulative state.
    // Complexity: O(trades + periods) instead of O(periods × trades).
    // Filter out invalid-date entries here too — they'd otherwise sort to the
    // top and either cause the walk to do nothing useful or produce phantom state.
    const sortedBuys  = fBuys.filter(b => isValidDate(b.date)).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    const sortedSells = fSells.filter(s => isValidDate(s.date)).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    const sortedAdj   = fAdj.filter(a => isValidDate(a.date)).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    // Running state — accumulated as we walk through periods
    let runningCash = startingCashTotal;
    const runningBuys = {};   // stk → [{shares, costPer}, ...]   (FIFO lots open RIGHT NOW)
    let bi = 0, si = 0, ai = 0;  // indexes into sorted arrays — never go backwards

    // Helper: drain queue up to and including a given date (inclusive)
    const advanceTo = (endDate) => {
      // Process buys that occurred up to endDate
      while (bi < sortedBuys.length && sortedBuys[bi].date <= endDate) {
        const b = sortedBuys[bi++];
        const shares = b.mkt === "IDX" ? b.qty * 100 : b.qty;
        if (b._xfer) runningCash -= b._xferFee || 0;   // transfer in: only the receiving broker's charge
        else runningCash -= shares * b.px + (b.fee || 0);
        if (!runningBuys[b.stk]) runningBuys[b.stk] = [];
        runningBuys[b.stk].push({ shares, costPer: b.px + (b.fee || 0) / shares });
      }
      // Sells (FIFO consume the queue)
      while (si < sortedSells.length && sortedSells[si].date <= endDate) {
        const s = sortedSells[si++];
        const shares = s.mkt === "IDX" ? s.qty * 100 : s.qty;
        if (!s._xfer) runningCash += shares * s.px - (s.fee || 0);  // transfer out: no cash
        if (!runningBuys[s.stk]) continue;
        let toSell = shares;
        while (toSell > 0 && runningBuys[s.stk].length > 0) {
          const lot = runningBuys[s.stk][0];
          if (lot.shares <= toSell) { toSell -= lot.shares; runningBuys[s.stk].shift(); }
          else { lot.shares -= toSell; toSell = 0; }
        }
      }
      // Adjustments
      while (ai < sortedAdj.length && sortedAdj[ai].date <= endDate) {
        const a = sortedAdj[ai++];
        const v = +a.amount || 0;
        if (a.type === "deposit")      runningCash += v;
        else if (a.type === "withdraw") runningCash -= v;
        else                            runningCash += v;
      }
    };

    // Pre-compute period-flows by walking adjustments separately for each boundary
    // (small lookup, doesn't dominate).
    //
    // IMPORTANT: only `deposit` and `withdraw` count as real cash flows for TWR.
    // `adjust` (reconciliation) entries plug equity discrepancies and DO change
    // running cash for end-equity computation — but they're NOT external money
    // movements, so they must not enter the TWR flow term. Treating them as flows
    // distorts TWR (e.g. a +Rp581jt reconciliation in April makes April's TWR
    // appear catastrophically negative because TWR subtracts the "deposit" as if
    // the user added that much capital).
    const periodEndDates = allKeys.map(periodEnd);
    const flowsByPeriodIdx = allKeys.map((_, idx) => {
      const startDate = idx === 0 ? "0000-00-00" : periodEndDates[idx - 1];
      const endDate = periodEndDates[idx];
      let flow = 0;
      for (const a of sortedAdj) {
        if (a.date > startDate && a.date <= endDate) {
          const v = +a.amount || 0;
          if (a.type === "deposit") flow += v;
          else if (a.type === "withdraw") flow -= v;
          // adjust / reconciliation: NOT a cash flow — skip
        } else if (a.date > endDate) break;  // sortedAdj is chronological
      }
      // Transfer legs in scope are flows (in +, out −). With no broker filter both
      // legs are in scope and cancel; filtered to one broker, only its side counts.
      for (const leg of fBuys)  if (leg._xfer && leg.date > startDate && leg.date <= endDate) flow += xferLegValue(leg, histPrices);
      for (const leg of fSells) if (leg._xfer && leg.date > startDate && leg.date <= endDate) flow -= xferLegValue(leg, histPrices);
      return flow;
    });

    // Pre-aggregate manual monthly entries by period: sum bal + sum fd across brokers.
    // When available for a period, this OVERRIDES the auto-computed equity — Excel data
    // is the source of truth for past months.
    const manualByPeriod = {};
    monthly.forEach(m => {
      if (!m.brk || m.brk === "_index" || m.bal == null) return;
      // Apply market filter — manual entries don't have an explicit mkt field, derive from broker name
      const mMkt = US_BROKERS.includes(m.brk) ? "US" : "IDX";
      if (perfMkt !== "ALL" && mMkt !== perfMkt) return;
      // Investor/broker filters: manual entries have no investor (entered when single-investor),
      // so apply broker filter only — investor filter falls through (treated as if it matches everyone)
      if (perfBrk !== "ALL" && m.brk !== perfBrk) return;
      if (!manualByPeriod[m.period]) manualByPeriod[m.period] = { bal: 0, fd: 0, count: 0 };
      manualByPeriod[m.period].bal += +m.bal || 0;
      manualByPeriod[m.period].fd  += +m.fd  || 0;
      manualByPeriod[m.period].count++;
    });

    const points = allKeys.map((key, idx) => {
      const endDate = periodEndDates[idx];
      const isLast = idx === allKeys.length - 1;

      // Advance running state up through endDate (always do this so isLast period works)
      advanceTo(endDate);

      // Prefer manual data for past periods
      const manual = !isLast ? manualByPeriod[key] : null;
      if (manual && manual.count > 0) {
        return {
          period: key, endDate, equity: manual.bal,
          cash: null, stockValue: null, stockCostBasis: null,
          flowsThisPeriod: manual.fd,  // override flows with manual fd
          isLast, pricedAll: true, source: "manual",
        };
      }

      // Auto-compute (current period or periods without manual data)
      let stockCostBasis = 0;
      let stockValue = 0;
      let pricedAll = true;
      for (const stk in runningBuys) {
        const lots = runningBuys[stk];
        if (lots.length === 0) continue;
        let totalShares = 0, totalCost = 0;
        for (const l of lots) { totalShares += l.shares; totalCost += l.shares * l.costPer; }
        stockCostBasis += totalCost;
        const histClose = histPrices[stk]?.[key];
        if (isLast && live.prices?.[stk]) {
          stockValue += totalShares * live.prices[stk];
        } else if (histClose != null) {
          stockValue += totalShares * histClose;
        } else {
          stockValue += totalCost;
          if (isLast) pricedAll = false;
        }
      }

      const equity = runningCash + stockValue;
      return {
        period: key, endDate, equity,
        cash: runningCash, stockValue, stockCostBasis,
        flowsThisPeriod: flowsByPeriodIdx[idx], isLast, pricedAll, source: "auto",
      };
    });

    // Compute period-over-period Time-Weighted Return (TWR).
    // TWR formula:  (end_equity − start_equity − net_flows_during_period) / start_equity
    //
    // Skip TWR computation entirely when:
    //  - First period (no prev)
    //  - prev.equity is negative or below operating-size threshold
    //  - prev.equity is too small relative to the flow
    // In skip cases, propagate prev.cumTwr unchanged (don't introduce garbage).
    //
    // ALSO: detect & flag impossible equity (negative). Negative equity is a sign
    // that the user's starting cash is misconfigured; we float it at zero for
    // display purposes and mark the row.
    const finalEquity = points.length > 0 ? Math.abs(points[points.length - 1].equity) : 0;
    const TWR_MIN_BASE = Math.max(Math.min(finalEquity * 0.05, 1_000_000), 100_000);
    points.forEach((p, i) => {
      // Flag impossible equity (negative) — likely a starting-cash data issue
      p.equityFlag = p.equity < 0 ? "negative" : null;
      if (i === 0) {
        p.twr = null;
        p.totalReturn = 0;
        p.cumTwr = 0;
        return;
      }
      const prev = points[i - 1];
      const flow = p.flowsThisPeriod || 0;
      // Skip TWR if prev equity is negative, missing, or below operating size
      if (!prev.equity || prev.equity < TWR_MIN_BASE) {
        p.twr = null;
        p.cumTwr = prev.cumTwr ?? 0;
        p.totalReturn = p.equity - prev.equity;
        return;
      }
      // Skip TWR at the manual→auto boundary. Manual equity reflects the user's
      // actual broker balance (source of truth). Auto equity is derived from
      // running cash + stock value with no historical price feed and depends on
      // starting-cash configuration. Mixing the two in a TWR formula produces
      // garbage (e.g. equity goes UP from manual Rp321jt to auto Rp569jt but
      // TWR shows -95% because the auto equity can't be compared to manual).
      // Honest answer: "—" until forward periods are also tracked manually OR
      // the user reconciles auto with broker statements.
      if (prev.source === "manual" && p.source === "auto") {
        p.twr = null;
        p.cumTwr = prev.cumTwr ?? 0;
        p.totalReturn = p.equity - prev.equity;
        return;
      }
      let raw = (p.equity - prev.equity - flow) / prev.equity * 100;
      if (raw < -95) raw = -95;
      if (raw > 1000) raw = 1000;
      p.twr = raw;
      // Compound, but clamp the cumulative result too
      let nextCum = (1 + (prev.cumTwr || 0) / 100) * (1 + raw / 100) * 100 - 100;
      if (nextCum < -99) nextCum = -99;
      if (nextCum > 100000) nextCum = 100000;
      p.cumTwr = nextCum;
      p.totalReturn = p.equity - prev.equity;
    });

    // Map into index returns from the manual `monthly` data (IHSG / S&P)
    // Index data is captured monthly, so for non-monthly frequencies we approximate
    const indexByMonth = {};
    monthly.forEach(m => {
      // Some monthly entries are duplicated per broker; pick the FIRST per period
      // and merge ALL index fields (ihsg, sp500, lq45, ndx, etc.) — pulling whichever
      // row in the period has the value. This way different rows can supply different
      // index values for the same period.
      INDEX_REGISTRY.forEach(idx => {
        if (m[idx.key] != null) {
          if (!indexByMonth[m.period]) indexByMonth[m.period] = {};
          if (indexByMonth[m.period][idx.key] == null) indexByMonth[m.period][idx.key] = m[idx.key];
        }
      });
    });
    const indexAtPeriodEnd = (key) => {
      // Use the index value of the LAST month within this period
      let lastMonth;
      if (perfFreq === "monthly") lastMonth = key;
      else if (perfFreq === "quarterly") {
        const [y, q] = key.split("-Q");
        lastMonth = `${y}-${String(+q * 3).padStart(2, "0")}`;
      } else {
        lastMonth = `${key}-12`;
      }
      return indexByMonth[lastMonth] || null;
    };
    // Attach raw index values to each period point
    points.forEach(p => {
      const idx = indexAtPeriodEnd(p.period);
      if (idx) {
        INDEX_REGISTRY.forEach(I => {
          if (idx[I.key] != null) p[I.key] = idx[I.key];
        });
      }
    });
    // Compute period returns and cumulative returns for EACH index in registry
    INDEX_REGISTRY.forEach(I => {
      // Period returns: (cur - prev) / prev * 100
      points.forEach((p, i) => {
        const ret = `${I.key}Return`;
        if (i === 0 || p[I.key] == null) { p[ret] = null; return; }
        const prev = points[i - 1];
        if (prev[I.key]) {
          p[ret] = (p[I.key] - prev[I.key]) / prev[I.key] * 100;
        }
      });
      // Cumulative returns
      let cum = 0;
      const cumKey = `cum_${I.key}`;
      points.forEach(p => {
        const ret = p[`${I.key}Return`];
        if (ret != null) cum = (1 + cum / 100) * (1 + ret / 100) * 100 - 100;
        p[cumKey] = p[I.key] != null ? cum : null;
      });
    });

    // Apply date range filter LAST so cumulative returns are still anchored to the first
    // period of activity (otherwise filtering to mid-2024 would distort the cumulative view).
    let filtered = points;
    if (perfDateFrom) filtered = filtered.filter(p => p.period >= perfDateFrom);
    if (perfDateTo)   filtered = filtered.filter(p => p.period <= perfDateTo);

    return { points: filtered, allPoints: points };
  }, [buys, sells, adjustments, cash, live, monthly, histPrices, perfFreq, perfMkt, perfInv, perfBrk, perfDateFrom, perfDateTo, xferRaw]);

  // ── Per-broker breakdown — month-end balance + fund change + gain% per (investor+broker) ──
  // For the same period set as equityCurve, computes balance, fund flows, and gain
  // for each broker account separately. Useful when comparing how different brokers
  // performed in the same month — like the user's spreadsheet view.
  //
  // "Balance" = cash + market value (live prices for current month, cost basis fallback)
  // "Fund Δ" = deposits − withdrawals during that period for this broker
  // "Gain %" = (end_bal − start_bal − fund_change) / start_bal × 100  (same TWR formula)
  // Per-broker breakdown only computed when actually viewing the Performance tab
  // — this is an expensive O(periods × brokers × trades) compute and there's no
  // benefit to running it when the user is on Dashboard, Trade History, etc.
  const brokerCurves = useMemo(() => {
    if (tab !== "perf") return { brokers: [], rows: [] };
    if (!showBrokerTable) return { brokers: [], rows: [] };
    if (equityCurve.points.length === 0) return { brokers: [], rows: [] };

    // Discover all unique (inv, brk) tuples in scope of the current filters,
    // excluding empty placeholder broker "_index" used for index-only rows.
    const tuples = new Set();
    const matches = (x) => {
      if (perfMkt !== "ALL" && x.mkt !== perfMkt) return false;
      if (perfInv !== "ALL" && x.inv !== perfInv) return false;
      if (perfBrk !== "ALL" && x.brk !== perfBrk) return false;
      return true;
    };
    const allBuysX  = [...buys, ...xferRaw.legBuys];    // + transfer legs (no cash)
    const allSellsX = [...sells, ...xferRaw.legSells];
    [...allBuysX, ...allSellsX].forEach(t => {
      if (matches(t)) tuples.add(`${t.inv}::${t.brk}`);
    });
    const brokerList = [...tuples].sort();
    if (brokerList.length === 0) return { brokers: [], rows: [] };

    // Helper: end-of-period date
    const periodEnd = (key) => {
      if (perfFreq === "yearly") return `${key}-12-31`;
      if (perfFreq === "quarterly") {
        const [y, q] = key.split("-Q");
        const lastMonth = +q * 3;
        const lastDay = new Date(Date.UTC(+y, lastMonth, 0)).getUTCDate();
        return `${y}-${String(lastMonth).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      }
      const [y, m] = key.split("-");
      const lastDay = new Date(Date.UTC(+y, +m, 0)).getUTCDate();
      return `${y}-${m}-${String(lastDay).padStart(2, "0")}`;
    };

    // For each broker, build a month-end snapshot table.
    // PRIORITY: use the manual `monthly` entries (entered from user's Excel) when
    // available — these are the most accurate since they reflect the actual broker
    // statement. Fall back to auto-computed values only for periods without manual data.
    //   manual entry: { period, brk, bal, fd }   (bal = balance, fd = fund delta)
    //   auto-compute: cash + market_value (only used when bal is missing)
    const allKeys = equityCurve.allPoints ? equityCurve.allPoints.map(p => p.period) : equityCurve.points.map(p => p.period);

    // Index manual entries by (broker, period) for O(1) lookup
    // Note: manual entries use just broker name (e.g. "Semesta"); we match against the broker
    // portion of brokerKey ("Robert::Semesta" → "Semesta"). Investor-level granularity
    // isn't in manual data — these were entered when there was just one investor.
    const manualByBrkPeriod = {};
    monthly.forEach(m => {
      if (m.brk && m.brk !== "_index" && m.bal != null) {
        const key = `${m.brk}::${m.period}`;
        if (!manualByBrkPeriod[key]) manualByBrkPeriod[key] = m;
      }
    });

    const data = {};
    const periodEndDates = allKeys.map(periodEnd);

    brokerList.forEach(bk => {
      data[bk] = new Map();
      const [inv, brk] = bk.split("::");
      const startingCash = +cash[bk] || 0;
      // Sort once by date — only used for periods WITHOUT manual entries
      const myBuys  = allBuysX.filter(b => b.inv === inv && b.brk === brk).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      const mySells = allSellsX.filter(s => s.inv === inv && s.brk === brk).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      const myAdj   = adjustments.filter(a => a.inv === inv && a.brk === brk).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

      // Running state for auto-compute (only used for non-manual periods)
      let cashBal = startingCash;
      const lots = {};
      let bi = 0, si = 0, ai = 0;

      allKeys.forEach((key, idx) => {
        const endDate = periodEndDates[idx];
        const isLast = idx === allKeys.length - 1;

        // Drain events up to endDate so running state is current — needed even when using
        // manual data, because we want the LAST period to use auto-computed live values.
        let periodFlows = 0;
        while (bi < myBuys.length && myBuys[bi].date <= endDate) {
          const b = myBuys[bi++];
          const shares = b.mkt === "IDX" ? b.qty * 100 : b.qty;
          if (b._xfer) { periodFlows += xferLegValue(b, histPrices); cashBal -= b._xferFee || 0; }  // transfer in: flow + the charge
          else cashBal -= shares * b.px + (b.fee || 0);
          if (!lots[b.stk]) lots[b.stk] = [];
          lots[b.stk].push({ shares, costPer: b.px + (b.fee || 0) / shares });
        }
        while (si < mySells.length && mySells[si].date <= endDate) {
          const s = mySells[si++];
          const shares = s.mkt === "IDX" ? s.qty * 100 : s.qty;
          if (s._xfer) periodFlows -= xferLegValue(s, histPrices);   // transfer out: flow only, no cash
          else cashBal += shares * s.px - (s.fee || 0);
          if (!lots[s.stk]) continue;
          let toSell = shares;
          while (toSell > 0 && lots[s.stk].length > 0) {
            const lot = lots[s.stk][0];
            if (lot.shares <= toSell) { toSell -= lot.shares; lots[s.stk].shift(); }
            else { lot.shares -= toSell; toSell = 0; }
          }
        }
        while (ai < myAdj.length && myAdj[ai].date <= endDate) {
          const a = myAdj[ai++];
          const v = +a.amount || 0;
          if (a.type === "deposit")      { cashBal += v; periodFlows += v; }
          else if (a.type === "withdraw") { cashBal -= v; periodFlows -= v; }
          else                            { cashBal += v; periodFlows += v; }
        }

        // Check for manual entry first
        const manual = manualByBrkPeriod[`${brk}::${key}`];
        if (manual && manual.bal != null) {
          // Use Excel-imported value
          data[bk].set(key, {
            balance: +manual.bal,
            flows: +manual.fd || 0,
            cashBal: null,
            marketValue: null,
            source: "manual",
          });
          return;
        }

        // Auto-compute for periods without manual data (typically current/future)
        let marketValue = 0;
        for (const stk in lots) {
          const own = getOwnership(stk);
          if (own.multiplier === 0) continue;
          let shares = 0, cost = 0;
          for (const l of lots[stk]) { shares += l.shares; cost += l.shares * l.costPer; }
          shares *= own.multiplier;
          cost   *= own.multiplier;
          const histClose = histPrices[stk]?.[key];
          if (isLast && live.prices?.[stk]) marketValue += shares * live.prices[stk];
          else if (histClose != null)        marketValue += shares * histClose;
          else                                marketValue += cost;
        }

        const balance = cashBal + marketValue;
        data[bk].set(key, { balance, flows: periodFlows, cashBal, marketValue, source: "auto" });
      });
    });

    // Build display rows — one per period with all brokers' values inline
    let rows = allKeys.map((period, idx) => {
      const row = { period, brokers: {} };
      brokerList.forEach(bk => {
        const cur = data[bk].get(period);
        const prev = idx > 0 ? data[bk].get(allKeys[idx - 1]) : null;
        // Gain%: same TWR formula. Skip if prev balance is too small.
        let gainPct = null;
        if (prev && prev.balance > 0) {
          const flow = cur.flows;
          // Only meaningful if prev.balance is large enough
          const minBase = Math.max(Math.abs(flow) * 0.1, Math.abs(cur.balance) * 0.01, 100_000);
          if (prev.balance >= minBase) {
            let raw = (cur.balance - prev.balance - flow) / prev.balance * 100;
            if (raw < -95) raw = -95;
            if (raw > 1000) raw = 1000;
            gainPct = raw;
          }
        }
        row.brokers[bk] = { ...cur, gainPct };
      });
      return row;
    });

    // Apply date filter (same as equityCurve)
    if (perfDateFrom) rows = rows.filter(r => r.period >= perfDateFrom);
    if (perfDateTo)   rows = rows.filter(r => r.period <= perfDateTo);

    return { brokers: brokerList, rows };
  }, [tab, showBrokerTable, buys, sells, adjustments, cash, live, monthly, histPrices, perfFreq, perfMkt, perfInv, perfBrk, perfDateFrom, perfDateTo, equityCurve, xferRaw]);

  // ── KPIs ────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const idxClosed = closed.filter(s => s.mkt === "IDX");
    const usClosed  = closed.filter(s => s.mkt === "US");
    return {
      idxReal:  idxClosed.reduce((s, r) => s + r.profit, 0),
      usReal:   usClosed.reduce((s, r) => s + r.profit, 0),
      idxUnreal: openEnriched.filter(p => p.mkt === "IDX" && p.unreal != null).reduce((s, p) => s + p.unreal, 0),
      usUnreal:  openEnriched.filter(p => p.mkt === "US"  && p.unreal != null).reduce((s, p) => s + p.unreal, 0),
      winRate: closed.length ? closed.filter(s => s.profit > 0).length / closed.length * 100 : 0,
      openCount: openPos.length,
      closedCount: closed.length,
    };
  }, [closed, openEnriched, openPos]);

  // ── Pre-IPO deal CRUD handlers ───────────────────────────────
  // Open the deal-editor form, either blank (new) or pre-filled from an existing deal.
  const openDealForm = (deal) => {
    if (deal) {
      setDealForm({
        id: deal.id,
        company: deal.company || "",
        currency: deal.currency || "USD",
        holder: deal.holder || "Raymond",
        status: deal.status || "active",
        principal: deal.principal != null ? String(deal.principal) : "",
        spvFeePct: deal.spvFeePct != null ? String(deal.spvFeePct) : "",
        spvFeeFlat: deal.spvFeeFlat != null ? String(deal.spvFeeFlat) : "",
        carryPct: deal.carryPct != null ? String(deal.carryPct) : "",
        mgmtFeePct: deal.mgmtFeePct != null ? String(deal.mgmtFeePct) : "",
        mgmtFeeYears: deal.mgmtFeeYears != null ? String(deal.mgmtFeeYears) : "",
        mgmtFeePaidYears: deal.mgmtFeePaidYears != null ? String(deal.mgmtFeePaidYears) : "",
        initialDate: deal.initialDate || "",
        initialTransfer: deal.initialTransfer != null ? String(deal.initialTransfer) : "",
        initialTransferIDR: deal.initialTransferIDR != null ? String(deal.initialTransferIDR) : "",
        initialFxRate: deal.initialFxRate != null ? String(deal.initialFxRate) : "",
        note: deal.note || "",
        currentValuation: deal.currentValuation != null ? String(deal.currentValuation) : "",
        postMoneyAtEntry: deal.postMoneyAtEntry != null ? String(deal.postMoneyAtEntry) : "",
      });
    } else {
      setDealForm(emptyDealForm());
    }
    setDealFormOpen(true);
  };
  // Persist the form (creates new or updates existing).
  const saveDealForm = () => {
    if (!dealForm.company.trim()) return flash2("⚠ Company name is required.");
    if (!dealForm.principal || +dealForm.principal <= 0) return flash2("⚠ Principal must be > 0.");
    const cleaned = {
      id: dealForm.id || `preipo_${Date.now()}`,
      company: dealForm.company.trim(),
      currency: dealForm.currency,
      holder: dealForm.holder.trim() || "—",
      status: dealForm.status,
      principal: +dealForm.principal,
      spvFeePct: +dealForm.spvFeePct || 0,
      spvFeeFlat: +dealForm.spvFeeFlat || 0,
      carryPct: +dealForm.carryPct || 0,
      mgmtFeePct: +dealForm.mgmtFeePct || 0,
      mgmtFeeYears: +dealForm.mgmtFeeYears || 0,
      mgmtFeePaidYears: +dealForm.mgmtFeePaidYears || 0,
      initialDate: dealForm.initialDate || null,
      initialTransfer: +dealForm.initialTransfer || 0,
      initialTransferIDR: dealForm.initialTransferIDR ? +dealForm.initialTransferIDR : null,
      initialFxRate: dealForm.initialFxRate ? +dealForm.initialFxRate : null,
      note: dealForm.note || "",
      currentValuation: dealForm.currentValuation ? +dealForm.currentValuation : null,
      postMoneyAtEntry: dealForm.postMoneyAtEntry ? +dealForm.postMoneyAtEntry : null,
      events: dealForm.id ? (preipo.find(d => d.id === dealForm.id)?.events || []) : [],
    };
    setPreipo(prev => {
      const existing = prev.findIndex(d => d.id === cleaned.id);
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = cleaned;
        return next;
      }
      return [...prev, cleaned];
    });
    flash2(dealForm.id ? `✓ Updated ${cleaned.company}` : `✓ Added ${cleaned.company}`);
    setDealForm(emptyDealForm());
    setDealFormOpen(false);
  };
  const removeDeal = (id) => {
    const d = preipo.find(x => x.id === id);
    if (!d) return;
    if (!window.confirm(`Delete ${d.company} and all its events? This cannot be undone.`)) return;
    setPreipo(prev => prev.filter(x => x.id !== id));
    setPreipoDetailId(null);
    flash2(`✓ Deleted ${d.company}`);
  };
  // Reset Pre-IPO data to the built-in corrected seed (with latest valuations).
  // Clears the localStorage fallback copy too, so the seed takes effect cleanly.
  const resetPreipoToSeed = () => {
    if (!window.confirm("Reset ALL Pre-IPO deals to the built-in seed?\n\nThis replaces your current Pre-IPO deals (including any edits) with the corrected defaults, including the latest entry/current valuations. Your stock trades are NOT affected.\n\nContinue?")) return;
    try { if (typeof window !== "undefined") window.localStorage.removeItem("tt:preipo:local"); } catch (e) {}
    setPreipo(INIT_PREIPO);
    // Re-write the local mirror with the seed so it persists.
    try { if (typeof window !== "undefined") window.localStorage.setItem("tt:preipo:local", JSON.stringify(INIT_PREIPO)); } catch (e) {}
    flash2("✓ Pre-IPO reset to corrected seed");
  };
  // Add a sub-event (refund, partial exit, etc.) to a specific deal.
  const addDealEvent = (dealId) => {
    if (!eventForm.amount || +eventForm.amount <= 0) return flash2("⚠ Amount must be > 0.");
    if (!eventForm.date) return flash2("⚠ Date is required.");
    const newEvent = {
      id: `evt_${Date.now()}`,
      date: eventForm.date,
      type: eventForm.type,
      amount: +eventForm.amount,
      note: eventForm.note || "",
    };
    setPreipo(prev => prev.map(d => {
      if (d.id !== dealId) return d;
      const events = [...(d.events || []), newEvent].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      // Auto-update status based on event type for convenience.
      let status = d.status;
      if (newEvent.type === "exit") status = "exited";
      else if (newEvent.type === "partial_exit" && status === "active") status = "partial_exit";
      // Note: refund doesn't auto-change status — user uses the status field to mark cancelled/failed.
      return { ...d, events, status };
    }));
    setEventForm(emptyEventForm());
    flash2(`✓ ${eventForm.type.replace("_", " ")} of ${eventForm.amount} added`);
  };
  const removeDealEvent = (dealId, eventId) => {
    if (!window.confirm("Delete this event?")) return;
    setPreipo(prev => prev.map(d => d.id === dealId
      ? { ...d, events: (d.events || []).filter(e => e.id !== eventId) }
      : d));
  };
  // Mark a deal as fully exited. Optionally records a final exit proceeds amount
  // (the money received on the closing sale) as an "exit" event, then flips status.
  // Use this when you've completely sold out of a position (your green-highlight rule).
  const markDealExited = (dealId) => {
    const d = preipo.find(x => x.id === dealId);
    if (!d) return;
    const finalAmtStr = window.prompt(
      `Mark "${d.company}" as FULLY EXITED.\n\n` +
      `Enter the final NET proceeds in ${d.currency} — the money that actually hit your account (carry, SPV, and management fees already deducted by the operator).\n\n` +
      `• If you've already logged all proceeds as partial-exit events, enter 0.\n` +
      `• Otherwise enter the remaining net amount received now.\n\n` +
      `Cancel to abort.`
    );
    if (finalAmtStr === null) return;  // cancelled
    const finalAmt = +finalAmtStr || 0;
    const exitDate = window.prompt(`Exit date (YYYY-MM-DD):`, todayLocal()) || todayLocal();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(exitDate)) return flash2("⚠ Invalid date — aborted.");
    setPreipo(prev => prev.map(x => {
      if (x.id !== dealId) return x;
      const events = [...(x.events || [])];
      if (finalAmt > 0) {
        events.push({ id: `evt_${Date.now()}`, date: exitDate, type: "exit", amount: finalAmt, note: "Final exit — position fully closed" });
        events.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      }
      return { ...x, status: "exited", events };
    }));
    // Recompute summary for the flash
    const updated = { ...d, status: "exited", events: finalAmt > 0 ? [...(d.events || []), { type: "exit", amount: finalAmt }] : d.events };
    const net = dealNetPnl(updated);
    flash2(`✓ ${d.company} marked fully exited · Net P&L ${d.currency === "IDR" ? idrExact(net) : "$" + net.toLocaleString("en-US", {maximumFractionDigits: 2})}`);
  };
  // Quick re-open (undo accidental exit) — flips back to active.
  const reopenDeal = (dealId) => {
    setPreipo(prev => prev.map(x => x.id === dealId ? { ...x, status: (x.events || []).some(e => e.type === "partial_exit") ? "partial_exit" : "active" } : x));
    flash2("✓ Deal re-opened");
  };

  // ── Save trade ───────────────────────────────────────────────
  const saveTrade = () => {
    if (!bf.stk || !bf.qty || !bf.px || !bf.date) return flash2("Fill all required fields.");
    // Safety: ensure broker is valid for the selected market. Protects against
    // stale broker values in state that the dropdown already reset visually.
    const validBrokers = bf.mkt === "IDX" ? IDX_BROKERS : US_BROKERS;
    if (!validBrokers.includes(bf.brk)) {
      return flash2(`⚠ Broker "${bf.brk}" is not valid for ${bf.mkt}. Select a valid broker.`);
    }
    const id = `${bf.type[0]}${Date.now()}`;
    const qty = +bf.qty, px = +bf.px;
    const shares = bf.mkt === "IDX" ? qty * 100 : qty;
    const val = shares * px;
    // Auto-fee for IDX trades is the percentage component (commission + levy + VAT etc.).
    // Stamp duty is applied separately below as a per (broker, date) charge — see
    // bookStampForDay. This matches how brokers actually charge: Rp 10k once per
    // trading day per broker, regardless of how many trades that day.
    // IPO: no broker fee.
    // Commission only. Stamp duty (Rp 10k) is NOT baked in here — it's decided by
    // reconcileStampGroups below, which applies the per-account, per-day ≥Rp10M rule.
    const autoFee = bf.isIpo
      ? 0
      : calcAutoFee(brokerFees, { mkt: bf.mkt, brk: bf.brk, type: bf.type, gross: val });
    const fee = bf.fee !== "" ? +bf.fee : autoFee;

    const noteBase = bf.note || "";
    const note = bf.isIpo ? (noteBase ? `IPO · ${noteBase}` : "IPO") : noteBase;
    const rec = { id, mkt: bf.mkt, inv: bf.inv, brk: bf.brk, date: bf.date, stk: bf.stk.toUpperCase(), qty, px, fee, note };
    if (bf.isIpo) rec.isIpo = true;

    const nextBuys  = bf.type === "BUY"  ? [...buys, rec]  : buys;
    const nextSells = bf.type === "SELL" ? [...sells, rec] : sells;
    const r = reconcileStampGroups(nextBuys, nextSells, [stampGroupKey(rec)]);
    setBuys(r.buys.slice().sort((a, b) => a.date.localeCompare(b.date)));
    setSells(r.sells.slice().sort((a, b) => a.date.localeCompare(b.date)));

    const dayHasStamp = [...r.buys, ...r.sells].some(t => stampGroupKey(t) === stampGroupKey(rec) && t._stampDay > 0);
    const wasIpo = bf.isIpo;
    setBf(p => ({ ...p, date: "", stk: "", qty: "", px: "", fee: "", note: "", isIpo: false }));
    flash2(`✓ ${wasIpo ? "IPO " : ""}${bf.type} saved — ${rec.stk}${dayHasStamp ? ` · Rp10k meterai (day ≥ Rp10jt)` : ""}`);
  };

  // Stamp duty (Bea Meterai): Rp 10,000 per ACCOUNT (investor+broker) per DAY, only when
  // that day's transaction total (buys + sells, ex-IPO) reaches Rp 10M. Applied via the pure
  // reconcileStampGroups() on every add/edit/delete — see the helper near the top of the file.

  // ── Save monthly ─────────────────────────────────────────────
  const saveMonthly = () => {
    if (!mf.period || !mf.ihsg || !mf.brk || !mf.bal) return flash2("Fill period, IHSG, broker, balance.");
    const id = `m${Date.now()}`;
    const rec = { id, period: mf.period, ihsg: +mf.ihsg, sp500: mf.sp500 ? +mf.sp500 : null, brk: mf.brk, bal: +mf.bal, fd: mf.fd ? +mf.fd : 0 };
    setMonthly(p => {
      const filtered = p.filter(x => !(x.period === mf.period && x.brk === mf.brk));
      return [...filtered, rec].sort((a, b) => a.period.localeCompare(b.period) || a.brk.localeCompare(b.brk));
    });
    setMf(p => ({ ...p, period: "", bal: "", fd: "", sp500: "" }));
    flash2(`✓ Saved ${mf.period} — ${mf.brk}`);
  };

  // ── Batch trade entry ──────────────────────────────────────────────
  // Row-based batch: user fills multiple rows (each with date/stock/qty/price/fee/type),
  // sees live per-row totals and a grand total, then commits everything at once.
  const updateBatchRow = (id, field, value) => {
    setBatchRows(rows => rows.map(r => r.id === id ? { ...r, [field]: value } : r));
  };
  const addBatchRow = (afterId = null) => {
    setBatchRows(rows => {
      const last = afterId ? rows.find(r => r.id === afterId) : rows[rows.length - 1];
      // Inherit type from previous row to speed up entry. Date and fee are shared, not per-row.
      const newRow = emptyBatchRow();
      if (last) { newRow.type = last.type || "BUY"; newRow.isIpo = !!last.isIpo; }
      return [...rows, newRow];
    });
  };
  const removeBatchRow = (id) => {
    setBatchRows(rows => rows.length > 1 ? rows.filter(r => r.id !== id) : rows);
  };
  const duplicateBatchRow = (id) => {
    setBatchRows(rows => {
      const idx = rows.findIndex(r => r.id === id);
      if (idx < 0) return rows;
      const copy = { ...rows[idx], id: `r_${Date.now()}_${Math.random().toString(36).slice(2,7)}` };
      return [...rows.slice(0, idx + 1), copy, ...rows.slice(idx + 1)];
    });
  };
  const clearBatchRows = () => {
    if (!confirm("Clear all batch rows?")) return;
    setBatchRows([emptyBatchRow()]);
  };

  // Compute per-row preview values (gross, fee, net). Pure derivation, no state.
  // Fee = gross × (buyFeePct or sellFeePct) / 100, rounded to whole rupiah for IDX.
  // Note: stamp duty (Rp 10k/day) is NOT included in per-row fees here. It's decided by
  // reconcileStampGroups (per-account, per-day ≥Rp10M rule) after the batch is added.
  const computeBatchRow = (r) => {
    const qty = +r.qty || 0;
    const px  = +r.px  || 0;
    const shares = bf.mkt === "IDX" ? qty * 100 : qty;
    const gross  = shares * px;
    // IPO allotment (IDX BUY only): no broker fee, and excluded from stamp duty by reconcile.
    const isIpo = !!r.isIpo && r.type === "BUY" && bf.mkt === "IDX";
    const feePct = isIpo ? 0 : +(r.type === "BUY" ? batchShared.buyFeePct : batchShared.sellFeePct) || 0;
    const feeRaw = gross * feePct / 100;
    const fee = bf.mkt === "IDX" ? Math.round(feeRaw) : +feeRaw.toFixed(2);
    const net = r.type === "BUY" ? gross + fee : gross - fee;
    const dateValid = batchShared.date && /^\d{4}-\d{2}-\d{2}$/.test(batchShared.date);
    const isValid = dateValid && r.stk.trim() && qty > 0 && px > 0;
    return { qty, px, fee, feePct, shares, gross, net, isValid, isIpo };
  };

  // Save all valid rows as new trades
  const saveBatch = () => {
    const validBrokers = bf.mkt === "IDX" ? IDX_BROKERS : US_BROKERS;
    if (!validBrokers.includes(bf.brk)) {
      return flash2(`⚠ Broker "${bf.brk}" is not valid for ${bf.mkt}. Fix in form first.`);
    }
    const valid = batchRows.filter(r => computeBatchRow(r).isValid);
    if (valid.length === 0) return flash2("⚠ No valid rows. Each row needs date, stock, qty > 0, price > 0.");
    const invalid = batchRows.length - valid.length;
    const proceed = invalid === 0
      ? confirm(`Import ${valid.length} trade${valid.length !== 1 ? "s" : ""} to ${bf.inv} · ${bf.brk}?`)
      : confirm(`Import ${valid.length} valid trade${valid.length !== 1 ? "s" : ""} to ${bf.inv} · ${bf.brk}?\n\n${invalid} row${invalid !== 1 ? "s" : ""} will be skipped (incomplete).`);
    if (!proceed) return;

    const newBuys = [], newSells = [];
    const stamp = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    let n = 0;
    valid.forEach(r => {
      const stkClean = r.stk.trim().toUpperCase();
      const id = `${r.type[0]}_batch_${stamp}_${++n}`;
      const preview = computeBatchRow(r);
      const trade = {
        id, mkt: bf.mkt, inv: bf.inv, brk: bf.brk,
        date: batchShared.date, stk: stkClean, qty: +r.qty, px: +r.px, fee: preview.fee,  // commission only
      };
      if (preview.isIpo) { trade.isIpo = true; trade.note = "IPO"; }
      if (r.type === "BUY") newBuys.push(trade);
      else newSells.push(trade);
    });
    // Stamp is decided by the per-account, per-day ≥Rp10M rule (reconcile), not per batch.
    const key = `${bf.inv}||${bf.brk}||${batchShared.date}`;
    const r = reconcileStampGroups([...buys, ...newBuys], [...sells, ...newSells], [key]);
    setBuys(r.buys.slice().sort((a, b) => a.date.localeCompare(b.date)));
    setSells(r.sells.slice().sort((a, b) => a.date.localeCompare(b.date)));
    const dayHasStamp = [...r.buys, ...r.sells].some(t => stampGroupKey(t) === key && t._stampDay > 0);
    const stampMsg = dayHasStamp ? ` · Rp10k meterai (day ≥ Rp10jt)` : "";
    flash2(`✓ Imported ${valid.length} trades (${newBuys.length} buys, ${newSells.length} sells) to ${bf.inv} · ${bf.brk}${stampMsg}`);
    setBatchRows([emptyBatchRow()]);
  };

  // ── Live prices ──────────────────────────────────────────────
  // fetchLive — refresh live prices using Yahoo Finance via /api/yahoo-quote.
  // Free, fast (~1-2 seconds for 30 tickers), no rate limits.
  //   fetchLive()                  → all open positions
  //   fetchLive(["INTC", "AMD"])   → just those tickers
  //   fetchLive(null, true)        → all open positions but SKIP already-fresh (< 4h old)
  const fetchLive = useCallback(async (tickersFilter = null, skipFresh = false) => {
    const FRESH_MS = 4 * 3600 * 1000;  // 4 hours — prices younger than this are considered fresh
    const now = Date.now();
    let idxTickers = [...new Set(openPos.filter(p => p.mkt === "IDX").map(p => p.stk))];
    let usTickers  = [...new Set(openPos.filter(p => p.mkt === "US").map(p => p.stk))];

    if (tickersFilter) {
      const wanted = new Set(tickersFilter);
      idxTickers = idxTickers.filter(t => wanted.has(t));
      usTickers  = usTickers.filter(t => wanted.has(t));
    }
    if (skipFresh) {
      const ts = live.timestamps || {};
      const isStale = t => !ts[t] || (now - new Date(ts[t]).getTime()) > FRESH_MS;
      idxTickers = idxTickers.filter(isStale);
      usTickers  = usTickers.filter(isStale);
    }
    if (!idxTickers.length && !usTickers.length) {
      return flash2(skipFresh ? "✓ All prices are fresh — nothing to refetch" : "No open positions to fetch");
    }
    setLoading(p => ({ ...p, live: true }));

    // Build (yahooSymbol, originalTicker, mkt) tuples.
    // IDX tickers need .JK suffix on Yahoo; US tickers go as-is.
    // Tickers with ownership suffixes (.RF, .RUDI) are stripped to their base
    // ticker for the Yahoo lookup. The price returned is the FULL share price —
    // the ownership multiplier is applied LATER when computing equity/P&L.
    // Skip .RUDI tickers entirely since they're not the user's holding (multiplier = 0).
    const requests = [
      ...idxTickers.map(t => {
        const own = getOwnership(t);
        return { ticker: t, yahoo: `${own.baseTicker}.JK`, mkt: "IDX", multiplier: own.multiplier };
      }),
      ...usTickers.map(t => {
        const own = getOwnership(t);
        return { ticker: t, yahoo: own.baseTicker, mkt: "US", multiplier: own.multiplier };
      }),
    ].filter(r => r.multiplier > 0);  // skip .RUDI (not yours)

    // Also fetch the benchmark indexes themselves (IHSG, S&P 500) so the current
    // month's index columns in the Performance table can show a live value. Stored
    // under synthetic tickers "__ihsg" / "__sp500". Only when not filtering to
    // specific position tickers (a full refresh) so single-ticker refetches stay cheap.
    if (!tickersFilter) {
      INDEX_REGISTRY.forEach(I => {
        requests.push({ ticker: `__${I.key}`, yahoo: I.yahoo, mkt: I.mkt, multiplier: 1 });
      });
    }

    // Yahoo doesn't rate-limit aggressively, so fetch in parallel with a small
    // concurrency cap to avoid hammering the proxy. Process in chunks of 10.
    const CONCURRENCY = 10;
    const allPrices = {};
    const failed = [];

    const fetchOne = async ({ ticker, yahoo, mkt }) => {
      try {
        const r = await fetch(`/api/yahoo-quote?ticker=${encodeURIComponent(yahoo)}`);
        if (!r.ok) {
          failed.push(ticker);
          return;
        }
        const data = await r.json();
        // Yahoo returns: { chart: { result: [{ meta: { regularMarketPrice, ... }, ... }] } }
        const result = data?.chart?.result?.[0];
        const px = result?.meta?.regularMarketPrice;
        if (typeof px === "number" && !isNaN(px) && px > 0) {
          allPrices[ticker] = px;
        } else {
          failed.push(ticker);
        }
      } catch (err) {
        console.warn(`Yahoo fetch failed for ${ticker}:`, err);
        failed.push(ticker);
      }
    };

    try {
      // Process in concurrent batches
      for (let i = 0; i < requests.length; i += CONCURRENCY) {
        const batch = requests.slice(i, i + CONCURRENCY);
        if (i > 0) {
          flash2(`Fetching ${i + batch.length}/${requests.length}…`);
        }
        await Promise.all(batch.map(fetchOne));
      }

      // Compute trading day from the first successful fetch
      // (we don't track tradingDay per ticker in this version — it's roughly today)
      const tradingDay = new Date().toISOString().slice(0, 10);
      const nowISO = new Date().toISOString();
      const requestedSet = new Set(requests.map(r => r.ticker));

      setLive(prev => {
        const oldPrices = prev.prices || {};
        const oldTimestamps = prev.timestamps || {};
        const newPrices = { ...oldPrices };
        const newTimestamps = { ...oldTimestamps };
        // Update fetched ones; preserve old prices for failed tickers.
        requestedSet.forEach(t => {
          if (t in allPrices) {
            newPrices[t] = allPrices[t];
            newTimestamps[t] = nowISO;
          }
          // else: leave the old price in place (Yahoo couldn't find it — usually delisted/illiquid)
        });
        return {
          ...prev,
          prices: newPrices,
          timestamps: newTimestamps,
          tradingDay,
          at: nowISO,
          source: "yahoo",
          note: failed.length > 0 ? `${failed.length} ticker(s) not found on Yahoo` : "",
        };
      });

      const fetchedCount = Object.keys(allPrices).length;
      if (fetchedCount === requests.length) {
        flash2(`✓ All ${fetchedCount} prices updated (Yahoo Finance)`);
      } else if (fetchedCount > 0) {
        flash2(`✓ Got ${fetchedCount}/${requests.length}. Missing on Yahoo: ${failed.slice(0, 5).join(", ")}${failed.length > 5 ? ` +${failed.length - 5} more` : ""}`);
      } else {
        flash2(`⚠ No prices fetched. Yahoo may be down — check console.`);
      }
    } catch (e) {
      flash2("⚠ Fetch failed: " + e.message.slice(0, 120));
    }
    finally { setLoading(p => ({ ...p, live: false })); }
  }, [openPos, live]);


  // ── Auto-fetch index historical closes via Yahoo Finance ──────────────────
  // One Yahoo call returns the full multi-year history at monthly interval.
  // We extract the close on the last trading day of each month and merge into
  // the `monthly` array. Only fills missing months — doesn't overwrite manual
  // entries.
  const fetchIndexHistory = useCallback(async (indexKey, indexLabel) => {
    const idx = INDEX_REGISTRY.find(I => I.key === indexKey);
    if (!idx) return flash2(`⚠ Unknown index: ${indexKey}`);

    setLoading(p => ({ ...p, idx: true }));
    flash2(`Fetching ${indexLabel} from Yahoo Finance…`);

    try {
      // Use 10y range — covers most users' history. Yahoo accepts 1d|5d|1mo|3mo|6mo|1y|2y|5y|10y|max
      const resp = await fetch(`/api/yahoo-quote?ticker=${encodeURIComponent(idx.yahoo)}&history=1&range=10y&interval=1mo`);
      if (!resp.ok) {
        flash2(`⚠ Yahoo returned ${resp.status} for ${indexLabel}`);
        return;
      }
      const data = await resp.json();
      const result = data?.chart?.result?.[0];
      if (!result) {
        flash2(`⚠ No data returned for ${indexLabel}`);
        return;
      }
      // Yahoo returns parallel arrays: timestamp[] (Unix seconds), indicators.quote[0].close[]
      const timestamps = result.timestamp || [];
      const closes = result.indicators?.quote?.[0]?.close || [];
      if (timestamps.length === 0 || closes.length === 0) {
        flash2(`⚠ Empty data for ${indexLabel}`);
        return;
      }

      // Build {YYYY-MM: closeValue} from the parallel arrays.
      // Yahoo's monthly interval gives the OPEN of each month — so we want the
      // close of the LAST trading day in each month. We can derive this from the
      // PREVIOUS month's data point (since the last close before a new month
      // begins IS the previous month's last close).
      // Alternative: just use what Yahoo gives. For long-term comparisons the
      // delta is negligible, and the "monthly close" Yahoo returns is typically
      // the close at end-of-period anyway when you query with interval=1mo.
      const closesByMonth = {};
      timestamps.forEach((ts, i) => {
        const close = closes[i];
        if (close == null || isNaN(close)) return;
        const d = new Date(ts * 1000);
        const ym = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
        closesByMonth[ym] = +close.toFixed(2);
      });

      // Determine which months we actually need (from earliest trade onwards)
      const allEvents = [...buys, ...sells];
      if (allEvents.length === 0) {
        flash2("⚠ No trades yet — add trades first");
        return;
      }
      const earliestDate = allEvents.reduce((min, e) => !min || e.date < min ? e.date : min, "");
      const startKey = earliestDate.slice(0, 7);

      // Merge into `monthly` array. Fills missing values, and ALWAYS refreshes the
      // two most recent months (their close may have been provisional/missing before).
      const fetchedMonths = Object.keys(closesByMonth).filter(m => m >= startKey).sort();
      const recentTwo = new Set(fetchedMonths.slice(-2));
      let added = 0, skipped = 0;
      setMonthly(prev => {
        const next = [...prev];
        fetchedMonths.forEach(period => {
          const val = closesByMonth[period];
          // Find any existing row for this period — keep its existing index value if set,
          // unless it's one of the two most recent months (always refresh those).
          const idxRow = next.findIndex(r => r.period === period);
          if (idxRow >= 0) {
            if (next[idxRow][indexKey] != null && !recentTwo.has(period)) { skipped++; return; }
            next[idxRow] = { ...next[idxRow], [indexKey]: val };
            added++;
          } else {
            // Create a placeholder row using "_index" as broker
            next.push({ period, brk: "_index", bal: null, fd: null, [indexKey]: val });
            added++;
          }
        });
        return next.sort((a, b) => a.period.localeCompare(b.period));
      });

      if (added === 0) {
        flash2(`✓ ${indexLabel}: already up to date`);
      } else {
        flash2(`✓ ${indexLabel}: ${added} months fetched${skipped > 0 ? ` (${skipped} preserved)` : ""}`);
      }
    } catch (e) {
      flash2("⚠ Index fetch failed: " + e.message.slice(0, 120));
    } finally {
      setLoading(p => ({ ...p, idx: false }));
    }
  }, [buys, sells, monthly]);

  // ── Fetch historical monthly closes for stocks (Yahoo) ─────────────────
  // Used to mark-to-market past months in the per-broker breakdown.
  // Strategy:
  //  - Walk the unique tickers from buys+sells (not openPos — we want the
  //    history of every stock you've ever held, including closed ones).
  //  - For each ticker, only fetch if not already cached, OR if last cached
  //    month is older than current month.
  //  - One Yahoo call per ticker (10y monthly history). They're free, parallel.
  //  - Apply ownership multiplier for display, but cache the FULL price.
  const fetchHistoricalPrices = useCallback(async () => {
    const allTickers = [...new Set([...buys.map(b => b.stk), ...sells.map(s => s.stk)])];
    if (allTickers.length === 0) return flash2("No trades yet");

    const today = new Date();
    const currentMonth = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, "0")}`;

    // Skip tickers that already have data for this month (cache is fresh)
    const toFetch = allTickers.filter(t => {
      const own = getOwnership(t);
      if (own.multiplier === 0) return false;  // skip .RUDI
      const cached = histPrices[t];
      if (!cached) return true;  // never fetched
      const cachedMonths = Object.keys(cached);
      if (cachedMonths.length === 0) return true;
      // If we have data for the current month, it's fresh
      return !cachedMonths.includes(currentMonth);
    });

    if (toFetch.length === 0) {
      return flash2(`✓ Historical prices already cached for all ${allTickers.length} tickers`);
    }

    setLoading(p => ({ ...p, hist: true }));
    flash2(`Fetching historical prices for ${toFetch.length} ticker${toFetch.length !== 1 ? "s" : ""}…`);

    const CONCURRENCY = 6;
    const failed = [];
    const fetched = {};

    const fetchOne = async (ticker) => {
      const own = getOwnership(ticker);
      const isIDX = !US_BROKERS.includes("Stockbit") || ticker.match(/^[A-Z]{4}/);  // crude — check market
      // Better: get market from any buy of this ticker
      const buyOfThis = buys.find(b => b.stk === ticker) || sells.find(s => s.stk === ticker);
      const mkt = buyOfThis?.mkt || "IDX";
      const yahooSym = mkt === "IDX" ? `${own.baseTicker}.JK` : own.baseTicker;
      try {
        const r = await fetch(`/api/yahoo-quote?ticker=${encodeURIComponent(yahooSym)}&history=1&range=10y&interval=1mo`);
        if (!r.ok) { failed.push(ticker); return; }
        const data = await r.json();
        const result = data?.chart?.result?.[0];
        const timestamps = result?.timestamp || [];
        const closes = result?.indicators?.quote?.[0]?.close || [];
        if (timestamps.length === 0) { failed.push(ticker); return; }
        const monthlyMap = {};
        timestamps.forEach((ts, i) => {
          const close = closes[i];
          if (close == null || isNaN(close)) return;
          const d = new Date(ts * 1000);
          const ym = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
          monthlyMap[ym] = +close.toFixed(mkt === "IDX" ? 0 : 4);
        });
        if (Object.keys(monthlyMap).length === 0) {
          failed.push(ticker);
          return;
        }
        fetched[ticker] = monthlyMap;
      } catch (err) {
        console.warn(`Hist fetch failed for ${ticker}:`, err);
        failed.push(ticker);
      }
    };

    try {
      for (let i = 0; i < toFetch.length; i += CONCURRENCY) {
        const batch = toFetch.slice(i, i + CONCURRENCY);
        if (i > 0) flash2(`Fetching ${i + batch.length}/${toFetch.length}…`);
        await Promise.all(batch.map(fetchOne));
      }
      // Merge into histPrices state
      if (Object.keys(fetched).length > 0) {
        setHistPrices(prev => {
          const next = { ...prev };
          Object.entries(fetched).forEach(([ticker, monthly]) => {
            next[ticker] = { ...(next[ticker] || {}), ...monthly };
          });
          return next;
        });
      }
      const ok = Object.keys(fetched).length;
      if (ok === toFetch.length) {
        flash2(`✓ Historical prices fetched for ${ok} ticker${ok !== 1 ? "s" : ""}`);
      } else if (ok > 0) {
        flash2(`✓ Got ${ok}/${toFetch.length}. Missing on Yahoo: ${failed.slice(0, 5).join(", ")}${failed.length > 5 ? ` +${failed.length - 5} more` : ""}`);
      } else {
        flash2(`⚠ No historical prices fetched`);
      }
    } catch (e) {
      flash2("⚠ Historical fetch failed: " + e.message.slice(0, 120));
    } finally {
      setLoading(p => ({ ...p, hist: false }));
    }
  }, [buys, sells, histPrices]);

  // Manual price entry
  const [manualPriceStock, setManualPriceStock] = useState("");
  const [manualPriceValue, setManualPriceValue] = useState("");
  const setManualPrice = () => {
    if (!manualPriceStock || !manualPriceValue) return flash2("⚠ Select stock and enter price");
    const val = +manualPriceValue;
    if (isNaN(val) || val <= 0) return flash2("⚠ Invalid price");
    setLive(prev => ({
      ...prev,
      prices: { ...(prev.prices || {}), [manualPriceStock]: val },
      at: new Date().toISOString(),
      note: "manual entry",
    }));
    flash2(`✓ ${manualPriceStock} = ${val} set manually`);
    setManualPriceValue("");
  };
  const clearPrice = (stk) => {
    setLive(prev => {
      const next = { ...(prev.prices || {}) };
      delete next[stk];
      return { ...prev, prices: next };
    });
    flash2(`✓ ${stk} price cleared`);
  };

  // ── Auto-refresh once daily after market close ────────────
  // IDX closes 16:00 WIB (UTC+7) = 09:00 UTC
  // Auto-refresh policy: when the app is opened (or while it's open), automatically
  // refetch live prices if the last fetch is older than the most recent expected
  // market close. This handles the common case where the user closes the browser
  // overnight and reopens it the next day — without their previous "auto-fetch
  // window" approach (which silently skipped if you weren't online during 22:15
  // UTC). Now: every time the app loads, if data is stale → fetch it.
  //
  // Logic:
  // - If user has NO open positions, do nothing.
  // - If `live.at` is missing or before yesterday's expected close, refetch.
  // - Also check periodically (every 30 min) while app is open, in case the
  //   user leaves it open across a market close.
  useEffect(() => {
    if (!autoRefresh) return;
    const check = () => {
      if (openPos.length === 0) return;
      const now = new Date();
      const lastAt = live.at ? new Date(live.at) : null;

      // Compute the most recent market close expected to be available.
      // For simplicity, use US market close at 21:00 UTC (4 PM ET, post-DST).
      // Walk back day-by-day until we find the most recent past US close.
      const lastClose = new Date(now);
      lastClose.setUTCMinutes(0, 0, 0);
      lastClose.setUTCHours(21);  // 21:00 UTC ≈ US market close
      // If the close hasn't happened yet today, use yesterday's
      if (lastClose > now) lastClose.setUTCDate(lastClose.getUTCDate() - 1);
      // Walk back over weekends — Saturday (6) and Sunday (0) — to last weekday close
      while (lastClose.getUTCDay() === 0 || lastClose.getUTCDay() === 6) {
        lastClose.setUTCDate(lastClose.getUTCDate() - 1);
      }

      // If never fetched, or last fetch is before this most recent close, refetch
      if (!lastAt || lastAt < lastClose) {
        fetchLive();
      }
    };
    // Initial check on mount + state change
    check();
    // Periodic check every 30 minutes (catches the case where the app is left
    // open across a market close, e.g. user opens it before US close, leaves it
    // open, US closes, prices need to be refetched 30 min later)
    const iv = setInterval(check, 30 * 60 * 1000);
    return () => clearInterval(iv);
  }, [autoRefresh, live.at, openPos, fetchLive]);

  // ── Floating P&L aggregated per STOCK (all lots combined) ──
  const floatingPL = useMemo(() => {
    const map = {};
    openEnriched.forEach(p => {
      const k = `${p.inv}::${p.brk}::${p.stk}::${p.mkt}`;
      if (!map[k]) map[k] = {
        inv: p.inv, brk: p.brk, stk: p.stk, mkt: p.mkt,
        totalQty: 0, totalUnits: 0, totalCost: 0, avgCost: 0,
        livePrice: p.lp, currentVal: 0, unrealized: 0, unrealPct: 0,
        lotCount: 0, firstBuy: p.date, lastBuy: p.date,
      };
      const r = map[k];
      r.totalQty += p.qty;
      r.totalUnits += p.units;
      r.totalCost += p.totalCost;
      r.lotCount++;
      r.firstBuy = p.date < r.firstBuy ? p.date : r.firstBuy;
      r.lastBuy  = p.date > r.lastBuy  ? p.date : r.lastBuy;
    });
    // Compute aggregates
    Object.values(map).forEach(r => {
      r.avgCost = r.totalCost / r.totalUnits;
      if (r.livePrice) {
        r.currentVal  = r.totalUnits * r.livePrice;
        r.unrealized  = r.currentVal - r.totalCost;
        r.unrealPct   = r.totalCost > 0 ? r.unrealized / r.totalCost * 100 : 0;
      }
    });
    return Object.values(map).sort((a, b) => {
      // Sort by biggest absolute unrealized first
      return Math.abs(b.unrealized || 0) - Math.abs(a.unrealized || 0);
    });
  }, [openEnriched]);

  // ── AI Weekly Analysis ───────────────────────────────────────
  const runAI = useCallback(async () => {
    setLoading(p => ({ ...p, ai: true })); setAiText("");

    // Aggregate open positions BY STOCK (not per-lot). This gives AI a cleaner
    // view: one line per ticker with blended avg cost + current P&L.
    const byStock = {};
    openEnriched.forEach(p => {
      const key = `${p.stk}::${p.mkt}`;
      if (!byStock[key]) {
        byStock[key] = {
          stock: p.stk, mkt: p.mkt,
          totalShares: 0, totalCost: 0, livePrice: p.lp || null,
          lotCount: 0, oldestDate: p.date,
        };
      }
      const r = byStock[key];
      const shares = p.mkt === "IDX" ? p.qty * 100 : p.qty;
      r.totalShares += shares;
      r.totalCost   += p.totalCost;
      r.lotCount    += 1;
      if (p.date < r.oldestDate) r.oldestDate = p.date;
    });

    // Stock selection strategy:
    // - Include ALL US stocks (you typically have 3-5; they're meaningful individually)
    // - Include top IDX stocks by cost basis up to total MAX_STOCKS
    // Can't compare cost basis across markets because IDR vs USD have vastly different scales
    const MAX_STOCKS = 18;
    const allStocks = Object.values(byStock).map(r => {
      const avgCost = r.totalShares ? r.totalCost / r.totalShares : 0;
      const unrealPct = r.livePrice ? ((r.livePrice - avgCost) / avgCost) * 100 : null;
      const daysHeld = Math.round((new Date() - new Date(r.oldestDate)) / 86400000);
      return {
        stock: r.stock,
        mkt: r.mkt,
        qty: r.mkt === "IDX" ? `${r.totalShares / 100}L` : `${r.totalShares}sh`,
        avgCost: r.mkt === "IDX" ? `Rp${R(avgCost)}` : `$${R(avgCost, 3)}`,
        livePrice: r.livePrice ? (r.mkt === "IDX" ? `Rp${r.livePrice}` : `$${r.livePrice}`) : "?",
        unrealPct: unrealPct != null ? pct(unrealPct) : "?",
        heldDays: daysHeld,
        _costForSort: r.totalCost,
      };
    });
    const usStocks  = allStocks.filter(s => s.mkt === "US");
    const idxStocks = allStocks.filter(s => s.mkt === "IDX").sort((a, b) => b._costForSort - a._costForSort);
    // Always keep all US stocks, fill remaining slots with top IDX
    const idxSlots = Math.max(0, MAX_STOCKS - usStocks.length);
    const selectedIdx = idxStocks.slice(0, idxSlots);
    const stocks = [...usStocks, ...selectedIdx].map(({ _costForSort, ...rest }) => rest);
    const omittedCount = allStocks.length - stocks.length;

    try {
      const res = await fetch("/api/anthropic-proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-5",
          max_tokens: 6000,
          tools: [{ type: "web_search_20250305", name: "web_search", max_uses: 15 }],
          system: `Kamu adalah analis saham senior yang menganalisis portofolio investor Indonesia dengan posisi di IDX dan US market, serta memberikan rekomendasi saham prospektif dari pasar luas.
PENTING: Analisis SEMUA saham yang diberikan, baik IDX maupun US. Jangan skip pasar manapun.
Gunakan MAKSIMAL 10-12 pencarian web untuk efisiensi. Cari: kondisi IHSG + S&P 500, saham individual yang penting, DAN 2-3 saham prospektif yang TIDAK dimiliki investor.
Buat laporan analisis dalam Bahasa Indonesia dengan format:

## 🗓 Ringkasan Eksekutif
(Kondisi IHSG DAN S&P 500/NASDAQ hari ini — 2-3 kalimat per pasar)

## 📊 Analisis Portofolio Saat Ini
### Saham IDX yang Dimiliki
(Untuk setiap saham IDX: kondisi terkini, tren, outlook — ringkas 1-2 kalimat)

### Saham US yang Dimiliki
(Untuk setiap saham US: kondisi terkini, tren, outlook — ringkas 1-2 kalimat)

## 💡 Rekomendasi Posisi Saat Ini
(Tiap saham yang dimiliki: **HOLD** / **ADD** / **REDUCE** / **SELL** + alasan spesifik 1 kalimat — WAJIB mencakup semua saham IDX dan US yang dimiliki)

## 🎯 Peluang Saham Baru (Buy Recommendation)
Cari dan rekomendasikan 2-3 saham PROSPEKTIF dari pasar (IDX atau US) yang BELUM dimiliki investor. Fokus pada:
- Saham dengan katalis positif terbaru (earnings beat, corporate action, sektor rotation)
- Valuasi menarik (PER/PBV di bawah rata-rata industri)
- Momentum teknikal positif atau breakout patterns
Untuk tiap saham rekomendasi:
- **TICKER** (IDX/US) — current price, valuasi singkat
- Katalis spesifik & alasan prospektif (2-3 kalimat)
- Target price atau entry zone yang wajar
- Risiko kunci

## ⚠️ Risiko Utama
(Maks 4 bullet points — campuran risiko IDX dan US)

## ✅ Aksi Prioritas Minggu Ini
(Numbered list, actionable dan spesifik untuk kedua pasar)

Gunakan data nyata dari pencarian. Berikan angka spesifik. Bersikap tegas.`,
          messages: [{
            role: "user",
            content: `Analisis portofolio saya. Total ${stocks.length} saham${omittedCount ? ` (${omittedCount} posisi kecil IDX dilewati untuk efisiensi)` : ""}.
IDX: ${stocks.filter(s => s.mkt === "IDX").length} saham
US:  ${stocks.filter(s => s.mkt === "US").length} saham

**Posisi Terbuka Saat Ini:**
${JSON.stringify(stocks, null, 2)}

WAJIB dilakukan:
1. Berikan analisis + rekomendasi HOLD/ADD/REDUCE/SELL untuk SETIAP saham di atas, termasuk saham US.
2. Cari 2-3 saham PROSPEKTIF dari pasar luas (IDX atau US) yang BELUM ada di portofolio saya. Ini penting — berikan rekomendasi BUY dengan katalis dan target price.

Jangan hanya fokus pada saham yang sudah dimiliki — investor ingin ide saham baru yang layak dibeli minggu ini.`,
          }]
        })
      });

      if (!res.ok) {
        const errText = await res.text();
        let errMsg = `API ${res.status}`;
        try {
          const errJson = JSON.parse(errText);
          errMsg = errJson.error?.message || errJson.message || errText.slice(0, 300);
        } catch { errMsg = errText.slice(0, 300); }
        if (res.status === 429) {
          throw new Error(`Rate limit hit (429). Wait a minute and try again, or upgrade your Anthropic tier.`);
        }
        if (res.status === 504 || res.status === 502) {
          throw new Error(`Server timeout (${res.status}). Analysis took too long — try again in a moment.`);
        }
        throw new Error(errMsg);
      }

      const data = await res.json();
      if (data.error) {
        throw new Error(data.error.message || JSON.stringify(data.error));
      }
      const txt = (data.content || []).filter(c => c.type === "text").map(c => c.text).join("\n");
      if (!txt.trim()) {
        throw new Error("AI returned empty response. Try again or check API status.");
      }
      setAiText(txt);
    } catch (e) {
      console.error("AI analysis error:", e);
      setAiText(`❌ **Error running analysis**\n\n\`\`\`\n${e.message}\n\`\`\`\n\n**Possible fixes:**\n- Wait 1-2 minutes if you hit a rate limit (you've been fetching live prices too)\n- Check your Anthropic account has credits at https://console.anthropic.com\n- If error persists, open browser console (F12) and check network tab for details`);
    }
    finally { setLoading(p => ({ ...p, ai: false })); }
  }, [openEnriched]);

  // ── PDF Import (supports multiple files at once) ─────────────
  const handlePDF = useCallback(async e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setLoading(p => ({ ...p, pdf: true })); setPdfRes(null);

    const aggregate = { market: null, broker: null, date: null, currency: null, investor: null, account_no: null, expectedSettlement: null, trades: [], _files: files.length };
    let successCount = 0;

    for (const file of files) {
      try {
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result.split(",")[1]);
          r.onerror = rej;
          r.readAsDataURL(file);
        });
        const single = await extractPdfTrades(b64);
        if (single?.trades?.length) {
          // Merge metadata (first valid wins, but log if conflicting)
          aggregate.market   ||= single.market;
          aggregate.broker   ||= single.broker;
          aggregate.date     ||= single.date;
          aggregate.currency ||= single.currency;
          aggregate.investor ||= single.investor;
          aggregate.account_no ||= single.account_no;
          // Sum expected settlements across multiple PDFs (each PDF has its own settlement)
          if (single.expected_settlement != null) {
            aggregate.expectedSettlement = (aggregate.expectedSettlement || 0) + Number(single.expected_settlement);
          }
          // STAMP DUTY HANDLING — positional-free.
          // AI was instructed to EXCLUDE stamp duty from per-row fees and report
          // the total separately. We add that total to the LAST trade of this PDF
          // (doesn't matter which row receives it — settlement total stays correct,
          // and FIFO realized P&L is only affected by Rp 10,000 on one trade which
          // is a rounding-level impact compared to the actual stamp being on whatever
          // arbitrary row Semesta chose).
          const stampTotal = Number(single.total_stamp_duty || 0);
          if (stampTotal > 0 && single.trades.length > 0) {
            const lastTrade = single.trades[single.trades.length - 1];
            lastTrade.fee = (lastTrade.fee || 0) + stampTotal;
            lastTrade._stampApplied = stampTotal;  // legacy marker (kept for UI compatibility)
            lastTrade._stampDay = stampTotal;      // canonical marker for "this trade carries the day's stamp"
          }
          // Tag each trade with source file so user can see which PDF it came from
          single.trades.forEach(t => aggregate.trades.push({ ...t, _source: file.name }));
          successCount++;
        }
      } catch (err) {
        console.error(`PDF ${file.name} failed:`, err);
      }
    }

    if (aggregate.trades.length) {
      setPdfRes(aggregate);
      flash2(`✓ Extracted ${aggregate.trades.length} trades from ${successCount}/${files.length} PDF${files.length > 1 ? "s" : ""}`);
    } else {
      flash2(`⚠ No trades extracted from ${files.length} file${files.length > 1 ? "s" : ""}`);
    }
    setLoading(p => ({ ...p, pdf: false }));
    if (fileRef.current) fileRef.current.value = "";
  }, []);

  // Single-PDF extraction helper. Uses pdf.js + per-broker regex locally.
  // No upload, no API, no cost. See parsePdfLocally / detectBroker / parseMirae /
  // parseStockbit / parseSemesta at the top of this file.
  const extractPdfTrades = useCallback(async (b64) => {
    return await parsePdfLocally(b64);
  }, []);

  const importTrade = t => {
    setBf(p => ({ ...p, type: t.type, mkt: pdfRes?.market || "IDX", brk: pdfRes?.broker || p.brk, date: pdfRes?.date || "", stk: t.stock, qty: String(t.qty), px: String(t.price), fee: String(t.fee) }));
    setTab("new");
  };

  const importAllPDFTrades = () => {
    if (!pdfRes?.trades?.length) return;
    const date = pdfRes.date || todayLocal();
    const mkt  = pdfRes.market || "IDX";
    const brk  = pdfRes.broker || "Semesta";
    const inv  = pdfRes.investor || "Robert";
    let newBuys = [], newSells = [];
    pdfRes.trades.forEach((t, i) => {
      const noteParts = [];
      if (pdfRes.account_no) noteParts.push(`Acct ${pdfRes.account_no}`);
      if (t.is_ipo) noteParts.push("IPO");
      if (t._source) noteParts.push(t._source);
      const rec = {
        id: `pdf${Date.now()}_${i}`,
        mkt, inv, brk, date,
        stk: String(t.stock).trim().toUpperCase(),
        qty: +t.qty,
        px: +t.price,
        fee: +(t.fee || 0),
        note: noteParts.join(" · "),
      };
      // Preserve stamp-day marker so subsequent manual entries for this
      // (broker, date) skip adding another Rp 10k stamp.
      if (t._stampDay) rec._stampDay = t._stampDay;
      if (t.type === "BUY")  newBuys.push(rec);
      if (t.type === "SELL") newSells.push(rec);
    });
    if (newBuys.length)  setBuys(p  => [...p, ...newBuys].sort((a, b) => a.date.localeCompare(b.date)));
    if (newSells.length) setSells(p => [...p, ...newSells].sort((a, b) => a.date.localeCompare(b.date)));
    const ipoCount = pdfRes.trades.filter(t => t.is_ipo).length;
    const ipoSuffix = ipoCount ? ` (${ipoCount} IPO)` : "";
    flash2(`✓ Imported ${newBuys.length} buys + ${newSells.length} sells${ipoSuffix}`);
    setPdfRes(null);
    setTab("hist");
  };

  // ── Excel bulk import (2-step: parse workbook, pick sheets, confirm) ─
  const xlsxRef = useRef();
  const [xlsxBook, setXlsxBook] = useState(null);       // { wb, sheets: [{name, inv, brk, mkt, buyCount, sellCount, selected}] }

  const handleExcel = useCallback(async e => {
    const file = e.target.files[0]; if (!file) return;
    setLoading(p => ({ ...p, xlsx: true })); setXlsxBook(null);
    try {
      const buf = await file.arrayBuffer();
      // IMPORTANT: We do NOT use cellDates:true. That option converts Excel dates
      // to JS Date objects in a way that subtly shifts them — serial 46120 ("April 8 2026")
      // becomes 2026-04-07T16:59:48Z which evaluates to "April 7" in positive timezones
      // (Jakarta UTC+7). Instead we keep dates as numeric serials and convert ourselves.
      const wb = XLSX.read(buf, { type: "array" });
      console.log("[Excel] Loaded workbook:", { sheetCount: wb.SheetNames.length, sheets: wb.SheetNames });
      // Default broker/investor inference from sheet name
      const guess = (name) => {
        const n = name.toLowerCase();
        const mkt = n.startsWith("us ") || n.includes("interactive") || n.includes("schwab") ? "US" : "IDX";
        const inv = n.includes("yanuar") ? "Yanuar"
                  : n.includes("eliz")    ? "Elizabeth"
                  : n.includes("tomy")    ? "Tomy"
                  : n.includes("franky")  ? "Franky"
                  : n.includes("reya")    ? "Reya"
                  : n.includes("edwin")   ? "Edwin"
                  : "Robert";
        let brk = "Legacy";
        if (n.includes("osk"))        brk = "OSK";
        else if (n.includes("mirae")) brk = "Mirae Asset";
        else if (n.includes("semesta margin"))  brk = "Semesta Margin";
        else if (n.includes("semesta regular")) brk = "Semesta Regular";
        else if (n.includes("semesta"))         brk = "Semesta";
        else if (n.includes("stockbit"))        brk = "Stockbit";
        else if (n.includes("am capital"))      brk = "AM Capital";
        else if (n.includes("interactive"))     brk = "Interactive Brokers";
        else if (n.includes("schwab"))          brk = "Schwab";
        else if (n.includes("bni"))             brk = "BNI Sekuritas";
        return { inv, brk, mkt };
      };
      // Count trades per sheet — flexible column matching
      const sheets = wb.SheetNames.map(name => {
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { raw: true, defval: null });
        let buyCount = 0, sellCount = 0;
        // Detect columns by looking at keys of first few rows (case-insensitive contains)
        const headers = rows.length ? Object.keys(rows[0]) : [];
        const findCol = (patterns) => headers.find(h => patterns.some(p => h.toLowerCase().includes(p.toLowerCase())));
        const stkCol     = findCol(["stock", "ticker", "saham"]);
        const buyDateCol = findCol(["date buy", "tanggal beli", "buy date"]);
        const sellDateCol= findCol(["date sell", "tanggal jual", "sell date"]);
        const buyPxCol   = findCol(["price/unit buy", "price buy", "harga beli", "buy price"]);
        const sellPxCol  = findCol(["price/unit sell", "price sell", "harga jual", "sell price"]);
        rows.forEach(r => {
          const stock = stkCol ? r[stkCol] : null;
          if (!stock) return;
          if (buyDateCol  && r[buyDateCol]  && buyPxCol  && r[buyPxCol])  buyCount++;
          if (sellDateCol && r[sellDateCol] && sellPxCol && r[sellPxCol]) sellCount++;
        });
        const g = guess(name);
        const isTrade = buyCount + sellCount > 0;
        console.log(`[Excel] Sheet "${name}": ${rows.length} rows, ${buyCount} buys, ${sellCount} sells, headers:`, headers);
        return {
          name, ...g,
          buyCount, sellCount,
          selected: isTrade,      // auto-select if trades detected
          rowCount: rows.length,
          headers,                 // store for tooltip/display
          _cols: { stkCol, buyDateCol, sellDateCol, buyPxCol, sellPxCol },  // for later import
        };
      });
      window.__xlsxWb = wb;
      setXlsxBook({ sheets, fileName: file.name });
      const totalBuys = sheets.reduce((a, s) => a + s.buyCount, 0);
      const totalSells = sheets.reduce((a, s) => a + s.sellCount, 0);
      flash2(`✓ ${file.name} loaded — ${sheets.length} sheets, ${totalBuys} buys + ${totalSells} sells detected`);
    } catch (e) {
      console.error("[Excel] Parse error:", e);
      flash2("⚠ Excel error: " + e.message);
    }
    finally { setLoading(p => ({ ...p, xlsx: false })); if (xlsxRef.current) xlsxRef.current.value = ""; }
  }, []);

  // Update one sheet's config
  const updateSheet = (idx, key, val) => {
    setXlsxBook(p => ({ ...p, sheets: p.sheets.map((s, i) => i === idx ? { ...s, [key]: val } : s) }));
  };

  const toggleAllSheets = (on) => {
    setXlsxBook(p => ({ ...p, sheets: p.sheets.map(s => ({ ...s, selected: on })) }));
  };

  const confirmExcelImport = (mode) => {
    if (!xlsxBook || !window.__xlsxWb) return;
    const wb = window.__xlsxWb;
    const selected = xlsxBook.sheets.filter(s => s.selected);
    if (!selected.length) return flash2("⚠ No sheets selected");
    const newBuys = [], newSells = [];
    let counter = 0;
    // CRITICAL: Use a unique session stamp as prefix so IDs don't collide with
    // IDs from previous imports. Without this, re-importing creates IDs like xb1, xb2
    // that clash with xb1, xb2 from old Dropbox data, causing pairedBuyId/pairedSellId
    // to resolve to the wrong record (resulting in wildly wrong P&L calculations).
    const sessionStamp = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    const makeBuyId  = () => `xb_${sessionStamp}_${++counter}`;
    const makeSellId = () => `xs_${sessionStamp}_${++counter}`;
    const toDate = d => {
      if (!d) return null;
      if (d instanceof Date) {
        // Use LOCAL date components, not UTC.
        // Excel stores dates as local midnight. toISOString() converts to UTC
        // which for users in positive timezones (Jakarta UTC+7) pushes the date
        // back by 1 day. We need the date as the user sees it locally.
        const y  = d.getFullYear();
        const m  = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${dd}`;
      }
      if (typeof d === "number") {
        // Excel serial date. Must avoid JavaScript Date-based conversion entirely
        // since that introduces timezone drift. Compute Y/M/D directly from the serial
        // using Gregorian calendar math. Excel epoch is 1900-01-01 day 1, with the
        // well-known "1900 leap year" bug: Excel thinks Feb 29 1900 exists, so we
        // subtract 1 for serials > 60. Serial 1 = 1900-01-01, serial 61 = 1900-03-01.
        let serial = Math.floor(d);
        if (serial > 60) serial -= 1;  // fix 1900 leap year bug
        // Days since 1900-01-01 (serial 1 = 1900-01-01)
        const daysFrom1900 = serial - 1;
        const ms = Date.UTC(1900, 0, 1) + daysFrom1900 * 86400000;
        const jsDate = new Date(ms);
        // Use UTC getters since we constructed via UTC — no tz drift
        const y  = jsDate.getUTCFullYear();
        const m  = String(jsDate.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(jsDate.getUTCDate()).padStart(2, "0");
        return `${y}-${m}-${dd}`;
      }
      return String(d).slice(0, 10);
    };
    selected.forEach(meta => {
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[meta.name], { raw: true, defval: null });
      if (!rows.length) return;
      const headers = Object.keys(rows[0]);
      // "find" returns first header matching any pattern (case-insensitive)
      const find = (pats, skip = []) => headers.find(h => !skip.includes(h) && pats.some(p => h.toLowerCase().includes(p.toLowerCase())));

      const cStk      = find(["stock", "ticker", "saham"]);
      const cBuyDate  = find(["date buy", "tanggal beli", "buy date"]);
      const cSellDate = find(["date sell", "tanggal jual", "sell date"]);
      const cBuyPx    = find(["price/unit buy", "price buy", "harga beli", "buy price"]);
      const cSellPx   = find(["price/unit sell", "price sell", "harga jual", "sell price"]);
      const cBuyFee   = find(["buy fees", "buy fee", "fee buy", "biaya beli"]);
      const cSellFee  = find(["sell fees", "sell fee", "fee sell", "biaya jual"]);

      // IMPORTANT: Excel often has TWO "Lot" columns (Lot for buy, Lot_1 or Lot.1 for sell
      // after SheetJS auto-dedup). Also handles space-padded column names like ' Unit '.
      // Matches: Lot, Lot_1, Lot.1, Lot_2, etc. (trimmed, case-insensitive)
      const allLotCols  = headers.filter(h => /^lot([._]\d+)?$/i.test(h.trim()));
      const allUnitCols = headers.filter(h => /^unit\s*([._]\d+)?$/i.test(h.trim()));

      // First Lot column = buy qty, second Lot column (Lot.1) = sell qty.
      // If only one Lot column exists, use it for both (won't match if different sides).
      const cBuyLot  = allLotCols[0];
      const cSellLot = allLotCols[1] || allLotCols[0];
      const cBuyUnit  = allUnitCols[0];
      const cSellUnit = allUnitCols[1] || allUnitCols[0];

      console.log(`[Excel] Sheet "${meta.name}" columns:`, {
        stock: cStk, buyDate: cBuyDate, sellDate: cSellDate,
        buyPx: cBuyPx, sellPx: cSellPx,
        buyFee: cBuyFee, sellFee: cSellFee,
        lotCols: allLotCols, unitCols: allUnitCols,
        usingBuyLot: cBuyLot, usingSellLot: cSellLot,
      });

      let sheetBuys = 0, sheetSells = 0, skipped = 0, orphanSells = 0;
      let lastStock = null;  // Remember the most recent valid stock for orphan continuation rows

      rows.forEach((r, idx) => {
        let stock = cStk ? r[cStk] : null;
        const buyQty  = meta.mkt === "IDX" ? +(r[cBuyLot]  || 0) : +(r[cBuyUnit]  || 0);
        const sellQty = meta.mkt === "IDX" ? +(r[cSellLot] || 0) : +(r[cSellUnit] || 0);
        const buyPx   = cBuyPx  ? +(r[cBuyPx]  || 0) : 0;
        const sellPx  = cSellPx ? +(r[cSellPx] || 0) : 0;
        const buyFee  = cBuyFee  ? +(r[cBuyFee]  || 0) : 0;
        const sellFee = cSellFee ? +(r[cSellFee] || 0) : 0;
        const buyDate  = cBuyDate  ? toDate(r[cBuyDate])  : null;
        const sellDate = cSellDate ? toDate(r[cSellDate]) : null;

        // ORPHAN-SELL HANDLING: if row has NO stock but has valid sell data,
        // inherit stock from the previous row. This handles Excel's "one buy split
        // across multiple sell rows" pattern (e.g. TMPO Jan 18 buy 492, sold 192 same row,
        // then 300 more on a following row with empty Stock cell).
        if (!stock && sellDate && sellQty && sellPx && !buyDate && !buyQty) {
          if (lastStock) {
            stock = lastStock;
            orphanSells++;
            console.log(`[Excel] Row ${idx + 1}: orphan sell inherited Stock="${lastStock}" — ${sellQty} lots on ${sellDate} @ ${sellPx}`);
          } else {
            console.warn(`[Excel] Row ${idx + 1}: orphan sell but no previous stock to inherit — skipped`);
          }
        }

        if (!stock) { skipped++; return; }
        const stkClean = String(stock).trim().toUpperCase();
        lastStock = stkClean;  // remember for any following orphan-sell row

        const hasBuy  = buyDate && buyQty && buyPx;
        const hasSell = sellDate && sellQty && sellPx;

        if (hasBuy && hasSell) {
          // PAIRED: same row has both. Create linked buy + sell with pairing IDs
          // so FIFO treats them as a matched pair (bypasses the global FIFO queue).
          const buyId  = makeBuyId();
          const sellId = makeSellId();
          newBuys.push({
            id: buyId, mkt: meta.mkt, inv: meta.inv, brk: meta.brk,
            date: buyDate, stk: stkClean, qty: buyQty, px: buyPx, fee: buyFee,
            pairedSellId: sellId,
          });
          newSells.push({
            id: sellId, mkt: meta.mkt, inv: meta.inv, brk: meta.brk,
            date: sellDate, stk: stkClean, qty: sellQty, px: sellPx, fee: sellFee,
            pairedBuyId: buyId,
          });
          sheetBuys++;
          sheetSells++;
        } else if (hasBuy) {
          // Buy-only row → enters FIFO queue unpaired
          newBuys.push({ id: makeBuyId(), mkt: meta.mkt, inv: meta.inv, brk: meta.brk, date: buyDate, stk: stkClean, qty: buyQty, px: buyPx, fee: buyFee });
          sheetBuys++;
        } else if (hasSell) {
          // Sell-only row → consumes from FIFO queue unpaired
          newSells.push({ id: makeSellId(), mkt: meta.mkt, inv: meta.inv, brk: meta.brk, date: sellDate, stk: stkClean, qty: sellQty, px: sellPx, fee: sellFee });
          sheetSells++;
        }
      });

      console.log(`[Excel] Sheet "${meta.name}" imported: ${sheetBuys} buys + ${sheetSells} sells${orphanSells ? ` (${orphanSells} orphan sells recovered)` : ""} (${skipped} rows skipped, no stock)`);

      // Diagnostic: compute net open per stock for this sheet alone
      const netByStk = {};
      newBuys.filter(b => b.brk === meta.brk && b.inv === meta.inv)
        .forEach(b => { netByStk[b.stk] = netByStk[b.stk] || {bought: 0, sold: 0}; netByStk[b.stk].bought += b.qty; });
      newSells.filter(s => s.brk === meta.brk && s.inv === meta.inv)
        .forEach(s => { netByStk[s.stk] = netByStk[s.stk] || {bought: 0, sold: 0}; netByStk[s.stk].sold += s.qty; });
      const stillOpen = Object.entries(netByStk)
        .filter(([_, v]) => v.bought > v.sold)
        .map(([stk, v]) => ({ stk, bought: v.bought, sold: v.sold, openQty: v.bought - v.sold }))
        .sort((a, b) => b.openQty - a.openQty);
      const fullyClosed = Object.entries(netByStk).filter(([_, v]) => v.bought === v.sold && v.bought > 0).length;
      const oversold = Object.entries(netByStk).filter(([_, v]) => v.sold > v.bought).map(([stk, v]) => ({stk, bought: v.bought, sold: v.sold, oversoldBy: v.sold - v.bought}));
      console.log(`[Excel] Sheet "${meta.name}" NET: ${stillOpen.length} stocks still open (${stillOpen.reduce((a, x) => a + x.openQty, 0)} lots), ${fullyClosed} stocks fully closed, ${oversold.length} oversold (bug!)`);
      if (stillOpen.length) console.table(stillOpen);
      if (oversold.length) { console.warn("[Excel] Oversold stocks — these have more sells than buys:"); console.table(oversold); }
    });
    if (mode === "replace") {
      setBuys(newBuys.sort((a, b) => a.date.localeCompare(b.date)));
      setSells(newSells.sort((a, b) => a.date.localeCompare(b.date)));
    } else if (mode === "replace-selected") {
      // Remove existing trades for accounts being imported, keep other accounts
      const affected = new Set(selected.map(s => `${s.inv}::${s.brk}`));
      setBuys(p => [...p.filter(b => !affected.has(`${b.inv}::${b.brk}`)), ...newBuys].sort((a, b) => a.date.localeCompare(b.date)));
      setSells(p => [...p.filter(s => !affected.has(`${s.inv}::${s.brk}`)), ...newSells].sort((a, b) => a.date.localeCompare(b.date)));
    } else {
      setBuys(p => [...p, ...newBuys].sort((a, b) => a.date.localeCompare(b.date)));
      setSells(p => [...p, ...newSells].sort((a, b) => a.date.localeCompare(b.date)));
    }
    const modeLabel = mode === "replace" ? "replaced all" : mode === "replace-selected" ? "replaced selected accounts" : "appended";
    flash2(`✓ Imported ${newBuys.length} buys + ${newSells.length} sells (${modeLabel})`);
    setXlsxBook(null);
    delete window.__xlsxWb;
  };

  // ── Cash & adjustment handlers ───────────────────────────────
  const [cashForm, setCashForm] = useState({ inv: "Robert", brk: "Stockbit", amount: "", mode: "starting", date: "" });
  // Date shift cutoff for the Danger Zone timezone-fix utility. Defaults to 2026-04-15
  // (user started PDF imports around Apr 16). Only trades strictly before this get shifted.
  const [dateShiftCutoff, setDateShiftCutoff] = useState("2026-04-15");
  // Split form — for "✂️ Stock Splits" section in Setup and the Splits tab
  const [splitForm, setSplitForm] = useState({ stk: "", mkt: "IDX", date: "", type: "forward", oldQty: "1", newQty: "2", note: "" });
  const ssf = (k, v) => setSplitForm(p => ({ ...p, [k]: v }));
  const [renameForm, setRenameForm] = useState({ from: "", to: "" });
  const setCashBalance = () => {
    const amt = +cashForm.amount;
    if (cashForm.amount === "" || isNaN(amt)) return flash2("⚠ Enter a valid amount");
    const k = `${cashForm.inv}::${cashForm.brk}`;
    const mkt = US_BROKERS.includes(cashForm.brk) ? "US" : "IDX";
    const mode = cashForm.mode || "starting";

    if (mode === "target") {
      // Reconcile: create an "adjust" entry for (target − current derived)
      const c = cashByAccount[k] || { current: 0 };
      const diff = amt - c.current;
      if (Math.abs(diff) < 0.01) {
        flash2("✓ Balance already matches broker — no adjustment needed");
        return;
      }
      // Check if a prior reconciliation exists — warn user
      const priorReconcile = adjustments.find(a => a.inv === cashForm.inv && a.brk === cashForm.brk && a.note?.startsWith("Reconcile to broker balance"));
      if (priorReconcile && !confirm(`A previous reconciliation adjustment already exists for ${cashForm.inv} · ${cashForm.brk} (${money(priorReconcile.amount, mkt)} on ${priorReconcile.date}).\n\nAdding another will stack on top. Recommended: delete the old one first.\n\nContinue anyway?`)) {
        return;
      }
      const useDate = cashForm.date || todayLocal();
      setAdjustments(p => [...p, {
        id: `a${Date.now()}`,
        date: useDate,
        inv: cashForm.inv,
        brk: cashForm.brk,
        type: "adjust",
        amount: diff,
        note: `Reconcile to broker balance: target ${money(amt, mkt)}`,
        stk: "",
      }].sort((a, b) => a.date.localeCompare(b.date)));
      flash2(`✓ Reconciled ${cashForm.inv} · ${cashForm.brk} — adjustment ${diff >= 0 ? "+" : ""}${money(Math.abs(diff), mkt)}`);
    } else {
      // Set starting cash (overwrite)
      const existing = cash[k];
      if (existing != null && existing !== amt && !confirm(`Starting cash for ${cashForm.inv} · ${cashForm.brk} is already set to ${money(existing, mkt)}.\n\nOverwrite with ${money(amt, mkt)}?`)) {
        return;
      }
      setCash(p => ({ ...p, [k]: amt }));
      flash2(`✓ Starting balance set: ${cashForm.inv} · ${cashForm.brk} = ${money(amt, mkt)}`);
    }
    // Reset form — keep investor/broker/mode, clear amount + date
    setCashForm(p => ({ ...p, amount: "", date: "" }));
  };

  const [adjForm, setAdjForm] = useState({ date: "", inv: "Robert", brk: "Stockbit", type: "deposit", amount: "", note: "", stk: "" });
  // History filter state
  const [histFilter, setHistFilter] = useState({ stock: "", inv: "All", brk: "All", view: "detail", dateFrom: "", dateTo: "" });  // view: "detail" | "bystock"
  // Dashboard filter state
  const [dashFilter, setDashFilter] = useState({ inv: "All", brk: "All", mkt: "All" });
  const setAf = (k, v) => setAdjForm(p => ({ ...p, [k]: v }));
  const addAdjustment = () => {
    if (!adjForm.date || !adjForm.amount) return flash2("Fill date and amount");
    const amt = adjForm.type === "withdraw" ? -Math.abs(+adjForm.amount) : +adjForm.amount;
    setAdjustments(p => [...p, { id: `a${Date.now()}`, date: adjForm.date, inv: adjForm.inv, brk: adjForm.brk, type: adjForm.type, amount: amt, note: adjForm.note, stk: adjForm.stk ? adjForm.stk.toUpperCase() : "" }].sort((a, b) => a.date.localeCompare(b.date)));
    flash2(`✓ ${adjForm.type.toUpperCase()} recorded`);
    setAdjForm(p => ({ ...p, date: "", amount: "", note: "", stk: "" }));
  };
  const deleteAdjustment = (id) => setAdjustments(p => p.filter(a => a.id !== id));

  const resetAllData = () => {
    if (!confirm("Delete ALL trades, cash, adjustments, and monthly records? This cannot be undone.")) return;
    setBuys([]); setSells([]); setMonthly([]); setCash({}); setAdjustments([]); setSplits([]); setTransfers([]); setLive({}); setAiText("");
    flash2("✓ All data cleared — ready for fresh import");
  };

  // Trade edit/delete
  const [editingTrade, setEditingTrade] = useState(null);  // { type: "BUY"|"SELL", id, data }
  const openTradeEdit = (type, trade) => setEditingTrade({ type, id: trade.id, data: { ...trade } });
  const closeTradeEdit = () => setEditingTrade(null);
  const saveTradeEdit = () => {
    if (!editingTrade) return;
    const { type, id, data } = editingTrade;
    const cleaned = {
      ...data,
      qty: +data.qty,
      px: +data.px,
      fee: +(data.fee || 0),
      stk: String(data.stk).trim().toUpperCase(),
    };
    if (!cleaned.stk || !cleaned.date || !cleaned.qty || !cleaned.px) {
      return flash2("⚠ Stock, date, qty, and price are required");
    }
    // Recompute stamp for BOTH the trade's old day-group and its new one (broker/date/investor
    // may have changed), so the ≥Rp10M-per-day rule stays correct on either side.
    const orig = (type === "BUY" ? buys : sells).find(t => t.id === id);
    const keys = [orig && stampGroupKey(orig), stampGroupKey(cleaned)];
    const nextBuys  = type === "BUY"  ? buys.map(b => b.id === id ? cleaned : b)  : buys;
    const nextSells = type === "SELL" ? sells.map(s => s.id === id ? cleaned : s) : sells;
    const r = reconcileStampGroups(nextBuys, nextSells, keys);
    setBuys(r.buys.slice().sort((a, b) => a.date.localeCompare(b.date)));
    setSells(r.sells.slice().sort((a, b) => a.date.localeCompare(b.date)));
    flash2(`✓ ${type} updated — ${cleaned.stk} ${cleaned.qty}${cleaned.mkt === "IDX" ? "L" : "sh"} @ ${cleaned.mkt === "IDX" ? "Rp" : "$"}${cleaned.px}`);
    closeTradeEdit();
  };
  const deleteTrade = (type, id) => {
    const trade = (type === "BUY" ? buys : sells).find(t => t.id === id);
    if (!trade) return;
    const label = `${type} ${trade.stk} ${trade.qty}${trade.mkt === "IDX" ? "L" : "sh"} @ ${trade.mkt === "IDX" ? "Rp" : "$"}${trade.px} on ${trade.date}`;
    // If this trade is paired, warn the user that the pair will be broken
    const pairedId = type === "BUY" ? trade.pairedSellId : trade.pairedBuyId;
    const extraWarning = pairedId
      ? `\n\n⚠ This trade is paired. Its partner will become unpaired and fall back to FIFO matching.`
      : "";
    if (!confirm(`Delete this trade?\n\n${label}${extraWarning}\n\nFIFO will recompute automatically. This cannot be undone.`)) return;

    const key = stampGroupKey(trade);
    let nb = buys, ns = sells;
    if (type === "BUY") {
      nb = buys.filter(b => b.id !== id);
      // Also remove pairedBuyId on the paired sell so it becomes unpaired cleanly
      if (pairedId) ns = sells.map(s => s.id === pairedId ? (({ pairedBuyId, ...rest }) => rest)(s) : s);
    } else {
      ns = sells.filter(s => s.id !== id);
      if (pairedId) nb = buys.map(b => b.id === pairedId ? (({ pairedSellId, ...rest }) => rest)(b) : b);
    }
    // Removing a trade can drop its day below Rp10M (or remove the stamp bearer itself) —
    // reconcile re-homes/clears the stamp for that account-day.
    const r = reconcileStampGroups(nb, ns, [key]);
    setBuys(r.buys);
    setSells(r.sells);
    flash2(`✓ Deleted ${label}`);
  };

  // Stock split CRUD ───────────────────────────────────────────
  // ratio is always "new_shares / old_shares" so:
  //   2:1 forward → ratio = 2   (old 100 becomes 200, price halved)
  //   1:10 reverse → ratio = 0.1 (old 1000 becomes 100, price 10x)
  const addSplit = () => {
    if (!splitForm.stk || !splitForm.date) { flash2("Stock and date are required"); return; }
    const oldQ = parseFloat(splitForm.oldQty);
    const newQ = parseFloat(splitForm.newQty);
    if (!oldQ || !newQ || oldQ <= 0 || newQ <= 0) { flash2("Enter valid ratio numbers"); return; }
    const ratio = newQ / oldQ;
    if (ratio === 1) { flash2("Ratio results in no change — skipped"); return; }
    // Double-check type matches ratio direction
    const impliedType = ratio > 1 ? "forward" : "reverse";
    if (impliedType !== splitForm.type) {
      if (!confirm(`Your ratio (${newQ}:${oldQ}) indicates a ${impliedType} split, but you selected "${splitForm.type}". Continue anyway?`)) return;
    }
    const newSplit = {
      id: `sp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      stk: splitForm.stk.toUpperCase().trim(),
      mkt: splitForm.mkt,
      date: splitForm.date,
      ratio,
      oldQty: oldQ,
      newQty: newQ,
      type: splitForm.type,
      note: splitForm.note || "",
    };
    setSplits(p => [...p, newSplit].sort((a, b) => a.date.localeCompare(b.date)));
    setSplitForm({ stk: "", mkt: splitForm.mkt, date: "", type: "forward", oldQty: "1", newQty: "2", note: "" });
    flash2(`✓ Added ${newSplit.stk} ${newQ}:${oldQ} ${newSplit.type} split on ${newSplit.date}`);
  };

  const deleteSplit = (id) => {
    const sp = splits.find(s => s.id === id);
    if (!sp) return;
    if (!confirm(`Delete ${sp.stk} ${sp.newQty}:${sp.oldQty} ${sp.type} split on ${sp.date}?\n\nAll historical trades will revert to original qty/price.`)) return;
    setSplits(p => p.filter(s => s.id !== id));
    flash2(`✓ Deleted ${sp.stk} split`);
  };

  // ── Broker-to-broker stock transfers ─────────────────────────────
  // One form moves several stocks at once. `sel` = { [stk]: { on, qty, fee } }; blank qty =
  // full holding, blank fee = the RECEIVING broker's default transfer charge per stock.
  const emptyXferForm = () => ({ mkt: "IDX", inv: "Robert", fromBrk: "", toBrk: "", date: todayLocal(), note: "", sel: {} });
  const [xferForm, setXferForm] = useState(emptyXferForm);
  // Changing market/investor/source broker invalidates the stock selection.
  const sxf = (k, v) => setXferForm(p => ({ ...p, [k]: v, ...(["mkt", "inv", "fromBrk"].includes(k) ? { sel: {} } : {}) }));
  const setXferSel = (stk, patch) => setXferForm(p => ({ ...p, sel: { ...p.sel, [stk]: { ...(p.sel[stk] || {}), ...patch } } }));
  // Default transfer charge per stock, billed by the RECEIVING broker (Setup → Broker Fees).
  const xferFeeDefault = (brk) => {
    const v = brokerFees?.[brk]?.xfer;
    return v === "" || v == null || isNaN(+v) ? 0 : +v;
  };
  // Current open holdings of one investor at one broker: [{ stk, qty, cost }].
  const holdingsAt = (mkt, inv, brk) => {
    const m = {};
    openPos.filter(o => o.mkt === mkt && o.inv === inv && o.brk === brk).forEach(o => {
      if (!m[o.stk]) m[o.stk] = { stk: o.stk, qty: 0, cost: 0 };
      m[o.stk].qty += o.qty; m[o.stk].cost += o.totalCost;
    });
    return Object.values(m).map(h => ({ ...h, qty: R(h.qty, 4) })).filter(h => h.qty > 0).sort((a, b) => a.stk.localeCompare(b.stk));
  };

  // Simulate a transfer list: which transfers exceed available lots, and which REAL sells
  // end up selling more than their account holds (FIFO finds no lots → bogus P&L).
  const xferImpact = (nextTransfers) => {
    const adjB = applySplits(buys, splits), adjS = applySplits(sells, splits);
    const xf = expandTransfers(adjB, adjS, applySplits(nextTransfers, splits));
    const { results } = runFIFO([...adjB, ...xf.legBuys], [...adjS, ...xf.legSells]);
    const shortSells = results.filter(r => !r._xfer && !r._paired && r.matched.reduce((s, m) => s + m.used, 0) < r.qty - 1e-9).map(r => r.id);
    return { issues: xf.issues, shortSells };
  };

  const addTransfer = () => {
    const f = xferForm;
    if (!f.fromBrk || !f.toBrk || !f.date) return flash2("Pick both brokers and the date.");
    if (f.fromBrk === f.toBrk) return flash2("⚠ From and To broker must be different.");
    const valid = f.mkt === "IDX" ? IDX_BROKERS : US_BROKERS;
    if (!valid.includes(f.fromBrk) || !valid.includes(f.toBrk)) return flash2(`⚠ Both brokers must be ${f.mkt} brokers.`);
    const unit = f.mkt === "IDX" ? "lots" : "shares";
    const held = Object.fromEntries(holdingsAt(f.mkt, f.inv, f.fromBrk).map(h => [h.stk, h.qty]));
    const picked = Object.entries(f.sel).filter(([, s]) => s.on);
    if (picked.length === 0) return flash2("⚠ Tick at least one stock to transfer.");

    const base = Date.now();
    const recs = [];
    for (const [stk, s] of picked) {
      const qty = s.qty === "" || s.qty == null ? held[stk] : +s.qty;
      if (!(qty > 0)) return flash2(`⚠ Enter a qty for ${stk}.`);
      const fee = s.fee === "" || s.fee == null ? xferFeeDefault(f.toBrk) : +s.fee || 0;
      recs.push({ id: `x${base}_${recs.length}`, mkt: f.mkt, inv: f.inv, stk, qty, date: f.date, fromBrk: f.fromBrk, toBrk: f.toBrk, fee, note: f.note || "" });
    }
    const next = [...transfers, ...recs];

    const before = xferImpact(transfers);
    const after  = xferImpact(next);
    const over = recs.map(r => ({ r, i: after.issues.find(x => x.id === r.id) })).filter(x => x.i);
    if (over.length) return flash2(`⚠ Not enough at ${f.fromBrk} on ${f.date}: ` + over.map(x => `${x.r.stk} only ${R(x.i.available, 4)} ${unit}`).join(", "));
    const newShort = after.shortSells.filter(id => !before.shortSells.includes(id));
    const warn = newShort.length
      ? `\n\n⚠ ${newShort.length} later sell${newShort.length !== 1 ? "s" : ""} at ${f.fromBrk} would no longer have enough shares to sell (their P&L will be wrong). Check the date/qty.`
      : "";
    const totalFee = recs.reduce((s, r) => s + r.fee, 0);
    const lines = recs.map(r => `• ${r.stk}: ${r.qty} ${unit}${r.fee ? ` · charge ${money(r.fee, f.mkt)}` : ""}`).join("\n");
    if (!confirm(`Transfer ${recs.length} stock${recs.length !== 1 ? "s" : ""} (${f.inv})\n${f.fromBrk} → ${f.toBrk} on ${f.date}\n\n${lines}\n\nOldest lots move first (FIFO) with their original cost. No P&L.${totalFee ? `\nTotal charge ${money(totalFee, f.mkt)} deducted from ${f.toBrk} cash.` : ""}${warn}`)) return;

    setTransfers(next.sort((a, b) => a.date.localeCompare(b.date)));
    setXferForm(p => ({ ...emptyXferForm(), mkt: p.mkt, inv: p.inv, fromBrk: p.fromBrk, toBrk: p.toBrk, date: p.date }));
    flash2(`✓ Transferred ${recs.length} stock${recs.length !== 1 ? "s" : ""}: ${f.fromBrk} → ${f.toBrk}${totalFee ? ` · charge ${money(totalFee, f.mkt)}` : ""}`);
  };

  const deleteTransfer = (id) => {
    const t = transfers.find(x => x.id === id);
    if (!t) return;
    const next = transfers.filter(x => x.id !== id);
    const before = xferImpact(transfers), after = xferImpact(next);
    const newShort = after.shortSells.filter(sid => !before.shortSells.includes(sid)).length;
    const newIssues = after.issues.filter(i => !before.issues.some(b => b.id === i.id)).length;
    const warn = newShort || newIssues
      ? `\n\n⚠ ${t.toBrk} has already sold/transferred some of these shares — ${newShort + newIssues} later entr${newShort + newIssues !== 1 ? "ies" : "y"} would no longer have enough ${t.stk}.`
      : "";
    if (!confirm(`Delete transfer of ${t.qty} ${t.stk} (${t.inv}) ${t.fromBrk} → ${t.toBrk} on ${t.date}?\n\nThe shares return to ${t.fromBrk}.${warn}`)) return;
    setTransfers(next);
    flash2(`✓ Deleted ${t.stk} transfer`);
  };

  // Prefill the transfer form from an open position row and jump to the Transfers tab.
  const startTransferFrom = (p) => {
    setXferForm({ ...emptyXferForm(), mkt: p.mkt, inv: p.inv, fromBrk: p.brk, sel: { [p.stk]: { on: true, qty: "", fee: "" } } });
    setTab("xfer");
  };

  // Auto-pair existing unpaired buys and sells that look like they were originally
  // on the same Excel row. Matching criteria: same inv+brk+stk+mkt, matching qty,
  // buy.date <= sell.date, sell.date within N days of buy.date (configurable, default same-day),
  // AND the sell is not already paired.
  // This is a retroactive fix for data imported before the pairing logic existed.
  const autoPairTrades = (daysWindow = 0) => {
    // Group unpaired buys by matching key
    const buyKey = b => `${b.inv}::${b.brk}::${b.stk}::${b.mkt}::${b.qty}`;
    const sellKey = s => `${s.inv}::${s.brk}::${s.stk}::${s.mkt}::${s.qty}`;

    const unpairedBuys = buys.filter(b => !b.pairedSellId);
    const unpairedSells = sells.filter(s => !s.pairedBuyId);

    // For each unpaired sell, try to find an earliest-matching unpaired buy on-or-before the sell date
    const buyUsed = new Set();  // ids of buys consumed by this pairing pass
    const pairs = [];  // [{ buyId, sellId }]

    // Sort sells by date ascending so earlier sells get priority on matching buys
    const sortedSells = [...unpairedSells].sort((a, b) => a.date.localeCompare(b.date));

    sortedSells.forEach(sell => {
      const sk = sellKey(sell);
      // Find candidate buys with same inv+brk+stk+mkt+qty, buy.date <= sell.date,
      // and sell.date - buy.date <= daysWindow
      const sellTime = new Date(sell.date).getTime();
      const windowMs = daysWindow * 86400000;

      const candidates = unpairedBuys.filter(b =>
        !buyUsed.has(b.id) &&
        buyKey(b) === sk &&
        b.date <= sell.date &&
        (sellTime - new Date(b.date).getTime()) <= windowMs
      ).sort((a, b) => a.date.localeCompare(b.date));  // earliest buy first

      if (candidates.length > 0) {
        const buy = candidates[0];
        buyUsed.add(buy.id);
        pairs.push({ buyId: buy.id, sellId: sell.id });
      }
    });

    return pairs;
  };

  const runAutoPair = (daysWindow) => {
    const pairs = autoPairTrades(daysWindow);
    if (pairs.length === 0) {
      flash2(`No pairable trades found (within ${daysWindow}-day window)`);
      return;
    }
    const label = daysWindow === 0 ? "same-day" : `within ${daysWindow} day${daysWindow === 1 ? "" : "s"}`;
    if (!confirm(
      `Auto-pair ${pairs.length} buy-sell trade${pairs.length === 1 ? "" : "s"} (${label})?\n\n` +
      `This links each sell to a matching buy (same stock, same qty, same account, buy.date ≤ sell.date) so FIFO treats them as a paired row instead of using the global queue.\n\n` +
      `Previously calculated P&L may change — paired trades use Excel-style row matching.\n\n` +
      `Export JSON backup first if you haven't already.`
    )) return;

    const pairedBuyIds = new Set(pairs.map(p => p.buyId));
    const pairedSellIds = new Set(pairs.map(p => p.sellId));
    const buyToSell = Object.fromEntries(pairs.map(p => [p.buyId, p.sellId]));
    const sellToBuy = Object.fromEntries(pairs.map(p => [p.sellId, p.buyId]));

    setBuys(p => p.map(b => pairedBuyIds.has(b.id) ? { ...b, pairedSellId: buyToSell[b.id] } : b));
    setSells(p => p.map(s => pairedSellIds.has(s.id) ? { ...s, pairedBuyId: sellToBuy[s.id] } : s));
    flash2(`✓ Paired ${pairs.length} trade${pairs.length === 1 ? "" : "s"} (${label})`);
  };

  const unpairAll = () => {
    const paired = buys.filter(b => b.pairedSellId).length;
    if (paired === 0) { flash2("No paired trades to unpair"); return; }
    if (!confirm(`Unpair ALL ${paired} currently-paired trades?\n\nAll trades will go back to using the global FIFO queue. Export backup first.`)) return;
    setBuys(p => p.map(b => { const { pairedSellId, ...rest } = b; return rest; }));
    setSells(p => p.map(s => { const { pairedBuyId, ...rest } = s; return rest; }));
    flash2(`✓ Unpaired all ${paired} trades`);
  };

  // "Close at cost" — add a matching sell at cost-per-unit price to zero out a phantom open lot.
  // Useful for cleaning up leftover fractional positions from Excel typos without polluting realized P&L.
  const closeAtCost = (openLot) => {
    if (!openLot || !openLot.qty) return;
    const price = Math.round(openLot.costPerUnit * 100) / 100;  // round to 2 decimals
    const today = todayLocal();
    const msg = `Mark as CLOSED at cost basis?\n\n` +
      `${openLot.stk} · ${openLot.inv} · ${openLot.brk}\n` +
      `${openLot.qty}${openLot.mkt === "IDX" ? " lots" : " shares"} @ cost ${openLot.mkt === "IDX" ? "Rp" : "$"}${price}\n\n` +
      `This creates a balancing sell dated today with ZERO P&L impact — the position drops to zero.\n` +
      `Use this for phantom leftovers from Excel typos.\n\n` +
      `If the position has unrealized gains/losses, those will NOT show in Realized P&L (since sell price = cost).\n` +
      `For a real sale, use "Add Trade" instead with the actual sell price.`;
    if (!confirm(msg)) return;

    const balancingSell = {
      id: `close_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      mkt: openLot.mkt,
      inv: openLot.inv,
      brk: openLot.brk,
      date: today,
      stk: openLot.stk,
      qty: openLot.qty,
      px: price,
      fee: 0,
      note: "Closed at cost (phantom cleanup)",
    };
    setSells(p => [...p, balancingSell].sort((a, b) => a.date.localeCompare(b.date)));
    flash2(`✓ Closed ${openLot.stk} ${openLot.qty}${openLot.mkt === "IDX" ? "L" : "sh"} at cost`);
  };

  // Bulk close all open positions that match a filter (e.g., all Semesta positions)
  const bulkCloseAtCost = (filterFn, label) => {
    const candidates = openPos.filter(filterFn);
    if (candidates.length === 0) {
      flash2(`No open positions match: ${label}`);
      return;
    }
    const stocks = [...new Set(candidates.map(p => p.stk))];
    const msg = `Close ALL ${candidates.length} open buy lots at cost basis?\n\n` +
      `Filter: ${label}\n` +
      `Stocks affected (${stocks.length}): ${stocks.slice(0, 8).join(", ")}${stocks.length > 8 ? ` +${stocks.length - 8} more` : ""}\n\n` +
      `Each position gets a balancing sell dated today at cost basis — ZERO P&L impact.\n\n` +
      `This cannot be undone (you'd have to manually delete each balancing sell).`;
    if (!confirm(msg)) return;

    const today = todayLocal();
    const newSells = candidates.map((p, i) => ({
      id: `bulk_close_${Date.now()}_${i}`,
      mkt: p.mkt,
      inv: p.inv,
      brk: p.brk,
      date: today,
      stk: p.stk,
      qty: p.qty,
      px: Math.round(p.costPerUnit * 100) / 100,
      fee: 0,
      note: "Bulk closed at cost (phantom cleanup)",
    }));
    setSells(p => [...p, ...newSells].sort((a, b) => a.date.localeCompare(b.date)));
    flash2(`✓ Closed ${newSells.length} lots across ${stocks.length} stocks at cost`);
  };

  // Remove all data for a specific investor+broker combination
  const removeAccount = (inv, brk) => {
    if (!inv || !brk) return;
    // Count what will be affected
    const buysToDelete  = buys.filter(b => b.inv === inv && b.brk === brk).length;
    const sellsToDelete = sells.filter(s => s.inv === inv && s.brk === brk).length;
    const adjsToDelete  = adjustments.filter(a => a.inv === inv && a.brk === brk).length;
    const cashKey = `${inv}::${brk}`;
    const hasCash = cash[cashKey] != null;
    const monthlyKey = brk;  // monthly records use broker only, keep these (they're shared)

    const total = buysToDelete + sellsToDelete + adjsToDelete + (hasCash ? 1 : 0);
    if (total === 0) {
      flash2(`No data found for ${inv} · ${brk}`);
      return;
    }

    const msg = `Remove ALL data for ${inv} · ${brk}?\n\n` +
      `This will delete:\n` +
      `  • ${buysToDelete} buy transactions\n` +
      `  • ${sellsToDelete} sell transactions\n` +
      `  • ${adjsToDelete} cash adjustments\n` +
      (hasCash ? `  • Cash starting balance\n` : "") +
      `\nMonthly performance records (shared across brokers) will be kept.\n\nThis cannot be undone.`;
    if (!confirm(msg)) return;

    setBuys(p => p.filter(b => !(b.inv === inv && b.brk === brk)));
    setSells(p => p.filter(s => !(s.inv === inv && s.brk === brk)));
    setAdjustments(p => p.filter(a => !(a.inv === inv && a.brk === brk)));
    setCash(p => {
      const next = { ...p };
      delete next[cashKey];
      return next;
    });
    flash2(`✓ Removed ${inv} · ${brk} — ${total} records deleted`);
  };

  // Rename a broker across ALL records (trades, cash, adjustments, monthly)
  // Useful for merging (e.g., "Semesta Regular" → "Semesta") or fixing typos
  const renameBroker = (oldName, newName) => {
    if (!oldName || !newName) return;
    if (oldName === newName) {
      flash2("⚠ Old and new names are the same");
      return;
    }
    const buyCount  = buys.filter(b => b.brk === oldName).length;
    const sellCount = sells.filter(s => s.brk === oldName).length;
    const adjCount  = adjustments.filter(a => a.brk === oldName).length;
    const cashCount = Object.keys(cash).filter(k => k.endsWith(`::${oldName}`)).length;
    const monCount  = monthly.filter(m => m.brk === oldName).length;
    const total = buyCount + sellCount + adjCount + cashCount + monCount;

    if (total === 0) {
      flash2(`No records found for broker "${oldName}"`);
      return;
    }

    // Check for collisions (if target account already exists, cash records will merge)
    const collidingCash = Object.keys(cash).filter(k => {
      const [inv, brk] = k.split("::");
      return brk === oldName && cash[`${inv}::${newName}`] != null;
    });

    const msg = `Rename broker "${oldName}" → "${newName}"?\n\n` +
      `This will update:\n` +
      `  • ${buyCount} buy transactions\n` +
      `  • ${sellCount} sell transactions\n` +
      `  • ${adjCount} cash adjustments\n` +
      `  • ${cashCount} cash balances\n` +
      `  • ${monCount} monthly records\n` +
      (collidingCash.length ? `\n⚠ ${collidingCash.length} cash balance(s) will MERGE (summing starting cash):\n${collidingCash.map(k => "  • " + k.split("::")[0]).join("\n")}\n` : "") +
      `\nAfter rename, FIFO will match buys and sells under the new broker name.\nThis cannot be undone (but you can rename back).`;
    if (!confirm(msg)) return;

    setBuys(p  => p.map(b  => b.brk === oldName ? { ...b,  brk: newName } : b));
    setSells(p => p.map(s  => s.brk === oldName ? { ...s,  brk: newName } : s));
    setAdjustments(p => p.map(a => a.brk === oldName ? { ...a, brk: newName } : a));
    setMonthly(p => p.map(m => m.brk === oldName ? { ...m, brk: newName } : m));
    setCash(p => {
      const next = {};
      Object.entries(p).forEach(([k, v]) => {
        const [inv, brk] = k.split("::");
        const targetBrk = brk === oldName ? newName : brk;
        const newKey = `${inv}::${targetBrk}`;
        // If key collision, sum the values
        next[newKey] = (next[newKey] || 0) + v;
      });
      return next;
    });
    flash2(`✓ Renamed "${oldName}" → "${newName}" across ${total} records`);
  };
  const downloadFile = (filename, content, mime) => {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const exportJSON = () => {
    const data = {
      version: "1.0",
      exportedAt: new Date().toISOString(),
      buys, sells, monthly, cash, adjustments, splits, transfers, brokerFees, applyStamp, autoRefresh,
      live,   // preserves last live prices
      aiText, // preserves last AI analysis
    };
    const filename = `TradeTracker_backup_${todayLocal()}.json`;
    downloadFile(filename, JSON.stringify(data, null, 2), "application/json");
    flash2(`✓ Backup downloaded: ${filename}`);
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Buys
    const buysSheet = buys.map(b => ({
      ID: b.id, Market: b.mkt, Investor: b.inv, Broker: b.brk,
      Date: b.date, Stock: b.stk,
      Qty: b.qty,
      "Qty Unit": b.mkt === "IDX" ? "lot" : "shares",
      "Shares": b.mkt === "IDX" ? b.qty * 100 : b.qty,
      "Price per Share": b.px,
      "Total Value": (b.mkt === "IDX" ? b.qty * 100 : b.qty) * b.px,
      Fee: b.fee,
      "Total Cost": (b.mkt === "IDX" ? b.qty * 100 : b.qty) * b.px + b.fee,
      Note: b.note || "",
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buysSheet), "Buys");

    // Sheet 2: Sells
    const sellsSheet = sells.map(s => ({
      ID: s.id, Market: s.mkt, Investor: s.inv, Broker: s.brk,
      Date: s.date, Stock: s.stk,
      Qty: s.qty,
      "Qty Unit": s.mkt === "IDX" ? "lot" : "shares",
      "Shares": s.mkt === "IDX" ? s.qty * 100 : s.qty,
      "Price per Share": s.px,
      "Total Value": (s.mkt === "IDX" ? s.qty * 100 : s.qty) * s.px,
      Fee: s.fee,
      "Net Revenue": (s.mkt === "IDX" ? s.qty * 100 : s.qty) * s.px - s.fee,
      Note: s.note || "",
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sellsSheet), "Sells");

    // Sheet 3: FIFO Realized P&L
    const closedSheet = closed.map(s => ({
      "Stock": s.stk, "Market": s.mkt, "Investor": s.inv, "Broker": s.brk,
      "Sell Date": s.date, "Qty": s.qty, "Sell Price": s.px,
      "FIFO Buy Date": s.matched[0]?.buyDate || "",
      "FIFO Avg Cost per Unit": s.sellShares > 0 ? R(s.totalCostBasis / s.sellShares, 4) : 0,
      "Revenue (net of fee)": s.netRevenue,
      "Cost Basis": s.totalCostBasis,
      "Realized P&L": s.profit,
      "Return %": R(s.pct, 2),
      "Days Held": s.days,
      "Lots Matched": s.matched.length,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(closedSheet), "FIFO P&L");

    // Sheet 4: Open Positions
    const openSheet = openEnriched.map(p => ({
      "Stock": p.stk, "Market": p.mkt, "Investor": p.inv, "Broker": p.brk,
      "Buy Date": p.date, "Qty Remaining": p.qty,
      "Shares Remaining": p.units,
      "Buy Price": p.px,
      "Cost per Unit (avg)": R(p.costPerUnit, 4),
      "Total Cost": p.totalCost,
      "Live Price": p.lp || "",
      "Current Value": p.curVal || "",
      "Unrealized P&L": p.unreal || "",
      "Unrealized %": p.unrealPct != null ? R(p.unrealPct, 2) : "",
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(openSheet), "Open Positions");

    // Sheet 5: Monthly Performance
    const monthlySheet = monthly.map(m => ({
      Period: m.period, IHSG: m.ihsg, "S&P 500": m.sp500 || "",
      Broker: m.brk, "Balance (IDR/USD)": m.bal, "Fund Delta": m.fd,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(monthlySheet), "Monthly Performance");

    // Sheet 6: Cash & Adjustments
    const cashSheet = Object.entries(cash).map(([k, v]) => {
      const [inv, brk] = k.split("::");
      return { Investor: inv, Broker: brk, "Starting Cash": v };
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(cashSheet), "Starting Cash");

    const adjSheet = adjustments.map(a => ({
      Date: a.date, Investor: a.inv, Broker: a.brk, Type: a.type,
      Stock: a.stk || "", Amount: a.amount, Note: a.note || "",
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(adjSheet), "Adjustments");

    // Sheet 7: Per-stock floating P&L summary
    const flSheet = floatingPL.map(r => ({
      Stock: r.stk, Market: r.mkt, Investor: r.inv, Broker: r.brk,
      "Total Qty": r.totalQty, "Total Shares": r.totalUnits,
      "Avg Cost": R(r.avgCost, 4),
      "Live Price": r.livePrice || "",
      "Cost Basis": r.totalCost,
      "Current Value": r.currentVal || "",
      "Floating P&L": r.unrealized || "",
      "Return %": r.livePrice ? R(r.unrealPct, 2) : "",
      "Open Lots": r.lotCount,
      "First Buy": r.firstBuy, "Last Buy": r.lastBuy,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(flSheet), "Floating P&L");

    const filename = `TradeTracker_export_${todayLocal()}.xlsx`;
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    downloadFile(filename, new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    flash2(`✓ Excel export downloaded: ${filename}`);
  };

  const jsonRestoreRef = useRef();
  const handleJSONRestore = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!data.version || !Array.isArray(data.buys)) {
          flash2("⚠ Invalid backup file");
          return;
        }
        if (!confirm(`Restore backup from ${data.exportedAt?.slice(0, 10) || "unknown date"}?\n\n` +
                     `This will REPLACE:\n` +
                     `• ${data.buys.length} buys\n` +
                     `• ${data.sells.length} sells\n` +
                     `• ${data.monthly.length} monthly records\n` +
                     `• ${data.adjustments?.length || 0} adjustments\n` +
                     `• ${data.splits?.length || 0} splits\n` +
                     `• ${data.transfers?.length || 0} transfers\n\n` +
                     `Current data will be lost.`)) return;
        setBuys(data.buys || []);
        setSells(data.sells || []);
        setMonthly(data.monthly || []);
        setCash(data.cash || {});
        setAdjustments(data.adjustments || []);
        setSplits(data.splits || []);
        setTransfers(data.transfers || []);
        if (data.brokerFees) setBrokerFees(data.brokerFees);
        setApplyStamp(data.applyStamp ?? false);
        setAutoRefresh(data.autoRefresh ?? true);
        if (data.live) setLive(data.live);
        if (data.aiText) setAiText(data.aiText);
        flash2(`✓ Restored from backup (${data.exportedAt?.slice(0, 10) || "unknown"})`);
      } catch (err) {
        flash2("⚠ Restore failed: " + err.message);
      }
    };
    reader.readAsText(file);
    if (jsonRestoreRef.current) jsonRestoreRef.current.value = "";
  };

  // Flash helper
  const flash2 = msg => { setFlash(msg); setTimeout(() => setFlash(""), 3000); };

  // Preview calc — mirrors saveTrade's auto-fee logic so the displayed fee
  // matches what saveTrade will actually apply when you click Save.
  const preview = useMemo(() => {
    const qty = +bf.qty || 0, px = +bf.px || 0;
    const shares = bf.mkt === "IDX" ? qty * 100 : qty;
    const val = shares * px;
    // Stamp duty preview: Rp 10k applies once per account (investor+broker) per day, only if
    // that day's total transaction value (buys + sells, ex-IPO) reaches Rp 10M. Estimate
    // whether THIS trade would trigger it: IDX, not IPO, day not already stamped, and the
    // day total including this trade clears the threshold.
    const dayKey = `${bf.inv}||${bf.brk}||${bf.date}`;
    const dayTrades = [...buys, ...sells].filter(t => stampGroupKey(t) === dayKey);
    const dayHasBearer = dayTrades.some(t => t._stampDay > 0);
    const existingGross = dayTrades.filter(t => !isIpoTrade(t)).reduce((s, t) => s + stampTradeGross(t), 0);
    const willGetStamp = bf.mkt === "IDX" && !bf.isIpo && !!bf.brk && !!bf.date && !dayHasBearer
      && (existingGross + val) >= STAMP_THRESHOLD;
    const autoFee = bf.isIpo
      ? 0
      : calcAutoFee(brokerFees, { mkt: bf.mkt, brk: bf.brk, type: bf.type, gross: val }) + (willGetStamp ? STAMP : 0);
    const fee = bf.fee !== "" ? +bf.fee : autoFee;
    const net = bf.type === "BUY" ? -(val + fee) : val - fee;
    const feeRate = brokerFeeRate(brokerFees, bf.brk, bf.mkt, bf.type);
    return { shares, val, fee, net, autoFee, willGetStamp, feeRate };
  }, [bf, buys, sells, brokerFees]);

  // Batch mode: load the selected IDX broker's default buy/sell % into the shared fee
  // fields whenever the broker (or its configured rates) changes. Still editable per batch.
  useEffect(() => {
    if (bf.mkt !== "IDX") return;
    setBatchShared(p => ({
      ...p,
      buyFeePct:  String(brokerFeeRate(brokerFees, bf.brk, "IDX", "BUY")),
      sellFeePct: String(brokerFeeRate(brokerFees, bf.brk, "IDX", "SELL")),
    }));
  }, [bf.mkt, bf.brk, brokerFees]);

  // Unique stocks open
  const openTickers = useMemo(() => ({
    idx: [...new Set(openPos.filter(p => p.mkt === "IDX").map(p => p.stk))],
    us:  [...new Set(openPos.filter(p => p.mkt === "US").map(p => p.stk))],
  }), [openPos]);

  // ════════════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════════════
  // Two top-level sections, each with its own tab set. The section toggle
  // (Stocks | Pre-IPO) swaps the entire sidebar tab list.
  const STOCK_TABS = [
    { id: "dash",   ico: "◈", lbl: "Dashboard"       },
    { id: "open",   ico: "⬡", lbl: "Open Positions"  },
    { id: "hist",   ico: "≋", lbl: "Trade History"   },
    { id: "splits", ico: "✂", lbl: "Stock Splits"    },
    { id: "xfer",   ico: "⇄", lbl: "Transfers"       },
    { id: "perf",   ico: "▲", lbl: "Performance"     },
    { id: "new",    ico: "＋", lbl: "Add Trade"       },
    { id: "pdf",    ico: "⌗", lbl: "Import PDF"      },
    { id: "ai",     ico: "✦", lbl: "AI Analysis"     },
    { id: "setup",  ico: "⚙", lbl: "Setup · Cash"    },
  ];
  const PREIPO_TABS = [
    { id: "preipo_dash",  ico: "◈", lbl: "Dashboard"  },
    { id: "preipo_deals", ico: "◇", lbl: "Deals"      },
  ];
  const TABS = section === "preipo" ? PREIPO_TABS : STOCK_TABS;
  // Switch top-level section and reset to that section's first tab.
  const switchSection = (s) => {
    setSection(s);
    setTab(s === "preipo" ? "preipo_dash" : "dash");
    if (isMobile) setMobileNavOpen(false);
  };

  return (
    <div style={{ fontFamily: "'IBM Plex Sans',system-ui,sans-serif", background: "#080c14", minHeight: "100vh", color: "#b8c4dc", display: "flex" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:#1e2a40;border-radius:2px}
        input,select,button{font-family:'IBM Plex Sans',system-ui!important}
        input:focus,select:focus{outline:none;border-color:#3b82f6!important;box-shadow:0 0 0 2px rgba(59,130,246,.12)!important}
        .row:hover > td { background:rgba(255,255,255,.025)!important }
        .nav-btn { transition:background .1s,color .1s }
        .nav-btn:hover { background:rgba(255,255,255,.04)!important }
        .chip { display:inline-flex;align-items:center;padding:2px 7px;border-radius:3px;font-size:10px;font-weight:600;letter-spacing:.5px;font-family:'IBM Plex Mono',monospace }

        /* ── Mobile responsive overrides ──────────────────────
         * Stack most multi-column grids into a single column on phones.
         * Applies broadly to common grid template patterns used in the app.
         * Individual components can opt out by adding className="keep-grid".
         */
        @media (max-width: 768px) {
          /* Make all dashboard-style grids collapse to one column.
             NOTE: React renders inline styles kebab-case in the DOM, so we must match
             "grid-template-columns" (the camelCase variant never matched — dead rule). */
          [style*="grid-template-columns"]:not(.keep-grid) {
            grid-template-columns: 1fr !important;
          }
          /* Slightly tighter card padding on mobile (match rendered kebab/no-quote form) */
          [style*="padding: 14px 18px"]:not(.keep-pad),
          [style*="padding: 16px 18px"]:not(.keep-pad) {
            padding: 12px 14px !important;
          }
          /* Tables stay in horizontal scroll — make sure touch works
             and a visible shadow hint shows there's more to scroll */
          div[style*="overflowX"], div[style*="overflow-x"] {
            -webkit-overflow-scrolling: touch !important;
            overflow-x: auto !important;
            scroll-snap-type: x proximity;
          }
          /* Tables themselves should not shrink — let them keep their natural
             width so the wrapping div actually has something to scroll. */
          table {
            min-width: max-content;
          }
          /* Big KPI numbers shrink a bit so they fit */
          body { font-size: 14px; }
          /* Force all inputs to have at least 16px font so iOS doesn't zoom */
          input, select, textarea { font-size: 16px !important; }
          /* Allow main to constrain width but DO NOT hide overflow —
             child wrappers must scroll. */
          main { min-width: 0; max-width: 100vw; overflow-x: clip; }
          main > div { max-width: 100%; }
          /* Visual hint for horizontally scrollable areas */
          .scroll-hint {
            background: linear-gradient(to right, transparent calc(100% - 14px), rgba(96,165,250,.15) 100%);
          }
        }
      `}</style>

      {/* ── LOAD-ERROR BANNER ── shows when the cloud read failed (corrupt/syncing).
           Critical: tells the user their data is SAFE and that what they see is NOT a reset.
           Saves are disabled while this is up (isLoaded stays false). ─────────────── */}
      {loadError && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9998, background: "#7f1d1d", color: "#fff", padding: "10px 14px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 12, fontSize: 13, fontWeight: 600, borderBottom: "1px solid #b91c1c", lineHeight: 1.4 }}>
          <span>⚠ Tidak bisa memuat data Anda — file mungkin sedang tersinkron atau rusak. <b>Data Anda AMAN</b> dan ini <b>BUKAN</b> reset; penyimpanan dinonaktifkan sementara dan sistem mencoba memuat ulang…</span>
          <button onClick={() => window.location.reload()} style={{ background: "#fff", color: "#7f1d1d", border: "none", borderRadius: 6, padding: "5px 12px", fontWeight: 700, cursor: "pointer" }}>Muat ulang</button>
        </div>
      )}

      {/* ── MOBILE TOP BAR (visible only on mobile) ──────────── */}
      {isMobile && (
        <header style={{ position: "fixed", top: 0, left: 0, right: 0, height: 48, background: "#060a10", borderBottom: "1px solid #1e293b", display: "flex", alignItems: "center", padding: "0 12px", gap: 10, zIndex: 40 }}>
          <button
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
            style={{ background: "transparent", border: "1px solid #1e293b", borderRadius: 6, padding: "6px 10px", color: "#93c5fd", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
          >☰</button>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
            <div style={{ width: 26, height: 26, background: "linear-gradient(135deg,#1d4ed8,#1e40af)", borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#93c5fd" }}>TT</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#e2e8f0" }}>TradeTracker</div>
          </div>
          <div style={{ fontSize: 11, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", textAlign: "right" }}>
            {TABS.find(t => t.id === tab)?.lbl || ""}
          </div>
        </header>
      )}

      {/* ── SIDEBAR (fixed drawer on mobile, pinned on desktop) ─────── */}
      {isMobile && mobileNavOpen && (
        <div
          onClick={() => setMobileNavOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 35 }}
        />
      )}
      <aside style={{
        width: 176, flexShrink: 0, background: "#060a10", borderRight: "1px solid #1e293b",
        display: "flex", flexDirection: "column", position: "fixed",
        inset: isMobile ? "0 auto 0 0" : "0 auto 0 0",
        zIndex: isMobile ? 36 : 30,
        transform: isMobile ? (mobileNavOpen ? "translateX(0)" : "translateX(-100%)") : "none",
        transition: "transform .22s ease",
      }}>
        <div style={{ padding: "18px 14px 14px", borderBottom: "1px solid #1e293b", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1d4ed8,#1e40af)", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, color: "#93c5fd", flexShrink: 0 }}>TT</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, color: "#e2e8f0", letterSpacing: "-.2px" }}>TradeTracker</div>
              <div style={{ fontSize: 14, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", letterSpacing: ".8px", marginTop: 1 }}>IDX · US · FIFO</div>
            </div>
          </div>
          {isMobile && (
            <button
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close menu"
              style={{ background: "transparent", border: "none", color: "#6b7280", fontSize: 20, cursor: "pointer", padding: 0, lineHeight: 1 }}
            >✕</button>
          )}
        </div>
        {/* ── Section toggle: Stocks | Pre-IPO ── */}
        <div style={{ padding: "10px 10px 4px", display: "flex", gap: 4 }}>
          <button
            onClick={() => switchSection("stocks")}
            style={{ flex: 1, padding: "8px 6px", borderRadius: 7, border: `1px solid ${section === "stocks" ? "rgba(59,130,246,.4)" : "#1e293b"}`, cursor: "pointer", background: section === "stocks" ? "rgba(59,130,246,.12)" : "transparent", color: section === "stocks" ? "#60a5fa" : "#6b7280", fontWeight: 600, fontSize: 13 }}>
            ◰ Stocks
          </button>
          <button
            onClick={() => switchSection("preipo")}
            style={{ flex: 1, padding: "8px 6px", borderRadius: 7, border: `1px solid ${section === "preipo" ? "rgba(167,139,250,.4)" : "#1e293b"}`, cursor: "pointer", background: section === "preipo" ? "rgba(167,139,250,.12)" : "transparent", color: section === "preipo" ? "#a78bfa" : "#6b7280", fontWeight: 600, fontSize: 13 }}>
            ◇ Pre-IPO
          </button>
        </div>
        <nav style={{ flex: 1, padding: "8px 6px", display: "flex", flexDirection: "column", gap: 1 }}>
          {TABS.map(t => {
            const accent = section === "preipo" ? "#a78bfa" : "#60a5fa";
            const accentBg = section === "preipo" ? "rgba(167,139,250,.1)" : "rgba(59,130,246,.1)";
            return (
            <button key={t.id} className="nav-btn" onClick={() => { setTab(t.id); if (isMobile) setMobileNavOpen(false); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", borderRadius: 6, border: "none", cursor: "pointer", background: tab === t.id ? accentBg : "transparent", color: tab === t.id ? accent : "#6b7280", fontWeight: tab === t.id ? 600 : 400, fontSize: 14, textAlign: "left" }}>
              <span style={{ fontSize: 15, width: 14, textAlign: "center", opacity: .85 }}>{t.ico}</span>
              {t.lbl}
            </button>
            );
          })}
        </nav>
        {/* Mini stats */}
        <div style={{ padding: "10px 12px", borderTop: "1px solid #1e293b", fontFamily: "'IBM Plex Mono',monospace", fontSize: 14, display: "flex", flexDirection: "column", gap: 4 }}>
          {[
            ["IDX Realized", idr(kpi.idxReal), gc(kpi.idxReal)],
            ["US Realized",  usd(kpi.usReal),  gc(kpi.usReal)],
            ["Open Lots",    kpi.openCount,      "#60a5fa"],
            ["Win Rate",     pct(kpi.winRate),   "#a3e635"],
          ].map(([l, v, c]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "#64748b" }}>{l}</span>
              <span style={{ color: c }}>{v}</span>
            </div>
          ))}
        </div>
      </aside>

      {/* ── MAIN ─────────────────────────────────────────────── */}
      <main style={{ marginLeft: isMobile ? 0 : 176, marginTop: isMobile ? 48 : 0, flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Topbar */}
        <header style={{ padding: isMobile ? "10px 14px" : "12px 22px", borderBottom: "1px solid #1e293b", display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(8,12,20,.95)", backdropFilter: "blur(8px)", position: "sticky", top: isMobile ? 48 : 0, zIndex: 20, gap: 8, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1 style={{ fontSize: isMobile ? 15 : 17, fontWeight: 600, color: "#e2e8f0", letterSpacing: "-.3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{TABS.find(t => t.id === tab)?.lbl}</h1>
            <div style={{ fontSize: isMobile ? 11 : 14, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{buys.length} lots · {kpi.openCount} open · {kpi.closedCount} closed</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {flash && <span style={{ fontSize: isMobile ? 11 : 14, color: flash.startsWith("⚠") ? "#f87171" : "#34d399", fontFamily: "'IBM Plex Mono',monospace" }}>{flash}</span>}
            {live.at && !isMobile && <span style={{ fontSize: 14, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>live {new Date(live.at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>}
            <Btn onClick={() => fetchLive()} loading={loading.live} label={isMobile ? "⟳" : "⟳ Live Prices"} />
            {/* Account chip + logout */}
            {account && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: isMobile ? 0 : 8, paddingLeft: isMobile ? 0 : 12, borderLeft: isMobile ? "none" : "1px solid #1e293b" }}>
                {!isMobile && (
                  <div style={{ fontSize: 14, color: "#9ca3af", lineHeight: 1.2, textAlign: "right" }}>
                    <div style={{ fontWeight: 500 }}>{account.name}</div>
                    <div style={{ fontSize: 14, color: "#64748b" }}>{account.email}</div>
                  </div>
                )}
                <button onClick={onLogout} title="Sign out" style={{
                  background: "rgba(248,113,113,.06)", border: "1px solid rgba(248,113,113,.2)",
                  borderRadius: 6, color: "#f87171", cursor: "pointer", fontSize: isMobile ? 12 : 14,
                  padding: isMobile ? "4px 8px" : "5px 10px", fontWeight: 500, fontFamily: "'IBM Plex Sans',system-ui"
                }}>
                  {isMobile ? "⎋" : "Sign out"}
                </button>
              </div>
            )}
          </div>
        </header>

        <div style={{ padding: isMobile ? 12 : 22, flex: 1 }}>

          {/* ══ DASHBOARD ══════════════════════════════════════ */}
          {tab === "dash" && (() => {
            // Derive filter options from ALL data sources (trades, cash, adjustments)
            const allInvSet = new Set();
            const allBrkSet = new Set();
            [...buys, ...sells, ...adjustments].forEach(t => { allInvSet.add(t.inv); allBrkSet.add(t.brk); });
            Object.keys(cash).forEach(k => {
              const [inv, brk] = k.split("::");
              if (inv) allInvSet.add(inv);
              if (brk) allBrkSet.add(brk);
            });
            const dashInvestors = [...allInvSet].filter(Boolean).sort();
            const dashBrokers   = [...allBrkSet].filter(Boolean).sort();
            const matchesDash = (t) =>
              (dashFilter.mkt === "All" || t.mkt === dashFilter.mkt) &&
              (dashFilter.inv === "All" || t.inv === dashFilter.inv) &&
              (dashFilter.brk === "All" || t.brk === dashFilter.brk);

            // Filtered datasets
            const fClosed    = closed.filter(matchesDash);
            const fOpenEnr   = openEnriched.filter(matchesDash);
            const fFloating  = floatingPL.filter(matchesDash);
            const fAccounts  = allAccounts.filter(a =>
              (dashFilter.mkt === "All" || a.mkt === dashFilter.mkt) &&
              (dashFilter.inv === "All" || a.inv === dashFilter.inv) &&
              (dashFilter.brk === "All" || a.brk === dashFilter.brk)
            );
            const fKpi = {
              idxReal:   fClosed.filter(s => s.mkt === "IDX").reduce((s, r) => s + r.profit, 0),
              usReal:    fClosed.filter(s => s.mkt === "US").reduce((s, r) => s + r.profit, 0),
              idxUnreal: fOpenEnr.filter(p => p.mkt === "IDX" && p.unreal != null).reduce((s, p) => s + p.unreal, 0),
              usUnreal:  fOpenEnr.filter(p => p.mkt === "US"  && p.unreal != null).reduce((s, p) => s + p.unreal, 0),
              openCount: fOpenEnr.length,
              closedCount: fClosed.length,
              winRate: fClosed.length ? fClosed.filter(s => s.profit > 0).length / fClosed.length * 100 : 0,
            };
            const isFiltered = dashFilter.inv !== "All" || dashFilter.brk !== "All" || dashFilter.mkt !== "All";

            return (
            <div>
              {/* Filter bar */}
              <Card style={{ padding: "10px 14px", marginBottom: 14, background: isFiltered ? "linear-gradient(135deg,#0a0e18,#0f1a30)" : undefined }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div style={{ fontSize: 14, color: "#64748b", textTransform: "uppercase", letterSpacing: ".7px", fontWeight: 600, alignSelf: "center", marginRight: 4 }}>Filter →</div>
                  <div style={{ minWidth: 140 }}>
                    <FF l="Market" v={dashFilter.mkt} onC={v => setDashFilter(p => ({ ...p, mkt: v }))} opts={["All", "IDX", "US"]} />
                  </div>
                  <div style={{ minWidth: 140 }}>
                    <FF l="Investor" v={dashFilter.inv} onC={v => setDashFilter(p => ({ ...p, inv: v }))} opts={["All", ...dashInvestors]} />
                  </div>
                  <div style={{ minWidth: 180 }}>
                    <FF l="Broker" v={dashFilter.brk} onC={v => setDashFilter(p => ({ ...p, brk: v }))} opts={["All", ...dashBrokers]} />
                  </div>
                  {isFiltered && (
                    <button onClick={() => setDashFilter({ inv: "All", brk: "All", mkt: "All" })} style={{ padding: "7px 12px", border: "1px solid rgba(167,139,250,.3)", background: "rgba(167,139,250,.08)", borderRadius: 6, color: "#a78bfa", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
                      × Clear Filters
                    </button>
                  )}
                  {isFiltered && (
                    <div style={{ marginLeft: "auto", fontSize: 14, color: "#a78bfa", alignSelf: "center", fontFamily: "'IBM Plex Mono',monospace" }}>
                      Showing: {fKpi.openCount} open · {fKpi.closedCount} closed · {fAccounts.length} account{fAccounts.length !== 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              </Card>

              {/* KPI grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 18 }}>
                {[
                  { l: "IDX Realized P&L",  v: idrExact(fKpi.idxReal),   c: gc(fKpi.idxReal),   sub: `${fClosed.filter(s=>s.mkt==="IDX").length} trades closed` },
                  { l: "US Realized P&L",   v: usd(fKpi.usReal),    c: gc(fKpi.usReal),    sub: `${fClosed.filter(s=>s.mkt==="US").length} trades closed` },
                  { l: "IDX Unrealized",    v: live.prices ? idrExact(fKpi.idxUnreal) : "—",    c: gc(fKpi.idxUnreal), sub: live.prices ? "vs cost basis" : "fetch live prices" },
                  { l: "US Unrealized",     v: live.prices ? usd(fKpi.usUnreal) : "—",     c: gc(fKpi.usUnreal),  sub: live.prices ? "vs cost basis" : "fetch live prices" },
                ].map(k => (
                  <Card key={k.l} style={{ padding: "16px 18px" }}>
                    <div style={{ fontSize: 14, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".8px", fontWeight: 600, marginBottom: 10 }}>{k.l}</div>
                    <div style={{ fontSize: k.v.length > 14 ? 22 : k.v.length > 10 ? 26 : 30, fontWeight: 600, color: k.c, fontFamily: "'IBM Plex Mono',monospace", letterSpacing: "-1px", marginBottom: 5, wordBreak: "break-all" }}>{k.v}</div>
                    <div style={{ fontSize: 14, color: "#94a3b8" }}>{k.sub}</div>
                  </Card>
                ))}
              </div>


              <div style={{ display: "grid", gridTemplateColumns: "5fr 4fr", gap: 14 }}>
                {/* Equity per Account — cash + stock value = total */}
                <Card style={{ padding: "14px 16px", gridColumn: "1 / -1", marginBottom: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <SH style={{ margin: 0 }}>💰 Equity per Account ({fAccounts.length})</SH>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>
                        cash + stocks at live price
                      </span>
                      <button onClick={() => setTab("setup")} style={{ fontSize: 14, color: "#60a5fa", background: "transparent", border: "none", cursor: "pointer", fontFamily: "'IBM Plex Mono',monospace" }}>Configure →</button>
                    </div>
                  </div>
                  {fAccounts.length > 0 ? (() => {
                    // Compute equity rows: cash + current stock value per account.
                    // When some positions lack live prices, we fall back to their cost basis
                    // as a placeholder value. This produces a best-effort equity estimate
                    // instead of "hiding" the equity number entirely when any price is missing.
                    const rows = fAccounts.map(a => {
                      const c = cashByAccount[a.key] || { current: 0 };
                      const positions = fOpenEnr.filter(p => p.inv === a.inv && p.brk === a.brk);
                      let stockValueLive = 0;          // sum of current value of priced positions
                      let costBasisUnpriced = 0;        // sum of cost basis of unpriced positions
                      let pricedCount = 0;
                      let totalCount = positions.length;
                      let costBasis = 0;                // full cost basis (all positions)
                      positions.forEach(p => {
                        costBasis += p.totalCost;
                        if (p.curVal != null) {
                          stockValueLive += p.curVal;
                          pricedCount++;
                        } else {
                          costBasisUnpriced += p.totalCost;
                        }
                      });
                      const hasAllPrices = totalCount === 0 || pricedCount === totalCount;
                      // Equity estimate: cash + live value of priced + cost of unpriced (fallback)
                      const stockValueEst = stockValueLive + costBasisUnpriced;
                      const equity = c.current + stockValueEst;
                      return {
                        ...a,
                        cash: c.current,
                        stockValueLive, stockValueEst, costBasisUnpriced,
                        pricedCount, totalCount, hasAllPrices,
                        equity, costBasis,
                      };
                    });
                    // Sort by equity descending
                    rows.sort((x, y) => y.equity - x.equity);
                    // Totals by market — include all rows (even those with unpriced) since
                    // we're now using cost-basis fallback for missing prices.
                    const idxTotal = rows.filter(r => r.mkt === "IDX").reduce((s, r) => s + r.equity, 0);
                    const usTotal  = rows.filter(r => r.mkt === "US" ).reduce((s, r) => s + r.equity, 0);
                    // Flag whether any row has unpriced positions — affects banner appearance
                    const idxHasUnpriced = rows.some(r => r.mkt === "IDX" && !r.hasAllPrices);
                    const usHasUnpriced  = rows.some(r => r.mkt === "US"  && !r.hasAllPrices);
                    return (
                      <>
                        {/* Grand totals strip */}
                        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                          {idxTotal > 0 && (
                            <div style={{ padding: "8px 14px", background: "linear-gradient(135deg, rgba(59,130,246,.08), rgba(29,78,216,.04))", border: "1px solid rgba(59,130,246,.25)", borderRadius: 7 }}>
                              <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>
                                Total IDX Equity{idxHasUnpriced && <span style={{ color: "#fbbf24", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>· est.</span>}
                              </div>
                              <div style={{ fontSize: 22, fontWeight: 700, color: "#60a5fa", fontFamily: "'IBM Plex Mono',monospace", marginTop: 2 }}>{moneyExact(idxTotal, "IDX")}</div>
                            </div>
                          )}
                          {usTotal > 0 && (
                            <div style={{ padding: "8px 14px", background: "linear-gradient(135deg, rgba(251,191,36,.08), rgba(180,83,9,.04))", border: "1px solid rgba(251,191,36,.25)", borderRadius: 7 }}>
                              <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>
                                Total US Equity{usHasUnpriced && <span style={{ color: "#fbbf24", marginLeft: 6, textTransform: "none", letterSpacing: 0 }}>· est.</span>}
                              </div>
                              <div style={{ fontSize: 22, fontWeight: 700, color: "#fbbf24", fontFamily: "'IBM Plex Mono',monospace", marginTop: 2 }}>{moneyExact(usTotal, "US")}</div>
                            </div>
                          )}
                        </div>
                        {/* Per-account cards */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
                          {rows.map(r => (
                            <div
                              key={r.key + r.mkt}
                              style={{ padding: "12px 14px", background: "#060a10", borderRadius: 8, border: "1px solid #1e293b", cursor: "pointer", transition: "border-color .15s" }}
                              onClick={() => setDashFilter({ inv: r.inv, brk: r.brk, mkt: r.mkt })}
                              onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(59,130,246,.3)"}
                              onMouseLeave={e => e.currentTarget.style.borderColor = "#1e293b"}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                <span style={{ fontSize: 14, color: "#cbd5e1", fontWeight: 500 }}>{r.inv} · {r.brk}</span>
                                <Chip mkt={r.mkt} />
                              </div>
                              {/* Total equity — always shown, but colored to indicate estimate quality */}
                              <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 22, fontWeight: 700, color: r.hasAllPrices ? "#34d399" : "#fbbf24", marginBottom: 6 }}>
                                {moneyExact(r.equity, r.mkt)}
                                {!r.hasAllPrices && <span style={{ color: "#fbbf24", fontSize: 11, marginLeft: 6, fontWeight: 500 }}>est.</span>}
                              </div>
                              {/* Breakdown — cash + stocks */}
                              <div style={{ display: "flex", gap: 12, fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", flexWrap: "wrap" }}>
                                <span style={{ color: gc(r.cash) }}>💵 {moneyExact(r.cash, r.mkt)}</span>
                                {r.totalCount > 0 && (
                                  <span style={{ color: "#a78bfa" }}>
                                    📊 {moneyExact(r.stockValueEst, r.mkt)}
                                    {!r.hasAllPrices && <span style={{ color: "#64748b", marginLeft: 4 }}>({r.pricedCount}/{r.totalCount} live)</span>}
                                  </span>
                                )}
                              </div>
                              {/* Cost basis reference */}
                              {r.totalCount > 0 && (
                                <div style={{ fontSize: 11, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", marginTop: 4 }}>
                                  cost basis: {moneyExact(r.costBasis, r.mkt)}
                                  {r.hasAllPrices && (
                                    <span style={{ color: gc(r.stockValueLive - r.costBasis), marginLeft: 6 }}>
                                      ({r.stockValueLive > r.costBasis ? "+" : ""}{moneyExact(r.stockValueLive - r.costBasis, r.mkt)})
                                    </span>
                                  )}
                                  {!r.hasAllPrices && (
                                    <span style={{ color: "#fbbf24", marginLeft: 6, fontSize: 10 }}>
                                      (fetch live prices for unrealized gain)
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })() : (
                    <div style={{ textAlign: "center", padding: 20, fontSize: 14, color: "#64748b" }}>
                      {isFiltered ? "No accounts match the filter." : "No accounts yet. Go to Setup to add starting balances."}
                    </div>
                  )}
                </Card>
              </div>

              {/* Floating P&L per Stock (aggregated) */}
              <Card style={{ padding: "14px 16px", marginTop: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <SH style={{ margin: 0 }}>📈 Floating P&L per Stock ({fFloating.length}{isFiltered && floatingPL.length !== fFloating.length ? ` / ${floatingPL.length}` : ""})</SH>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {live.at && (() => {
                      const ageMs = Date.now() - new Date(live.at).getTime();
                      const ageHr = Math.floor(ageMs / 3600000);
                      const ageDay = Math.floor(ageMs / 86400000);
                      const stale = ageHr >= 8;
                      const label = ageDay > 0 ? `${ageDay}d ago` : ageHr > 0 ? `${ageHr}h ago` : `${Math.max(1, Math.floor(ageMs / 60000))}m ago`;
                      // Check trading day staleness (markets closed weekends, holidays)
                      const today = todayLocal();
                      const tdStale = live.tradingDay && live.tradingDay < today;
                      return (
                        <span style={{ fontSize: 12, color: stale ? "#fbbf24" : "#94a3b8", fontFamily: "'IBM Plex Mono',monospace" }} title={new Date(live.at).toLocaleString()}>
                          {stale ? "⚠ " : ""}fetched {label}
                          {live.tradingDay && <span style={{ marginLeft: 6, color: tdStale ? "#fbbf24" : "#64748b" }}> · market: {live.tradingDay}</span>}
                        </span>
                      );
                    })()}
                    <button onClick={() => setAutoRefresh(p => !p)} style={{ padding: "4px 10px", background: autoRefresh ? "rgba(52,211,153,.08)" : "rgba(107,114,128,.06)", border: `1px solid ${autoRefresh ? "rgba(52,211,153,.25)" : "#0f1825"}`, borderRadius: 5, color: autoRefresh ? "#34d399" : "#6b7280", cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace" }}>
                      {autoRefresh ? "AUTO DAILY ✓" : "AUTO OFF"}
                    </button>
                    {/* Only fetch missing/pending prices — lighter on the API */}
                    {fFloating.some(r => !r.livePrice) && (
                      <Btn
                        onClick={() => fetchLive(fFloating.filter(r => !r.livePrice).map(r => r.stk))}
                        loading={loading.live}
                        label={`⟳ Missing (${fFloating.filter(r => !r.livePrice).length})`}
                        small
                      />
                    )}
                    <Btn onClick={() => fetchLive()} loading={loading.live} label="⟳ Refresh" small />
                  </div>
                </div>
                {fFloating.length > 0 ? (() => {
                  const floatToggle = makeSortToggle(floatSort, setFloatSort);
                  const floatGetters = {
                    stk:         r => r.stk,
                    mkt:         r => r.mkt,
                    acct:        r => `${r.inv} · ${r.brk}`,
                    qty:         r => r.totalQty,
                    avgCost:     r => r.avgCost,
                    livePrice:   r => r.livePrice,
                    totalCost:   r => r.totalCost,
                    currentVal:  r => r.livePrice ? r.currentVal : null,
                    unrealized:  r => r.livePrice ? r.unrealized : null,
                    absUnreal:   r => r.livePrice && r.unrealized != null ? Math.abs(r.unrealized) : null,
                    unrealPct:   r => r.livePrice ? r.unrealPct : null,
                  };
                  const sortedFloat = sortRows(fFloating, floatSort.key, floatSort.dir, floatGetters);
                  return (
                  <>
                    {/* Mobile-only hint that the table extends past the visible area */}
                    {isMobile && (
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                        <span>← swipe to see Cost Basis · Market Value · P&L · Return % →</span>
                      </div>
                    )}
                    {/* Horizontal-scroll wrapper — without this, mobile users can't see
                        the right-side columns (Cost Basis / Market Value / P&L / Return %)
                        because the table extends past the viewport with no scroll affordance. */}
                    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", marginRight: -16, marginLeft: -16, paddingLeft: 16, paddingRight: 16 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 900 }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid #1e293b" }}>
                        <SortTh label="Stock"        col="stk"        align="left"  sortKey={floatSort.key} sortDir={floatSort.dir} onSort={floatToggle} />
                        <SortTh label="Mkt"          col="mkt"        align="left"  sortKey={floatSort.key} sortDir={floatSort.dir} onSort={floatToggle} />
                        <SortTh label="Acct"         col="acct"       align="left"  sortKey={floatSort.key} sortDir={floatSort.dir} onSort={floatToggle} />
                        <SortTh label="Qty"          col="qty"        align="right" sortKey={floatSort.key} sortDir={floatSort.dir} onSort={floatToggle} />
                        <SortTh label="Avg Cost"     col="avgCost"    align="right" sortKey={floatSort.key} sortDir={floatSort.dir} onSort={floatToggle} />
                        <SortTh label="Live Price"   col="livePrice"  align="right" sortKey={floatSort.key} sortDir={floatSort.dir} onSort={floatToggle} />
                        <SortTh label="Cost Basis"   col="totalCost"  align="right" sortKey={floatSort.key} sortDir={floatSort.dir} onSort={floatToggle} />
                        <SortTh label="Market Value" col="currentVal" align="right" sortKey={floatSort.key} sortDir={floatSort.dir} onSort={floatToggle} />
                        <SortTh label="Floating P&L" col="unrealized" align="right" sortKey={floatSort.key} sortDir={floatSort.dir} onSort={floatToggle} />
                        <SortTh label="Return %"     col="unrealPct"  align="right" sortKey={floatSort.key} sortDir={floatSort.dir} onSort={floatToggle} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFloat.map(r => {
                        const ts = live.timestamps?.[r.stk];
                        let freshness = null;
                        if (ts && r.livePrice) {
                          const ageMs = Date.now() - new Date(ts).getTime();
                          const ageMin = Math.floor(ageMs / 60000);
                          const ageHr  = Math.floor(ageMs / 3600000);
                          const ageDay = Math.floor(ageMs / 86400000);
                          let label, color;
                          if (ageMin < 1)      { label = "just now"; color = "#34d399"; }
                          else if (ageMin < 60){ label = `${ageMin}m ago`; color = "#34d399"; }
                          else if (ageHr < 8)  { label = `${ageHr}h ago`; color = "#94a3b8"; }
                          else if (ageHr < 24) { label = `${ageHr}h ago`; color = "#fbbf24"; }
                          else                 { label = `${ageDay}d old`; color = "#f87171"; }
                          freshness = <div style={{ fontSize: 10, color, fontWeight: 400, marginTop: 1 }}>{label}</div>;
                        }
                        return (
                        <tr key={r.inv + r.brk + r.stk} className="row" style={{ cursor: "pointer" }} onClick={() => { setHistFilter(p => ({ ...p, stock: r.stk, view: "detail" })); setTab("hist"); }}>
                          <td style={{ padding: "6px 10px", textAlign: "left", fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700, whiteSpace: "nowrap" }}>
                            <div style={{ color: "#e2e8f0" }}>{r.stk}</div>
                            {/* On mobile, surface the market value here so it's visible without
                                horizontal scrolling. P&L color hint included. */}
                            {isMobile && r.livePrice && (
                              <div style={{ fontSize: 11, fontWeight: 500, marginTop: 2, color: gc(r.unrealized) }}>
                                {money(r.currentVal, r.mkt)}
                                {r.unrealPct != null && (
                                  <span style={{ marginLeft: 4, color: gc(r.unrealPct) }}>
                                    ({r.unrealPct > 0 ? "+" : ""}{r.unrealPct.toFixed(1)}%)
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <TD><Chip mkt={r.mkt} /></TD>
                          <TD sm xs>{r.inv} · {r.brk}</TD>
                          <TD right mono>{r.totalQty}{r.mkt === "IDX" ? "L" : "sh"}</TD>
                          <TD right mono xs>{r.mkt === "IDX" ? `Rp${R(r.avgCost)}` : `$${R(r.avgCost, 3)}`}</TD>
                          <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, whiteSpace: "nowrap" }}>
                            {r.livePrice ? (
                              <>
                                <div>{r.mkt === "IDX" ? `Rp${r.livePrice?.toLocaleString("id-ID")}` : `$${r.livePrice}`}</div>
                                {freshness}
                              </>
                            ) : (
                              <span style={{ color: "#fbbf24", fontSize: 13 }} title="Click Refresh to fetch this price">⟳ pending</span>
                            )}
                          </td>
                          <TD right mono xs>{money(r.totalCost, r.mkt)}</TD>
                          <TD right mono>{r.livePrice ? money(r.currentVal, r.mkt) : "—"}</TD>
                          <TD right mono bold color={gc(r.unrealized)}>{r.livePrice ? money(r.unrealized, r.mkt) : "—"}</TD>
                          <TD right mono color={gc(r.unrealPct)}>{r.livePrice ? pct(r.unrealPct) : "—"}</TD>
                        </tr>
                        );
                      })}
                      {/* Totals row */}
                      <tr style={{ borderTop: "2px solid #334155" }}>
                        <td colSpan={6} style={{ padding: "9px 8px", fontSize: 14, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>Total IDX (only with live price)</td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: "#9ca3af", fontSize: 14 }}>
                          {idr(fFloating.filter(r => r.mkt === "IDX" && r.livePrice).reduce((s, r) => s + r.totalCost, 0))}
                        </td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: "#9ca3af", fontSize: 14 }}>
                          {idr(fFloating.filter(r => r.mkt === "IDX" && r.livePrice).reduce((s, r) => s + r.currentVal, 0))}
                        </td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, color: gc(fKpi.idxUnreal) }}>
                          {live.prices ? idr(fKpi.idxUnreal) : "—"}
                        </td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: gc(fKpi.idxUnreal) }}>
                          {(() => {
                            const idxCost = fFloating.filter(r => r.mkt === "IDX" && r.livePrice).reduce((s, r) => s + r.totalCost, 0);
                            return idxCost > 0 ? pct(fKpi.idxUnreal / idxCost * 100) : "—";
                          })()}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={6} style={{ padding: "9px 8px", fontSize: 14, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>Total US (only with live price)</td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: "#9ca3af", fontSize: 14 }}>
                          {usd(fFloating.filter(r => r.mkt === "US" && r.livePrice).reduce((s, r) => s + r.totalCost, 0))}
                        </td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: "#9ca3af", fontSize: 14 }}>
                          {usd(fFloating.filter(r => r.mkt === "US" && r.livePrice).reduce((s, r) => s + r.currentVal, 0))}
                        </td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, color: gc(fKpi.usUnreal) }}>
                          {live.prices ? usd(fKpi.usUnreal) : "—"}
                        </td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: gc(fKpi.usUnreal) }}>
                          {(() => {
                            const usCost = fFloating.filter(r => r.mkt === "US" && r.livePrice).reduce((s, r) => s + r.totalCost, 0);
                            return usCost > 0 ? pct(fKpi.usUnreal / usCost * 100) : "—";
                          })()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                  </>
                  );
                })() : (
                  <Empty>{isFiltered ? "No positions match the filter." : "No open positions. Add some trades first."}</Empty>
                )}
                {!live.prices && fFloating.length > 0 && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(251,191,36,.05)", borderRadius: 6, border: "1px solid rgba(251,191,36,.15)", fontSize: 14, color: "#fbbf24" }}>
                    ⓘ Click "⟳ Refresh" to fetch live prices via AI search, or use <b>Manual Price Entry</b> below.
                  </div>
                )}
                {live.prices && fFloating.some(r => !r.livePrice) && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(251,191,36,.05)", borderRadius: 6, border: "1px solid rgba(251,191,36,.15)", fontSize: 14, color: "#fbbf24" }}>
                    ⓘ Missing: <b>{fFloating.filter(r => !r.livePrice).map(r => r.stk).join(", ")}</b> — retry Refresh or enter manually below.
                  </div>
                )}

                {/* Manual price entry panel */}
                {fFloating.length > 0 && (
                  <div style={{ marginTop: 14, padding: "12px 14px", background: "#060a10", borderRadius: 8, border: "1px solid #1e293b" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#a78bfa", textTransform: "uppercase", letterSpacing: ".6px" }}>✎ Manual Price Entry</div>
                      <div style={{ fontSize: 14, color: "#6b7280" }}>Type prices directly if AI search fails or you want to check what-if scenarios</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr auto", gap: 10, alignItems: "end" }}>
                      <FF l="Stock" v={manualPriceStock} onC={setManualPriceStock} opts={["", ...[...new Set(fFloating.map(r => r.stk))]]} />
                      <FF l={(() => {
                        const mkt = fFloating.find(r => r.stk === manualPriceStock)?.mkt;
                        return `Price ${mkt === "IDX" ? "(Rp per share)" : mkt === "US" ? "($ per share)" : ""}`;
                      })()} v={manualPriceValue} onC={setManualPriceValue} type="number" ph={fFloating.find(r => r.stk === manualPriceStock)?.mkt === "IDX" ? "e.g. 8500" : "e.g. 150.25"} />
                      <Btn onClick={setManualPrice} label="Set Price" />
                    </div>

                    {/* List of currently set prices with clear option */}
                    {live.prices && Object.keys(live.prices).length > 0 && (
                      <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {Object.entries(live.prices).filter(([, v]) => v != null).map(([stk, px]) => {
                          const mkt = floatingPL.find(r => r.stk === stk)?.mkt || (US_BROKERS.some(b => openPos.find(p => p.stk === stk && p.brk === b)) ? "US" : "IDX");
                          return (
                            <span key={stk} style={{ fontSize: 14, background: "#0a0e18", border: "1px solid #1e293b", borderRadius: 5, padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'IBM Plex Mono',monospace" }}>
                              <span style={{ color: "#60a5fa", fontWeight: 600 }}>{stk}</span>
                              <span style={{ color: "#d1d5db" }}>= {mkt === "IDX" ? `Rp${px?.toLocaleString("id-ID")}` : `$${px}`}</span>
                              <button onClick={() => clearPrice(stk)} style={{ background: "transparent", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }} title="Clear this price">×</button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </Card>

              <div style={{ display: "grid", gridTemplateColumns: "5fr 4fr", gap: 14, marginTop: 14 }}>
                {/* Open positions */}
                <Card style={{ padding: "14px 16px" }}>
                  <SH>Open Positions ({fKpi.openCount} lots)</SH>
                  {fOpenEnr.slice(0, 10).map(p => (
                    <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #1e293b" }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span className="chip" style={{ background: p.mkt === "IDX" ? "#0f2040" : "#1a1600", color: p.mkt === "IDX" ? "#60a5fa" : "#fbbf24" }}>{p.mkt}</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 15, color: "#e2e8f0", fontFamily: "'IBM Plex Mono',monospace" }}>{p.stk}</div>
                          <div style={{ fontSize: 14, color: "#6b7280" }}>{p.inv} · {p.brk}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 14, color: "#9ca3af", fontFamily: "'IBM Plex Mono',monospace" }}>
                          {p.qty}{p.mkt === "IDX" ? "lot" : "sh"} @ {p.mkt === "IDX" ? `Rp${p.px.toLocaleString("id-ID")}` : `$${p.px}`}
                        </div>
                        {p.unrealPct != null && (
                          <div style={{ fontSize: 14, fontFamily: "'IBM Plex Mono',monospace", color: gc(p.unrealPct) }}>{pct(p.unrealPct)} · {money(p.unreal, p.mkt)}</div>
                        )}
                        {p.lp && <div style={{ fontSize: 14, color: "#64748b" }}>live: {p.mkt === "IDX" ? `Rp${p.lp?.toLocaleString("id-ID")}` : `$${p.lp}`}</div>}
                      </div>
                    </div>
                  ))}
                  {fKpi.openCount === 0 && <Empty>{isFiltered ? "No open positions match filter." : "No open positions."}</Empty>}
                </Card>

                {/* Recent FIFO closes */}
                <Card style={{ padding: "14px 16px" }}>
                  <SH>Recent Closed (FIFO P&L)</SH>
                  {fClosed.slice(-8).reverse().map(s => (
                    <div key={s.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #1e293b" }}>
                      <div>
                        <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                          <span className="chip" style={{ background: s.mkt === "IDX" ? "#0f2040" : "#1a1600", color: s.mkt === "IDX" ? "#60a5fa" : "#fbbf24" }}>{s.mkt}</span>
                          <span style={{ fontWeight: 600, fontSize: 15, color: "#e2e8f0", fontFamily: "'IBM Plex Mono',monospace" }}>{s.stk}</span>
                        </div>
                        <div style={{ fontSize: 14, color: "#6b7280", marginTop: 2 }}>{dt(s.date)} · {s.qty}{s.mkt === "IDX" ? "lot" : "sh"} · {s.days}d held</div>
                        {s.matched[0] && <div style={{ fontSize: 14, color: "#52606d" }}>FIFO from {dt(s.matched[0].buyDate)} @ {s.mkt === "IDX" ? `Rp${s.matched[0].buyPx?.toLocaleString("id-ID")}` : `$${s.matched[0].buyPx}`}</div>}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 600, fontSize: 15, color: gc(s.profit), fontFamily: "'IBM Plex Mono',monospace" }}>{money(s.profit, s.mkt)}</div>
                        <div style={{ fontSize: 14, color: gc(s.pct), fontFamily: "'IBM Plex Mono',monospace" }}>{pct(s.pct)}</div>
                      </div>
                    </div>
                  ))}
                  {fClosed.length === 0 && <Empty>{isFiltered ? "No closed trades match filter." : "No closed trades yet."}</Empty>}
                </Card>
              </div>
            </div>
          );
          })()}

          {/* ══ OPEN POSITIONS ═════════════════════════════════ */}
          {tab === "open" && (
            <Card style={{ padding: "14px 18px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <SH style={{ margin: 0 }}>FIFO Open Lots ({kpi.openCount})</SH>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 14, color: "#94a3b8", fontFamily: "'IBM Plex Mono',monospace" }}>
                    IDX: {openTickers.idx.join(", ") || "—"} · US: {openTickers.us.join(", ") || "—"}
                  </span>
                  <Btn onClick={fetchLive} loading={loading.live} label="⟳ Prices" small />
                </div>
              </div>
              {kpi.openCount === 0 ? (
                <Empty>No open positions.</Empty>
              ) : (() => {
                const openSortToggle = makeSortToggle(openSort, setOpenSort);
                const openGetters = {
                  stk:        r => r.stk,
                  mkt:        r => r.mkt,
                  inv:        r => r.inv,
                  brk:        r => r.brk,
                  date:       r => r.date,
                  qty:        r => r.qty,
                  px:         r => r.px,
                  costUnit:   r => r.costPerUnit,
                  totalCost:  r => r.totalCost,
                  lp:         r => r.lp,
                  curVal:     r => r.curVal,
                  unreal:     r => r.unreal,
                  unrealPct:  r => r.unrealPct,
                  absUnreal:  r => r.unreal == null ? null : Math.abs(r.unreal),
                };
                const sortedOpen = sortRows(openEnriched, openSort.key, openSort.dir, openGetters);
                return (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #1e293b" }}>
                          <SortTh label="Stock"      col="stk"       align="left"  sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Mkt"        col="mkt"       align="left"  sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Investor"   col="inv"       align="left"  sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Broker"     col="brk"       align="left"  sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Date"       col="date"      align="left"  sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Qty"        col="qty"       align="right" sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Buy Px"     col="px"        align="right" sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Cost/Unit"  col="costUnit"  align="right" sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Total Cost" col="totalCost" align="right" sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Live Px"    col="lp"        align="right" sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Cur. Val"   col="curVal"    align="right" sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Unreal P&L" col="unreal"    align="right" sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <SortTh label="Unreal %"   col="unrealPct" align="right" sortKey={openSort.key} sortDir={openSort.dir} onSort={openSortToggle} />
                          <th style={{ padding: "8px 10px", textAlign: "right", color: "#94a3b8", fontWeight: 600, fontSize: 14, textTransform: "uppercase", letterSpacing: ".5px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedOpen.map(p => (
                          <tr key={p.id} className="row" style={{ borderBottom: "1px solid #0f1825" }}>
                            <TD mono bold>{p.stk}</TD>
                            <TD><Chip mkt={p.mkt} /></TD>
                            <TD sm>{p.inv}</TD>
                            <TD sm xs>{p.brk}</TD>
                            <TD sm mono>{dt(p.date)}</TD>
                            <TD right mono>{p.qty}{p.mkt === "IDX" ? "L" : "sh"}</TD>
                            <TD right mono>{p.mkt === "IDX" ? `Rp${p.px.toLocaleString("id-ID")}` : `$${p.px}`}</TD>
                            <TD right mono xs>{p.mkt === "IDX" ? `Rp${R(p.costPerUnit)}` : `$${R(p.costPerUnit, 4)}`}</TD>
                            <TD right mono>{money(p.totalCost, p.mkt)}</TD>
                            <TD right mono>{p.lp ? (p.mkt === "IDX" ? `Rp${p.lp?.toLocaleString("id-ID")}` : `$${p.lp}`) : <span style={{color:"#64748b"}}>—</span>}</TD>
                            <TD right mono>{p.curVal != null ? money(p.curVal, p.mkt) : <span style={{color:"#64748b"}}>—</span>}</TD>
                            <TD right mono color={gc(p.unreal)}>{p.unreal != null ? money(p.unreal, p.mkt) : <span style={{color:"#64748b"}}>—</span>}</TD>
                            <TD right mono color={gc(p.unrealPct)}>{p.unrealPct != null ? pct(p.unrealPct) : <span style={{color:"#64748b"}}>—</span>}</TD>
                            <td style={{ padding: "6px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                              <button onClick={() => startTransferFrom(p)} title="Transfer this stock to another broker" style={{ padding: "4px 8px", background: "rgba(52,211,153,.08)", border: "1px solid rgba(52,211,153,.2)", borderRadius: 5, color: "#34d399", cursor: "pointer", fontSize: 12, marginRight: 4 }}>⇄</button>
                              {p._xfer ? (
                                <button onClick={() => setTab("xfer")} title={`${p.note || "Transferred in"} — manage on the Transfers tab`} style={{ padding: "4px 8px", background: "rgba(167,139,250,.08)", border: "1px solid rgba(167,139,250,.2)", borderRadius: 5, color: "#a78bfa", cursor: "pointer", fontSize: 12 }}>Transferred</button>
                              ) : (<>
                              <button onClick={() => openTradeEdit("BUY", p)} title="Edit this buy lot" style={{ padding: "4px 8px", background: "rgba(96,165,250,.08)", border: "1px solid rgba(96,165,250,.2)", borderRadius: 5, color: "#60a5fa", cursor: "pointer", fontSize: 12, marginRight: 4 }}>✎</button>
                              <button onClick={() => closeAtCost(p)} title="Mark as closed at cost basis (adds a balancing sell with zero P&L impact — for phantom leftovers from Excel typos)" style={{ padding: "4px 8px", background: "rgba(167,139,250,.08)", border: "1px solid rgba(167,139,250,.2)", borderRadius: 5, color: "#a78bfa", cursor: "pointer", fontSize: 12, marginRight: 4 }}>✕ Close</button>
                              <button onClick={() => deleteTrade("BUY", p.id)} title="Delete this buy lot" style={{ padding: "4px 8px", background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.2)", borderRadius: 5, color: "#f87171", cursor: "pointer", fontSize: 12 }}>🗑</button>
                              </>)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </Card>
          )}

          {/* ══ TRADE HISTORY ══════════════════════════════════ */}
          {tab === "hist" && (() => {
            // Filter logic
            const allStocks = [...new Set([...buys, ...sells].map(t => t.stk))].sort();
            const allInv = [...new Set([...buys, ...sells].map(t => t.inv))];
            const allBrk = [...new Set([...buys, ...sells].map(t => t.brk))];
            const matches = (t) =>
              (!histFilter.stock || t.stk === histFilter.stock) &&
              (histFilter.inv === "All" || t.inv === histFilter.inv) &&
              (histFilter.brk === "All" || t.brk === histFilter.brk) &&
              (!histFilter.dateFrom || t.date >= histFilter.dateFrom) &&
              (!histFilter.dateTo   || t.date <= histFilter.dateTo);
            const fBuys   = buys.filter(matches);
            const fSells  = closed.filter(matches);

            // Per-stock aggregation
            const byStock = {};
            [...fBuys, ...fSells.map(s => ({ ...s, _isSell: true }))].forEach(t => {
              const k = `${t.inv}::${t.brk}::${t.stk}::${t.mkt}`;
              if (!byStock[k]) byStock[k] = {
                inv: t.inv, brk: t.brk, stk: t.stk, mkt: t.mkt,
                totalBought: 0, totalSold: 0, buyCount: 0, sellCount: 0,
                totalBuyValue: 0, totalSellValue: 0, totalFees: 0,
                realizedPL: 0, firstDate: t.date, lastDate: t.date,
              };
              const r = byStock[k];
              r.firstDate = t.date < r.firstDate ? t.date : r.firstDate;
              r.lastDate  = t.date > r.lastDate  ? t.date : r.lastDate;
              const shares = t.mkt === "IDX" ? t.qty * 100 : t.qty;
              if (t._isSell) {
                r.sellCount++;
                r.totalSold += t.qty;
                r.totalSellValue += shares * t.px;
                r.totalFees += t.fee;
                r.realizedPL += t.profit;
              } else {
                r.buyCount++;
                r.totalBought += t.qty;
                r.totalBuyValue += shares * t.px;
                r.totalFees += t.fee;
              }
            });
            // Add unrealized to each aggregated row
            Object.values(byStock).forEach(r => {
              const openLots = openEnriched.filter(p => p.inv === r.inv && p.brk === r.brk && p.stk === r.stk);
              r.openQty = openLots.reduce((a, p) => a + p.qty, 0);
              r.totalOpenCost = openLots.reduce((a, p) => a + p.totalCost, 0);
              const lp = live.prices?.[r.stk];
              r.unrealized = lp ? openLots.reduce((a, p) => a + (p.units * lp - p.totalCost), 0) : null;
            });
            const stockAgg = Object.values(byStock).sort((a, b) => a.stk.localeCompare(b.stk));

            return (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Filter bar */}
              <Card style={{ padding: "12px 16px" }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div style={{ minWidth: 180 }}>
                    <FF l="Filter by Stock" v={histFilter.stock} onC={v => setHistFilter(p => ({ ...p, stock: v }))} opts={["", ...allStocks]} />
                  </div>
                  <div style={{ minWidth: 140 }}>
                    <FF l="Investor" v={histFilter.inv} onC={v => setHistFilter(p => ({ ...p, inv: v }))} opts={["All", ...allInv]} />
                  </div>
                  <div style={{ minWidth: 160 }}>
                    <FF l="Broker" v={histFilter.brk} onC={v => setHistFilter(p => ({ ...p, brk: v }))} opts={["All", ...allBrk]} />
                  </div>
                  <div style={{ minWidth: 140 }}>
                    <FF l="From Date" v={histFilter.dateFrom} onC={v => setHistFilter(p => ({ ...p, dateFrom: v }))} type="date" />
                  </div>
                  <div style={{ minWidth: 140 }}>
                    <FF l="To Date" v={histFilter.dateTo} onC={v => setHistFilter(p => ({ ...p, dateTo: v }))} type="date" />
                  </div>
                  <div style={{ display: "flex", gap: 3, padding: 3, background: "#060a10", borderRadius: 7, marginLeft: "auto" }}>
                    {[{id:"detail",lbl:"Detail View"},{id:"bystock",lbl:"By Security"}].map(v => (
                      <button key={v.id} onClick={() => setHistFilter(p => ({ ...p, view: v.id }))} style={{ padding: "6px 12px", border: "none", borderRadius: 5, cursor: "pointer", background: histFilter.view === v.id ? "rgba(59,130,246,.12)" : "transparent", color: histFilter.view === v.id ? "#60a5fa" : "#6b7280", fontWeight: 600, fontSize: 14 }}>{v.lbl}</button>
                    ))}
                  </div>
                  {(histFilter.stock || histFilter.inv !== "All" || histFilter.brk !== "All" || histFilter.dateFrom || histFilter.dateTo) && (
                    <button onClick={() => setHistFilter({ stock: "", inv: "All", brk: "All", view: histFilter.view, dateFrom: "", dateTo: "" })} style={{ padding: "6px 12px", border: "1px solid #1e293b", borderRadius: 6, background: "transparent", color: "#9ca3af", cursor: "pointer", fontSize: 14 }}>× Clear</button>
                  )}
                </div>
                {/* Quick date presets */}
                <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", marginRight: 4 }}>Quick range:</span>
                  {[
                    { label: "Today", fn: () => { const d = todayLocal(); setHistFilter(p => ({ ...p, dateFrom: d, dateTo: d })); } },
                    { label: "Yesterday", fn: () => { const d = daysAgoLocal(1); setHistFilter(p => ({ ...p, dateFrom: d, dateTo: d })); } },
                    { label: "7d", fn: () => { setHistFilter(p => ({ ...p, dateFrom: daysAgoLocal(7), dateTo: todayLocal() })); } },
                    { label: "30d", fn: () => { setHistFilter(p => ({ ...p, dateFrom: daysAgoLocal(30), dateTo: todayLocal() })); } },
                    { label: "MTD", fn: () => { const n = new Date(); const y = n.getFullYear(); const m = String(n.getMonth()+1).padStart(2,"0"); setHistFilter(p => ({ ...p, dateFrom: `${y}-${m}-01`, dateTo: todayLocal() })); } },
                    { label: "YTD", fn: () => { const n = new Date(); setHistFilter(p => ({ ...p, dateFrom: `${n.getFullYear()}-01-01`, dateTo: todayLocal() })); } },
                    { label: "All", fn: () => setHistFilter(p => ({ ...p, dateFrom: "", dateTo: "" })) },
                  ].map(p => (
                    <button
                      key={p.label}
                      onClick={p.fn}
                      style={{ padding: "4px 10px", background: "rgba(96,165,250,.06)", border: "1px solid rgba(96,165,250,.2)", borderRadius: 5, color: "#60a5fa", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace" }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </Card>

              {/* Activity summary — total buy/sell volume, fees, and stamp duty estimate
                 for the filtered period. Useful for tax-time and cash-flow analysis. */}
              {(fBuys.length > 0 || fSells.length > 0) && (() => {
                // Calculate totals separately for IDX and US
                const calcBuys = trades => {
                  const grossSum = trades.reduce((s, b) => {
                    const shares = b.mkt === "IDX" ? b.qty * 100 : b.qty;
                    return s + shares * b.px;
                  }, 0);
                  const feeSum = trades.reduce((s, b) => s + (b.fee || 0), 0);
                  return { grossSum, feeSum, count: trades.length, netSum: grossSum + feeSum };
                };
                const calcSells = trades => {
                  const grossSum = trades.reduce((s, t) => {
                    const shares = t.mkt === "IDX" ? t.qty * 100 : t.qty;
                    return s + shares * t.px;
                  }, 0);
                  const feeSum = trades.reduce((s, t) => s + (t.fee || 0), 0);
                  return { grossSum, feeSum, count: trades.length, netSum: grossSum - feeSum };
                };
                const idxBuys = fBuys.filter(b => b.mkt === "IDX");
                const idxSells = fSells.filter(s => s.mkt === "IDX");
                const usBuys = fBuys.filter(b => b.mkt === "US");
                const usSells = fSells.filter(s => s.mkt === "US");

                // Stamp duty estimate: count distinct (broker × date) combinations within the
                // period for IDX trades. Each combination represents one trading-day confirmation
                // that may incur Rp 10,000 Bea Materai (typical Semesta behavior). This is an
                // ESTIMATE since not every confirmation has stamp (only when total > threshold).
                const idxDays = new Set([
                  ...idxBuys.map(b => `${b.brk}::${b.date}`),
                  ...idxSells.map(s => `${s.brk}::${s.date}`),
                ]);
                const stampEstimate = idxDays.size * 10000;

                const ib = calcBuys(idxBuys);
                const is = calcSells(idxSells);
                const ub = calcBuys(usBuys);
                const us = calcSells(usSells);

                const rangeLbl = !histFilter.dateFrom && !histFilter.dateTo ? "All time"
                              : histFilter.dateFrom === histFilter.dateTo ? histFilter.dateFrom
                              : `${histFilter.dateFrom || "earliest"} → ${histFilter.dateTo || "today"}`;

                const Section = ({ label, color, buys, sells, mkt, showStamp }) => {
                  if (buys.count === 0 && sells.count === 0) return null;
                  const fmt = mkt === "IDX" ? idrExact : usd;
                  const cashOut = buys.netSum;          // money that left your account
                  const cashIn  = sells.netSum;         // money received
                  const netFlow = cashIn - cashOut;
                  return (
                    <div style={{ padding: "12px 14px", background: "#060a10", borderRadius: 8, border: "1px solid #1e293b" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</span>
                        <span style={{ fontSize: 11, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>
                          {buys.count} buy{buys.count !== 1 ? "s" : ""} · {sells.count} sell{sells.count !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                        <div style={{ padding: "8px 10px", background: "rgba(52,211,153,.04)", border: "1px solid rgba(52,211,153,.15)", borderRadius: 6 }}>
                          <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 2 }}>Total Buy</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#34d399", fontFamily: "'IBM Plex Mono',monospace" }}>{fmt(buys.netSum)}</div>
                          <div style={{ fontSize: 10, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", marginTop: 2 }}>
                            gross {fmt(buys.grossSum)} + fees {fmt(buys.feeSum)}
                          </div>
                        </div>
                        <div style={{ padding: "8px 10px", background: "rgba(248,113,113,.04)", border: "1px solid rgba(248,113,113,.15)", borderRadius: 6 }}>
                          <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 2 }}>Total Sell</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#f87171", fontFamily: "'IBM Plex Mono',monospace" }}>{fmt(sells.netSum)}</div>
                          <div style={{ fontSize: 10, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", marginTop: 2 }}>
                            gross {fmt(sells.grossSum)} − fees {fmt(sells.feeSum)}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 14, fontSize: 11, color: "#94a3b8", fontFamily: "'IBM Plex Mono',monospace", flexWrap: "wrap", marginBottom: 8 }}>
                        <span>Total fees: <span style={{color:"#fbbf24"}}>{fmt(buys.feeSum + sells.feeSum)}</span></span>
                        {showStamp && (
                          <span title={`${idxDays.size} unique broker-day combinations × Rp 10,000`}>
                            Stamp duty (est): <span style={{color:"#fbbf24"}}>{fmt(stampEstimate)}</span> <span style={{color:"#64748b"}}>({idxDays.size} day{idxDays.size !== 1 ? "s" : ""})</span>
                          </span>
                        )}
                      </div>
                      <div style={{ padding: "8px 12px", background: netFlow >= 0 ? "rgba(52,211,153,.06)" : "rgba(248,113,113,.06)", border: `1px solid ${netFlow >= 0 ? "rgba(52,211,153,.2)" : "rgba(248,113,113,.2)"}`, borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>NET CASH FLOW</span>
                        <span style={{ fontSize: 17, fontWeight: 700, color: gc(netFlow), fontFamily: "'IBM Plex Mono',monospace" }} title="Sells received minus buys spent">
                          {netFlow >= 0 ? "+" : ""}{fmt(netFlow)}
                        </span>
                      </div>
                    </div>
                  );
                };

                return (
                  <Card style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
                      <SH style={{ margin: 0 }}>💰 Activity Summary</SH>
                      <span style={{ fontSize: 12, color: "#94a3b8", fontFamily: "'IBM Plex Mono',monospace" }}>
                        <span style={{color:"#cbd5e1"}}>{rangeLbl}</span> · {fBuys.length + fSells.length} trade{fBuys.length + fSells.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: (idxBuys.length || idxSells.length) && (usBuys.length || usSells.length) ? "1fr 1fr" : "1fr", gap: 12 }}>
                      <Section label="IDX Market" color="#60a5fa" buys={ib} sells={is} mkt="IDX" showStamp />
                      <Section label="US Market"  color="#fbbf24" buys={ub} sells={us} mkt="US"  showStamp={false} />
                    </div>
                  </Card>
                );
              })()}

              {/* Period P&L summary — only shows if a date filter is applied, or always? Show always with "All time" label */}
              {(() => {
                const idxTrades = fSells.filter(s => s.mkt === "IDX");
                const usTrades  = fSells.filter(s => s.mkt === "US");
                const idxRealized = idxTrades.reduce((s, r) => s + r.profit, 0);
                const usRealized  = usTrades.reduce((s, r) => s + r.profit, 0);
                const idxFees = idxTrades.reduce((s, r) => s + (r.fee || 0), 0);
                const usFees  = usTrades.reduce((s, r) => s + (r.fee || 0), 0);
                const idxRevenue = idxTrades.reduce((s, r) => s + r.netRevenue, 0);
                const usRevenue  = usTrades.reduce((s, r) => s + r.netRevenue, 0);
                // Split into winning and losing trades
                const idxWinners = idxTrades.filter(s => s.profit > 0);
                const idxLosers  = idxTrades.filter(s => s.profit < 0);
                const usWinners  = usTrades.filter(s => s.profit > 0);
                const usLosers   = usTrades.filter(s => s.profit < 0);
                const idxTotalProfit = idxWinners.reduce((s, r) => s + r.profit, 0);
                const idxTotalLoss   = idxLosers.reduce((s, r) => s + r.profit, 0);  // negative number
                const usTotalProfit  = usWinners.reduce((s, r) => s + r.profit, 0);
                const usTotalLoss    = usLosers.reduce((s, r) => s + r.profit, 0);   // negative number
                const idxWins = idxWinners.length;
                const usWins  = usWinners.length;
                const idxByPL = [...idxTrades].sort((a, b) => b.profit - a.profit);
                const usByPL  = [...usTrades].sort((a, b) => b.profit - a.profit);
                const rangeLabel = !histFilter.dateFrom && !histFilter.dateTo ? "All time"
                                 : histFilter.dateFrom === histFilter.dateTo ? histFilter.dateFrom
                                 : `${histFilter.dateFrom || "earliest"} → ${histFilter.dateTo || "today"}`;

                if (fSells.length === 0) {
                  return (
                    <Card style={{ padding: "14px 16px" }}>
                      <SH style={{ margin: 0, marginBottom: 8 }}>📅 Realized P&L Summary</SH>
                      <div style={{ padding: "16px", background: "#060a10", borderRadius: 7, fontSize: 14, color: "#64748b", textAlign: "center", border: "1px solid #1e293b" }}>
                        No closed trades match the current filter (<span style={{color:"#94a3b8", fontFamily:"'IBM Plex Mono',monospace"}}>{rangeLabel}</span>)
                      </div>
                    </Card>
                  );
                }
                return (
                  <Card style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <SH style={{ margin: 0 }}>📅 Realized P&L Summary</SH>
                      <span style={{ fontSize: 12, color: "#94a3b8", fontFamily: "'IBM Plex Mono',monospace" }}>
                        <span style={{color:"#cbd5e1"}}>{rangeLabel}</span> · {fSells.length} closed trade{fSells.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: idxTrades.length && usTrades.length ? "1fr 1fr" : "1fr", gap: 12 }}>
                      {idxTrades.length > 0 && (
                        <div style={{ padding: "14px 16px", background: "#060a10", borderRadius: 8, border: "1px solid #1e293b" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <span style={{ fontSize: 13, color: "#60a5fa", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>IDX Market</span>
                            <span style={{ fontSize: 11, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>
                              {idxTrades.length} trades · {idxWins}W/{idxTrades.length - idxWins}L · {(idxWins/idxTrades.length*100).toFixed(0)}% WR
                            </span>
                          </div>
                          {/* Profit / Loss / Net breakdown */}
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                            <div style={{ padding: "8px 10px", background: "rgba(52,211,153,.05)", border: "1px solid rgba(52,211,153,.2)", borderRadius: 6 }}>
                              <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 2 }}>Total Profit</div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: "#34d399", fontFamily: "'IBM Plex Mono',monospace" }}>
                                {idrExact(idxTotalProfit)}
                              </div>
                              <div style={{ fontSize: 10, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", marginTop: 2 }}>
                                {idxWinners.length} winning trade{idxWinners.length !== 1 ? "s" : ""}
                              </div>
                            </div>
                            <div style={{ padding: "8px 10px", background: "rgba(248,113,113,.05)", border: "1px solid rgba(248,113,113,.2)", borderRadius: 6 }}>
                              <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 2 }}>Total Loss</div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: "#f87171", fontFamily: "'IBM Plex Mono',monospace" }}>
                                {idrExact(idxTotalLoss)}
                              </div>
                              <div style={{ fontSize: 10, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", marginTop: 2 }}>
                                {idxLosers.length} losing trade{idxLosers.length !== 1 ? "s" : ""}
                              </div>
                            </div>
                          </div>
                          {/* Net P&L — the big final sum */}
                          <div style={{ padding: "10px 12px", background: idxRealized >= 0 ? "rgba(52,211,153,.08)" : "rgba(248,113,113,.08)", border: `1px solid ${idxRealized >= 0 ? "rgba(52,211,153,.3)" : "rgba(248,113,113,.3)"}`, borderRadius: 6, marginBottom: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                              <span style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>NET REALIZED P&L</span>
                              <span style={{ fontSize: 22, fontWeight: 700, color: gc(idxRealized), fontFamily: "'IBM Plex Mono',monospace" }}>
                                {idrExact(idxRealized)}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 14, fontSize: 11, color: "#94a3b8", fontFamily: "'IBM Plex Mono',monospace", marginBottom: 10 }}>
                            <span>Revenue: {idrExact(idxRevenue)}</span>
                            <span>Fees: {idrExact(idxFees)}</span>
                          </div>
                          {idxByPL.length > 0 && (
                            <div style={{ borderTop: "1px solid #1e293b", paddingTop: 8 }}>
                              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>TOP MOVERS</div>
                              {idxByPL.slice(0, 3).map(s => (
                                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", padding: "2px 0" }}>
                                  <span style={{ color: "#cbd5e1" }}>{s.stk} <span style={{color:"#64748b"}}>{dt(s.date)}</span></span>
                                  <span style={{ color: gc(s.profit) }}>{idrExact(s.profit)} <span style={{color:gc(s.pct), fontSize:10}}>({pct(s.pct)})</span></span>
                                </div>
                              ))}
                              {idxByPL.length > 3 && idxByPL[idxByPL.length - 1].profit < 0 && (
                                <>
                                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, marginBottom: 4 }}>WORST</div>
                                  {idxByPL.slice(-2).reverse().filter(s => s.profit < 0).map(s => (
                                    <div key={s.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", padding: "2px 0" }}>
                                      <span style={{ color: "#cbd5e1" }}>{s.stk} <span style={{color:"#64748b"}}>{dt(s.date)}</span></span>
                                      <span style={{ color: gc(s.profit) }}>{idrExact(s.profit)} <span style={{color:gc(s.pct), fontSize:10}}>({pct(s.pct)})</span></span>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {usTrades.length > 0 && (
                        <div style={{ padding: "14px 16px", background: "#060a10", borderRadius: 8, border: "1px solid #1e293b" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <span style={{ fontSize: 13, color: "#fbbf24", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>US Market</span>
                            <span style={{ fontSize: 11, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>
                              {usTrades.length} trades · {usWins}W/{usTrades.length - usWins}L · {(usWins/usTrades.length*100).toFixed(0)}% WR
                            </span>
                          </div>
                          {/* Profit / Loss breakdown */}
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                            <div style={{ padding: "8px 10px", background: "rgba(52,211,153,.05)", border: "1px solid rgba(52,211,153,.2)", borderRadius: 6 }}>
                              <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 2 }}>Total Profit</div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: "#34d399", fontFamily: "'IBM Plex Mono',monospace" }}>
                                {usd(usTotalProfit)}
                              </div>
                              <div style={{ fontSize: 10, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", marginTop: 2 }}>
                                {usWinners.length} winning trade{usWinners.length !== 1 ? "s" : ""}
                              </div>
                            </div>
                            <div style={{ padding: "8px 10px", background: "rgba(248,113,113,.05)", border: "1px solid rgba(248,113,113,.2)", borderRadius: 6 }}>
                              <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 2 }}>Total Loss</div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: "#f87171", fontFamily: "'IBM Plex Mono',monospace" }}>
                                {usd(usTotalLoss)}
                              </div>
                              <div style={{ fontSize: 10, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", marginTop: 2 }}>
                                {usLosers.length} losing trade{usLosers.length !== 1 ? "s" : ""}
                              </div>
                            </div>
                          </div>
                          {/* Net P&L — the big final sum */}
                          <div style={{ padding: "10px 12px", background: usRealized >= 0 ? "rgba(52,211,153,.08)" : "rgba(248,113,113,.08)", border: `1px solid ${usRealized >= 0 ? "rgba(52,211,153,.3)" : "rgba(248,113,113,.3)"}`, borderRadius: 6, marginBottom: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                              <span style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>NET REALIZED P&L</span>
                              <span style={{ fontSize: 22, fontWeight: 700, color: gc(usRealized), fontFamily: "'IBM Plex Mono',monospace" }}>
                                {usd(usRealized)}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 14, fontSize: 11, color: "#94a3b8", fontFamily: "'IBM Plex Mono',monospace", marginBottom: 10 }}>
                            <span>Revenue: {usd(usRevenue)}</span>
                            <span>Fees: {usd(usFees)}</span>
                          </div>
                          {usByPL.length > 0 && (
                            <div style={{ borderTop: "1px solid #1e293b", paddingTop: 8 }}>
                              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>TOP MOVERS</div>
                              {usByPL.slice(0, 3).map(s => (
                                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", padding: "2px 0" }}>
                                  <span style={{ color: "#cbd5e1" }}>{s.stk} <span style={{color:"#64748b"}}>{dt(s.date)}</span></span>
                                  <span style={{ color: gc(s.profit) }}>{usd(s.profit)} <span style={{color:gc(s.pct), fontSize:10}}>({pct(s.pct)})</span></span>
                                </div>
                              ))}
                              {usByPL.length > 3 && usByPL[usByPL.length - 1].profit < 0 && (
                                <>
                                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, marginBottom: 4 }}>WORST</div>
                                  {usByPL.slice(-2).reverse().filter(s => s.profit < 0).map(s => (
                                    <div key={s.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", padding: "2px 0" }}>
                                      <span style={{ color: "#cbd5e1" }}>{s.stk} <span style={{color:"#64748b"}}>{dt(s.date)}</span></span>
                                      <span style={{ color: gc(s.profit) }}>{usd(s.profit)} <span style={{color:gc(s.pct), fontSize:10}}>({pct(s.pct)})</span></span>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })()}

              {/* By Security grouped view */}
              {histFilter.view === "bystock" && (
                <Card style={{ padding: "14px 18px" }}>
                  <SH>Per-Security Summary ({stockAgg.length} positions)</SH>
                  {stockAgg.length === 0 ? (
                    <Empty>No trades match the filter.</Empty>
                  ) : (() => {
                    const isDateFiltered = !!(histFilter.dateFrom || histFilter.dateTo);
                    const aggToggle = makeSortToggle(aggSort, setAggSort);
                    const aggGetters = {
                      stk:        r => r.stk,
                      mkt:        r => r.mkt,
                      inv:        r => r.inv,
                      brk:        r => r.brk,
                      buyCount:   r => r.buyCount,
                      sellCount:  r => r.sellCount,
                      firstDate:  r => r.firstDate,
                      lastDate:   r => r.lastDate,
                      totalFees:  r => r.totalFees,
                      realizedPL: r => r.realizedPL,
                      absPL:      r => Math.abs(r.realizedPL || 0),
                      openQty:    r => r.openQty,
                      unrealized: r => r.unrealized,
                    };
                    const sortedAgg = sortRows(stockAgg, aggSort.key, aggSort.dir, aggGetters);
                    return (
                      <div style={{ overflowX: "auto" }}>
                        {isDateFiltered && (
                          <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "'IBM Plex Mono',monospace", marginBottom: 8, padding: "6px 10px", background: "rgba(96,165,250,.05)", border: "1px solid rgba(96,165,250,.15)", borderRadius: 5 }}>
                            ℹ Showing only trades within {histFilter.dateFrom || "earliest"} → {histFilter.dateTo || "today"}. Open Qty / Unrealized columns hidden because they reflect current state.
                          </div>
                        )}
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid #1e293b" }}>
                              <SortTh label="Stock"         col="stk"        align="left"  sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                              <SortTh label="Mkt"           col="mkt"        align="left"  sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                              <SortTh label="Investor"      col="inv"        align="left"  sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                              <SortTh label="Broker"        col="brk"        align="left"  sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                              <SortTh label="Buys"          col="buyCount"   align="right" sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                              <SortTh label="Sells"         col="sellCount"  align="right" sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                              <SortTh label="First Date"    col="firstDate"  align="right" sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                              <SortTh label="Last Date"     col="lastDate"   align="right" sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                              <SortTh label="Total Fees"    col="totalFees"  align="right" sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                              <SortTh label="Realized P&L"  col="realizedPL" align="right" sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                              {!isDateFiltered && (
                                <>
                                  <SortTh label="Open Qty"      col="openQty"    align="right" sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                                  <SortTh label="Unreal. P&L"   col="unrealized" align="right" sortKey={aggSort.key} sortDir={aggSort.dir} onSort={aggToggle} />
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedAgg.map(r => (
                              <tr key={r.inv+r.brk+r.stk} className="row" style={{ cursor: "pointer", borderBottom: "1px solid #0f1825" }} onClick={() => setHistFilter(p => ({ ...p, stock: r.stk, view: "detail" }))}>
                                <TD mono bold>{r.stk}</TD>
                                <TD><Chip mkt={r.mkt} /></TD>
                                <TD sm>{r.inv}</TD>
                                <TD sm xs>{r.brk}</TD>
                                <TD right mono color="#34d399">{r.buyCount}</TD>
                                <TD right mono color="#f87171">{r.sellCount}</TD>
                                <TD right sm mono xs>{dt(r.firstDate)}</TD>
                                <TD right sm mono xs>{dt(r.lastDate)}</TD>
                                <TD right mono xs>{money(r.totalFees, r.mkt)}</TD>
                                <TD right mono bold color={gc(r.realizedPL)}>{r.sellCount ? money(r.realizedPL, r.mkt) : <span style={{color:"#64748b"}}>—</span>}</TD>
                                {!isDateFiltered && (
                                  <>
                                    <TD right mono>{r.openQty ? `${r.openQty}${r.mkt==="IDX"?"L":"sh"}` : <span style={{color:"#64748b"}}>—</span>}</TD>
                                    <TD right mono color={gc(r.unrealized)}>{r.unrealized != null ? money(r.unrealized, r.mkt) : <span style={{color:"#64748b"}}>—</span>}</TD>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                  <div style={{ fontSize: 14, color: "#94a3b8", marginTop: 10, textAlign: "center", fontFamily: "'IBM Plex Mono',monospace" }}>Click any row to see detail for that stock</div>
                </Card>
              )}

              {/* Detail view */}
              {histFilter.view === "detail" && (
                <>
                  {/* If single-stock filter, show the transaction timeline */}
                  {histFilter.stock && (
                    <Card style={{ padding: "14px 18px", background: "linear-gradient(135deg,#0a0e18,#0f1825)" }}>
                      <SH>Transaction Timeline — {histFilter.stock}</SH>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                        <thead><tr style={{ borderBottom: "1px solid #1e293b" }}>
                          {["Date","Type","Mkt","Inv","Broker","Qty","Price","Value","Fee","P&L (if sell)"].map(h => (
                            <th key={h} style={{ padding: "7px 8px", textAlign: h==="Date"||h==="Inv"||h==="Broker"?"left":"right", color: "#64748b", fontWeight: 600, fontSize: 14, textTransform: "uppercase", letterSpacing: ".5px", whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {[...fBuys.map(b => ({...b, _type:"BUY"})), ...fSells.map(s => ({...s, _type:"SELL"}))]
                            .sort((a,b) => a.date.localeCompare(b.date))
                            .map(t => {
                              const shares = t.mkt === "IDX" ? t.qty * 100 : t.qty;
                              const value = shares * t.px;
                              return (
                                <tr key={t.id} className="row">
                                  <TD sm mono>{dt(t.date)}</TD>
                                  <TD><span className="chip" style={{background:t._type==="BUY"?"rgba(52,211,153,.12)":"rgba(248,113,113,.12)",color:t._type==="BUY"?"#34d399":"#f87171"}}>{t._type}</span></TD>
                                  <TD><Chip mkt={t.mkt} /></TD>
                                  <TD sm>{t.inv}</TD>
                                  <TD sm xs>{t.brk}</TD>
                                  <TD right mono>{t.qty}{t.mkt==="IDX"?"L":"sh"}</TD>
                                  <TD right mono>{t.mkt==="IDX"?`Rp${t.px?.toLocaleString("id-ID")}`:`$${t.px}`}</TD>
                                  <TD right mono xs>{money(value, t.mkt)}</TD>
                                  <TD right mono xs>{money(t.fee, t.mkt)}</TD>
                                  <TD right mono bold color={t._type==="SELL"?gc(t.profit):"#64748b"}>{t._type==="SELL"?money(t.profit,t.mkt):"—"}</TD>
                                </tr>
                              );
                            })
                          }
                        </tbody>
                      </table>
                    </Card>
                  )}

                  {/* Closed FIFO table */}
                  <Card style={{ padding: "14px 18px" }}>
                    <SH>Closed Trades — FIFO Realized P&L ({fSells.length})</SH>
                    {fSells.length === 0 ? (
                      <Empty>No closed trades match the filter.</Empty>
                    ) : (() => {
                      const histToggle = makeSortToggle(histSort, setHistSort);
                      const histGetters = {
                        stk:      s => s.stk,
                        mkt:      s => s.mkt,
                        inv:      s => s.inv,
                        brk:      s => s.brk,
                        date:     s => s.date,
                        qty:      s => s.qty,
                        px:       s => s.px,
                        buyDate:  s => s.matched[0]?.buyDate,
                        costU:    s => s.sellShares ? s.totalCostBasis / s.sellShares : 0,
                        revenue:  s => s.netRevenue,
                        cost:     s => s.totalCostBasis,
                        profit:   s => s.profit,
                        absPL:    s => Math.abs(s.profit || 0),
                        pctRet:   s => s.pct,
                        days:     s => s.days,
                      };
                      const sortedHist = sortRows(fSells, histSort.key, histSort.dir, histGetters);
                      return (
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ borderBottom: "1px solid #1e293b" }}>
                                <SortTh label="Stock"       col="stk"     align="left"  sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Mkt"         col="mkt"     align="left"  sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Inv"         col="inv"     align="left"  sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Broker"      col="brk"     align="left"  sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Sell Date"   col="date"    align="left"  sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Qty"         col="qty"     align="right" sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Sell Px"     col="px"      align="right" sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="FIFO Buy"    col="buyDate" align="left"  sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Cost/U"      col="costU"   align="right" sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Revenue"     col="revenue" align="right" sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Cost Basis"  col="cost"    align="right" sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Profit"      col="profit"  align="right" sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Return"      col="pctRet"  align="right" sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <SortTh label="Days"        col="days"    align="right" sortKey={histSort.key} sortDir={histSort.dir} onSort={histToggle} />
                                <th style={{ padding: "8px 10px", textAlign: "right", color: "#94a3b8", fontWeight: 600, fontSize: 14, textTransform: "uppercase", letterSpacing: ".5px" }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedHist.map(s => (
                                <tr key={s.id} className="row" style={{ borderBottom: "1px solid #0f1825" }}>
                                  <TD mono bold>{s.stk}</TD>
                                  <TD><Chip mkt={s.mkt} /></TD>
                                  <TD sm>{s.inv}</TD>
                                  <TD sm xs>{s.brk}</TD>
                                  <TD sm mono>{dt(s.date)}</TD>
                                  <TD right mono>{s.qty}{s.mkt === "IDX" ? "L" : "sh"}</TD>
                                  <TD right mono>{s.mkt === "IDX" ? `Rp${s.px?.toLocaleString("id-ID")}` : `$${s.px}`}</TD>
                                  <TD sm mono xs>
                                    {s.matched[0] ? (
                                      <>
                                        {dt(s.matched[0].buyDate)}
                                        {s._paired && <span title="Paired Excel-style row (not FIFO)" style={{ marginLeft: 4, padding: "1px 5px", fontSize: 9, borderRadius: 3, background: "rgba(96,165,250,.1)", color: "#60a5fa", fontWeight: 600 }}>PAIR</span>}
                                      </>
                                    ) : <span style={{color:"#64748b"}}>—</span>}
                                  </TD>
                                  <TD right mono xs>{s.matched[0] ? (s.mkt === "IDX" ? `Rp${R(s.totalCostBasis / s.sellShares)}` : `$${R(s.totalCostBasis / s.sellShares, 4)}`) : <span style={{color:"#64748b"}}>—</span>}</TD>
                                  <TD right mono>{money(s.netRevenue, s.mkt)}</TD>
                                  <TD right mono xs>{money(s.totalCostBasis, s.mkt)}</TD>
                                  <TD right mono bold color={gc(s.profit)}>{money(s.profit, s.mkt)}</TD>
                                  <TD right mono color={gc(s.pct)}>{pct(s.pct)}</TD>
                                  <TD right mono xs>{s.days}d</TD>
                                  <td style={{ padding: "6px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                                    <button onClick={() => openTradeEdit("SELL", s)} title="Edit" style={{ padding: "4px 8px", background: "rgba(96,165,250,.08)", border: "1px solid rgba(96,165,250,.2)", borderRadius: 5, color: "#60a5fa", cursor: "pointer", fontSize: 12, marginRight: 4 }}>✎</button>
                                    <button onClick={() => deleteTrade("SELL", s.id)} title="Delete" style={{ padding: "4px 8px", background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.2)", borderRadius: 5, color: "#f87171", cursor: "pointer", fontSize: 12 }}>🗑</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </Card>

                  {/* All buy lots */}
                  <Card style={{ padding: "14px 18px" }}>
                    <SH>Buy Lots ({fBuys.length})</SH>
                    {fBuys.length === 0 ? (
                      <Empty>No buy lots match the filter.</Empty>
                    ) : (() => {
                      const buysToggle = makeSortToggle(buysSort, setBuysSort);
                      const buysGetters = {
                        stk:    b => b.stk,
                        mkt:    b => b.mkt,
                        inv:    b => b.inv,
                        brk:    b => b.brk,
                        date:   b => b.date,
                        qty:    b => b.qty,
                        px:     b => b.px,
                        value:  b => (b.mkt === "IDX" ? b.qty * 100 : b.qty) * b.px,
                        fee:    b => b.fee,
                        status: b => openEnriched.some(p => p.id === b.id) ? "OPEN" : "SOLD",
                      };
                      const sortedBuys = sortRows(fBuys, buysSort.key, buysSort.dir, buysGetters);
                      return (
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ borderBottom: "1px solid #1e293b" }}>
                                <SortTh label="Stock"    col="stk"    align="left"  sortKey={buysSort.key} sortDir={buysSort.dir} onSort={buysToggle} />
                                <SortTh label="Mkt"      col="mkt"    align="left"  sortKey={buysSort.key} sortDir={buysSort.dir} onSort={buysToggle} />
                                <SortTh label="Investor" col="inv"    align="left"  sortKey={buysSort.key} sortDir={buysSort.dir} onSort={buysToggle} />
                                <SortTh label="Broker"   col="brk"    align="left"  sortKey={buysSort.key} sortDir={buysSort.dir} onSort={buysToggle} />
                                <SortTh label="Date"     col="date"   align="left"  sortKey={buysSort.key} sortDir={buysSort.dir} onSort={buysToggle} />
                                <SortTh label="Qty"      col="qty"    align="right" sortKey={buysSort.key} sortDir={buysSort.dir} onSort={buysToggle} />
                                <SortTh label="Price"    col="px"     align="right" sortKey={buysSort.key} sortDir={buysSort.dir} onSort={buysToggle} />
                                <SortTh label="Value"    col="value"  align="right" sortKey={buysSort.key} sortDir={buysSort.dir} onSort={buysToggle} />
                                <SortTh label="Fee"      col="fee"    align="right" sortKey={buysSort.key} sortDir={buysSort.dir} onSort={buysToggle} />
                                <SortTh label="Status"   col="status" align="left"  sortKey={buysSort.key} sortDir={buysSort.dir} onSort={buysToggle} />
                                <th style={{ padding: "8px 10px", textAlign: "right", color: "#94a3b8", fontWeight: 600, fontSize: 14, textTransform: "uppercase", letterSpacing: ".5px" }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedBuys.map(b => {
                                const shares = b.mkt === "IDX" ? b.qty * 100 : b.qty;
                                const fullyClosed = !openEnriched.some(p => p.id === b.id);
                                return (
                                  <tr key={b.id} className="row" style={{ borderBottom: "1px solid #0f1825" }}>
                                    <TD mono bold>{b.stk}</TD>
                                    <TD><Chip mkt={b.mkt} /></TD>
                                    <TD sm>{b.inv}</TD>
                                    <TD sm xs>{b.brk}</TD>
                                    <TD sm mono>{dt(b.date)}</TD>
                                    <TD right mono>{b.qty}{b.mkt === "IDX" ? "L" : "sh"}</TD>
                                    <TD right mono>{b.mkt === "IDX" ? `Rp${b.px?.toLocaleString("id-ID")}` : `$${b.px}`}</TD>
                                    <TD right mono>{money(shares * b.px, b.mkt)}</TD>
                                    <TD right mono xs>{money(b.fee, b.mkt)}</TD>
                                    <TD>
                                      <span className="chip" style={{ background: fullyClosed ? "#0a2010" : "#1a1200", color: fullyClosed ? "#4ade80" : "#fbbf24" }}>
                                        {fullyClosed ? "SOLD" : "OPEN"}
                                      </span>
                                    </TD>
                                    <td style={{ padding: "6px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                                      <button onClick={() => openTradeEdit("BUY", b)} title="Edit" style={{ padding: "4px 8px", background: "rgba(96,165,250,.08)", border: "1px solid rgba(96,165,250,.2)", borderRadius: 5, color: "#60a5fa", cursor: "pointer", fontSize: 12, marginRight: 4 }}>✎</button>
                                      <button onClick={() => deleteTrade("BUY", b.id)} title="Delete" style={{ padding: "4px 8px", background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.2)", borderRadius: 5, color: "#f87171", cursor: "pointer", fontSize: 12 }}>🗑</button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </Card>
                </>
              )}
            </div>
          );
          })()}

          {/* ══ MONTHLY P&L ════════════════════════════════════ */}
          {tab === "perf" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* ── AUTO-COMPUTED EQUITY CURVE — primary view ───────────────── */}
              <Card style={{ padding: "14px 18px" }}>
                <div style={{ marginBottom: 14 }}>
                  <SH style={{ margin: 0 }}>📈 Portfolio Performance vs Index</SH>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                    Auto-computed from trades, cash adjustments, and live prices · Time-Weighted Return (excludes cash flow distortion)
                  </div>
                </div>

                {/* Toolbar row 1: frequency + market + investor + broker */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }} className="keep-grid">
                  {/* Frequency */}
                  <div>
                    <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600, marginBottom: 4 }}>Frequency</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {[["monthly","Monthly"],["quarterly","Quarterly"],["yearly","Yearly"]].map(([k, lbl]) => (
                        <button key={k} onClick={() => setPerfFreq(k)}
                          style={{ padding: "5px 10px", borderRadius: 5, border: `1px solid ${perfFreq === k ? "rgba(59,130,246,.3)" : "#0f1825"}`, cursor: "pointer", background: perfFreq === k ? "rgba(59,130,246,.08)" : "transparent", color: perfFreq === k ? "#60a5fa" : "#6b7280", fontWeight: perfFreq === k ? 600 : 400, fontSize: 12 }}
                        >{lbl}</button>
                      ))}
                    </div>
                  </div>
                  {/* Market */}
                  <div>
                    <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600, marginBottom: 4 }}>Market</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {[["ALL","All"],["IDX","🇮🇩"],["US","🇺🇸"]].map(([k, lbl]) => (
                        <button key={k} onClick={() => { setPerfMkt(k); setPerfBrk("ALL"); }}
                          style={{ padding: "5px 10px", borderRadius: 5, border: `1px solid ${perfMkt === k ? "rgba(167,139,250,.3)" : "#0f1825"}`, cursor: "pointer", background: perfMkt === k ? "rgba(167,139,250,.08)" : "transparent", color: perfMkt === k ? "#a78bfa" : "#6b7280", fontWeight: perfMkt === k ? 600 : 400, fontSize: 12 }}
                        >{lbl}</button>
                      ))}
                    </div>
                  </div>
                  {/* Investor */}
                  <div>
                    <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600, marginBottom: 4 }}>Investor</div>
                    <select
                      value={perfInv}
                      onChange={e => { setPerfInv(e.target.value); setPerfBrk("ALL"); }}
                      style={{ width: "100%", padding: "5px 8px", background: "#06090f", color: "#e2e8f0", border: "1px solid #1e293b", borderRadius: 5, fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", cursor: "pointer" }}
                    >
                      <option value="ALL">All Investors</option>
                      {/* Build investor list from actual buys/sells data so we only show ones with activity */}
                      {[...new Set([...buys.map(b => b.inv), ...sells.map(s => s.inv)])].sort().map(inv => (
                        <option key={inv} value={inv}>{inv}</option>
                      ))}
                    </select>
                  </div>
                  {/* Broker */}
                  <div>
                    <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600, marginBottom: 4 }}>Broker</div>
                    <select
                      value={perfBrk}
                      onChange={e => setPerfBrk(e.target.value)}
                      style={{ width: "100%", padding: "5px 8px", background: "#06090f", color: "#e2e8f0", border: "1px solid #1e293b", borderRadius: 5, fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", cursor: "pointer" }}
                    >
                      <option value="ALL">All Brokers</option>
                      {/* Filter broker list by chosen market and chosen investor (if any) — keep it relevant */}
                      {(() => {
                        const candidates = new Set();
                        [...buys, ...sells].forEach(t => {
                          if (perfMkt !== "ALL" && t.mkt !== perfMkt) return;
                          if (perfInv !== "ALL" && t.inv !== perfInv) return;
                          candidates.add(t.brk);
                        });
                        return [...candidates].sort().map(brk => <option key={brk} value={brk}>{brk}</option>);
                      })()}
                    </select>
                  </div>
                </div>

                {/* Toolbar row 2: date range */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }} className="keep-grid">
                  <div>
                    <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600, marginBottom: 4 }}>From (YYYY-MM)</div>
                    <input
                      type="month"
                      value={perfDateFrom}
                      onChange={e => setPerfDateFrom(e.target.value)}
                      style={{ width: "100%", padding: "5px 8px", background: "#06090f", color: "#e2e8f0", border: "1px solid #1e293b", borderRadius: 5, fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", colorScheme: "dark" }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600, marginBottom: 4 }}>To (YYYY-MM)</div>
                    <input
                      type="month"
                      value={perfDateTo}
                      onChange={e => setPerfDateTo(e.target.value)}
                      style={{ width: "100%", padding: "5px 8px", background: "#06090f", color: "#e2e8f0", border: "1px solid #1e293b", borderRadius: 5, fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", colorScheme: "dark" }}
                    />
                  </div>
                  {/* Reset filters button (clears date + investor + broker, keeps freq/market) */}
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    {(perfDateFrom || perfDateTo || perfInv !== "ALL" || perfBrk !== "ALL") && (
                      <button
                        onClick={() => { setPerfDateFrom(""); setPerfDateTo(""); setPerfInv("ALL"); setPerfBrk("ALL"); }}
                        style={{ width: "100%", padding: "5px 12px", background: "transparent", border: "1px solid #1e293b", borderRadius: 5, color: "#94a3b8", cursor: "pointer", fontSize: 12 }}
                      >✕ Clear filters</button>
                    )}
                  </div>
                </div>

                {/* Index multiselect — chips */}
                <div style={{ marginBottom: 12, padding: "10px 12px", background: "#060a10", border: "1px solid #1e293b", borderRadius: 7 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                    <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>
                      Compare Against ({perfIndex.length} selected)
                    </div>
                    <button
                      onClick={() => setPerfIndex([])}
                      disabled={perfIndex.length === 0}
                      style={{ padding: "3px 9px", background: "transparent", border: "1px solid #1e293b", borderRadius: 4, color: perfIndex.length ? "#6b7280" : "#475569", cursor: perfIndex.length ? "pointer" : "default", fontSize: 11 }}
                    >Clear</button>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {INDEX_REGISTRY.map(I => {
                      const selected = perfIndex.includes(I.key);
                      // Coverage indicator: how many monthly periods have data for this index
                      const coverage = monthly.filter(m => m[I.key] != null).length;
                      return (
                        <button
                          key={I.key}
                          onClick={() => setPerfIndex(prev => selected ? prev.filter(x => x !== I.key) : [...prev, I.key])}
                          title={`${I.long} · ${coverage} months of data available`}
                          style={{
                            padding: "5px 10px", borderRadius: 5, cursor: "pointer", fontSize: 12, fontWeight: 500,
                            background: selected ? `${I.color}22` : "transparent",
                            border: `1px solid ${selected ? I.color : "#1e293b"}`,
                            color: selected ? I.color : "#6b7280",
                          }}
                        >
                          {I.label}
                          {coverage > 0 && <span style={{ marginLeft: 5, fontSize: 10, opacity: .7 }}>({coverage})</span>}
                        </button>
                      );
                    })}
                  </div>
                  {/* Auto-fetch buttons for the selected indexes */}
                  {perfIndex.length > 0 && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #0f1825", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: "#64748b" }}>Missing data?</span>
                      {perfIndex.map(key => {
                        const I = INDEX_REGISTRY.find(x => x.key === key);
                        if (!I) return null;
                        return (
                          <button
                            key={key}
                            onClick={() => fetchIndexHistory(I.key, I.long)}
                            disabled={loading.idx}
                            style={{ padding: "4px 10px", background: `${I.color}11`, border: `1px solid ${I.color}33`, borderRadius: 4, color: I.color, cursor: loading.idx ? "wait" : "pointer", fontSize: 11, fontWeight: 600 }}
                          >
                            {loading.idx ? "⏳" : "⟳"} Fetch {I.label} history
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {equityCurve.points.length === 0
                  ? <Empty>No trades yet — start adding trades to see your performance curve.</Empty>
                  : (
                    <>
                      {/* Warning banner when equity goes negative — signals missing starting cash */}
                      {(() => {
                        const negCount = equityCurve.points.filter(p => p.equity < 0).length;
                        if (negCount === 0) return null;
                        return (
                          <div style={{ marginBottom: 12, padding: "10px 14px", background: "rgba(251,191,36,.06)", border: "1px solid rgba(251,191,36,.3)", borderRadius: 7, fontSize: 13, color: "#fbbf24", lineHeight: 1.6 }}>
                            <b>⚠ {negCount} period{negCount !== 1 ? "s" : ""} show negative equity</b> — this happens when your <b>starting cash balance</b> is missing or too low for your trade history.
                            <br />
                            <span style={{ color: "#cbd5e1", fontSize: 12 }}>
                              Fix: go to <b>Setup · Cash</b> and use "Reconcile to broker balance" — pick each broker, enter your CURRENT broker balance, and select date 2026-05-03. The app will auto-create an adjustment that makes the math correct.
                            </span>
                          </div>
                        );
                      })()}
                      {/* Summary stats strip */}
                      {(() => {
                        const last = equityCurve.points[equityCurve.points.length - 1];
                        const first = equityCurve.points[0];
                        const totalGrowth = last.equity - first.equity;
                        const totalFlows = equityCurve.points.reduce((s, p) => s + (p.flowsThisPeriod || 0), 0);
                        // Investment performance excluding deposits/withdrawals
                        const trueGain = totalGrowth - totalFlows;
                        const fmt = perfMkt === "US" ? usd : idr;
                        // Build dynamic per-index KPI boxes for each selected index
                        const indexBoxes = perfIndex.map(key => {
                          const I = INDEX_REGISTRY.find(x => x.key === key);
                          if (!I) return null;
                          const cumKey = `cum_${key}`;
                          const cumIdx = last[cumKey];
                          const alpha = cumIdx != null && last.cumTwr != null ? last.cumTwr - cumIdx : null;
                          // When alpha is unavailable (e.g. TWR can't compute due to data issues),
                          // still show the index's own cumulative return so the box isn't empty.
                          if (alpha != null) {
                            return (
                              <KPIBox
                                key={key}
                                label={`vs ${I.label}`}
                                value={pct(alpha)}
                                color={gc(alpha)}
                                hint={`${I.label}: ${pct(cumIdx)}`}
                              />
                            );
                          }
                          if (cumIdx != null) {
                            return (
                              <KPIBox
                                key={key}
                                label={`${I.label} return`}
                                value={pct(cumIdx)}
                                color={gc(cumIdx)}
                                hint="Alpha unavailable — TWR couldn't be computed (check starting cash)"
                              />
                            );
                          }
                          return (
                            <KPIBox
                              key={key}
                              label={`vs ${I.label}`}
                              value="—"
                              color="#64748b"
                              hint={`Click "Fetch ${I.label} history" to populate`}
                            />
                          );
                        });
                        return (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
                            <KPIBox label="Current Equity" value={fmt(last.equity)} color="#34d399" />
                            <KPIBox label="Cumulative TWR" value={pct(last.cumTwr)} color={gc(last.cumTwr)} hint="Time-weighted return — strips out cash flow effects" />
                            {indexBoxes}
                            <KPIBox label="Net Flows" value={fmt(totalFlows)} color={totalFlows >= 0 ? "#60a5fa" : "#fbbf24"} hint="Total deposits − withdrawals" />
                            <KPIBox label="Investment Gain" value={fmt(trueGain)} color={gc(trueGain)} hint="Equity growth excluding deposits" />
                          </div>
                        );
                      })()}

                      {/* Per-period table — columns adapt to selected indexes */}
                      <div style={{ overflowX: "auto", borderRadius: 7, border: "1px solid #1e293b" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", minWidth: 720 + perfIndex.length * 90 }}>
                          <thead>
                            <tr style={{ background: "#060a10", borderBottom: "1px solid #1e293b" }}>
                              {[["Period", "period"], ["End Equity", "equity"], ["Period TWR", "twr"], ["Cum. TWR", "cumTwr"]].map(([h, col]) => {
                                const active = perfTableSort.key === col;
                                return (
                                  <th key={h} onClick={() => setPerfTableSort(s => ({ key: col, dir: s.key === col && s.dir === "desc" ? "asc" : "desc" }))}
                                      style={{ padding: "8px 10px", textAlign: h === "Period" ? "left" : "right", color: active ? "#cbd5e1" : "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", cursor: "pointer", userSelect: "none" }}>
                                    {h}{active ? (perfTableSort.dir === "desc" ? " ↓" : " ↑") : ""}
                                  </th>
                                );
                              })}
                              {perfIndex.map(key => {
                                const I = INDEX_REGISTRY.find(x => x.key === key);
                                return (
                                  <th key={key} style={{ padding: "8px 10px", textAlign: "right", color: I?.color || "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>
                                    vs {I?.label || key}
                                  </th>
                                );
                              })}
                              <th onClick={() => setPerfTableSort(s => ({ key: "flowsThisPeriod", dir: s.key === "flowsThisPeriod" && s.dir === "desc" ? "asc" : "desc" }))}
                                  style={{ padding: "8px 10px", textAlign: "right", color: perfTableSort.key === "flowsThisPeriod" ? "#cbd5e1" : "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", cursor: "pointer", userSelect: "none" }}>
                                Net Flows{perfTableSort.key === "flowsThisPeriod" ? (perfTableSort.dir === "desc" ? " ↓" : " ↑") : ""}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              // Sort points by the active column. Period sorts as a string
                              // (YYYY-MM / YYYY-Qn / YYYY all sort lexicographically). Numeric
                              // columns sort by value; null/undefined sink to the bottom.
                              const dir = perfTableSort.dir === "asc" ? 1 : -1;
                              const k = perfTableSort.key;
                              return equityCurve.points.slice().sort((a, b) => {
                                let av = a[k], bv = b[k];
                                if (k === "period") return av < bv ? -dir : av > bv ? dir : 0;
                                if (av == null && bv == null) return 0;
                                if (av == null) return 1;   // nulls always at bottom regardless of dir
                                if (bv == null) return -1;
                                return (av - bv) * dir;
                              });
                            })().map(p => {
                              const fmt = perfMkt === "US" ? usd : idr;
                              return (
                                <tr key={p.period} style={{ borderBottom: "1px solid #0f1825" }}>
                                  <td style={{ padding: "7px 10px", color: "#cbd5e1", fontWeight: 600 }}>
                                    {p.period}
                                    {p.isLast && !p.pricedAll && <span style={{ color: "#fbbf24", fontSize: 10, marginLeft: 6 }} title="Some positions lack live prices — using cost basis as fallback">est</span>}
                                  </td>
                                  <td style={{ padding: "7px 10px", textAlign: "right", color: "#e2e8f0" }}>{fmt(p.equity)}</td>
                                  <td style={{ padding: "7px 10px", textAlign: "right", color: gc(p.twr), fontWeight: 600 }}>{pct(p.twr)}</td>
                                  <td style={{ padding: "7px 10px", textAlign: "right", color: gc(p.cumTwr) }}>{pct(p.cumTwr)}</td>
                                  {perfIndex.map(key => {
                                    const ret = p[`${key}Return`];
                                    const alpha = ret != null && p.twr != null ? p.twr - ret : null;
                                    // Show alpha (TWR vs index) when we have both.
                                    // When TWR isn't computable (prev equity negative/missing), still show
                                    // the index's own period return so the column isn't empty.
                                    return (
                                      <td key={key} style={{ padding: "7px 10px", textAlign: "right", color: alpha != null ? gc(alpha) : (ret != null ? "#94a3b8" : "#475569") }}>
                                        {alpha != null
                                          ? <span title={`Alpha: TWR (${pct(p.twr)}) − Index (${pct(ret)})`}>{pct(alpha)}</span>
                                          : ret != null
                                          ? <span title="Index period return — alpha unavailable because TWR couldn't be computed for this period" style={{ fontStyle: "italic" }}>{pct(ret)}</span>
                                          : <span style={{ color: "#475569" }}>—</span>}
                                      </td>
                                    );
                                  })}
                                  <td style={{ padding: "7px 10px", textAlign: "right", color: p.flowsThisPeriod > 0 ? "#60a5fa" : p.flowsThisPeriod < 0 ? "#fbbf24" : "#475569" }}>
                                    {p.flowsThisPeriod ? `${p.flowsThisPeriod > 0 ? "+" : ""}${fmt(p.flowsThisPeriod)}` : "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ marginTop: 10, padding: "10px 12px", background: "rgba(96,165,250,.04)", border: "1px solid rgba(96,165,250,.15)", borderRadius: 7, fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>
                        <b style={{color:"#cbd5e1"}}>How this is calculated:</b><br/>
                        • <b>End equity</b> = cash + cost basis of open positions (or live value for current period)<br/>
                        • <b>Period TWR</b> = (end_equity − start_equity − net_flows) / start_equity — strips out the effect of you depositing/withdrawing cash<br/>
                        • <b>vs Index</b> = TWR minus index return for the same period (alpha) — needs index data, click "Fetch history" buttons above to auto-populate
                      </div>
                    </>
                  )
                }
              </Card>

              {/* ── PER-BROKER MONTH-BY-MONTH BREAKDOWN (matches user's spreadsheet view) ── */}
              {/* Heavy compute + render with many brokers/months — opt-in via button to keep tab snappy */}
              {!showBrokerTable ? (
                <Card style={{ padding: "14px 18px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <SH style={{ margin: 0 }}>📋 Per-Broker Breakdown</SH>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                        Month-end balance, fund flows, and gain% per broker — like your spreadsheet.
                      </div>
                    </div>
                    <button
                      onClick={() => setShowBrokerTable(true)}
                      style={{ padding: "8px 16px", background: "rgba(167,139,250,.08)", border: "1px solid rgba(167,139,250,.3)", borderRadius: 6, color: "#a78bfa", cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                    >
                      Show Per-Broker Table →
                    </button>
                  </div>
                  <div style={{ marginTop: 10, fontSize: 11, color: "#64748b" }}>
                    Computing this view across all your brokers and months can be slow with large trade history. Use the filters above (especially Broker and date range) to narrow the scope before viewing.
                  </div>
                </Card>
              ) : brokerCurves.brokers.length > 0 && brokerCurves.rows.length > 0 ? (
                <Card style={{ padding: "14px 18px" }}>
                  <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <SH style={{ margin: 0 }}>📋 Per-Broker Breakdown</SH>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                        Month-end balance, fund flows, and gain% per (Investor · Broker). Same TWR formula applied per broker.
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <button
                        onClick={() => setShowBrokerTable(false)}
                        style={{ padding: "6px 12px", background: "transparent", border: "1px solid #1e293b", borderRadius: 5, color: "#94a3b8", cursor: "pointer", fontSize: 12 }}
                      >
                        ✕ Hide
                      </button>
                    </div>
                  </div>
                  {/* Historical prices fetch — populates past-month market values from Yahoo */}
                  <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                    {(() => {
                      const allTickers = [...new Set([...buys.map(b => b.stk), ...sells.map(s => s.stk)])].filter(t => getOwnership(t).multiplier > 0);
                      const cached = allTickers.filter(t => histPrices[t] && Object.keys(histPrices[t]).length > 0).length;
                      const total = allTickers.length;
                      return (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                          <button
                            onClick={fetchHistoricalPrices}
                            disabled={loading.hist}
                            style={{ padding: "6px 14px", background: "rgba(96,165,250,.08)", border: "1px solid rgba(96,165,250,.25)", borderRadius: 6, color: "#60a5fa", cursor: loading.hist ? "wait" : "pointer", fontWeight: 600, fontSize: 13 }}
                          >
                            {loading.hist ? "⏳ Fetching…" : "⟳ Fetch Historical Prices"}
                          </button>
                          <div style={{ fontSize: 11, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>
                            {cached === 0
                              ? "Cache: empty — past months use cost basis"
                              : cached === total
                              ? `Cache: ${cached}/${total} tickers ✓`
                              : `Cache: ${cached}/${total} tickers (partial)`}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ overflowX: "auto", borderRadius: 7, border: "1px solid #1e293b" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", minWidth: 480 + brokerCurves.brokers.length * 240 }}>
                      <thead>
                        <tr style={{ background: "#060a10", borderBottom: "1px solid #1e293b" }}>
                          <th rowSpan={2} style={{ padding: "6px 8px", textAlign: "left", color: "#cbd5e1", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", borderRight: "1px solid #1e293b", verticalAlign: "bottom" }}>
                            Period
                          </th>
                          {/* IHSG group — only show if any IHSG data is available */}
                          {brokerCurves.rows.some(r => equityCurve.allPoints?.find(p => p.period === r.period)?.ihsg != null) && (
                            <th colSpan={2} style={{ padding: "6px 8px", textAlign: "center", color: "#60a5fa", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", borderRight: "1px solid #1e293b" }}>
                              IHSG
                            </th>
                          )}
                          {brokerCurves.brokers.map(bk => {
                            const [inv, brk] = bk.split("::");
                            return (
                              <th key={bk} colSpan={3} style={{ padding: "6px 8px", textAlign: "center", color: "#a78bfa", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", borderRight: "1px solid #1e293b" }}>
                                {inv} · {brk}
                              </th>
                            );
                          })}
                        </tr>
                        <tr style={{ background: "#060a10", borderBottom: "1px solid #1e293b" }}>
                          {brokerCurves.rows.some(r => equityCurve.allPoints?.find(p => p.period === r.period)?.ihsg != null) && (
                            <>
                              <th style={{ padding: "5px 8px", textAlign: "right", color: "#64748b", fontSize: 10, fontWeight: 500 }}>Index</th>
                              <th style={{ padding: "5px 8px", textAlign: "right", color: "#64748b", fontSize: 10, fontWeight: 500, borderRight: "1px solid #1e293b" }}>Gain%</th>
                            </>
                          )}
                          {brokerCurves.brokers.map(bk => (
                            <Fragment key={bk}>
                              <th style={{ padding: "5px 8px", textAlign: "right", color: "#64748b", fontSize: 10, fontWeight: 500 }}>Balance</th>
                              <th style={{ padding: "5px 8px", textAlign: "right", color: "#64748b", fontSize: 10, fontWeight: 500 }}>Fund Δ</th>
                              <th style={{ padding: "5px 8px", textAlign: "right", color: "#64748b", fontSize: 10, fontWeight: 500, borderRight: "1px solid #1e293b" }}>Gain%</th>
                            </Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {brokerCurves.rows.slice().reverse().map(r => {
                          // IHSG values from the unified equityCurve compute
                          const idxPoint = equityCurve.allPoints?.find(p => p.period === r.period);
                          const ihsgVal = idxPoint?.ihsg;
                          const ihsgRet = idxPoint?.ihsgReturn;
                          const showIhsgCols = brokerCurves.rows.some(rr => equityCurve.allPoints?.find(p => p.period === rr.period)?.ihsg != null);
                          return (
                            <tr key={r.period} style={{ borderBottom: "1px solid #0f1825" }}>
                              <td style={{ padding: "6px 8px", color: "#cbd5e1", fontWeight: 600, borderRight: "1px solid #1e293b" }}>{r.period}</td>
                              {showIhsgCols && (
                                <>
                                  <td style={{ padding: "6px 8px", textAlign: "right", color: "#cbd5e1" }}>
                                    {ihsgVal != null ? ihsgVal.toFixed(2) : <span style={{ color: "#475569" }}>—</span>}
                                  </td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", color: gc(ihsgRet), borderRight: "1px solid #1e293b" }}>
                                    {ihsgRet != null ? pct(ihsgRet) : <span style={{ color: "#475569" }}>—</span>}
                                  </td>
                                </>
                              )}
                              {brokerCurves.brokers.map(bk => {
                                const cell = r.brokers[bk];
                                const isIDX = !US_BROKERS.includes(bk.split("::")[1]);
                                const fmt = isIDX ? idr : usd;
                                const balColor = cell.balance > 0 ? "#cbd5e1" : cell.balance < 0 ? "#f87171" : "#64748b";
                                return (
                                  <Fragment key={bk}>
                                    <td style={{ padding: "6px 8px", textAlign: "right", color: balColor }}>{fmt(cell.balance)}</td>
                                    <td style={{ padding: "6px 8px", textAlign: "right", color: cell.flows > 0 ? "#60a5fa" : cell.flows < 0 ? "#fbbf24" : "#475569" }}>
                                      {cell.flows ? `${cell.flows > 0 ? "+" : ""}${fmt(cell.flows)}` : <span style={{ color: "#475569" }}>—</span>}
                                    </td>
                                    <td style={{ padding: "6px 8px", textAlign: "right", color: gc(cell.gainPct), fontWeight: 600, borderRight: "1px solid #1e293b" }}>
                                      {cell.gainPct != null ? pct(cell.gainPct) : <span style={{ color: "#475569" }}>—</span>}
                                    </td>
                                  </Fragment>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: 10, padding: "10px 12px", background: "rgba(167,139,250,.04)", border: "1px solid rgba(167,139,250,.15)", borderRadius: 7, fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>
                    <b style={{color:"#cbd5e1"}}>Note:</b> Past months use HISTORICAL CLOSE prices when cached (click "Fetch Historical Prices" above to populate from Yahoo). Tickers without historical data fall back to cost basis. Current month always uses live prices when available.
                  </div>
                </Card>
              ) : (
                <Card style={{ padding: "14px 18px" }}>
                  <Empty>No broker data for current filters.</Empty>
                  <button
                    onClick={() => setShowBrokerTable(false)}
                    style={{ marginTop: 8, padding: "6px 12px", background: "transparent", border: "1px solid #1e293b", borderRadius: 5, color: "#94a3b8", cursor: "pointer", fontSize: 12 }}
                  >
                    ✕ Hide
                  </button>
                </Card>
              )}

              {/* ── MONTHLY PERFORMANCE LEDGER ─────────────────────────────────── */}
              {/* Past months come from the hardcoded seed (built from Excel through 2026-03).
                  Forward months: use "Record Month-End Balance" form below to add new entries. */}
              <Card style={{ padding: "14px 18px" }}>
                <SH>📊 Monthly Performance Ledger</SH>
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10, lineHeight: 1.6 }}>
                  Historical monthly broker balances (Semesta, Mirae Asset, Stockbit) are baked into the app from your Excel through <b>2026-03</b>, plus IHSG <b>2026-04 = 6,956.80</b>. For new months going forward, use the <b>Record Month-End Balance</b> form below.
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button
                    onClick={() => {
                      const ok = window.confirm(
                        "Reset ALL monthly performance data and restore from the built-in seed?\n\n" +
                        "This will:\n" +
                        "• DELETE every entry in your monthly performance ledger\n" +
                        "• Restore the Excel-based seed (102 entries: Mar 2022 → Apr 2026)\n\n" +
                        "Trades, cash, and adjustments are NOT touched — only the monthly performance ledger.\n\n" +
                        "Use this when Performance shows wrong/negative equity due to stale data."
                      );
                      if (!ok) return;
                      setMonthly(INIT_MONTHLY);
                      flash2(`✓ Reset complete: ${INIT_MONTHLY.length} entries restored from seed`);
                    }}
                    style={{ padding: "8px 16px", background: "rgba(251,113,133,.1)", border: "1px solid rgba(251,113,133,.3)", borderRadius: 6, color: "#fb7185", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                  >
                    ↺ Reset & Reseed from Excel
                  </button>
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    {monthly.length} entries currently · seed has {INIT_MONTHLY.length}
                  </span>
                </div>
              </Card>

              {/* Chart */}
              <Card style={{ padding: "14px 18px" }}>
                <SH>Monthly Return vs Benchmark (%) — Manual Tracking</SH>
                <PerfChart rows={perfRows} brokers={allBrokers} />
              </Card>

              {/* Table */}
              <Card style={{ padding: "14px 18px" }}>
                <SH>Monthly Balance Detail</SH>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                    {(() => {
                      // Sortable header. Period/IHSG/S&P + each broker balance are sortable.
                      // Sorting is display-only — perfRows stays ascending so period-over-period
                      // % growth stays correct; we only reorder the rendered rows.
                      const sortCols = [
                        { label: "Period", key: "period", align: "left" },
                        { label: "IHSG",   key: "ihsg",   align: "right" },
                        { label: "IHSG %", key: null,     align: "right" },
                        { label: "S&P500", key: "sp500",  align: "right" },
                        { label: "S&P %",  key: null,     align: "right" },
                        ...allBrokers.flatMap(b => [
                          { label: b, key: `acct:${b}`, align: "right" },
                          { label: `${b.split(" ")[0]} %`, key: null, align: "right" },
                          { label: "Fund Δ", key: null, align: "right" },
                        ]),
                      ];
                      return (
                        <thead>
                          <tr style={{ borderBottom: "1px solid #1e293b" }}>
                            {sortCols.map((c, i) => {
                              const active = c.key && monthlyDetailSort.key === c.key;
                              return (
                                <th key={c.label + i}
                                    onClick={c.key ? () => setMonthlyDetailSort(s => ({ key: c.key, dir: s.key === c.key && s.dir === "desc" ? "asc" : "desc" })) : undefined}
                                    style={{ padding: "8px 10px", textAlign: c.align, color: active ? "#cbd5e1" : "#94a3b8", fontWeight: 600, fontSize: 14, textTransform: "uppercase", letterSpacing: ".5px", whiteSpace: "nowrap", cursor: c.key ? "pointer" : "default", userSelect: "none" }}>
                                  {c.label}{active ? (monthlyDetailSort.dir === "desc" ? " ↓" : " ↑") : ""}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                      );
                    })()}
                    <tbody>
                      {(() => {
                        const dir = monthlyDetailSort.dir === "asc" ? 1 : -1;
                        const k = monthlyDetailSort.key;
                        const getVal = (r) => {
                          if (k === "period") return r.period;
                          if (k === "ihsg")   return r.ihsg;
                          if (k === "sp500")  return r.sp500;
                          if (k && k.startsWith("acct:")) return r.accounts?.[k.slice(5)]?.bal;
                          return r.period;
                        };
                        return perfRows
                          .filter(r => (!perfDateFrom || r.period >= perfDateFrom) && (!perfDateTo || r.period <= perfDateTo))
                          .slice().sort((a, b) => {
                          const av = getVal(a), bv = getVal(b);
                          if (k === "period") return av < bv ? -dir : av > bv ? dir : 0;
                          if (av == null && bv == null) return 0;
                          if (av == null) return 1;
                          if (bv == null) return -1;
                          return (av - bv) * dir;
                        });
                      })().map(r => (
                        <tr key={r.period} className="row">
                          <TD mono sm>{r.period}</TD>
                          <TD right mono>{r.ihsg?.toFixed(0)}</TD>
                          <TD right mono color={gc(r.ihsgG)}>{pct(r.ihsgG)}</TD>
                          <TD right mono>{r.sp500?.toFixed(0) ?? "—"}</TD>
                          <TD right mono color={gc(r.sp500G)}>{pct(r.sp500G)}</TD>
                          {allBrokers.flatMap(b => {
                            const acc = r.accounts?.[b];
                            const g   = r.acctG?.[b];
                            const fd  = acc?.fd;
                            return [
                              <TD key={b + "b"} right mono sm>{acc?.bal != null ? <span title={acc.source !== "auto" ? "From your saved month-end record" : `${acc.cashReliable === false ? "Market value of holdings only (no cash balance set for this broker)" : `Cash ${acc.cash != null ? (acc.cash/1e6).toFixed(1)+"jt" : "?"} + Holdings ${(acc.mv/1e6).toFixed(1)}jt`}${acc.pricedAll === false ? " · some positions priced at last-known/cost" : ""}`} style={acc.source === "auto" ? { color: acc.cashReliable === false ? "#64748b" : acc.pricedAll === false ? "#fbbf24" : "#94a3b8" } : undefined}>{`${(acc.bal / 1e6).toFixed(0)}jt`}{acc.source === "auto" && <span style={{ fontSize: 9, opacity: .6, marginLeft: 3 }}>{acc.cashReliable === false ? "mv" : acc.pricedAll === false ? "~" : "•"}</span>}</span> : "—"}</TD>,
                              <TD key={b + "g"} right mono color={gc(g)}>{pct(g)}</TD>,
                              <TD key={b + "f"} right mono xs color={fd > 0 ? "#4ade80" : fd < 0 ? "#f87171" : "#6b7280"}>{fd != null ? `${fd >= 0 ? "+" : ""}${(fd / 1e6).toFixed(1)}jt` : "—"}</TD>,
                            ];
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {perfRows.length === 0 && <Empty>No monthly data yet.</Empty>}
              </Card>

              {/* Add month form */}
              <Card style={{ padding: "14px 18px" }}>
                <SH>Record Month-End Balance</SH>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 12 }}>
                  <FF l="Period (YYYY-MM)" v={mf.period}  onC={v => sm("period", v)}  ph="2024-03" />
                  <FF l="IHSG Closing"     v={mf.ihsg}    onC={v => sm("ihsg", v)}     ph="7300.5"   type="number" />
                  <FF l="S&P 500 Closing"  v={mf.sp500}   onC={v => sm("sp500", v)}    ph="5200.0"   type="number" />
                  <FF l="Broker / Account" v={mf.brk}     onC={v => sm("brk", v)}      opts={ALL_BROKERS} />
                  <FF l="Balance (IDR/USD)" v={mf.bal}    onC={v => sm("bal", v)}      ph="500000000" type="number" />
                  <FF l="Fund Δ (+ deposit / − withdraw)" v={mf.fd} onC={v => sm("fd", v)} ph="0" type="number" />
                </div>
                <Btn onClick={saveMonthly} label="Save Monthly Record" />
              </Card>
            </div>
          )}

          {/* ══ PRE-IPO DASHBOARD ═══════════════════════════════════ */}
          {tab === "preipo_dash" && (() => {
            const fmtCur = (n, cur) => {
              if (n == null || isNaN(n)) return "—";
              if (cur === "IDR") return idrExact(n);
              return `$${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
            };
            const totals = {};
            preipo.forEach(d => {
              if (!totals[d.currency]) totals[d.currency] = {
                count: 0, active: 0, partial: 0, exited: 0, cancelled: 0, failed: 0,
                costBasis: 0, proceeds: 0, netPnl: 0, idrFunded: 0,
                activeCostBasis: 0, currentValue: 0, hasValuation: false, unrealizedGain: 0,
              };
              const t = totals[d.currency];
              t.count++;
              if (d.status === "active") t.active++;
              else if (d.status === "partial_exit") t.partial++;
              else if (d.status === "exited") t.exited++;
              else if (d.status === "cancelled") t.cancelled++;
              else if (d.status === "failed_allocation") t.failed++;
              t.costBasis  += dealCostBasis(d);
              t.proceeds   += dealProceeds(d);
              t.netPnl     += dealNetPnl(d);
              t.idrFunded  += +d.initialTransferIDR || 0;
              if (d.status === "active" || d.status === "partial_exit") {
                t.activeCostBasis += dealCostBasis(d);
                const unr = dealUnrealized(d);
                if (unr) {
                  t.hasValuation = true;
                  t.currentValue += unr.currentValue;
                  t.unrealizedGain += unr.gain;
                } else {
                  t.currentValue += dealCostBasis(d);
                }
              }
            });
            // Show any open deal that has at least an entry valuation set. Current
            // valuation / multiple / unrealized columns render "—" until a current
            // valuation is also entered.
            const valuedDeals = preipo.filter(d => d.postMoneyAtEntry != null && (d.status === "active" || d.status === "partial_exit"));
            const realizedDeals = preipo.filter(d => dealProceeds(d) > 0);
            const topActive = [...preipo].filter(d => d.status === "active" || d.status === "partial_exit")
              .sort((a, b) => dealCostBasis(b) - dealCostBasis(a));
            return (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginBottom: 16 }}>
                  {Object.entries(totals).map(([cur, t]) => (
                    <Card key={cur} style={{ padding: "16px 18px" }}>
                      <div style={{ fontSize: 11, color: "#a78bfa", textTransform: "uppercase", letterSpacing: ".7px", fontWeight: 600 }}>{cur} Portfolio</div>
                      <div style={{ fontSize: 26, fontWeight: 700, color: "#e2e8f0", fontFamily: "'IBM Plex Mono',monospace", marginTop: 4 }}>{fmtCur(t.activeCostBasis, cur)}</div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>active cost basis · {t.active + t.partial} open deal{t.active + t.partial !== 1 ? "s" : ""}</div>
                      {t.hasValuation && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #1e293b" }}>
                          <div style={{ fontSize: 12, color: "#94a3b8" }}>Current est. value</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: gc(t.unrealizedGain), fontFamily: "'IBM Plex Mono',monospace" }}>
                            {fmtCur(t.currentValue, cur)}
                            <span style={{ fontSize: 12, marginLeft: 6 }}>({t.unrealizedGain >= 0 ? "+" : ""}{fmtCur(t.unrealizedGain, cur)})</span>
                          </div>
                        </div>
                      )}
                      {t.proceeds > 0 && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #1e293b" }}>
                          <div style={{ fontSize: 12, color: "#94a3b8" }}>Realized (net)</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: gc(t.netPnl), fontFamily: "'IBM Plex Mono',monospace" }}>{fmtCur(t.netPnl, cur)}</div>
                        </div>
                      )}
                      {cur === "USD" && t.idrFunded > 0 && (
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, fontFamily: "'IBM Plex Mono',monospace" }}>{idrExact(t.idrFunded)} wired to fund USD deals</div>
                      )}
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 8, lineHeight: 1.5 }}>
                        {t.active} active · {t.partial} partial · {t.exited} exited · {t.cancelled + t.failed} closed
                      </div>
                    </Card>
                  ))}
                </div>

                <Card style={{ padding: "14px 16px", marginBottom: 14 }}>
                  <SH>📈 Valuation Comparison</SH>
                  {valuedDeals.length > 0 && (
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, lineHeight: 1.5 }}>
                      Estimated stake value = (your transfer ÷ entry post-money) × current valuation. This ignores dilution from later rounds, so figures are an optimistic upper bound — actual value after dilution is somewhat lower.
                    </div>
                  )}
                  {valuedDeals.length === 0 ? (
                    <div style={{ fontSize: 13, color: "#64748b", padding: "12px 0", lineHeight: 1.6 }}>
                      No deals have an entry valuation set yet. Open a deal → Edit → fill <b style={{color:"#94a3b8"}}>Post-Money Valuation (entry)</b> to list it here, and add <b style={{color:"#94a3b8"}}>Current Valuation</b> to see the multiple and unrealized gain.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", marginRight: -16, marginLeft: -16, paddingLeft: 16, paddingRight: 16 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 760 }}>
                        <thead>
                          <tr style={{ borderBottom: "1px solid #1e293b" }}>
                            {["Company", "Entry Valuation", "Current Valuation", "Multiple", "Your Cost", "Your Stake Value", "Unrealized"].map((h, i) => (
                              <th key={h} style={{ padding: "8px 10px", textAlign: i === 0 ? "left" : "right", color: "#94a3b8", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {valuedDeals.map(d => {
                            const unr = dealUnrealized(d);  // null if no current valuation yet
                            return (
                              <tr key={d.id} className="row" style={{ cursor: "pointer", borderBottom: "1px solid #0f1825" }} onClick={() => { setTab("preipo_deals"); setPreipoDetailId(d.id); }}>
                                <td style={{ padding: "9px 10px", fontWeight: 600, color: "#e2e8f0", fontFamily: "'IBM Plex Mono',monospace" }}>{d.company}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: "#9ca3af" }}>{fmtCur(d.postMoneyAtEntry, d.currency)}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: d.currentValuation != null ? "#cbd5e1" : "#475569" }}>{d.currentValuation != null ? fmtCur(d.currentValuation, d.currency) : "not set"}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, color: unr && unr.valuationMultiple >= 1 ? "#34d399" : unr ? "#f87171" : "#475569" }}>
                                  {unr && unr.valuationMultiple != null ? `${unr.valuationMultiple.toFixed(2)}×` : "—"}
                                </td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: "#9ca3af" }}>{fmtCur(dealCostBasis(d), d.currency)}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: unr ? "#cbd5e1" : "#475569" }}>{unr ? fmtCur(unr.currentValue, d.currency) : "—"}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700, color: unr ? gc(unr.gain) : "#475569" }}>{unr ? `${unr.gain >= 0 ? "+" : ""}${fmtCur(unr.gain, d.currency)}` : "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {realizedDeals.length > 0 && (
                  <Card style={{ padding: "14px 16px", marginBottom: 14 }}>
                    <SH>✓ Realized / Partial Exits</SH>
                    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", marginRight: -16, marginLeft: -16, paddingLeft: 16, paddingRight: 16 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 620 }}>
                        <thead>
                          <tr style={{ borderBottom: "1px solid #1e293b" }}>
                            {["Company", "Status", "Cost Basis", "Net Proceeds", "Net P&L", "Return"].map((h, i) => (
                              <th key={h} style={{ padding: "8px 10px", textAlign: i < 2 ? "left" : "right", color: "#94a3b8", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {realizedDeals.map(d => {
                            const cb = dealCostBasis(d);
                            const proceeds = dealProceeds(d);
                            const net = dealNetPnl(d);
                            const ret = cb > 0 ? (net / cb) * 100 : null;
                            return (
                              <tr key={d.id} className="row" style={{ cursor: "pointer", borderBottom: "1px solid #0f1825" }} onClick={() => { setTab("preipo_deals"); setPreipoDetailId(d.id); }}>
                                <td style={{ padding: "9px 10px", fontWeight: 600, color: "#e2e8f0", fontFamily: "'IBM Plex Mono',monospace" }}>{d.company}</td>
                                <td style={{ padding: "9px 10px" }}>
                                  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: `${preipoStatusColors[d.status]}1a`, color: preipoStatusColors[d.status], border: `1px solid ${preipoStatusColors[d.status]}40` }}>
                                    {preipoStatusLabels[d.status]}
                                  </span>
                                </td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: "#9ca3af" }}>{fmtCur(cb, d.currency)}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", color: "#cbd5e1" }}>{fmtCur(proceeds, d.currency)}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700, color: gc(net) }}>{net >= 0 ? "+" : ""}{fmtCur(net, d.currency)}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, color: gc(ret) }}>{ret != null ? `${ret >= 0 ? "+" : ""}${ret.toFixed(1)}%` : "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}

                <Card style={{ padding: "14px 16px" }}>
                  <SH>💼 Largest Active Positions</SH>
                  {topActive.length === 0 ? (
                    <Empty>No active deals.</Empty>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                      {topActive.slice(0, 12).map(d => {
                        const unr = dealUnrealized(d);
                        return (
                          <div key={d.id} onClick={() => { setTab("preipo_deals"); setPreipoDetailId(d.id); }}
                               style={{ padding: "12px 14px", background: "#060a10", borderRadius: 8, border: "1px solid #1e293b", cursor: "pointer" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                              <span style={{ fontWeight: 600, color: "#e2e8f0", fontFamily: "'IBM Plex Mono',monospace" }}>{d.company}</span>
                              <span style={{ fontSize: 10, color: preipoStatusColors[d.status] }}>{preipoStatusLabels[d.status]}</span>
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: "#cbd5e1", fontFamily: "'IBM Plex Mono',monospace" }}>{fmtCur(dealCostBasis(d), d.currency)}</div>
                            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{d.holder} · {d.currency}</div>
                            {unr && (
                              <div style={{ fontSize: 12, marginTop: 4, color: gc(unr.gain), fontFamily: "'IBM Plex Mono',monospace" }}>
                                now {fmtCur(unr.currentValue, d.currency)} ({unr.gain >= 0 ? "+" : ""}{fmtCur(unr.gain, d.currency)})
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </div>
            );
          })()}

          {/* ══ PRE-IPO / PRIVATE INVESTMENTS ═══════════════════════ */}
          {tab === "preipo_deals" && (() => {
            // Filter by dropdowns + search query
            const q = preipoSearch.trim().toLowerCase();
            const filteredDealsRaw = preipo.filter(d => {
              if (preipoFilter.status !== "all" && d.status !== preipoFilter.status) return false;
              if (preipoFilter.currency !== "all" && d.currency !== preipoFilter.currency) return false;
              if (preipoFilter.holder !== "all" && d.holder !== preipoFilter.holder) return false;
              if (q && !(`${d.company} ${d.holder} ${d.note || ""}`.toLowerCase().includes(q))) return false;
              return true;
            });
            // Sort using shared sortRows infra. Getters map sort keys to comparable values.
            const preipoGetters = {
              company:    d => d.company,
              status:     d => d.status,
              initialDate:d => d.initialDate || "",
              principal:  d => +d.principal || 0,
              transferred:d => +d.initialTransfer || 0,
              costBasis:  d => dealCostBasis(d),
              proceeds:   d => dealProceeds(d),
              netPnl:     d => dealNetPnl(d),
              carryPct:   d => +d.carryPct || 0,
              holder:     d => d.holder,
            };
            const filteredDeals = sortRows(filteredDealsRaw, preipoSort.key, preipoSort.dir, preipoGetters);
            const preipoSortToggle = makeSortToggle(preipoSort, setPreipoSort);
            const allHolders = [...new Set(preipo.map(d => d.holder).filter(Boolean))].sort();
            // Currency display helpers
            const fmtCur = (n, cur) => {
              if (n == null || isNaN(n)) return "—";
              if (cur === "IDR") return idrExact(n);
              return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            };
            const isFormOpen = dealFormOpen || dealForm.id !== null;
            return (
              <div>
                {/* ── Add Deal toggle button ───────────────────────── */}
                {!isFormOpen && (
                  <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                    <button onClick={() => { setDealForm(emptyDealForm()); setDealFormOpen(true); }}
                            style={{ padding: "10px 18px", background: "linear-gradient(135deg,#7c3aed,#5b21b6)", border: "1px solid rgba(167,139,250,.3)", borderRadius: 8, color: "#ddd6fe", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                      ＋ Add New Deal
                    </button>
                    <button onClick={resetPreipoToSeed}
                            style={{ padding: "10px 16px", background: "rgba(251,191,36,.08)", border: "1px solid rgba(251,191,36,.3)", borderRadius: 8, color: "#fbbf24", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                            title="Replace all Pre-IPO deals with the built-in corrected seed (latest valuations)">
                      ↺ Reset to corrected seed
                    </button>
                  </div>
                )}

                {/* ── Add / Edit Deal form (only when open) ────────── */}
                {isFormOpen && (
                <Card style={{ padding: "14px 18px", marginBottom: 14, borderColor: "rgba(167,139,250,.35)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <SH style={{ margin: 0 }}>{dealForm.id ? "✎ Edit Deal" : "＋ Add Deal"}</SH>
                    <button onClick={() => { setDealForm(emptyDealForm()); setDealFormOpen(false); }} style={{ padding: "5px 12px", background: "rgba(107,114,128,.08)", border: "1px solid #0f1825", borderRadius: 5, color: "#9ca3af", cursor: "pointer", fontSize: 12 }}>
                      × Cancel
                    </button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                    <FF l="Company" v={dealForm.company} onC={v => setDealForm(p => ({ ...p, company: v }))} ph="Anthropic" />
                    <FF l="Currency" v={dealForm.currency} onC={v => setDealForm(p => ({ ...p, currency: v }))} opts={["USD", "IDR"]} />
                    <FF l="Holder / Custodian" v={dealForm.holder} onC={v => setDealForm(p => ({ ...p, holder: v }))} ph="Raymond" />
                    <FF l="Status" v={dealForm.status} onC={v => setDealForm(p => ({ ...p, status: v }))} opts={preipoStatusOptions.map(s => [s, preipoStatusLabels[s]])} />
                    <FF l={`Principal (${dealForm.currency})`} v={dealForm.principal} onC={v => setDealForm(p => ({ ...p, principal: v }))} type="number" ph="2000" />
                    <FF l="SPV / Comm Fee %" v={dealForm.spvFeePct} onC={v => setDealForm(p => ({ ...p, spvFeePct: v }))} type="number" ph="5" />
                    <FF l="Carry on Profit %" v={dealForm.carryPct} onC={v => setDealForm(p => ({ ...p, carryPct: v }))} type="number" ph="20" />
                    <FF l="Mgmt Fee % / yr" v={dealForm.mgmtFeePct} onC={v => setDealForm(p => ({ ...p, mgmtFeePct: v }))} type="number" ph="0.4" />
                    <FF l="Mgmt Years" v={dealForm.mgmtFeeYears} onC={v => setDealForm(p => ({ ...p, mgmtFeeYears: v }))} type="number" ph="10" />
                    <FF l="Mgmt Years Paid" v={dealForm.mgmtFeePaidYears} onC={v => setDealForm(p => ({ ...p, mgmtFeePaidYears: v }))} type="number" ph="0" />
                    <FF l="Transfer Date" v={dealForm.initialDate} onC={v => setDealForm(p => ({ ...p, initialDate: v }))} type="date" />
                    <FF l={`Initial Transfer (${dealForm.currency})`} v={dealForm.initialTransfer} onC={v => setDealForm(p => ({ ...p, initialTransfer: v }))} type="number" ph="2100" />
                    {/* IDR fields only relevant when the deal is in USD but funded with IDR */}
                    {dealForm.currency === "USD" && (
                      <>
                        <FF l="IDR Wired (if funded via IDR)" v={dealForm.initialTransferIDR} onC={v => setDealForm(p => ({ ...p, initialTransferIDR: v }))} type="number" ph="leave blank" />
                        <FF l="FX Rate (IDR/USD at transfer)" v={dealForm.initialFxRate} onC={v => setDealForm(p => ({ ...p, initialFxRate: v }))} type="number" ph="15500" />
                      </>
                    )}
                    <FF l="Post-Money Valuation (entry)" v={dealForm.postMoneyAtEntry} onC={v => setDealForm(p => ({ ...p, postMoneyAtEntry: v }))} type="number" ph="60000000" />
                    <FF l="Current Valuation (manual)" v={dealForm.currentValuation} onC={v => setDealForm(p => ({ ...p, currentValuation: v }))} type="number" ph="leave blank" />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <FF l="Note (optional)" v={dealForm.note} onC={v => setDealForm(p => ({ ...p, note: v }))} ph="e.g. inclusive 0.3% SPV, 10yr mgmt fee" />
                  </div>
                  <button onClick={saveDealForm} style={{ marginTop: 14, width: "100%", padding: "11px", background: "linear-gradient(135deg,#7c3aed,#5b21b6)", border: "1px solid rgba(167,139,250,.3)", borderRadius: 8, color: "#ddd6fe", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>
                    {dealForm.id ? `Save changes to ${dealForm.company || "deal"}` : `Add Deal${dealForm.company ? `: ${dealForm.company}` : ""}`}
                  </button>
                </Card>
                )}

                {/* ── Filter bar ────────────────────────────────────── */}
                <Card style={{ padding: "10px 14px", marginBottom: 12 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 600, alignSelf: "center", marginRight: 4 }}>Filter →</div>
                    <div style={{ minWidth: 140 }}>
                      <FF l="Status" v={preipoFilter.status} onC={v => setPreipoFilter(p => ({ ...p, status: v }))} opts={[["all", "All"], ...preipoStatusOptions.map(s => [s, preipoStatusLabels[s]])]} />
                    </div>
                    <div style={{ minWidth: 100 }}>
                      <FF l="Currency" v={preipoFilter.currency} onC={v => setPreipoFilter(p => ({ ...p, currency: v }))} opts={["all", "USD", "IDR"]} />
                    </div>
                    <div style={{ minWidth: 140 }}>
                      <FF l="Holder" v={preipoFilter.holder} onC={v => setPreipoFilter(p => ({ ...p, holder: v }))} opts={["all", ...allHolders]} />
                    </div>
                    <div style={{ minWidth: 180, flex: 1 }}>
                      <label style={{ display: "block", fontSize: 13, color: "#94a3b8", marginBottom: 5, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".6px" }}>Search</label>
                      <input
                        type="text" value={preipoSearch}
                        onChange={e => setPreipoSearch(e.target.value)}
                        placeholder="Company, holder, or note…"
                        style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 7, padding: "9px 11px", color: "#e2e8f0", fontSize: 15 }}
                      />
                    </div>
                    <div style={{ fontSize: 13, color: "#94a3b8", alignSelf: "center" }}>
                      Showing {filteredDeals.length} / {preipo.length} deals
                    </div>
                  </div>
                </Card>

                {/* ── Deal list (table) ─────────────────────────────── */}
                <Card style={{ padding: "14px 16px" }}>
                  <SH>📋 Deals ({filteredDeals.length})</SH>
                  {filteredDeals.length === 0 ? (
                    <Empty>No deals match the filter.</Empty>
                  ) : (
                    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", marginRight: -16, marginLeft: -16, paddingLeft: 16, paddingRight: 16 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
                        <thead>
                          <tr style={{ borderBottom: "1px solid #1e293b" }}>
                            <SortTh label="Company"    col="company"     align="left"  sortKey={preipoSort.key} sortDir={preipoSort.dir} onSort={preipoSortToggle} />
                            <SortTh label="Status"     col="status"      align="left"  sortKey={preipoSort.key} sortDir={preipoSort.dir} onSort={preipoSortToggle} />
                            <SortTh label="Date"       col="initialDate" align="left"  sortKey={preipoSort.key} sortDir={preipoSort.dir} onSort={preipoSortToggle} />
                            <SortTh label="Principal"  col="principal"   align="right" sortKey={preipoSort.key} sortDir={preipoSort.dir} onSort={preipoSortToggle} />
                            <SortTh label="Transferred" col="transferred" align="right" sortKey={preipoSort.key} sortDir={preipoSort.dir} onSort={preipoSortToggle} />
                            <SortTh label="Cost Basis" col="costBasis"   align="right" sortKey={preipoSort.key} sortDir={preipoSort.dir} onSort={preipoSortToggle} />
                            <SortTh label="Proceeds"   col="proceeds"    align="right" sortKey={preipoSort.key} sortDir={preipoSort.dir} onSort={preipoSortToggle} />
                            <SortTh label="Net P&L"    col="netPnl"      align="right" sortKey={preipoSort.key} sortDir={preipoSort.dir} onSort={preipoSortToggle} />
                            <SortTh label="Carry / SPV" col="carryPct"   align="right" sortKey={preipoSort.key} sortDir={preipoSort.dir} onSort={preipoSortToggle} />
                            <SortTh label="Holder"     col="holder"      align="left"  sortKey={preipoSort.key} sortDir={preipoSort.dir} onSort={preipoSortToggle} />
                          </tr>
                        </thead>
                        <tbody>
                          {filteredDeals.map(d => {
                            const cb = dealCostBasis(d);
                            const proceeds = dealProceeds(d);
                            const netPnl = dealNetPnl(d);
                            const carry = dealCarryInfo(d);
                            const evCount = (d.events || []).length;
                            return (
                              <tr key={d.id} className="row" style={{ cursor: "pointer", borderBottom: "1px solid #0f1825" }}
                                  onClick={() => setPreipoDetailId(d.id)}>
                                <td style={{ padding: "9px 10px" }}>
                                  <div style={{ fontWeight: 600, color: "#e2e8f0", fontFamily: "'IBM Plex Mono',monospace" }}>{d.company}</div>
                                  {evCount > 0 && <div style={{ fontSize: 10, color: "#64748b" }}>{evCount} event{evCount !== 1 ? "s" : ""}</div>}
                                </td>
                                <td style={{ padding: "9px 10px" }}>
                                  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: `${preipoStatusColors[d.status]}1a`, color: preipoStatusColors[d.status], border: `1px solid ${preipoStatusColors[d.status]}40` }}>
                                    {preipoStatusLabels[d.status] || d.status}
                                  </span>
                                </td>
                                <td style={{ padding: "9px 10px", fontFamily: "'IBM Plex Mono',monospace", fontSize: 12, color: "#9ca3af" }}>{d.initialDate || "—"}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace" }}>
                                  {fmtCur(d.principal, d.currency)}
                                </td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace" }}>
                                  {fmtCur(d.initialTransfer, d.currency)}
                                  {d.initialTransferIDR && (
                                    <div style={{ fontSize: 10, color: "#64748b" }}>{idrExact(d.initialTransferIDR)}</div>
                                  )}
                                </td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace" }}>{fmtCur(cb, d.currency)}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace" }}>{proceeds > 0 ? fmtCur(proceeds, d.currency) : "—"}</td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, color: proceeds > 0 ? gc(netPnl) : "#475569" }}>
                                  {proceeds > 0 ? fmtCur(netPnl, d.currency) : "—"}
                                </td>
                                <td style={{ padding: "9px 10px", textAlign: "right", fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: "#94a3b8" }}>
                                  {d.carryPct ? `${d.carryPct}%` : "—"} / {d.spvFeePct ? `${d.spvFeePct}%` : "—"}
                                  {carry > 0 && <div style={{ color: "#fbbf24" }}>owed {fmtCur(carry, d.currency)}</div>}
                                </td>
                                <td style={{ padding: "9px 10px", fontSize: 12, color: "#94a3b8" }}>{d.holder}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* ── Deal Detail Modal ──────────────────────────────── */}
                {preipoDetailId && (() => {
                  const d = preipo.find(x => x.id === preipoDetailId);
                  if (!d) return null;
                  const cb = dealCostBasis(d);
                  const proceeds = dealProceeds(d);
                  const netPnl = dealNetPnl(d);
                  const carry = dealCarryInfo(d);
                  const mgmt = dealMgmtFeeAccrued(d);
                  const unr = dealUnrealized(d);
                  return (
                    <div onClick={() => setPreipoDetailId(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                      <div onClick={e => e.stopPropagation()} style={{ background: "#0a0f18", border: "1px solid #1e293b", borderRadius: 10, maxWidth: 760, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 22 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: "#e2e8f0" }}>{d.company}</div>
                            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                              {d.currency} · held by {d.holder} ·{" "}
                              <span style={{ color: preipoStatusColors[d.status] }}>{preipoStatusLabels[d.status]}</span>
                            </div>
                          </div>
                          <button onClick={() => setPreipoDetailId(null)} style={{ background: "transparent", border: "none", color: "#9ca3af", fontSize: 24, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
                        </div>

                        {/* Economics summary */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 16 }}>
                          <div style={{ padding: "10px 12px", background: "#060a10", borderRadius: 6, border: "1px solid #1e293b" }}>
                            <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px" }}>Principal</div>
                            <div style={{ fontSize: 16, fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace", color: "#cbd5e1" }}>{fmtCur(d.principal, d.currency)}</div>
                          </div>
                          <div style={{ padding: "10px 12px", background: "#060a10", borderRadius: 6, border: "1px solid #1e293b" }}>
                            <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px" }}>Transferred</div>
                            <div style={{ fontSize: 16, fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace", color: "#cbd5e1" }}>{fmtCur(d.initialTransfer, d.currency)}</div>
                            {d.initialTransferIDR && (
                              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, fontFamily: "'IBM Plex Mono',monospace" }}>
                                {idrExact(d.initialTransferIDR)} @ {d.initialFxRate ? `Rp${d.initialFxRate.toLocaleString("id-ID")}/$` : "?"}
                              </div>
                            )}
                          </div>
                          <div style={{ padding: "10px 12px", background: "#060a10", borderRadius: 6, border: "1px solid #1e293b" }}>
                            <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px" }}>Cost Basis</div>
                            <div style={{ fontSize: 16, fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace", color: "#cbd5e1" }}>{fmtCur(cb, d.currency)}</div>
                            <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>after refunds</div>
                          </div>
                          {proceeds > 0 && (
                            <div style={{ padding: "10px 12px", background: "#060a10", borderRadius: 6, border: "1px solid #1e293b" }}>
                              <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px" }}>Proceeds</div>
                              <div style={{ fontSize: 16, fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace", color: "#cbd5e1" }}>{fmtCur(proceeds, d.currency)}</div>
                            </div>
                          )}
                          {proceeds > 0 && (
                            <div style={{ padding: "10px 12px", background: "#060a10", borderRadius: 6, border: "1px solid #1e293b" }}>
                              <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px" }}>Net Realized P&L</div>
                              <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'IBM Plex Mono',monospace", color: gc(netPnl) }}>{fmtCur(netPnl, d.currency)}</div>
                              <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>proceeds − cost basis</div>
                              {carry > 0 && <div style={{ fontSize: 10, color: "#64748b" }}>~{fmtCur(carry, d.currency)} carry skimmed by operator (est.)</div>}
                            </div>
                          )}
                          {unr && (
                            <div style={{ padding: "10px 12px", background: "rgba(167,139,250,.08)", borderRadius: 6, border: "1px solid rgba(167,139,250,.3)" }}>
                              <div style={{ fontSize: 10, color: "#a78bfa", textTransform: "uppercase", letterSpacing: ".5px" }}>Unrealized (current valuation)</div>
                              <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'IBM Plex Mono',monospace", color: gc(unr.netGain) }}>{fmtCur(unr.netGain, d.currency)}</div>
                              <div style={{ fontSize: 10, color: "#a78bfa", marginTop: 2 }}>your stake value: {fmtCur(unr.currentValue, d.currency)}</div>
                            </div>
                          )}
                        </div>

                        {/* Fee structure */}
                        <div style={{ padding: "10px 12px", background: "#060a10", borderRadius: 6, border: "1px solid #1e293b", marginBottom: 14, fontSize: 12, color: "#94a3b8", lineHeight: 1.7, fontFamily: "'IBM Plex Mono',monospace" }}>
                          <div><b style={{ color: "#cbd5e1" }}>Carry on profit:</b> {d.carryPct}% &nbsp;·&nbsp; <b style={{ color: "#cbd5e1" }}>SPV/Comm fee:</b> {d.spvFeePct}%{d.spvFeeFlat ? ` + flat ${fmtCur(d.spvFeeFlat, d.currency)}` : ""}</div>
                          {d.mgmtFeePct > 0 && (
                            <div><b style={{ color: "#cbd5e1" }}>Management fee:</b> {d.mgmtFeePct}%/year × {d.mgmtFeeYears} years ({d.mgmtFeePaidYears} paid)</div>
                          )}
                          {d.postMoneyAtEntry && <div><b style={{ color: "#cbd5e1" }}>Post-money at entry:</b> {fmtCur(d.postMoneyAtEntry, d.currency)}</div>}
                          {d.currentValuation && <div><b style={{ color: "#cbd5e1" }}>Current valuation:</b> {fmtCur(d.currentValuation, d.currency)}</div>}
                          {d.note && <div style={{ marginTop: 4, color: "#9ca3af", fontStyle: "italic" }}>"{d.note}"</div>}
                        </div>

                        {/* Event timeline */}
                        <SH style={{ marginTop: 16 }}>📅 Timeline</SH>
                        <div style={{ marginBottom: 12 }}>
                          {/* Initial transfer is always the first event */}
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", background: "rgba(96,165,250,.05)", borderRadius: 5, borderLeft: "3px solid #60a5fa", marginBottom: 6, fontSize: 13, fontFamily: "'IBM Plex Mono',monospace" }}>
                            <div>
                              <span style={{ color: "#60a5fa", fontWeight: 600 }}>● INITIAL</span>
                              <span style={{ color: "#94a3b8", marginLeft: 8 }}>{d.initialDate || "—"}</span>
                              <span style={{ color: "#cbd5e1", marginLeft: 10 }}>{d.note}</span>
                            </div>
                            <div style={{ color: "#cbd5e1", fontWeight: 600 }}>−{fmtCur(d.initialTransfer, d.currency)}</div>
                          </div>
                          {(d.events || []).map(e => {
                            const evColors = { refund: "#fbbf24", partial_exit: "#a78bfa", exit: "#34d399", additional_capital: "#60a5fa", fee_payment: "#f87171", note: "#6b7280" };
                            const evIcons = { refund: "↶", partial_exit: "↗", exit: "✓", additional_capital: "+", fee_payment: "−", note: "✎" };
                            const evLabels = { refund: "REFUND", partial_exit: "PARTIAL EXIT", exit: "FULL EXIT", additional_capital: "ADDITIONAL", fee_payment: "FEE PAID", note: "NOTE" };
                            const isInflow = e.type === "refund" || e.type === "partial_exit" || e.type === "exit";
                            return (
                              <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: `${evColors[e.type]}0a`, borderRadius: 5, borderLeft: `3px solid ${evColors[e.type]}`, marginBottom: 6, fontSize: 13, fontFamily: "'IBM Plex Mono',monospace" }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ color: evColors[e.type], fontWeight: 600 }}>{evIcons[e.type]} {evLabels[e.type]}</span>
                                  <span style={{ color: "#94a3b8", marginLeft: 8 }}>{e.date}</span>
                                  {e.note && <span style={{ color: "#cbd5e1", marginLeft: 10 }}>{e.note}</span>}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={{ color: isInflow ? "#34d399" : "#f87171", fontWeight: 600 }}>{isInflow ? "+" : "−"}{fmtCur(e.amount, d.currency)}</span>
                                  <button onClick={() => removeDealEvent(d.id, e.id)} style={{ background: "transparent", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 14, padding: "0 4px" }} title="Delete event">×</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Add event form */}
                        <div style={{ padding: "10px 12px", background: "#060a10", borderRadius: 6, border: "1px solid #1e293b", marginBottom: 14 }}>
                          <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8, fontWeight: 600 }}>＋ Add Event</div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                            <FF l="Type" v={eventForm.type} onC={v => setEventForm(p => ({ ...p, type: v }))}
                                opts={[
                                  ["partial_exit", "Partial Exit"],
                                  ["exit", "Full Exit"],
                                  ["refund", "Refund / Cancellation"],
                                  ["additional_capital", "Additional Capital"],
                                  ["fee_payment", "Fee Payment"],
                                  ["note", "Note (no $ impact)"],
                                ].map(([v]) => v)} />
                            <FF l="Date" v={eventForm.date} onC={v => setEventForm(p => ({ ...p, date: v }))} type="date" />
                            <FF l={`Amount (${d.currency})`} v={eventForm.amount} onC={v => setEventForm(p => ({ ...p, amount: v }))} type="number" ph="0" />
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <FF l="Note" v={eventForm.note} onC={v => setEventForm(p => ({ ...p, note: v }))} ph="e.g. tender offer, Q2 distribution" />
                          </div>
                          <button onClick={() => addDealEvent(d.id)} style={{ marginTop: 10, padding: "8px 16px", background: "rgba(52,211,153,.1)", border: "1px solid rgba(52,211,153,.3)", borderRadius: 6, color: "#34d399", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                            ＋ Add Event
                          </button>
                        </div>

                        {/* Edit / Delete deal */}
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #1e293b", paddingTop: 12, flexWrap: "wrap" }}>
                          {d.status !== "exited" && d.status !== "cancelled" && d.status !== "failed_allocation" && (
                            <button onClick={() => markDealExited(d.id)}
                                    style={{ padding: "7px 14px", background: "rgba(52,211,153,.12)", border: "1px solid rgba(52,211,153,.4)", borderRadius: 6, color: "#34d399", cursor: "pointer", fontSize: 13, fontWeight: 600, marginRight: "auto" }}>
                              ✓ Mark Fully Exited
                            </button>
                          )}
                          {d.status === "exited" && (
                            <button onClick={() => reopenDeal(d.id)}
                                    style={{ padding: "7px 14px", background: "rgba(251,191,36,.08)", border: "1px solid rgba(251,191,36,.3)", borderRadius: 6, color: "#fbbf24", cursor: "pointer", fontSize: 13, fontWeight: 600, marginRight: "auto" }}>
                              ↩ Re-open (undo exit)
                            </button>
                          )}
                          <button onClick={() => { openDealForm(d); setPreipoDetailId(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                  style={{ padding: "7px 14px", background: "rgba(96,165,250,.1)", border: "1px solid rgba(96,165,250,.3)", borderRadius: 6, color: "#60a5fa", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                            ✎ Edit Deal
                          </button>
                          <button onClick={() => removeDeal(d.id)}
                                  style={{ padding: "7px 14px", background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.3)", borderRadius: 6, color: "#f87171", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                            🗑 Delete Deal
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {/* ══ ADD TRADE ══════════════════════════════════════ */}
          {tab === "new" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 290px", gap: 16 }}>
              <Card style={{ padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#e2e8f0" }}>
                    {batchMode ? "Batch Trade Entry" : "Record Transaction"}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      onClick={() => { setBatchMode(false); setBatchPreview(null); }}
                      style={{ padding: "5px 14px", borderRadius: 5, border: `1px solid ${!batchMode ? "rgba(59,130,246,.3)" : "#0f1825"}`, cursor: "pointer", background: !batchMode ? "rgba(59,130,246,.08)" : "transparent", color: !batchMode ? "#60a5fa" : "#6b7280", fontSize: 13, fontWeight: !batchMode ? 600 : 400 }}
                    >
                      Single
                    </button>
                    <button
                      onClick={() => setBatchMode(true)}
                      title="Paste multiple trades at once"
                      style={{ padding: "5px 14px", borderRadius: 5, border: `1px solid ${batchMode ? "rgba(167,139,250,.35)" : "#0f1825"}`, cursor: "pointer", background: batchMode ? "rgba(167,139,250,.08)" : "transparent", color: batchMode ? "#a78bfa" : "#6b7280", fontSize: 13, fontWeight: batchMode ? 600 : 400 }}
                    >
                      ⚡ Batch
                    </button>
                  </div>
                </div>

                {/* BATCH MODE — interactive row-based entry */}
                {batchMode && (() => {
                  // Compute per-row preview values for ALL rows once per render
                  const rows = batchRows.map(r => ({ ...r, preview: computeBatchRow(r) }));
                  const valid = rows.filter(r => r.preview.isValid);
                  // Grand totals across all valid rows
                  const totals = valid.reduce((acc, r) => {
                    if (r.type === "BUY") {
                      acc.buyGross += r.preview.gross;
                      acc.buyFees  += r.preview.fee;
                      acc.buyNet   += r.preview.net;
                    } else {
                      acc.sellGross += r.preview.gross;
                      acc.sellFees  += r.preview.fee;
                      acc.sellNet   += r.preview.net;
                    }
                    return acc;
                  }, { buyGross:0, buyFees:0, buyNet:0, sellGross:0, sellFees:0, sellNet:0 });
                  const cashFlow = totals.sellNet - totals.buyNet;
                  const fmt = v => bf.mkt === "IDX" ? `Rp${Math.round(v).toLocaleString("id-ID")}` : `$${(+v).toFixed(2)}`;
                  const ccy = bf.mkt === "IDX" ? "Rp" : "$";
                  const qtyUnit = bf.mkt === "IDX" ? "lots" : "shares";

                  return (
                    <div>
                      {/* Helper text */}
                      <div style={{ padding: "10px 12px", background: "rgba(167,139,250,.04)", border: "1px solid rgba(167,139,250,.15)", borderRadius: 7, marginBottom: 12, fontSize: 13, color: "#94a3b8", lineHeight: 1.6 }}>
                        Add multiple trades, see live totals as you type, then commit them all at once. <b style={{color:"#cbd5e1"}}>Account, date, and fee % below</b> apply to ALL rows.
                      </div>

                      {/* Account selector — applies to all rows */}
                      <div style={{ padding: "12px 14px", background: "#060a10", borderRadius: 7, marginBottom: 12, border: "1px solid #1e293b" }}>
                        <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8, fontWeight: 600 }}>
                          Account ↓
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }} className="keep-grid">
                          <FF l="Market" v={bf.mkt} onC={v => setBf(p => {
                            const newBrokers = v === "IDX" ? IDX_BROKERS : US_BROKERS;
                            const newBrk = newBrokers.includes(p.brk) ? p.brk : newBrokers[0];
                            return { ...p, mkt: v, brk: newBrk };
                          })} opts={["IDX","US"]} />
                          <FF l="Investor" v={bf.inv} onC={v => sb("inv", v)} opts={INVESTORS} />
                          <FF l="Broker" v={bf.brk} onC={v => sb("brk", v)} opts={bf.mkt === "IDX" ? IDX_BROKERS : US_BROKERS} />
                        </div>
                      </div>

                      {/* Shared trade-day & fee % inputs — apply to ALL rows */}
                      <div style={{ padding: "12px 14px", background: "#060a10", borderRadius: 7, marginBottom: 12, border: "1px solid #1e293b" }}>
                        <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8, fontWeight: 600 }}>
                          Trade Day & Fee Rate ↓ <span style={{ color:"#475569", textTransform:"none", letterSpacing:0, fontWeight: 400 }}>· applies to every row below</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 10 }} className="keep-grid">
                          <div>
                            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Date</div>
                            <input
                              type="date"
                              value={batchShared.date}
                              onChange={e => sbs("date", e.target.value)}
                              style={{ width: "100%", padding: "7px 10px", background: "#06090f", color: "#e2e8f0", border: "1px solid #1e293b", borderRadius: 5, fontSize: 13, fontFamily: "'IBM Plex Mono',monospace" }}
                            />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                              Buy Fee <span style={{ color: "#475569" }}>(% of gross)</span>
                            </div>
                            <div style={{ position: "relative" }}>
                              <input
                                type="number" step="0.01" min="0"
                                value={batchShared.buyFeePct}
                                onChange={e => sbs("buyFeePct", e.target.value)}
                                placeholder="0.15"
                                style={{ width: "100%", padding: "7px 26px 7px 10px", background: "#06090f", color: "#34d399", border: "1px solid #1e293b", borderRadius: 5, fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", textAlign: "right", fontWeight: 600 }}
                              />
                              <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#64748b", fontSize: 12, pointerEvents: "none" }}>%</span>
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                              Sell Fee <span style={{ color: "#475569" }}>(% of gross)</span>
                            </div>
                            <div style={{ position: "relative" }}>
                              <input
                                type="number" step="0.01" min="0"
                                value={batchShared.sellFeePct}
                                onChange={e => sbs("sellFeePct", e.target.value)}
                                placeholder="0.25"
                                style={{ width: "100%", padding: "7px 26px 7px 10px", background: "#06090f", color: "#f87171", border: "1px solid #1e293b", borderRadius: 5, fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", textAlign: "right", fontWeight: 600 }}
                              />
                              <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#64748b", fontSize: 12, pointerEvents: "none" }}>%</span>
                            </div>
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 8, lineHeight: 1.5 }}>
                          Pre-filled from {bf.brk}'s default rates (Setup → Broker Fees). Fee is auto-computed per row from these rates × gross value. <span style={{ color: "#fbbf24" }}>· Rp 10,000 stamp duty is added per account/day when the day's total reaches Rp 10jt.</span>
                        </div>
                      </div>

                      {/* Rows table */}
                      <div style={{ overflowX: "auto", borderRadius: 7, border: "1px solid #1e293b" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", minWidth: 760 }}>
                          <thead>
                            <tr style={{ background: "#060a10", borderBottom: "1px solid #1e293b" }}>
                              <th style={{ padding: "8px 6px", textAlign: "center", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", width: 40 }}>#</th>
                              <th style={{ padding: "8px 6px", textAlign: "left",   color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", width: 70 }}>Type</th>
                              <th style={{ padding: "8px 6px", textAlign: "left",   color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", width: 100 }}>Stock</th>
                              <th style={{ padding: "8px 6px", textAlign: "right",  color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", width: 80 }}>Qty ({qtyUnit})</th>
                              <th style={{ padding: "8px 6px", textAlign: "right",  color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", width: 100 }}>Price</th>
                              <th style={{ padding: "8px 6px", textAlign: "right",  color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", width: 130 }}>Subtotal</th>
                              <th style={{ padding: "8px 6px", textAlign: "right",  color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", width: 100 }}>Fee</th>
                              <th style={{ padding: "8px 6px", textAlign: "right",  color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", width: 130 }}>Net</th>
                              <th style={{ padding: "8px 6px", textAlign: "center", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", width: 70 }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r, i) => {
                              const p = r.preview;
                              const inputStyle = {
                                width: "100%", padding: "5px 7px", background: "#06090f", color: "#e2e8f0",
                                border: "1px solid #1e293b", borderRadius: 4, fontSize: 13,
                                fontFamily: "'IBM Plex Mono',monospace",
                              };
                              const num = v => v == null || isNaN(v) ? "—" : fmt(v);
                              return (
                                <tr key={r.id} style={{ borderBottom: "1px solid #0f1825", background: p.isValid ? "transparent" : "rgba(248,113,113,.02)" }}>
                                  <td style={{ padding: "6px 4px", textAlign: "center", color: "#64748b", fontSize: 11 }}>{i + 1}</td>
                                  <td style={{ padding: "6px 4px" }}>
                                    <button
                                      onClick={() => setBatchRows(rs => rs.map(x => x.id === r.id ? { ...x, type: x.type === "BUY" ? "SELL" : "BUY", isIpo: false } : x))}
                                      title="Click to toggle BUY/SELL"
                                      style={{
                                        width: "100%", padding: "5px 6px", borderRadius: 4, cursor: "pointer", fontWeight: 600, fontSize: 12,
                                        background: r.type === "BUY" ? "rgba(52,211,153,.1)" : "rgba(248,113,113,.1)",
                                        border: `1px solid ${r.type === "BUY" ? "rgba(52,211,153,.3)" : "rgba(248,113,113,.3)"}`,
                                        color: r.type === "BUY" ? "#34d399" : "#f87171",
                                      }}
                                    >
                                      {r.type}
                                    </button>
                                    {r.type === "BUY" && bf.mkt === "IDX" && (
                                      <button
                                        onClick={() => updateBatchRow(r.id, "isIpo", !r.isIpo)}
                                        title="IPO allotment: no broker fee, no stamp duty"
                                        style={{
                                          width: "100%", marginTop: 3, padding: "2px 4px", borderRadius: 4, cursor: "pointer", fontWeight: 600, fontSize: 10,
                                          background: r.isIpo ? "rgba(167,139,250,.15)" : "transparent",
                                          border: `1px solid ${r.isIpo ? "rgba(167,139,250,.45)" : "#1e293b"}`,
                                          color: r.isIpo ? "#c4b5fd" : "#64748b",
                                        }}
                                      >
                                        {r.isIpo ? "✓ IPO" : "○ IPO"}
                                      </button>
                                    )}
                                  </td>
                                  <td style={{ padding: "6px 4px" }}>
                                    <input
                                      type="text" value={r.stk}
                                      onChange={e => updateBatchRow(r.id, "stk", e.target.value.toUpperCase())}
                                      placeholder={bf.mkt === "IDX" ? "BBYB" : "AAPL"}
                                      style={{ ...inputStyle, fontWeight: 600, textTransform: "uppercase" }}
                                    />
                                  </td>
                                  <td style={{ padding: "6px 4px" }}>
                                    <input type="number" value={r.qty} onChange={e => updateBatchRow(r.id, "qty", e.target.value)} placeholder="0" style={{ ...inputStyle, textAlign: "right" }} />
                                  </td>
                                  <td style={{ padding: "6px 4px" }}>
                                    <input type="number" value={r.px} onChange={e => updateBatchRow(r.id, "px", e.target.value)} placeholder="0" step="any" style={{ ...inputStyle, textAlign: "right" }} />
                                  </td>
                                  <td style={{ padding: "6px 4px", textAlign: "right", color: p.isValid ? "#cbd5e1" : "#475569", fontSize: 12 }}>
                                    {p.gross > 0 ? num(p.gross) : "—"}
                                    {p.isValid && (
                                      <div style={{ fontSize: 10, color: "#64748b" }}>
                                        {p.shares.toLocaleString("id-ID")} sh
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 12 }}>
                                    {p.gross > 0 ? (
                                      <span style={{ color: "#94a3b8" }}>
                                        {num(p.fee)}
                                        <div style={{ fontSize: 10, color: p.isIpo ? "#c4b5fd" : "#64748b" }}>{p.isIpo ? "IPO · no fee" : `@ ${p.feePct}%`}</div>
                                      </span>
                                    ) : (
                                      <span style={{ color: "#475569" }}>—</span>
                                    )}
                                  </td>
                                  <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 13, fontWeight: 600 }}>
                                    {p.isValid ? (
                                      <span style={{ color: r.type === "BUY" ? "#f87171" : "#34d399" }}>
                                        {r.type === "BUY" ? "−" : "+"}{num(p.net)}
                                      </span>
                                    ) : (
                                      <span style={{ color: "#475569" }}>—</span>
                                    )}
                                  </td>
                                  <td style={{ padding: "6px 4px", textAlign: "center" }}>
                                    <div style={{ display: "flex", gap: 3, justifyContent: "center" }}>
                                      <button onClick={() => duplicateBatchRow(r.id)} title="Duplicate row" style={{ padding: "3px 7px", background: "rgba(96,165,250,.06)", border: "1px solid rgba(96,165,250,.2)", borderRadius: 3, color: "#60a5fa", cursor: "pointer", fontSize: 12 }}>+</button>
                                      <button
                                        onClick={() => removeBatchRow(r.id)}
                                        disabled={rows.length === 1}
                                        title="Delete row"
                                        style={{ padding: "3px 7px", background: rows.length === 1 ? "rgba(107,114,128,.04)" : "rgba(248,113,113,.06)", border: `1px solid ${rows.length === 1 ? "#1e293b" : "rgba(248,113,113,.2)"}`, borderRadius: 3, color: rows.length === 1 ? "#475569" : "#f87171", cursor: rows.length === 1 ? "not-allowed" : "pointer", fontSize: 12 }}
                                      >🗑</button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Add row button */}
                      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                        <button
                          onClick={() => addBatchRow()}
                          style={{ padding: "7px 14px", background: "rgba(96,165,250,.08)", border: "1px solid rgba(96,165,250,.25)", borderRadius: 6, color: "#60a5fa", cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                        >
                          + Add Row
                        </button>
                        <button
                          onClick={clearBatchRows}
                          style={{ padding: "7px 12px", background: "transparent", border: "1px solid #1e293b", borderRadius: 6, color: "#6b7280", cursor: "pointer", fontSize: 13 }}
                        >
                          Clear All
                        </button>
                      </div>

                      {/* Grand totals strip */}
                      {valid.length > 0 && (
                        <div style={{ marginTop: 14, padding: "12px 14px", background: "#060a10", borderRadius: 8, border: "1px solid #1e293b" }}>
                          <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8, fontWeight: 600 }}>
                            Totals · {valid.length} valid row{valid.length !== 1 ? "s" : ""} {batchRows.length > valid.length && <span style={{ color: "#f87171", textTransform: "none", letterSpacing: 0 }}>· {batchRows.length - valid.length} incomplete</span>}
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, fontFamily: "'IBM Plex Mono',monospace" }}>
                            {totals.buyNet > 0 && (
                              <div style={{ padding: "8px 10px", background: "rgba(52,211,153,.04)", border: "1px solid rgba(52,211,153,.15)", borderRadius: 6 }}>
                                <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px" }}>Total BUY</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: "#34d399" }}>{fmt(totals.buyNet)}</div>
                                <div style={{ fontSize: 10, color: "#64748b" }}>gross {fmt(totals.buyGross)} + fees {fmt(totals.buyFees)}</div>
                              </div>
                            )}
                            {totals.sellNet > 0 && (
                              <div style={{ padding: "8px 10px", background: "rgba(248,113,113,.04)", border: "1px solid rgba(248,113,113,.15)", borderRadius: 6 }}>
                                <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px" }}>Total SELL</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: "#f87171" }}>{fmt(totals.sellNet)}</div>
                                <div style={{ fontSize: 10, color: "#64748b" }}>gross {fmt(totals.sellGross)} − fees {fmt(totals.sellFees)}</div>
                              </div>
                            )}
                            <div style={{ padding: "8px 10px", background: cashFlow >= 0 ? "rgba(52,211,153,.06)" : "rgba(248,113,113,.06)", border: `1px solid ${cashFlow >= 0 ? "rgba(52,211,153,.2)" : "rgba(248,113,113,.2)"}`, borderRadius: 6 }}>
                              <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px" }}>Net Cash Flow</div>
                              <div style={{ fontSize: 16, fontWeight: 700, color: gc(cashFlow) }}>{cashFlow >= 0 ? "+" : ""}{fmt(cashFlow)}</div>
                              <div style={{ fontSize: 10, color: "#64748b" }}>{cashFlow >= 0 ? "received" : "spent"}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Save button */}
                      <button
                        onClick={saveBatch}
                        disabled={valid.length === 0}
                        style={{
                          marginTop: 14, width: "100%", padding: "12px",
                          background: valid.length > 0 ? "linear-gradient(135deg,#1d4ed8,#1e40af)" : "rgba(107,114,128,.05)",
                          border: `1px solid ${valid.length > 0 ? "rgba(59,130,246,.3)" : "#1e293b"}`,
                          borderRadius: 8, color: valid.length > 0 ? "#93c5fd" : "#475569",
                          fontWeight: 600, fontSize: 16, cursor: valid.length > 0 ? "pointer" : "not-allowed",
                        }}
                      >
                        {valid.length > 0
                          ? `Save All ${valid.length} Trade${valid.length !== 1 ? "s" : ""} → ${bf.inv} · ${bf.brk}`
                          : "Add at least one valid row to save"}
                      </button>
                    </div>
                  );
                })()}

                {/* SINGLE MODE — original form */}
                {!batchMode && (<>
                {/* Type / market toggles */}
                <div style={{ display: "flex", gap: 4, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
                  {["BUY","SELL"].map(t => (
                    <button key={t} onClick={() => sb("type", t)} style={{ padding: "6px 18px", borderRadius: 6, border: `1px solid ${bf.type === t ? (t === "BUY" ? "rgba(52,211,153,.3)" : "rgba(248,113,113,.3)") : "#0f1825"}`, cursor: "pointer", background: bf.type === t ? (t === "BUY" ? "rgba(52,211,153,.08)" : "rgba(248,113,113,.08)") : "transparent", color: bf.type === t ? (t === "BUY" ? "#34d399" : "#f87171") : "#6b7280", fontWeight: bf.type === t ? 600 : 400, fontSize: 15 }}>
                      {t}
                    </button>
                  ))}
                  <div style={{ width: 1, background: "#0f1825", margin: "0 6px" }} />
                  {["IDX","US"].map(m => (
                    <button key={m} onClick={() => {
                      // When switching market, also reset the broker to a valid default for
                      // the new market. Without this, brk can be stuck on an IDX value (e.g.
                      // "Stockbit") while market says US, causing the trade to save with the
                      // wrong broker even though the dropdown visually appears reset.
                      setBf(p => {
                        if (p.mkt === m) return p;
                        const newBrokers = m === "IDX" ? IDX_BROKERS : US_BROKERS;
                        const newBrk = newBrokers.includes(p.brk) ? p.brk : newBrokers[0];
                        return { ...p, mkt: m, brk: newBrk };
                      });
                    }} style={{ padding: "6px 16px", borderRadius: 6, border: `1px solid ${bf.mkt === m ? "rgba(59,130,246,.3)" : "#0f1825"}`, cursor: "pointer", background: bf.mkt === m ? "rgba(59,130,246,.08)" : "transparent", color: bf.mkt === m ? "#60a5fa" : "#6b7280", fontWeight: bf.mkt === m ? 600 : 400, fontSize: 15 }}>
                      {m === "IDX" ? "🇮🇩 IDX" : "🇺🇸 US"}
                    </button>
                  ))}
                  {/* IPO toggle — only meaningful for IDX BUYs */}
                  {bf.type === "BUY" && bf.mkt === "IDX" && (
                    <>
                      <div style={{ width: 1, background: "#0f1825", margin: "0 6px" }} />
                      <button
                        onClick={() => sb("isIpo", !bf.isIpo)}
                        title="IPO / e-IPO subscription — no broker commission"
                        style={{
                          padding: "6px 14px",
                          borderRadius: 6,
                          border: `1px solid ${bf.isIpo ? "rgba(167,139,250,.35)" : "#0f1825"}`,
                          cursor: "pointer",
                          background: bf.isIpo ? "rgba(167,139,250,.1)" : "transparent",
                          color: bf.isIpo ? "#a78bfa" : "#6b7280",
                          fontWeight: bf.isIpo ? 600 : 400,
                          fontSize: 15,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span>{bf.isIpo ? "✓" : "○"}</span>IPO
                      </button>
                    </>
                  )}
                </div>
                {bf.isIpo && (
                  <div style={{
                    padding: "8px 12px",
                    background: "rgba(167,139,250,.06)",
                    border: "1px solid rgba(167,139,250,.18)",
                    borderRadius: 7,
                    fontSize: 14,
                    color: "#a78bfa",
                    marginBottom: 14,
                    lineHeight: 1.6,
                  }}>
                    💎 <b>IPO Subscription mode:</b> broker commission = 0 (no buy fee on Indonesian IPOs). Fee is blank by default — leave empty for no fee, or enter stamp duty (Rp10,000) if you paid it separately. Trade will be tagged "IPO" in notes.
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <FF l="Investor" v={bf.inv}  onC={v => sb("inv", v)}  opts={INVESTORS} />
                  <FF l="Broker"   v={bf.brk}  onC={v => sb("brk", v)}  opts={bf.mkt === "IDX" ? IDX_BROKERS : US_BROKERS} />
                  <FF l="Date"     v={bf.date} onC={v => sb("date", v)} type="date" />
                  <FF l="Ticker"   v={bf.stk}  onC={v => sb("stk", v.toUpperCase())} ph="BBCA / AAPL" />
                  <FF l={bf.mkt === "IDX" ? "Lot (1 lot = 100 shares)" : "Shares"} v={bf.qty} onC={v => sb("qty", v)} type="number" ph={bf.mkt === "IDX" ? "10" : "100"} />
                  <FF l={bf.mkt === "IDX" ? (bf.isIpo ? "IPO Price per share (Rp)" : "Price per share (Rp)") : "Price ($)"} v={bf.px} onC={v => sb("px", v)} type="number" ph={bf.mkt === "IDX" ? "8500" : "150.50"} />
                  <FF
                    l={bf.isIpo ? "Fee (leave blank = 0)" : "Fee (leave blank = auto)"}
                    v={bf.fee}
                    onC={v => sb("fee", v)}
                    type="number"
                    ph={bf.isIpo ? "auto: Rp 0 (IPO)" : bf.mkt === "IDX" ? `auto: ${preview.feeRate}%${preview.willGetStamp?" + Rp10k stamp":""}` : `auto: $${preview.feeRate}`}
                  />
                  <FF l="Note (optional)" v={bf.note} onC={v => sb("note", v)} ph={bf.isIpo ? "'IPO' will be auto-added" : ""} />
                </div>
                <button onClick={saveTrade} style={{ marginTop: 14, width: "100%", padding: "11px", background: "linear-gradient(135deg,#1d4ed8,#1e40af)", border: "1px solid rgba(59,130,246,.3)", borderRadius: 8, color: "#93c5fd", fontWeight: 600, fontSize: 16, cursor: "pointer" }}>
                  Save {bf.isIpo ? "IPO " : ""}{bf.type} — {bf.stk || "Trade"}
                </button>
                </>)}
              </Card>

              {/* Preview card */}
              <Card style={{ padding: 18 }}>
                <div style={{ fontSize: 14, color: "#64748b", textTransform: "uppercase", letterSpacing: ".8px", fontWeight: 600, marginBottom: 14 }}>Calculation Preview</div>
                <CRow l="Shares" v={`${preview.shares.toLocaleString("id-ID")} ${bf.mkt === "IDX" ? "lembar" : "shares"}`} />
                <CRow l="Trade Value" v={money(preview.val, bf.mkt)} />
                <CRow l={`Fee (${bf.isIpo ? "IPO" : bf.mkt === "IDX" ? `${preview.feeRate}%${preview.willGetStamp ? " + Rp10k" : ""}` : `$${preview.feeRate} flat`})`} v={money(preview.fee, bf.mkt)} sub />
                <div style={{ borderTop: "1px solid #1e293b", margin: "10px 0" }} />
                <CRow l={bf.type === "BUY" ? "Total Cash Out" : "Net Cash In"} v={money(Math.abs(preview.net), bf.mkt)} color={bf.type === "BUY" ? "#f87171" : "#34d399"} bold />
                {bf.mkt === "IDX" && (
                  <div style={{ marginTop: 14, padding: "10px 12px", background: "rgba(59,130,246,.04)", border: "1px solid #0f2040", borderRadius: 8, fontSize: 14, color: "#6b7280", lineHeight: 1.7 }}>
                    <b style={{ color: "#1d4ed8", display: "block", marginBottom: 4 }}>IDX Fee Rules</b>
                    {bf.brk}: Buy {brokerFeeRate(brokerFees, bf.brk, "IDX", "BUY")}% · Sell {brokerFeeRate(brokerFees, bf.brk, "IDX", "SELL")}% of value
                    {" "}<span onClick={() => setTab("setup")} style={{ color: "#60a5fa", cursor: "pointer" }}>(edit in Setup)</span><br />
                    Rp10,000 materai per account/day when day total ≥ Rp10jt<br />
                    1 lot = 100 lembar saham<br />
                    FIFO applied automatically on sell
                  </div>
                )}
                {bf.mkt === "US" && (
                  <div style={{ marginTop: 14, padding: "10px 12px", background: "rgba(251,191,36,.03)", border: "1px solid #1a1200", borderRadius: 8, fontSize: 14, color: "#6b7280", lineHeight: 1.7 }}>
                    <b style={{ color: "#b45309", display: "block", marginBottom: 4 }}>US (IBKR) Rules</b>
                    Commission ({bf.brk}): ${brokerFeeRate(brokerFees, bf.brk, "US", bf.type)} flat per trade<br />
                    No lot system — enter shares directly<br />
                    FIFO applied automatically on sell
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ══ IMPORT PDF ═════════════════════════════════════ */}
          {tab === "pdf" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Card style={{ padding: "40px 32px", textAlign: "center", border: "1px dashed #0f1825" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                <div style={{ fontWeight: 600, fontSize: 18, color: "#e2e8f0", marginBottom: 6 }}>Upload Broker Confirmation PDFs</div>
                <div style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.8, marginBottom: 6 }}>
                  IDX: Mirae Asset · Semesta · Stockbit · OSK · AM Capital · BNI · IPOT · Mandiri<br />
                  US: Interactive Brokers · Schwab · Fidelity
                </div>
                <div style={{ color: "#64748b", fontSize: 14, marginBottom: 22 }}>
                  AI auto-detects market, broker, fees, and trade type.<br />
                  Upload one PDF at a time (Semesta) or <b style={{ color: "#60a5fa" }}>multiple at once</b> (Mirae — one file per trade)
                </div>
                <label style={{ display: "inline-block", padding: "9px 24px", background: "rgba(59,130,246,.1)", border: "1px solid rgba(59,130,246,.25)", borderRadius: 8, cursor: "pointer", fontWeight: 600, color: "#60a5fa", fontSize: 15 }}>
                  {loading.pdf ? "🤖 Reading PDFs..." : "Choose PDF File(s)"}
                  <input ref={fileRef} type="file" accept=".pdf" multiple style={{ display: "none" }} onChange={handlePDF} disabled={loading.pdf} />
                </label>
              </Card>
              {pdfRes && (
                <Card style={{ padding: "16px 18px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 280 }}>
                      <div style={{ color: "#34d399", fontWeight: 600, fontSize: 16, marginBottom: 10 }}>
                        ✓ Report extracted — {pdfRes.trades?.length || 0} trades found
                      </div>

                      {/* EDITABLE MAPPING — override what was detected */}
                      <div style={{ background: "rgba(59,130,246,.04)", border: "1px solid rgba(59,130,246,.15)", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                        <div style={{ fontSize: 12, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600, marginBottom: 10 }}>
                          📌 Map Trades To Account (edit if needed)
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                          <div>
                            <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>Market</label>
                            <select
                              value={pdfRes.market || "IDX"}
                              onChange={e => setPdfRes(p => ({ ...p, market: e.target.value }))}
                              style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "7px 9px", color: "#e2e8f0", fontSize: 13, cursor: "pointer" }}
                            >
                              <option value="IDX">IDX</option>
                              <option value="US">US</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>Investor</label>
                            <select
                              value={pdfRes.investor || "Robert"}
                              onChange={e => setPdfRes(p => ({ ...p, investor: e.target.value }))}
                              style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "7px 9px", color: "#e2e8f0", fontSize: 13, cursor: "pointer" }}
                            >
                              {INVESTORS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>Broker</label>
                            <select
                              value={pdfRes.broker || ""}
                              onChange={e => setPdfRes(p => ({ ...p, broker: e.target.value }))}
                              style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "7px 9px", color: "#e2e8f0", fontSize: 13, cursor: "pointer" }}
                            >
                              {((pdfRes.market || "IDX") === "IDX" ? IDX_BROKERS : US_BROKERS).map(o => (
                                <option key={o} value={o}>{o}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>Trade Date</label>
                            <input
                              type="date"
                              value={pdfRes.date || ""}
                              onChange={e => setPdfRes(p => ({ ...p, date: e.target.value }))}
                              style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "7px 9px", color: "#e2e8f0", fontSize: 13 }}
                            />
                          </div>
                        </div>
                        {pdfRes.account_no && (
                          <div style={{ fontSize: 12, color: "#64748b", marginTop: 8, fontFamily: "'IBM Plex Mono',monospace" }}>
                            Account from PDF: <span style={{color:"#94a3b8"}}>{pdfRes.account_no}</span>
                            <span style={{ color: "#64748b" }}> · Currency: </span>
                            <span style={{color:"#94a3b8"}}>{pdfRes.currency}</span>
                          </div>
                        )}
                      </div>
                      {/* Summary of buy/sell counts + totals */}
                      {pdfRes.trades?.length > 0 && (() => {
                        const bs = pdfRes.trades.filter(t => t.type === "BUY");
                        const ss = pdfRes.trades.filter(t => t.type === "SELL");
                        const buyGross  = bs.reduce((s, t) => s + (t.gross || t.qty * t.price * (pdfRes.market==="IDX"?100:1)), 0);
                        const sellGross = ss.reduce((s, t) => s + (t.gross || t.qty * t.price * (pdfRes.market==="IDX"?100:1)), 0);
                        const buyFees   = bs.reduce((s, t) => s + (t.fee || 0), 0);
                        const sellFees  = ss.reduce((s, t) => s + (t.fee || 0), 0);
                        const totalFees = buyFees + sellFees;
                        // For IDX: buy-side fees INCREASE what you pay, sell-side fees DECREASE what you receive
                        const netPaid    = buyGross  + buyFees;   // You actually paid (buys + buy fees)
                        const netRecvd   = sellGross - sellFees;  // You actually received (sells - sell fees)
                        const settlement = netRecvd - netPaid;    // Positive = credit to you, negative = debit
                        const fmt = v => pdfRes.currency === "IDR" ? `${v < 0 ? "-" : ""}Rp${Math.abs(Math.round(v)).toLocaleString("id-ID")}` : `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`;
                        return (
                          <div style={{ marginTop: 10, padding: "10px 14px", background: "#060a10", borderRadius: 7, fontSize: 14, fontFamily: "'IBM Plex Mono',monospace" }}>
                            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
                              <span style={{ color: "#34d399" }}>{bs.length} BUYS: {fmt(buyGross)}</span>
                              <span style={{ color: "#f87171" }}>{ss.length} SELLS: {fmt(sellGross)}</span>
                              <span style={{ color: "#fbbf24" }}>Fees: {fmt(totalFees)}</span>
                            </div>
                            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13, color: "#94a3b8" }}>
                              <span>Net paid (buys + fees): <span style={{color:"#f87171"}}>{fmt(netPaid)}</span></span>
                              <span>Net received (sells − fees): <span style={{color:"#34d399"}}>{fmt(netRecvd)}</span></span>
                              <span style={{ color: settlement >= 0 ? "#34d399" : "#f87171", fontWeight: 600 }}>
                                Settlement: {settlement >= 0 ? "+" : ""}{fmt(settlement)}
                              </span>
                            </div>
                            {pdfRes.expectedSettlement != null && (() => {
                              const diff = settlement - pdfRes.expectedSettlement;
                              if (Math.abs(diff) < 0.5) return null;  // exact match
                              const isStampIssue = Math.abs(Math.abs(diff) - 10000) < 0.5;
                              return (
                                <div style={{ marginTop: 8, padding: "6px 10px", background: isStampIssue ? "rgba(251,191,36,.1)" : "rgba(239,68,68,.1)", border: `1px solid ${isStampIssue ? "rgba(251,191,36,.3)" : "rgba(239,68,68,.3)"}`, borderRadius: 5, fontSize: 12, color: isStampIssue ? "#fbbf24" : "#f87171" }}>
                                  ⚠ Settlement differs from PDF's expected ({fmt(pdfRes.expectedSettlement)}) by {fmt(diff)}.
                                  {isStampIssue && " This is likely a MISSED STAMP DUTY (Rp 10,000) on one trade. Find the trade with stamp duty and manually add Rp 10,000 to its fee before importing."}
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })()}
                    </div>
                    {pdfRes.trades?.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                        <button onClick={importAllPDFTrades} style={{
                          padding: "8px 16px",
                          background: "linear-gradient(135deg,#1d4ed8,#1e40af)",
                          border: "1px solid rgba(59,130,246,.4)",
                          borderRadius: 7, color: "#93c5fd",
                          cursor: "pointer", fontWeight: 600, fontSize: 14,
                          whiteSpace: "nowrap"
                        }}>
                          ⬇ Import All {pdfRes.trades.length} Trades
                        </button>
                        <button onClick={() => setPdfRes(null)} style={{
                          padding: "4px 10px", background: "transparent",
                          border: "1px solid #1e293b", borderRadius: 5,
                          color: "#6b7280", cursor: "pointer", fontSize: 10
                        }}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={{ fontSize: 14, color: "#64748b", textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 600, marginTop: 12, marginBottom: 4 }}>
                    Or import individually ↓
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8, fontStyle: "italic" }}>
                    💡 Stamp duty (Rp 10,000) is automatically added to the last trade. Fee fields are editable — click any value to correct if needed before importing.
                  </div>

                  {pdfRes.trades?.map((t, i) => {
                    const shares = pdfRes.market === "IDX" ? (t.qty || 0) * 100 : (t.qty || 0);
                    const gross = t.gross || shares * (t.price || 0);
                    const isIDX = pdfRes.market === "IDX";
                    const ccy   = pdfRes.currency === "IDR" ? "Rp" : "$";
                    const fmt   = v => v == null ? "—" : (pdfRes.currency === "IDR" ? `Rp${Math.round(v).toLocaleString("id-ID")}` : `$${(+v).toFixed(2)}`);
                    return (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#060a10", borderRadius: 8, marginBottom: 6, border: `1px solid ${t.type === "BUY" ? "#0a2010" : "#200a0a"}` }}>
                        <div style={{ display: "flex", gap: 12, alignItems: "center", flex: 1 }}>
                          <span className="chip" style={{ background: t.type === "BUY" ? "rgba(52,211,153,.1)" : "rgba(248,113,113,.1)", color: t.type === "BUY" ? "#34d399" : "#f87171" }}>{t.type}</span>
                          {t.is_ipo && <span className="chip" style={{ background: "rgba(167,139,250,.12)", color: "#a78bfa", fontWeight: 600 }}>IPO</span>}
                          <div style={{ minWidth: 80 }}>
                            <div style={{ fontWeight: 600, fontSize: 16, color: "#e2e8f0", fontFamily: "'IBM Plex Mono',monospace" }}>{t.stock}</div>
                            {t._source && pdfRes._files > 1 && <div style={{ fontSize: 12, color: "#64748b" }}>{t._source}</div>}
                          </div>
                          <div style={{ fontSize: 13, color: "#cbd5e1", fontFamily: "'IBM Plex Mono',monospace" }}>
                            {isIDX ? `${t.qty}L (${shares.toLocaleString("id-ID")}sh)` : `${t.qty}sh`}
                            <span style={{ color: "#64748b" }}> @ </span>
                            {ccy}{(t.price || 0).toLocaleString("id-ID")}
                          </div>
                          <div style={{ fontSize: 13, color: "#94a3b8", fontFamily: "'IBM Plex Mono',monospace" }}>
                            <span style={{ color: "#64748b" }}>gross: </span>{fmt(gross)}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ textAlign: "right", fontFamily: "'IBM Plex Mono',monospace" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                              <span style={{ fontSize: 13, color: "#94a3b8" }}>fee:</span>
                              <span style={{ fontSize: 12, color: "#64748b" }}>{ccy}</span>
                              <input
                                type="number"
                                value={t.fee || 0}
                                onChange={(e) => {
                                  const newFee = Math.max(0, +e.target.value || 0);
                                  setPdfRes(prev => {
                                    if (!prev) return prev;
                                    const newTrades = [...prev.trades];
                                    newTrades[i] = { ...newTrades[i], fee: newFee };
                                    return { ...prev, trades: newTrades };
                                  });
                                }}
                                style={{
                                  width: 80,
                                  padding: "3px 6px",
                                  background: "#0a1220",
                                  border: `1px solid ${t.fee >= 10000 ? "rgba(251,191,36,.3)" : "#1e293b"}`,
                                  borderRadius: 4,
                                  color: t.fee >= 10000 ? "#fbbf24" : "#e2e8f0",
                                  fontSize: 12,
                                  fontFamily: "'IBM Plex Mono',monospace",
                                  textAlign: "right",
                                }}
                              />
                              {t._stampApplied > 0 && <span style={{ fontSize: 11, color: "#fbbf24" }} title={`Rp${t._stampApplied.toLocaleString("id-ID")} stamp duty auto-added by app (not per-row in PDF)`}>📮</span>}
                              {t.is_ipo && t.fee === 0 && <span style={{ fontSize: 12, marginLeft: 4 }}>(IPO)</span>}
                            </div>
                            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                              net: {fmt(t.type === "BUY" ? gross + (t.fee || 0) : gross - (t.fee || 0))}
                            </div>
                          </div>
                          <button onClick={() => importTrade(t)} style={{ padding: "6px 14px", background: "rgba(59,130,246,.1)", border: "1px solid rgba(59,130,246,.2)", borderRadius: 6, color: "#60a5fa", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Import →</button>
                        </div>
                      </div>
                    );
                  })}
                </Card>
              )}
            </div>
          )}

          {/* ══ AI ANALYSIS ════════════════════════════════════ */}
          {tab === "ai" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Card style={{ padding: "16px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "#e2e8f0", marginBottom: 4 }}>✦ Weekly Portfolio Analysis</div>
                    <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6 }}>
                      AI searches current IHSG & US market conditions, then gives personalized<br />
                      buy/hold/sell recommendations in Bahasa Indonesia based on your cost basis.
                    </div>
                  </div>
                  <Btn onClick={runAI} loading={loading.ai} label="Run Analysis" />
                </div>
                {/* Open positions summary chips — aggregated by stock (one chip per unique ticker) */}
                {(() => {
                  const byStock = {};
                  openEnriched.forEach(p => {
                    const key = `${p.stk}::${p.mkt}`;
                    if (!byStock[key]) byStock[key] = { stk: p.stk, mkt: p.mkt, totalShares: 0, totalCost: 0, livePrice: p.lp || null };
                    const shares = p.mkt === "IDX" ? p.qty * 100 : p.qty;
                    byStock[key].totalShares += shares;
                    byStock[key].totalCost += p.totalCost;
                  });
                  const stocks = Object.values(byStock).map(r => {
                    const avgCost = r.totalShares ? r.totalCost / r.totalShares : 0;
                    const unrealPct = r.livePrice ? ((r.livePrice - avgCost) / avgCost) * 100 : null;
                    return {
                      stk: r.stk, mkt: r.mkt,
                      qty: r.mkt === "IDX" ? r.totalShares / 100 : r.totalShares,
                      unrealPct,
                    };
                  }).sort((a, b) => {
                    // Sort: unreal pct desc (nulls last)
                    if (a.unrealPct == null && b.unrealPct == null) return a.stk.localeCompare(b.stk);
                    if (a.unrealPct == null) return 1;
                    if (b.unrealPct == null) return -1;
                    return b.unrealPct - a.unrealPct;
                  });
                  return (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6, fontFamily: "'IBM Plex Mono',monospace" }}>
                        {stocks.length} unique stocks · {openEnriched.length} open lots · click Run Analysis for full report
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {stocks.map(p => (
                          <span key={p.stk + p.mkt} className="chip" style={{ background: "#0a0e18", color: p.unrealPct != null ? gc(p.unrealPct) : "#6b7280", fontSize: 14, padding: "3px 8px" }}>
                            {p.stk} {p.qty}{p.mkt === "IDX" ? "L" : "sh"} {p.unrealPct != null ? pct(p.unrealPct) : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </Card>

              {loading.ai && (
                <Card style={{ padding: 36, textAlign: "center" }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>🤖</div>
                  <div style={{ color: "#60a5fa", fontWeight: 600, fontSize: 16, marginBottom: 6 }}>AI sedang menganalisis portofolio…</div>
                  <div style={{ color: "#64748b", fontSize: 14 }}>Mencari kondisi IHSG & saham · Menghitung unrealized P&L · Menyusun rekomendasi</div>
                </Card>
              )}

              {!aiText && !loading.ai && (
                <Card style={{ padding: 40, textAlign: "center" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>✦</div>
                  <div style={{ color: "#e2e8f0", fontWeight: 600, marginBottom: 8 }}>Belum ada analisis</div>
                  <div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.8 }}>
                    Klik "Run Analysis" untuk mendapatkan:<br />
                    • Kondisi IHSG & US market terkini<br />
                    • Penilaian tiap posisi vs harga pasar<br />
                    • Rekomendasi HOLD / ADD / REDUCE / SELL<br />
                    • Risiko dan aksi prioritas minggu ini
                  </div>
                </Card>
              )}

              {aiText && !loading.ai && (
                <Card style={{ padding: "20px 24px" }}>
                  <div style={{ fontSize: 14, color: "#64748b", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 16, fontFamily: "'IBM Plex Mono',monospace" }}>
                    Analysis · {new Date().toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                  </div>
                  <AIMarkdown text={aiText} />
                </Card>
              )}
            </div>
          )}

          {/* ══ STOCK SPLITS ═══════════════════════════════════ */}
          {tab === "splits" && (() => {
            const allStocksForSplits = [...new Set([...buys, ...sells].map(t => t.stk))].sort();
            const sortedSplits = [...splits].sort((a, b) => b.date.localeCompare(a.date));
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Intro */}
                <Card style={{ padding: "16px 20px", background: "linear-gradient(135deg,#0a0e18,#0f1825)" }}>
                  <SH style={{ margin: 0, marginBottom: 6 }}>✂️ Stock Splits & Reverse Splits</SH>
                  <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.7 }}>
                    Corporate actions that change share count. Example: <b style={{color:"#cbd5e1"}}>BBCA 2-for-1 forward split</b> means 100 lots @ Rp 10,000 becomes 200 lots @ Rp 5,000 — same total value.
                    All historical buys/sells <b>before</b> the split date get adjusted automatically. FIFO, cost basis, and P&L recalculate correctly.
                    Original trade data is preserved (not modified) — splits are applied dynamically, so deleting a split restores everything.
                  </div>
                </Card>

                {/* Add form */}
                <Card style={{ padding: "16px 20px" }}>
                  <SH style={{ margin: 0, marginBottom: 12 }}>Add New Split</SH>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <FF l="Market" v={splitForm.mkt} onC={v => ssf("mkt", v)} opts={["IDX", "US"]} />
                    <div>
                      <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>Stock</label>
                      <input
                        list="split-stocks"
                        value={splitForm.stk}
                        onChange={e => ssf("stk", e.target.value.toUpperCase())}
                        placeholder="e.g. BBCA"
                        style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "8px 10px", color: "#e2e8f0", fontSize: 13, fontFamily: "'IBM Plex Mono',monospace" }}
                      />
                      <datalist id="split-stocks">
                        {allStocksForSplits.map(s => <option key={s} value={s} />)}
                      </datalist>
                    </div>
                    <FF l="Split Date" v={splitForm.date} onC={v => ssf("date", v)} type="date" />
                    <FF l="Type" v={splitForm.type} onC={v => {
                      // When user flips forward ↔ reverse, auto-prefill a sensible example
                      // Forward → 2 new for 1 old. Reverse → 1 new for 10 old.
                      if (v === "forward") setSplitForm(p => ({ ...p, type: v, newQty: "2", oldQty: "1" }));
                      else                  setSplitForm(p => ({ ...p, type: v, newQty: "1", oldQty: "10" }));
                    }} opts={["forward", "reverse"]} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr auto", gap: 12, alignItems: "end" }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>
                        New Shares Received
                      </label>
                      <input
                        type="number"
                        value={splitForm.newQty}
                        onChange={e => ssf("newQty", e.target.value)}
                        placeholder={splitForm.type === "forward" ? "2" : "1"}
                        style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "8px 10px", color: "#e2e8f0", fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", textAlign: "right" }}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>
                        For Every Old Share
                      </label>
                      <input
                        type="number"
                        value={splitForm.oldQty}
                        onChange={e => ssf("oldQty", e.target.value)}
                        placeholder={splitForm.type === "forward" ? "1" : "10"}
                        style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "8px 10px", color: "#e2e8f0", fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", textAlign: "right" }}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>Note (optional)</label>
                      <input
                        value={splitForm.note}
                        onChange={e => ssf("note", e.target.value)}
                        placeholder="e.g. announced Q3 2026"
                        style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "8px 10px", color: "#e2e8f0", fontSize: 13 }}
                      />
                    </div>
                    <Btn onClick={addSplit} label="＋ Add Split" />
                  </div>
                  {/* Live preview */}
                  {splitForm.stk && splitForm.oldQty && splitForm.newQty && parseFloat(splitForm.oldQty) > 0 && parseFloat(splitForm.newQty) > 0 && (() => {
                    const r = parseFloat(splitForm.newQty) / parseFloat(splitForm.oldQty);
                    return (
                      <div style={{ marginTop: 12, padding: "10px 14px", background: "#060a10", borderRadius: 7, border: "1px solid #1e293b", fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", color: "#94a3b8" }}>
                        Preview: <b style={{color:"#cbd5e1"}}>{splitForm.stk}</b> · ratio <b style={{color:r>1?"#34d399":"#fbbf24"}}>{r.toFixed(4)}×</b>
                        <br/>
                        Example: 100 {splitForm.mkt === "IDX" ? "lots" : "shares"} @ {splitForm.mkt === "IDX" ? "Rp1,000" : "$10"}
                        {" → "}
                        <b style={{color:"#cbd5e1"}}>{(100 * r).toLocaleString()} {splitForm.mkt === "IDX" ? "lots" : "shares"} @ {splitForm.mkt === "IDX" ? `Rp${Math.round(1000 / r).toLocaleString()}` : `$${(10 / r).toFixed(4)}`}</b>
                        {" (same total cost)"}
                      </div>
                    );
                  })()}
                </Card>

                {/* Splits list */}
                <Card style={{ padding: "16px 20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <SH style={{ margin: 0 }}>All Splits ({splits.length})</SH>
                    {splits.length > 0 && (
                      <span style={{ fontSize: 12, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>
                        Applied automatically to all buys/sells before each split date
                      </span>
                    )}
                  </div>
                  {splits.length === 0 ? (
                    <div style={{ padding: "28px", textAlign: "center", color: "#64748b", fontSize: 14 }}>
                      No splits yet. Add one above when a stock you own goes through a split.
                    </div>
                  ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #1e293b" }}>
                          <th style={{ padding: "8px 10px", textAlign: "left", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".5px" }}>Date</th>
                          <th style={{ padding: "8px 10px", textAlign: "left", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".5px" }}>Stock</th>
                          <th style={{ padding: "8px 10px", textAlign: "left", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".5px" }}>Mkt</th>
                          <th style={{ padding: "8px 10px", textAlign: "left", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".5px" }}>Type</th>
                          <th style={{ padding: "8px 10px", textAlign: "right", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".5px" }}>Ratio</th>
                          <th style={{ padding: "8px 10px", textAlign: "right", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".5px" }}>Multiplier</th>
                          <th style={{ padding: "8px 10px", textAlign: "right", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".5px" }}>Affects</th>
                          <th style={{ padding: "8px 10px", textAlign: "left", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".5px" }}>Note</th>
                          <th style={{ padding: "8px 10px", textAlign: "right", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".5px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSplits.map(sp => {
                          // Count affected trades
                          const affectedBuys  = buys.filter(b => b.stk === sp.stk && b.mkt === sp.mkt && b.date < sp.date).length;
                          const affectedSells = sells.filter(s => s.stk === sp.stk && s.mkt === sp.mkt && s.date < sp.date).length;
                          return (
                            <tr key={sp.id} style={{ borderBottom: "1px solid #0f1825" }}>
                              <td style={{ padding: "8px 10px", fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, color: "#cbd5e1" }}>{dt(sp.date)}</td>
                              <td style={{ padding: "8px 10px", fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{sp.stk}</td>
                              <td style={{ padding: "8px 10px" }}><Chip mkt={sp.mkt} /></td>
                              <td style={{ padding: "8px 10px", fontSize: 12 }}>
                                <span style={{ padding: "2px 8px", borderRadius: 4, background: sp.type === "forward" ? "rgba(52,211,153,.1)" : "rgba(251,191,36,.1)", color: sp.type === "forward" ? "#34d399" : "#fbbf24", fontWeight: 600 }}>
                                  {sp.type}
                                </span>
                              </td>
                              <td style={{ padding: "8px 10px", fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, textAlign: "right", color: "#cbd5e1" }}>
                                {sp.newQty}:{sp.oldQty}
                              </td>
                              <td style={{ padding: "8px 10px", fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, textAlign: "right", color: sp.ratio > 1 ? "#34d399" : "#fbbf24" }}>
                                {sp.ratio.toFixed(4)}×
                              </td>
                              <td style={{ padding: "8px 10px", fontFamily: "'IBM Plex Mono',monospace", fontSize: 12, textAlign: "right", color: "#94a3b8" }}>
                                {affectedBuys} buy{affectedBuys !== 1 ? "s" : ""} · {affectedSells} sell{affectedSells !== 1 ? "s" : ""}
                              </td>
                              <td style={{ padding: "8px 10px", fontSize: 12, color: "#94a3b8" }}>{sp.note || "—"}</td>
                              <td style={{ padding: "8px 10px", textAlign: "right" }}>
                                <button
                                  onClick={() => deleteSplit(sp.id)}
                                  title="Delete this split — trades revert to original qty/price"
                                  style={{ padding: "4px 10px", background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.2)", borderRadius: 5, color: "#f87171", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                                >
                                  🗑 Delete
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </Card>
              </div>
            );
          })()}

          {/* ══ TRANSFERS ══════════════════════════════════════ */}
          {tab === "xfer" && (() => {
            const f = xferForm;
            const unit = f.mkt === "IDX" ? "lots" : "shares";
            const brokersForMkt = f.mkt === "IDX" ? IDX_BROKERS : US_BROKERS;
            // Brokers where this investor holds something, and the holdings at the chosen source.
            const brokersWithHoldings = new Set(openPos.filter(o => o.mkt === f.mkt && o.inv === f.inv).map(o => o.brk));
            const holdingRows = f.fromBrk ? holdingsAt(f.mkt, f.inv, f.fromBrk) : [];
            const defFee = xferFeeDefault(f.toBrk);
            const pickedRows = holdingRows.filter(h => f.sel[h.stk]?.on);
            const totalCharge = pickedRows.reduce((s, h) => {
              const v = f.sel[h.stk]?.fee;
              return s + (v === "" || v == null ? defFee : +v || 0);
            }, 0);
            const allOn = holdingRows.length > 0 && pickedRows.length === holdingRows.length;
            const cellIn = { width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 5, padding: "5px 8px", color: "#e2e8f0", fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", textAlign: "right" };
            const issueById = Object.fromEntries((xferIssues || []).map(i => [i.id, i]));
            const sortedXfers = [...transfers].sort((a, b) => b.date.localeCompare(a.date));
            const th = { padding: "8px 10px", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".5px" };
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Card style={{ padding: "16px 20px", background: "linear-gradient(135deg,#0a0e18,#0f1825)" }}>
                  <SH style={{ margin: 0, marginBottom: 6 }}>⇄ Transfer Stock Between Brokers</SH>
                  <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.7 }}>
                    Moves shares from one broker account to another for the same investor. The <b style={{color:"#cbd5e1"}}>oldest lots move first (FIFO)</b> and keep their original cost,
                    so there is <b style={{color:"#cbd5e1"}}>no realized P&L and no cash movement</b>. The transfer charge is per stock name and is deducted from the <b style={{color:"#cbd5e1"}}>receiving</b> broker's cash (default set in Setup → Broker Fees).
                    Deleting a transfer returns the shares to the source broker.
                  </div>
                </Card>

                <Card style={{ padding: "16px 20px" }}>
                  <SH style={{ margin: 0, marginBottom: 12 }}>New Transfer</SH>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <FF l="Market" v={f.mkt} onC={v => setXferForm(p => ({ ...p, mkt: v, fromBrk: "", toBrk: "", sel: {} }))} opts={["IDX", "US"]} />
                    <FF l="Investor" v={f.inv} onC={v => sxf("inv", v)} opts={INVESTORS} />
                    <FF l="Transfer Date" v={f.date} onC={v => sxf("date", v)} type="date" />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <FF l="From Broker" v={f.fromBrk} onC={v => setXferForm(p => ({ ...p, fromBrk: v, toBrk: p.toBrk === v ? "" : p.toBrk, sel: {} }))}
                      opts={[["", "— select —"], ...brokersForMkt.map(b => [b, brokersWithHoldings.has(b) ? `${b} ●` : b])]} />
                    <FF l="To Broker" v={f.toBrk} onC={v => sxf("toBrk", v)} opts={[["", "— select —"], ...brokersForMkt.filter(b => b !== f.fromBrk)]} />
                  </div>

                  {!f.fromBrk ? (
                    <div style={{ padding: "18px", textAlign: "center", color: "#64748b", fontSize: 13, border: "1px dashed #1e293b", borderRadius: 7 }}>
                      Pick the sending broker to list {f.inv}'s stocks there (● = has holdings).
                    </div>
                  ) : holdingRows.length === 0 ? (
                    <div style={{ padding: "18px", textAlign: "center", color: "#64748b", fontSize: 13, border: "1px dashed #1e293b", borderRadius: 7 }}>
                      {f.inv} has no open {f.mkt} holdings at {f.fromBrk}.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto", border: "1px solid #1e293b", borderRadius: 7 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", minWidth: 560 }}>
                        <thead>
                          <tr style={{ background: "#060a10", borderBottom: "1px solid #1e293b" }}>
                            <th style={{ ...th, textAlign: "center", width: 40 }}>
                              <input type="checkbox" checked={allOn} title="Select all"
                                onChange={e => setXferForm(p => ({ ...p, sel: Object.fromEntries(holdingRows.map(h => [h.stk, { ...(p.sel[h.stk] || {}), on: e.target.checked }])) }))} />
                            </th>
                            <th style={{ ...th, textAlign: "left" }}>Stock</th>
                            <th style={{ ...th, textAlign: "right" }}>Held</th>
                            <th style={{ ...th, textAlign: "right" }}>Avg cost</th>
                            <th style={{ ...th, textAlign: "right", width: 120 }}>Qty ({unit})</th>
                            <th style={{ ...th, textAlign: "right", width: 130 }}>Charge ({f.mkt === "IDX" ? "Rp" : "$"})</th>
                          </tr>
                        </thead>
                        <tbody>
                          {holdingRows.map(h => {
                            const s = f.sel[h.stk] || {};
                            const units = f.mkt === "IDX" ? h.qty * 100 : h.qty;
                            return (
                              <tr key={h.stk} style={{ borderBottom: "1px solid #0f1825", background: s.on ? "rgba(52,211,153,.04)" : "transparent" }}>
                                <td style={{ padding: "5px 8px", textAlign: "center" }}>
                                  <input type="checkbox" checked={!!s.on} onChange={e => setXferSel(h.stk, { on: e.target.checked })} />
                                </td>
                                <td style={{ padding: "5px 8px", fontWeight: 600, color: "#e2e8f0", cursor: "pointer" }} onClick={() => setXferSel(h.stk, { on: !s.on })}>{h.stk}</td>
                                <td style={{ padding: "5px 8px", textAlign: "right", color: "#cbd5e1" }}>{h.qty}</td>
                                <td style={{ padding: "5px 8px", textAlign: "right", color: "#94a3b8" }}>{units > 0 ? money(h.cost / units, f.mkt) : "—"}</td>
                                <td style={{ padding: "5px 8px" }}>
                                  <input type="number" step="any" min="0" disabled={!s.on} value={s.qty ?? ""} placeholder={String(h.qty)}
                                    onChange={e => setXferSel(h.stk, { qty: e.target.value })} style={{ ...cellIn, opacity: s.on ? 1 : .4 }} />
                                </td>
                                <td style={{ padding: "5px 8px" }}>
                                  <input type="number" step="any" min="0" disabled={!s.on} value={s.fee ?? ""} placeholder={String(defFee)}
                                    onChange={e => setXferSel(h.stk, { fee: e.target.value })} style={{ ...cellIn, opacity: s.on ? 1 : .4 }} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {f.fromBrk && holdingRows.length > 0 && (
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
                      Blank qty = full holding. Blank charge = {f.toBrk ? `${f.toBrk}'s` : "the receiving broker's"} default {money(defFee, f.mkt)} per stock, billed by the receiving broker
                      {" "}<span onClick={() => setTab("setup")} style={{ color: "#60a5fa", cursor: "pointer" }}>(edit in Setup)</span>.
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "3fr auto", gap: 12, alignItems: "end", marginTop: 12 }}>
                    <FF l="Note (optional)" v={f.note} onC={v => sxf("note", v)} ph="e.g. moved to Stockbit for lower fees" />
                    <Btn onClick={addTransfer} label={pickedRows.length ? `⇄ Transfer ${pickedRows.length} stock${pickedRows.length !== 1 ? "s" : ""}${totalCharge ? ` · ${money(totalCharge, f.mkt)}` : ""}` : "⇄ Transfer"} />
                  </div>
                </Card>

                <Card style={{ padding: "16px 20px" }}>
                  <SH style={{ margin: 0, marginBottom: 12 }}>All Transfers ({transfers.length})</SH>
                  {transfers.length === 0 ? (
                    <div style={{ padding: "28px", textAlign: "center", color: "#64748b", fontSize: 14 }}>
                      No transfers yet. You can also start one from Open Positions with the ⇄ button.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                        <thead>
                          <tr style={{ borderBottom: "1px solid #1e293b" }}>
                            <th style={{ ...th, textAlign: "left" }}>Date</th>
                            <th style={{ ...th, textAlign: "left" }}>Stock</th>
                            <th style={{ ...th, textAlign: "left" }}>Investor</th>
                            <th style={{ ...th, textAlign: "left" }}>From → To</th>
                            <th style={{ ...th, textAlign: "right" }}>Qty</th>
                            <th style={{ ...th, textAlign: "right" }}>Charge</th>
                            <th style={{ ...th, textAlign: "left" }}>Note</th>
                            <th style={{ ...th, textAlign: "right" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedXfers.map(t => {
                            const issue = issueById[t.id];
                            return (
                              <tr key={t.id} style={{ borderBottom: "1px solid #0f1825" }}>
                                <td style={{ padding: "8px 10px", fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, color: "#cbd5e1", whiteSpace: "nowrap" }}>{dt(t.date)}</td>
                                <td style={{ padding: "8px 10px", fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{t.stk} <Chip mkt={t.mkt} /></td>
                                <td style={{ padding: "8px 10px", fontSize: 13, color: "#cbd5e1" }}>{t.inv}</td>
                                <td style={{ padding: "8px 10px", fontSize: 13, color: "#cbd5e1", whiteSpace: "nowrap" }}>{t.fromBrk} <span style={{ color: "#34d399" }}>→</span> {t.toBrk}</td>
                                <td style={{ padding: "8px 10px", fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, textAlign: "right", color: "#cbd5e1" }}>
                                  {t.qty}{t.mkt === "IDX" ? "L" : "sh"}
                                  {issue && <div title="The source broker didn't hold enough shares on this date (a buy was deleted or edited after the transfer)" style={{ fontSize: 11, color: "#fbbf24" }}>⚠ only {R(issue.available, 4)} held</div>}
                                </td>
                                <td style={{ padding: "8px 10px", fontFamily: "'IBM Plex Mono',monospace", fontSize: 13, textAlign: "right", color: "#94a3b8" }}>{t.fee ? money(t.fee, t.mkt) : "—"}</td>
                                <td style={{ padding: "8px 10px", fontSize: 12, color: "#94a3b8" }}>{t.note || "—"}</td>
                                <td style={{ padding: "8px 10px", textAlign: "right" }}>
                                  <button
                                    onClick={() => deleteTransfer(t.id)}
                                    title="Delete this transfer — shares return to the source broker"
                                    style={{ padding: "4px 10px", background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.2)", borderRadius: 5, color: "#f87171", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                                  >
                                    🗑 Delete
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            );
          })()}

          {/* ══ SETUP · CASH ═══════════════════════════════════ */}
          {tab === "setup" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Onboarding welcome */}
              <Card style={{ padding: "18px 22px", background: "linear-gradient(135deg,#0a0e18,#0f1825)" }}>
                <div style={{ fontSize: 17, fontWeight: 600, color: "#e2e8f0", marginBottom: 8 }}>⚙ Initial Setup</div>
                <div style={{ fontSize: 14, color: "#cbd5e1", lineHeight: 1.8 }}>
                  <b style={{color:"#60a5fa"}}>1.</b> <b>Import Excel</b> — load historical trades from <code style={{color:"#a78bfa"}}>saham.xlsx</code> (pick which sheets)<br />
                  <b style={{color:"#60a5fa"}}>2.</b> <b>Set Cash Balance</b> — tell the app how much idle cash is in each brokerage account (i.e. money deposited but not invested)<br />
                  <b style={{color:"#60a5fa"}}>3.</b> <b>Deposits / Withdrawals / Dividends</b> — record any money movements going forward so cash balance stays accurate<br />
                  <span style={{color:"#94a3b8", fontSize:11.5}}>Formula: Starting Cash + Sell Proceeds − Buy Costs + Deposits − Withdrawals + Dividends = Current Cash</span>
                </div>
              </Card>

              {/* Default commission per broker — used to auto-fill fees on new trades */}
              <Card style={{ padding: "14px 18px" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#e2e8f0", marginBottom: 3 }}>💸 Broker Fees (default commission)</div>
                <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.6, marginBottom: 12 }}>
                  Auto-fills the fee when you add a trade (single and batch) and leave Fee blank. IDX: % of trade value. US: flat $ per trade.
                  Leave blank to use the standard rate (IDX buy {IDX_BUY_PCT * 100}% / sell {IDX_SELL_PCT * 100}%, US ${US_FLAT_FEE}).
                  {" "}<b style={{ color: "#cbd5e1" }}>Transfer in / stock</b> is the fixed charge that broker bills per stock name when shares are transferred INTO it (Transfers tab).
                  <span style={{ color: "#64748b" }}> Changing a rate does not alter existing trades or transfers. Stamp duty is separate.</span>
                </div>
                {[["IDX", IDX_BROKERS], ["US", US_BROKERS]].map(([mkt, list]) => (
                  <div key={mkt} style={{ marginBottom: 10, overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #1e293b" }}>
                          <th style={{ padding: "6px 8px", textAlign: "left", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase" }}><Chip mkt={mkt} /> Broker</th>
                          <th style={{ padding: "6px 8px", textAlign: "right", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase" }}>Buy {mkt === "IDX" ? "%" : "$"}</th>
                          <th style={{ padding: "6px 8px", textAlign: "right", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase" }}>Sell {mkt === "IDX" ? "%" : "$"}</th>
                          <th style={{ padding: "6px 8px", textAlign: "right", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "uppercase" }} title="Charged by this broker when stock is transferred IN to it, per stock name">Transfer in / stock {mkt === "IDX" ? "Rp" : "$"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map(brk => (
                          <tr key={brk} style={{ borderBottom: "1px solid #0f1825" }}>
                            <td style={{ padding: "4px 8px", color: "#cbd5e1", whiteSpace: "nowrap" }}>{brk}</td>
                            {["buy", "sell", "xfer"].map(side => (
                              <td key={side} style={{ padding: "4px 8px", textAlign: "right" }}>
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  value={brokerFees?.[brk]?.[side] ?? ""}
                                  placeholder={side === "xfer" ? "0" : String(brokerFeeDefault(mkt, side === "buy" ? "BUY" : "SELL"))}
                                  onChange={e => {
                                    const v = e.target.value;
                                    setBrokerFees(p => {
                                      const row = { ...(p?.[brk] || {}), [side]: v };
                                      if (row[side] === "") delete row[side];
                                      const next = { ...(p || {}) };
                                      if (Object.keys(row).length) next[brk] = row; else delete next[brk];
                                      return next;
                                    });
                                  }}
                                  style={{ width: 100, background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "6px 8px", color: "#e2e8f0", fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", textAlign: "right" }}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </Card>

              {/* Stamp duty — informational only since v2 (auto per day) */}
              <Card style={{ padding: "14px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#e2e8f0", marginBottom: 3 }}>
                      Stamp Duty (Bea Materai) — auto-applied per day
                    </div>
                    <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.6 }}>
                      Rp 10,000 stamp duty is added automatically once per account (investor + broker) per day, only when that day's IDX transaction total (buys + sells, excluding IPO) reaches Rp 10,000,000. It is recalculated whenever you add, edit or delete a trade.
                      <br /><span style={{ color: "#64748b" }}>Applies to single-mode entry, batch entry, and PDF imports. No toggle needed.</span>
                    </div>
                  </div>
                  <div style={{ padding: "7px 14px", background: "rgba(52,211,153,.1)", border: "1px solid rgba(52,211,153,.3)", borderRadius: 8, color: "#34d399", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
                    ✓ AUTO
                  </div>
                </div>
                {/* Scoped backfill — broker + date range only. Avoids touching
                    historical Excel-imported trades whose fees already include stamp. */}
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #1e293b" }}>
                  <button
                    onClick={() => {
                      const brkInput = window.prompt(
                        "Backfill missing stamp duty for which broker?\n\n" +
                        "Type the broker name exactly (e.g. 'Semesta', 'Mirae Asset', 'Stockbit').\n\n" +
                        "Cancel to abort."
                      );
                      if (!brkInput) return;
                      const brk = brkInput.trim();
                      if (!IDX_BROKERS.includes(brk)) {
                        flash2(`⚠ "${brk}" is not a known IDX broker. Aborted.`);
                        return;
                      }
                      const fromInput = window.prompt(
                        `Start date (inclusive) for backfill — YYYY-MM-DD format.\n\n` +
                        `Only ${brk} trades on or after this date will be considered.\n\n` +
                        `Tip: pick the FIRST day you started adding trades manually without stamp.`
                      );
                      if (!fromInput) return;
                      const fromDate = fromInput.trim();
                      if (!/^\d{4}-\d{2}-\d{2}$/.test(fromDate)) {
                        flash2("⚠ Invalid date format. Use YYYY-MM-DD."); return;
                      }
                      const toInput = window.prompt(
                        `End date (inclusive) for backfill — YYYY-MM-DD format.\n\n` +
                        `Default: today.\n\n` +
                        `(Leave blank to use today's date.)`
                      );
                      const toDate = (toInput || todayLocal()).trim();
                      if (!/^\d{4}-\d{2}-\d{2}$/.test(toDate)) {
                        flash2("⚠ Invalid end date. Use YYYY-MM-DD."); return;
                      }
                      // Find (broker, date) groups in window that lack stamp
                      const groups = new Map();
                      const visit = (arr, type) => {
                        arr.forEach(t => {
                          if (t.mkt !== "IDX" || t.brk !== brk) return;
                          if (t.date < fromDate || t.date > toDate) return;
                          const key = t.date;
                          if (!groups.has(key)) groups.set(key, { bearerId: null, brk, date: t.date, candidates: [] });
                          const g = groups.get(key);
                          if (t._stampDay > 0 || t._stampApplied > 0) g.bearerId = t.id;
                          g.candidates.push({ id: t.id, type });
                        });
                      };
                      visit(buys, "BUY");
                      visit(sells, "SELL");
                      const missing = [...groups.values()].filter(g => !g.bearerId && g.candidates.length > 0);
                      if (missing.length === 0) {
                        flash2(`✓ ${brk} between ${fromDate} and ${toDate}: every day already has stamp duty booked. Nothing to backfill.`);
                        return;
                      }
                      const totalRp = missing.length * STAMP;
                      const sample = missing.slice(0, 10).map(g => `  • ${g.date}`).join("\n");
                      const more = missing.length > 10 ? `\n  ... and ${missing.length - 10} more` : "";
                      const ok = window.confirm(
                        `Backfill stamp duty for ${brk}: ${missing.length} day${missing.length !== 1 ? "s" : ""} between ${fromDate} and ${toDate}.\n\n` +
                        `Total cash adjustment: −Rp ${totalRp.toLocaleString("id-ID")}\n\n` +
                        `Affected days:\n${sample}${more}\n\n` +
                        `For each, Rp 10,000 will be added to the fee of the FIRST trade on that day.`
                      );
                      if (!ok) return;
                      const buyPatches = new Map(), sellPatches = new Map();
                      missing.forEach(g => {
                        const target = g.candidates[0];
                        if (target.type === "BUY") buyPatches.set(target.id, true);
                        else sellPatches.set(target.id, true);
                      });
                      setBuys(prev => prev.map(t => buyPatches.has(t.id)
                        ? { ...t, fee: (t.fee || 0) + STAMP, _stampDay: STAMP }
                        : t));
                      setSells(prev => prev.map(t => sellPatches.has(t.id)
                        ? { ...t, fee: (t.fee || 0) + STAMP, _stampDay: STAMP }
                        : t));
                      flash2(`✓ Backfilled ${missing.length} days for ${brk}: −Rp ${totalRp.toLocaleString("id-ID")} from cash.`);
                    }}
                    style={{ padding: "7px 14px", background: "rgba(251,191,36,.08)", border: "1px solid rgba(251,191,36,.3)", borderRadius: 6, color: "#fbbf24", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                  >
                    🔧 Backfill missing stamp duty…
                  </button>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 8, lineHeight: 1.5 }}>
                    For trades you added manually before this update (e.g. recent Semesta entries that are missing the day's Rp 10k stamp). You'll be prompted for broker + date range so historical Excel-imported trades aren't touched.
                  </div>
                </div>
              </Card>

              {/* Excel bulk import */}
              <Card style={{ padding: "14px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#e2e8f0", marginBottom: 3 }}>📂 Import Excel History (select sheets)</div>
                    <div style={{ fontSize: 14, color: "#6b7280" }}>Upload your <code style={{color:"#60a5fa"}}>saham.xlsx</code> — you'll then pick which sheets to import and assign investor/broker/market per sheet.</div>
                  </div>
                  <label style={{ padding: "7px 16px", background: "rgba(59,130,246,.1)", border: "1px solid rgba(59,130,246,.25)", borderRadius: 7, cursor: "pointer", color: "#60a5fa", fontWeight: 600, fontSize: 14, whiteSpace: "nowrap" }}>
                    {loading.xlsx ? "Parsing…" : "Choose .xlsx File"}
                    <input ref={xlsxRef} type="file" accept=".xlsx,.xlsm,.xls" style={{ display: "none" }} onChange={handleExcel} disabled={loading.xlsx} />
                  </label>
                </div>

                {xlsxBook && (
                  <div style={{ marginTop: 14, padding: 14, background: "#060a10", borderRadius: 8, border: "1px solid #1e293b" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div style={{ fontSize: 14, color: "#34d399", fontWeight: 600 }}>
                        ✓ {xlsxBook.fileName} — {xlsxBook.sheets.length} sheets
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => toggleAllSheets(true)}  style={{ padding: "4px 10px", background: "transparent", border: "1px solid #1e293b", borderRadius: 5, color: "#9ca3af", cursor: "pointer", fontSize: 14 }}>Select All</button>
                        <button onClick={() => toggleAllSheets(false)} style={{ padding: "4px 10px", background: "transparent", border: "1px solid #1e293b", borderRadius: 5, color: "#9ca3af", cursor: "pointer", fontSize: 14 }}>Deselect All</button>
                      </div>
                    </div>

                    <div style={{ padding: "10px 12px", background: "rgba(251,191,36,.05)", border: "1px solid rgba(251,191,36,.2)", borderRadius: 7, marginBottom: 10, fontSize: 13, color: "#fbbf24", lineHeight: 1.6 }}>
                      ⚠ <b>Verify the Broker column</b> for each sheet before importing. Especially for sheets named similarly like "Robert - Semesta" vs "Robert - Semesta Regular" — these are different accounts and must be mapped correctly.
                    </div>

                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #1e293b" }}>
                          {["✓", "Sheet Name", "Buys", "Sells", "Investor", "Broker", "Market"].map(h => (
                            <th key={h} style={{ padding: "7px 8px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: 14, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {xlsxBook.sheets.map((s, i) => {
                          const hasTrades = s.buyCount + s.sellCount > 0;
                          return (
                            <tr key={s.name} className="row" style={{ opacity: hasTrades ? 1 : 0.6 }}>
                              <td style={{ padding: "6px 8px" }}>
                                <input type="checkbox" checked={s.selected} onChange={e => updateSheet(i, "selected", e.target.checked)} style={{ cursor: "pointer", accentColor: "#3b82f6" }} />
                              </td>
                              <td style={{ padding: "6px 8px", color: "#d1d5db", fontFamily: "'IBM Plex Mono',monospace", fontSize: 14 }} title={s.headers?.join(", ") || ""}>
                                {s.name}
                                {!hasTrades && <span style={{ color: "#64748b", fontSize: 14, marginLeft: 6 }}>({s.rowCount} rows)</span>}
                              </td>
                              <td style={{ padding: "6px 8px", color: s.buyCount ? "#34d399" : "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>{s.buyCount || "—"}</td>
                              <td style={{ padding: "6px 8px", color: s.sellCount ? "#f87171" : "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>{s.sellCount || "—"}</td>
                              <td style={{ padding: "4px 8px" }}>
                                <select value={s.inv} onChange={e => updateSheet(i, "inv", e.target.value)} disabled={!s.selected} style={{ background: "#0a0e18", border: "1px solid #1e293b", borderRadius: 5, padding: "4px 6px", color: "#d1d5db", fontSize: 14, width: "100%" }}>
                                  {INVESTORS.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </td>
                              <td style={{ padding: "4px 8px" }}>
                                <select value={s.brk} onChange={e => updateSheet(i, "brk", e.target.value)} disabled={!s.selected} style={{ background: "#0a0e18", border: "1px solid #1e293b", borderRadius: 5, padding: "4px 6px", color: "#d1d5db", fontSize: 14, width: "100%" }}>
                                  <option value="Legacy">Legacy</option>
                                  {ALL_BROKERS.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </td>
                              <td style={{ padding: "4px 8px" }}>
                                <select value={s.mkt} onChange={e => updateSheet(i, "mkt", e.target.value)} disabled={!s.selected} style={{ background: "#0a0e18", border: "1px solid #1e293b", borderRadius: 5, padding: "4px 6px", color: "#d1d5db", fontSize: 14, width: "100%" }}>
                                  <option value="IDX">IDX</option>
                                  <option value="US">US</option>
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Confirm section */}
                    <div style={{ marginTop: 14, padding: 12, background: "#0a0e18", borderRadius: 7, borderLeft: "3px solid #3b82f6" }}>
                      <div style={{ fontSize: 14, color: "#cbd5e1", marginBottom: 6 }}>
                        Ready to import: <span style={{color:"#34d399",fontWeight:600}}>{xlsxBook.sheets.filter(s=>s.selected).reduce((a,s)=>a+s.buyCount,0)} buys</span>
                        {" + "}
                        <span style={{color:"#f87171",fontWeight:600}}>{xlsxBook.sheets.filter(s=>s.selected).reduce((a,s)=>a+s.sellCount,0)} sells</span>
                        {" "}from <span style={{color:"#60a5fa"}}>{xlsxBook.sheets.filter(s=>s.selected).length}</span> sheets
                      </div>
                      {/* Show which accounts will be affected in "replace selected" mode */}
                      {(() => {
                        const affectedAccts = [...new Set(xlsxBook.sheets.filter(s => s.selected).map(s => `${s.inv} · ${s.brk}`))];
                        return (
                          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10, fontFamily: "'IBM Plex Mono',monospace" }}>
                            Accounts: {affectedAccts.slice(0, 4).join(", ")}{affectedAccts.length > 4 ? ` + ${affectedAccts.length - 4} more` : ""}
                          </div>
                        );
                      })()}
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          onClick={() => confirmExcelImport("replace-selected")}
                          title="Delete existing buys/sells for ONLY the selected investor+broker combos, then import fresh. Other accounts untouched."
                          style={{ padding: "8px 16px", background: "rgba(96,165,250,.12)", border: "1px solid rgba(96,165,250,.35)", borderRadius: 7, color: "#60a5fa", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
                        >
                          🎯 Replace Selected Accounts
                        </button>
                        <button
                          onClick={() => confirmExcelImport("append")}
                          title="Add new trades without removing existing ones. May create duplicates if re-importing."
                          style={{ padding: "8px 16px", background: "rgba(52,211,153,.1)", border: "1px solid rgba(52,211,153,.25)", borderRadius: 7, color: "#34d399", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
                        >
                          ➕ Append to Existing
                        </button>
                        <button
                          onClick={() => confirmExcelImport("replace")}
                          title="Delete ALL trades from ALL accounts, then import fresh."
                          style={{ padding: "8px 16px", background: "rgba(248,113,113,.1)", border: "1px solid rgba(248,113,113,.25)", borderRadius: 7, color: "#f87171", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
                        >
                          ⚠ Replace ALL Data
                        </button>
                        <button onClick={() => { setXlsxBook(null); delete window.__xlsxWb; }} style={{ padding: "8px 16px", background: "transparent", border: "1px solid #1e293b", borderRadius: 7, color: "#94a3b8", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>Cancel</button>
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 10, lineHeight: 1.6 }}>
                        <b style={{ color: "#60a5fa" }}>Replace Selected</b> (recommended): only touches the accounts you're importing — other brokers stay intact.<br />
                        <b style={{ color: "#34d399" }}>Append</b>: adds new trades without removing existing ones (may duplicate if re-importing).<br />
                        <b style={{ color: "#f87171" }}>Replace ALL</b>: wipes your entire database first — only use if starting fresh.
                      </div>
                    </div>
                  </div>
                )}
              </Card>

              {/* Current cash balances per account */}
              <Card style={{ padding: "14px 18px" }}>
                <SH>💰 Current Cash Balance per Account</SH>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #1e293b" }}>
                      {["Investor", "Broker", "Mkt", "Starting", "− Buys", "+ Sells", "± Adjust", "Current Balance"].map(h => (
                        <th key={h} style={{ padding: "7px 8px", textAlign: ["Investor","Broker","Mkt"].includes(h) ? "left" : "right", color: "#64748b", fontWeight: 600, fontSize: 14, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allAccounts.map(a => {
                      const c = cashByAccount[a.key] || { starting: 0, bought: 0, sold: 0, adjusted: 0, current: 0 };
                      return (
                        <tr key={a.key + a.mkt} className="row">
                          <TD sm>{a.inv}</TD>
                          <TD sm xs>{a.brk}</TD>
                          <TD><Chip mkt={a.mkt} /></TD>
                          <TD right mono xs>{money(c.starting, a.mkt)}</TD>
                          <TD right mono xs color="#f87171">{money(c.bought, a.mkt)}</TD>
                          <TD right mono xs color="#34d399">{money(c.sold, a.mkt)}</TD>
                          <TD right mono xs color={c.adjusted > 0 ? "#34d399" : c.adjusted < 0 ? "#f87171" : "#6b7280"}>{money(c.adjusted, a.mkt)}</TD>
                          <TD right mono bold color={gc(c.current)}>{money(c.current, a.mkt)}</TD>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {allAccounts.length === 0 && <Empty>No accounts yet — add trades or set starting balance below.</Empty>}
              </Card>

              {/* Set starting cash form */}
              <Card style={{ padding: "14px 18px" }}>
                <SH>💵 Set Cash Balance per Broker Account</SH>

                <div style={{ padding: 12, background: "rgba(59,130,246,.04)", border: "1px solid rgba(59,130,246,.15)", borderRadius: 8, marginBottom: 14, fontSize: 14, color: "#cbd5e1", lineHeight: 1.7 }}>
                  <b style={{color:"#60a5fa"}}>What is "cash balance"?</b><br />
                  It's the idle money sitting in your brokerage account (Stockbit, Mirae, IBKR, etc.) — money you've deposited but <i>not yet used to buy stocks</i>.<br /><br />

                  <b style={{color:"#34d399"}}>Option A — "Set Cash Balance (Today)":</b> Simplest option. Open your broker app → check your available buying power / cash balance → type that amount here. The app treats this as your current cash and works backward.<br /><br />

                  <b style={{color:"#a78bfa"}}>Option B — "Set Historical Starting Cash":</b> Enter the cash you had in the account BEFORE you started trading (or before your earliest imported trade). The app then tracks: Starting + Sells − Buys + Deposits − Withdrawals = Current.
                </div>

                {/* Mode toggle */}
                <div style={{ display: "flex", gap: 3, padding: 3, background: "#060a10", borderRadius: 7, width: "fit-content", marginBottom: 12 }}>
                  {[
                    {id:"target",   lbl:"💰 Set Cash Balance (Today)"},
                    {id:"starting", lbl:"📅 Set Historical Starting Cash"},
                  ].map(m => (
                    <button key={m.id} onClick={() => setCashForm(p => ({ ...p, mode: m.id }))} style={{ padding: "7px 14px", border: "none", borderRadius: 5, cursor: "pointer", background: (cashForm.mode || "starting") === m.id ? "rgba(59,130,246,.12)" : "transparent", color: (cashForm.mode || "starting") === m.id ? "#60a5fa" : "#94a3b8", fontWeight: 600, fontSize: 14 }}>{m.lbl}</button>
                  ))}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: (cashForm.mode || "starting") === "target" ? "1fr 1fr 1fr 1fr auto" : "1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
                  <FF l="Investor" v={cashForm.inv} onC={v => setCashForm(p => ({ ...p, inv: v }))} opts={INVESTORS} />
                  <FF l="Broker Account" v={cashForm.brk} onC={v => setCashForm(p => ({ ...p, brk: v }))} opts={ALL_BROKERS} />
                  <FF
                    l={(cashForm.mode || "starting") === "target"
                        ? "Cash Balance Now (from broker app)"
                        : "Starting Cash (before first trade)"}
                    v={cashForm.amount}
                    onC={v => setCashForm(p => ({ ...p, amount: v }))}
                    type="number"
                    ph={US_BROKERS.includes(cashForm.brk) ? "5000.00" : "100000000"}
                  />
                  {(cashForm.mode || "starting") === "target" && (
                    <FF l="As of Date (optional)" v={cashForm.date} onC={v => setCashForm(p => ({ ...p, date: v }))} type="date" />
                  )}
                  <Btn onClick={setCashBalance} label={(cashForm.mode || "starting") === "target" ? "Save Balance" : "Save Starting"} />
                </div>

                {/* Preview derived balance */}
                {cashForm.inv && cashForm.brk && (() => {
                  const k = `${cashForm.inv}::${cashForm.brk}`;
                  const c = cashByAccount[k] || { starting: 0, bought: 0, sold: 0, adjusted: 0, current: 0 };
                  const mkt = US_BROKERS.includes(cashForm.brk) ? "US" : "IDX";
                  const target = +cashForm.amount || 0;
                  const diff = target - c.current;
                  return (
                    <div style={{ marginTop: 14, padding: 14, background: "#060a10", borderRadius: 7, border: "1px solid #1e293b", fontSize: 14 }}>
                      <div style={{ fontSize: 13, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 600, marginBottom: 10 }}>
                        Current Balance for {cashForm.inv} · {cashForm.brk}
                      </div>
                      <div style={{ fontFamily: "'IBM Plex Mono',monospace", display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 14 }}>
                        <div><div style={{color:"#94a3b8",fontSize:10,textTransform:"uppercase",marginBottom:3,letterSpacing:".5px"}}>Starting</div><div style={{color:"#cbd5e1", fontSize:13}}>{money(c.starting, mkt)}</div></div>
                        <div><div style={{color:"#94a3b8",fontSize:10,textTransform:"uppercase",marginBottom:3,letterSpacing:".5px"}}>− Buys</div><div style={{color:"#f87171", fontSize:13}}>{money(-c.bought, mkt)}</div></div>
                        <div><div style={{color:"#94a3b8",fontSize:10,textTransform:"uppercase",marginBottom:3,letterSpacing:".5px"}}>+ Sells</div><div style={{color:"#34d399", fontSize:13}}>{money(c.sold, mkt)}</div></div>
                        <div><div style={{color:"#94a3b8",fontSize:10,textTransform:"uppercase",marginBottom:3,letterSpacing:".5px"}}>± Adjust/Div</div><div style={{color:gc(c.adjusted), fontSize:13}}>{money(c.adjusted, mkt)}</div></div>
                        <div><div style={{color:"#94a3b8",fontSize:10,textTransform:"uppercase",marginBottom:3,letterSpacing:".5px"}}>= Current</div><div style={{color:gc(c.current),fontWeight:600, fontSize:14}}>{money(c.current, mkt)}</div></div>
                      </div>
                      {(cashForm.mode || "starting") === "target" && target !== 0 && Math.abs(diff) > 0.01 && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #1e293b", fontSize: 14, lineHeight: 1.7 }}>
                          <span style={{color:"#94a3b8"}}>Difference: app shows </span>
                          <span style={{color:"#cbd5e1", fontFamily:"'IBM Plex Mono',monospace"}}>{money(c.current, mkt)}</span>
                          <span style={{color:"#94a3b8"}}>, you entered </span>
                          <span style={{color:"#60a5fa", fontFamily:"'IBM Plex Mono',monospace", fontWeight:600}}>{money(target, mkt)}</span>
                          <span style={{color:"#94a3b8"}}> — an adjustment of </span>
                          <span style={{color:gc(diff), fontFamily:"'IBM Plex Mono',monospace", fontWeight:600}}>{diff >= 0 ? "+" : ""}{money(diff, mkt)}</span>
                          <span style={{color:"#94a3b8"}}> will be recorded to reconcile.</span>
                        </div>
                      )}
                      {(cashForm.mode || "starting") === "target" && Math.abs(diff) <= 0.01 && target !== 0 && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #1e293b", fontSize: 14, color: "#34d399" }}>
                          ✓ App balance already matches what you entered — no adjustment needed.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Card>

              {/* Add adjustment */}
              <Card style={{ padding: "14px 18px" }}>
                <SH>Deposit / Withdraw / Dividend / Adjustment</SH>
                <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 10 }}>
                  <b style={{color:"#34d399"}}>Deposit</b> adds cash · <b style={{color:"#f87171"}}>Withdraw</b> subtracts · <b style={{color:"#a78bfa"}}>Dividend</b> adds cash (with stock ticker) · <b style={{color:"#fbbf24"}}>Adjust</b> fixes discrepancies
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr) auto", gap: 10, alignItems: "end" }}>
                  <FF l="Date" v={adjForm.date} onC={v => setAf("date", v)} type="date" />
                  <FF l="Investor" v={adjForm.inv} onC={v => setAf("inv", v)} opts={INVESTORS} />
                  <FF l="Broker" v={adjForm.brk} onC={v => setAf("brk", v)} opts={ALL_BROKERS} />
                  <FF l="Type" v={adjForm.type} onC={v => setAf("type", v)} opts={["deposit", "withdraw", "dividend", "adjust"]} />
                  <FF l="Amount" v={adjForm.amount} onC={v => setAf("amount", v)} type="number" ph="1000000" />
                  <Btn onClick={addAdjustment} label="Add" />
                </div>
                <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: adjForm.type === "dividend" ? "1fr 2fr" : "1fr", gap: 10 }}>
                  {adjForm.type === "dividend" && (
                    <FF l="Stock Ticker" v={adjForm.stk} onC={v => setAf("stk", v.toUpperCase())} ph="BBCA, VOO, etc." />
                  )}
                  <FF l="Note (optional)" v={adjForm.note} onC={v => setAf("note", v)} ph={adjForm.type === "dividend" ? "Final dividend FY2024, etc." : "Reconciliation, corporate action, etc."} />
                </div>

                {adjustments.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 14, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>History ({adjustments.length})</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #1e293b" }}>
                          {["Date", "Investor", "Broker", "Type", "Stock", "Amount", "Note", ""].map(h => (
                            <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: 14, textTransform: "uppercase" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...adjustments].reverse().map(a => {
                          const mkt = US_BROKERS.includes(a.brk) ? "US" : "IDX";
                          const typeColor = { deposit: "#34d399", withdraw: "#f87171", dividend: "#a78bfa", adjust: "#fbbf24" }[a.type] || "#9ca3af";
                          const typeBg = { deposit: "rgba(52,211,153,.1)", withdraw: "rgba(248,113,113,.1)", dividend: "rgba(167,139,250,.1)", adjust: "rgba(251,191,36,.1)" }[a.type] || "rgba(107,114,128,.1)";
                          return (
                            <tr key={a.id} className="row">
                              <TD sm mono>{dt(a.date)}</TD>
                              <TD sm>{a.inv}</TD>
                              <TD sm xs>{a.brk}</TD>
                              <TD sm><span className="chip" style={{ background: typeBg, color: typeColor }}>{a.type}</span></TD>
                              <TD sm mono>{a.stk || "—"}</TD>
                              <TD right mono color={gc(a.amount)}>{money(a.amount, mkt)}</TD>
                              <TD sm xs>{a.note || "—"}</TD>
                              <TD><button onClick={() => deleteAdjustment(a.id)} style={{ background: "transparent", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 16 }}>×</button></TD>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* Backup & Export */}
              <Card style={{ padding: "14px 18px" }}>
                <SH>💾 Backup & Export</SH>
                <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 14, lineHeight: 1.7 }}>
                  Download your data to save in <b style={{color:"#60a5fa"}}>Dropbox, Google Drive, iCloud, or anywhere</b>. 
                  JSON for exact restore · Excel for viewing / analysis / sharing.
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  {/* JSON backup */}
                  <div style={{ padding: 14, background: "#060a10", borderRadius: 8, border: "1px solid #1e293b" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 21 }}>📦</span>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#e2e8f0" }}>Full Backup (JSON)</div>
                    </div>
                    <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 12 }}>
                      Complete snapshot: trades, cash, adjustments, monthly, live prices, AI analysis. Use for cross-device restore.
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button onClick={exportJSON} style={{ padding: "7px 14px", background: "rgba(59,130,246,.1)", border: "1px solid rgba(59,130,246,.25)", borderRadius: 7, color: "#60a5fa", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
                        ⬇ Download JSON
                      </button>
                      <label style={{ padding: "7px 14px", background: "rgba(167,139,250,.08)", border: "1px solid rgba(167,139,250,.2)", borderRadius: 7, color: "#a78bfa", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
                        ⬆ Restore from JSON
                        <input ref={jsonRestoreRef} type="file" accept=".json" onChange={handleJSONRestore} style={{ display: "none" }} />
                      </label>
                    </div>
                  </div>

                  {/* Excel export */}
                  <div style={{ padding: 14, background: "#060a10", borderRadius: 8, border: "1px solid #1e293b" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 21 }}>📊</span>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#e2e8f0" }}>Excel Export</div>
                    </div>
                    <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 12 }}>
                      Multi-sheet .xlsx: Buys · Sells · FIFO P&L · Open Positions · Monthly · Cash · Adjustments · Floating P&L
                    </div>
                    <button onClick={exportExcel} style={{ padding: "7px 14px", background: "rgba(52,211,153,.08)", border: "1px solid rgba(52,211,153,.25)", borderRadius: 7, color: "#34d399", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
                      ⬇ Download .xlsx
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 14, padding: "10px 12px", background: "rgba(59,130,246,.04)", border: "1px solid rgba(59,130,246,.12)", borderRadius: 7, fontSize: 14, color: "#6b7280", lineHeight: 1.6 }}>
                  💡 <b style={{color:"#60a5fa"}}>Dropbox workflow:</b> Download the JSON backup → save to your Dropbox folder (drag-drop, or use Dropbox desktop/mobile app). 
                  To restore on another device: download from Dropbox → click "Restore from JSON". 
                  For weekly sync: keep in synced Dropbox folder and re-export every Sunday.
                </div>

                <div style={{ marginTop: 10, fontSize: 14, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>
                  Current size: {buys.length} buys · {sells.length} sells · {monthly.length} monthly · {adjustments.length} adjustments · {splits.length} splits · {Object.keys(cash).length} starting cash
                </div>
              </Card>

              {/* Danger zone */}
              <Card style={{ padding: "14px 18px", borderColor: "rgba(248,113,113,.15)" }}>
                <SH style={{ color: "#f87171" }}>⚠ Danger Zone</SH>

                {/* Auto-pair existing trades that look like same-row Excel entries.
                    Pairs a sell with the earliest matching buy (same stk+qty+account, buy.date ≤ sell.date). */}
                {(() => {
                  const pairedCount = buys.filter(b => b.pairedSellId).length;
                  const unpairedBuys = buys.filter(b => !b.pairedSellId).length;
                  const unpairedSells = sells.filter(s => !s.pairedBuyId).length;
                  return (
                    <div style={{ padding: "12px 14px", background: "rgba(96,165,250,.04)", borderRadius: 8, border: "1px solid rgba(96,165,250,.2)", marginBottom: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#60a5fa", marginBottom: 6 }}>🔗 Auto-Pair Trades (Excel-style Row Matching)</div>
                      <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 10, lineHeight: 1.6 }}>
                        Current state: <b style={{color:"#cbd5e1"}}>{pairedCount}</b> paired · <b style={{color:"#cbd5e1"}}>{unpairedBuys}</b> unpaired buys · <b style={{color:"#cbd5e1"}}>{unpairedSells}</b> unpaired sells.<br/>
                        Paired trades use Excel-style row matching (sell's P&L comes from its specific buy, not FIFO queue). This fixes the case where day-trades get incorrectly matched to old unclosed lots via FIFO.<br/>
                        Click a button below to pair unpaired trades where buy and sell have: <b>same stock, same qty, same investor+broker, buy.date ≤ sell.date</b>, within the time window.
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button onClick={() => runAutoPair(0)} style={{ padding: "7px 14px", background: "rgba(96,165,250,.1)", border: "1px solid rgba(96,165,250,.25)", borderRadius: 6, color: "#60a5fa", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                          Pair same-day only (safest)
                        </button>
                        <button onClick={() => runAutoPair(1)} style={{ padding: "7px 14px", background: "rgba(96,165,250,.06)", border: "1px solid rgba(96,165,250,.2)", borderRadius: 6, color: "#60a5fa", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                          Pair within 1 day
                        </button>
                        <button onClick={() => runAutoPair(7)} style={{ padding: "7px 14px", background: "rgba(251,191,36,.06)", border: "1px solid rgba(251,191,36,.2)", borderRadius: 6, color: "#fbbf24", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                          Pair within 7 days
                        </button>
                        <button onClick={unpairAll} style={{ padding: "7px 14px", background: "rgba(248,113,113,.06)", border: "1px solid rgba(248,113,113,.2)", borderRadius: 6, color: "#f87171", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                          Unpair All (reset to FIFO)
                        </button>
                        <button
                          onClick={() => {
                            // Diagnostic: find top 20 biggest losses and log details
                            const sorted = [...closed].sort((a, b) => a.profit - b.profit).slice(0, 20);
                            console.log("━━━━━━━━━━━ TOP 20 BIGGEST LOSSES ━━━━━━━━━━━");
                            console.log(`Total closed trades: ${closed.length}`);
                            console.log(`Net realized P&L: Rp ${closed.reduce((a, s) => a + s.profit, 0).toLocaleString("id-ID")}`);
                            console.log(`Sum of top 20 losses: Rp ${sorted.reduce((a, s) => a + s.profit, 0).toLocaleString("id-ID")}`);
                            console.log("");
                            sorted.forEach((s, i) => {
                              const m = s.matched[0] || {};
                              console.log(
                                `${String(i+1).padStart(2)}. ${s.stk.padEnd(10)} ${s.brk.padEnd(15)} ` +
                                `sold ${s.date} ${s.qty}${s.mkt === "IDX" ? "L" : "sh"} @ ${s.px} ` +
                                `→ FIFO buy ${m.buyDate || "?"} @ ${m.buyPx ?? "?"} ` +
                                `[${s._paired ? "PAIR" : "FIFO"}] ` +
                                `profit: Rp ${Math.round(s.profit).toLocaleString("id-ID")} ` +
                                `(cost Rp ${Math.round(s.totalCostBasis).toLocaleString("id-ID")})`
                              );
                            });
                            flash2("✓ Top 20 losses logged to console (F12 → Console tab)");
                          }}
                          style={{ padding: "7px 14px", background: "rgba(168,85,247,.08)", border: "1px solid rgba(168,85,247,.25)", borderRadius: 6, color: "#c4b5fd", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                        >
                          📋 Log Top 20 Losses (debug)
                        </button>
                        <button
                          onClick={() => {
                            // Detect and fix duplicate trade IDs — the root cause of wrong paired P&L.
                            // Happens when multiple imports generate colliding IDs (xb1, xb2, ...)
                            // across different accounts, making pairedBuyId resolve to wrong buys.
                            const allIds = [...buys.map(b => b.id), ...sells.map(s => s.id)];
                            const idCounts = {};
                            allIds.forEach(id => { idCounts[id] = (idCounts[id] || 0) + 1; });
                            const dupIds = Object.entries(idCounts).filter(([_, n]) => n > 1);
                            if (dupIds.length === 0) {
                              flash2("✓ No duplicate IDs found — pairing is consistent");
                              return;
                            }
                            if (!confirm(
                              `Found ${dupIds.length} duplicate IDs across ${buys.length + sells.length} trades.\n\n` +
                              `Rebuild will: (1) assign each trade a fresh unique ID, ` +
                              `(2) re-link pairedBuyId/pairedSellId to correct partners where they still match.\n\n` +
                              `Existing paired relationships from the SAME import session (matching prefix) are preserved; ` +
                              `cross-contaminated pair refs are dropped.\n\n` +
                              `Export JSON backup first.`
                            )) return;
                            // Build a mapping from each OLD id to the trade's NEW id.
                            // Because IDs are duplicated, we must assign fresh IDs first,
                            // then for each pairedBuyId reference, resolve to the buy with MATCHING
                            // inv+brk+stk+mkt (sanity check to avoid cross-account pairing).
                            const session = `fix_${Date.now().toString(36)}`;
                            let n = 0;
                            const newBuys = buys.map(b => ({ ...b, _oldId: b.id, id: `xb_${session}_${++n}` }));
                            const newSells = sells.map(s => ({ ...s, _oldId: s.id, id: `xs_${session}_${++n}` }));
                            // For paired refs: find old buy/sell by old id + matching account, then re-link
                            const remapSellPair = (sell) => {
                              if (!sell.pairedBuyId) return sell;
                              const oldId = sell.pairedBuyId;
                              // Find candidate buys with this oldId
                              const candidates = newBuys.filter(b => b._oldId === oldId);
                              // Pick the one in the same account as the sell
                              const match = candidates.find(b =>
                                b.inv === sell.inv && b.brk === sell.brk && b.stk === sell.stk && b.mkt === sell.mkt
                              );
                              if (!match) {
                                // Drop bad reference
                                const { pairedBuyId, ...rest } = sell;
                                return rest;
                              }
                              return { ...sell, pairedBuyId: match.id };
                            };
                            const remapBuyPair = (buy) => {
                              if (!buy.pairedSellId) return buy;
                              const oldId = buy.pairedSellId;
                              const candidates = newSells.filter(s => s._oldId === oldId);
                              const match = candidates.find(s =>
                                s.inv === buy.inv && s.brk === buy.brk && s.stk === buy.stk && s.mkt === buy.mkt
                              );
                              if (!match) {
                                const { pairedSellId, ...rest } = buy;
                                return rest;
                              }
                              return { ...buy, pairedSellId: match.id };
                            };
                            const cleanBuys  = newBuys.map(remapBuyPair).map(({ _oldId, ...rest }) => rest);
                            const cleanSells = newSells.map(remapSellPair).map(({ _oldId, ...rest }) => rest);
                            const pairedBefore = buys.filter(b => b.pairedSellId).length;
                            const pairedAfter  = cleanBuys.filter(b => b.pairedSellId).length;
                            setBuys(cleanBuys);
                            setSells(cleanSells);
                            flash2(`✓ Rebuilt IDs. Pairs: ${pairedBefore} → ${pairedAfter} (${pairedBefore - pairedAfter} cross-account dropped)`);
                          }}
                          style={{ padding: "7px 14px", background: "rgba(250,204,21,.08)", border: "1px solid rgba(250,204,21,.25)", borderRadius: 6, color: "#fde047", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                        >
                          🔧 Rebuild IDs (fix dup collisions)
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* One-time date-shift fix: if Excel was imported with the OLD importer
                    that had the timezone bug (all dates shifted back by 1 day), use this
                    to shift Excel-imported buys + sells forward by 1 day as a one-time correction.
                    PDF-imported trades (date strings direct from AI) are NOT affected by the bug
                    and should NOT be shifted. Use the cutoff date to separate them. */}
                <div style={{ padding: "12px 14px", background: "rgba(248,113,113,.04)", borderRadius: 8, border: "1px solid rgba(248,113,113,.2)", marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#f87171", marginBottom: 6 }}>📅 Shift Excel-Imported Dates by ±1 Day (Timezone Fix)</div>
                  <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 12, lineHeight: 1.6 }}>
                    Excel imports before the timezone fix are all shifted back by 1 day due to UTC conversion from local midnight.
                    PDF-imported trades use string dates and are NOT affected.
                    Set a cutoff date — only trades BEFORE this date will be shifted. Default is the date of your first PDF import (trades on or after this date will be left alone).<br/>
                    <b style={{color:"#fbbf24"}}>⚠ Export JSON backup FIRST. Running twice will over-shift.</b>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: 10, alignItems: "end" }}>
                    <FF l="Only shift trades BEFORE" v={dateShiftCutoff} onC={v => setDateShiftCutoff(v)} type="date" />
                    <div style={{ fontSize: 11, color: "#64748b", paddingBottom: 8 }}>
                      (typical: date of your first PDF import)
                    </div>
                    <button
                      onClick={() => {
                        if (!dateShiftCutoff) { flash2("⚠ Set cutoff date first"); return; }
                        const toShiftBuys  = buys.filter(b => b.date < dateShiftCutoff);
                        const toShiftSells = sells.filter(s => s.date < dateShiftCutoff);
                        if (toShiftBuys.length === 0 && toShiftSells.length === 0) {
                          flash2("No trades found before cutoff date");
                          return;
                        }
                        if (!confirm(`Shift ${toShiftBuys.length} buys + ${toShiftSells.length} sells BEFORE ${dateShiftCutoff} forward by 1 day?\n\nTrades on or after ${dateShiftCutoff} (including PDF imports) will NOT be touched.\n\n⚠ Export JSON backup FIRST.`)) return;
                        const shift = d => {
                          if (!d) return d;
                          const dt = new Date(d + "T12:00:00");
                          dt.setDate(dt.getDate() + 1);
                          return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
                        };
                        setBuys(p => p.map(b => b.date < dateShiftCutoff ? ({ ...b, date: shift(b.date) }) : b));
                        setSells(p => p.map(s => s.date < dateShiftCutoff ? ({ ...s, date: shift(s.date) }) : s));
                        flash2(`✓ Shifted ${toShiftBuys.length} buys + ${toShiftSells.length} sells forward by 1 day`);
                      }}
                      style={{ padding: "7px 14px", background: "rgba(52,211,153,.1)", border: "1px solid rgba(52,211,153,.25)", borderRadius: 6, color: "#34d399", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                    >
                      +1 Day (shift forward)
                    </button>
                    <button
                      onClick={() => {
                        if (!dateShiftCutoff) { flash2("⚠ Set cutoff date first"); return; }
                        const toShiftBuys  = buys.filter(b => b.date < dateShiftCutoff);
                        const toShiftSells = sells.filter(s => s.date < dateShiftCutoff);
                        if (toShiftBuys.length === 0 && toShiftSells.length === 0) {
                          flash2("No trades found before cutoff date");
                          return;
                        }
                        if (!confirm(`Shift ${toShiftBuys.length} buys + ${toShiftSells.length} sells BEFORE ${dateShiftCutoff} BACKWARD by 1 day (undo)?\n\n⚠ Export JSON backup FIRST.`)) return;
                        const shift = d => {
                          if (!d) return d;
                          const dt = new Date(d + "T12:00:00");
                          dt.setDate(dt.getDate() - 1);
                          return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
                        };
                        setBuys(p => p.map(b => b.date < dateShiftCutoff ? ({ ...b, date: shift(b.date) }) : b));
                        setSells(p => p.map(s => s.date < dateShiftCutoff ? ({ ...s, date: shift(s.date) }) : s));
                        flash2(`✓ Shifted ${toShiftBuys.length} buys + ${toShiftSells.length} sells BACK by 1 day (undo)`);
                      }}
                      style={{ padding: "7px 14px", background: "rgba(251,191,36,.06)", border: "1px solid rgba(251,191,36,.2)", borderRadius: 6, color: "#fbbf24", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                    >
                      −1 Day (undo)
                    </button>
                  </div>
                </div>

                {/* Bulk close phantom positions — grouped by broker account */}
                {openPos.length > 0 && (() => {
                  // Group open positions by inv::brk
                  const byAcct = {};
                  openPos.forEach(p => {
                    const key = `${p.inv}::${p.brk}::${p.mkt}`;
                    if (!byAcct[key]) byAcct[key] = { inv: p.inv, brk: p.brk, mkt: p.mkt, count: 0, stocks: new Set() };
                    byAcct[key].count += 1;
                    byAcct[key].stocks.add(p.stk);
                  });
                  const acctList = Object.values(byAcct).sort((a, b) => b.count - a.count);
                  return (
                    <div style={{ padding: "12px 14px", background: "rgba(167,139,250,.04)", borderRadius: 8, border: "1px solid rgba(167,139,250,.15)", marginBottom: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#a78bfa", marginBottom: 6 }}>✕ Bulk Close Phantom Positions</div>
                      <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 12, lineHeight: 1.6 }}>
                        For each account with open positions, you can close ALL of them at their cost basis (zero P&L impact). Useful when Excel data has many legacy phantom positions from partial-sell typos accumulated over years. After closing, positions show as "SOLD" in Trade History with a note.
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {acctList.map(a => (
                          <div key={`${a.inv}::${a.brk}::${a.mkt}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#060a10", borderRadius: 6, border: "1px solid #1e293b" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                              <Chip mkt={a.mkt} />
                              <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{a.inv}</span>
                              <span style={{ color: "#94a3b8" }}>·</span>
                              <span style={{ color: "#cbd5e1" }}>{a.brk}</span>
                              <span style={{ color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", fontSize: 12 }}>
                                ({a.count} lot{a.count !== 1 ? "s" : ""} across {a.stocks.size} stock{a.stocks.size !== 1 ? "s" : ""})
                              </span>
                            </div>
                            <button
                              onClick={() => bulkCloseAtCost(p => p.inv === a.inv && p.brk === a.brk, `${a.inv} · ${a.brk}`)}
                              style={{
                                padding: "5px 12px",
                                background: "rgba(167,139,250,.1)",
                                border: "1px solid rgba(167,139,250,.25)",
                                borderRadius: 5,
                                color: "#a78bfa",
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              ✕ Close All at Cost
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Rename broker — merge duplicates, fix typos */}
                {(() => {
                  // List all unique broker names currently in use
                  const usedBrokers = new Set();
                  buys.forEach(b => usedBrokers.add(b.brk));
                  sells.forEach(s => usedBrokers.add(s.brk));
                  adjustments.forEach(a => usedBrokers.add(a.brk));
                  monthly.forEach(m => usedBrokers.add(m.brk));
                  Object.keys(cash).forEach(k => usedBrokers.add(k.split("::")[1]));
                  const brokerList = [...usedBrokers].filter(Boolean).sort();
                  if (brokerList.length === 0) return null;

                  return (
                    <div style={{ padding: "12px 14px", background: "rgba(251,191,36,.04)", borderRadius: 8, border: "1px solid rgba(251,191,36,.15)", marginBottom: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#fbbf24", marginBottom: 6 }}>✎ Rename Broker (Merge or Fix Typo)</div>
                      <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 12, lineHeight: 1.6 }}>
                        Rename a broker across ALL records — buys, sells, cash, adjustments, monthly. Useful for merging duplicate brokers (e.g., "Semesta Regular" → "Semesta") or fixing mistakes. If both names already exist, cash balances will be summed.
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr auto", gap: 10, alignItems: "end" }}>
                        <div>
                          <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>From (existing)</label>
                          <select
                            value={renameForm.from}
                            onChange={e => setRenameForm(p => ({ ...p, from: e.target.value }))}
                            style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "8px 10px", color: "#e2e8f0", fontSize: 13, cursor: "pointer" }}
                          >
                            <option value="">— pick broker to rename —</option>
                            {brokerList.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </div>
                        <div style={{ fontSize: 18, color: "#64748b", paddingBottom: 6 }}>→</div>
                        <div>
                          <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>To (new name)</label>
                          <input
                            type="text"
                            value={renameForm.to}
                            onChange={e => setRenameForm(p => ({ ...p, to: e.target.value }))}
                            placeholder="e.g. Semesta"
                            list="known-brokers"
                            style={{ width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 6, padding: "8px 10px", color: "#e2e8f0", fontSize: 13 }}
                          />
                          <datalist id="known-brokers">
                            {[...IDX_BROKERS, ...US_BROKERS].map(b => <option key={b} value={b} />)}
                          </datalist>
                        </div>
                        <button
                          onClick={() => {
                            if (!renameForm.from) return flash2("⚠ Pick a broker to rename");
                            if (!renameForm.to.trim()) return flash2("⚠ Enter new name");
                            renameBroker(renameForm.from, renameForm.to.trim());
                            setRenameForm({ from: "", to: "" });
                          }}
                          disabled={!renameForm.from || !renameForm.to.trim()}
                          style={{
                            padding: "8px 18px",
                            background: renameForm.from && renameForm.to.trim() ? "rgba(251,191,36,.1)" : "rgba(107,114,128,.08)",
                            border: `1px solid ${renameForm.from && renameForm.to.trim() ? "rgba(251,191,36,.3)" : "#1e293b"}`,
                            borderRadius: 6,
                            color: renameForm.from && renameForm.to.trim() ? "#fbbf24" : "#64748b",
                            cursor: renameForm.from && renameForm.to.trim() ? "pointer" : "not-allowed",
                            fontSize: 13,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Rename
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Per-account remover */}
                <div style={{ padding: "12px 14px", background: "rgba(248,113,113,.04)", borderRadius: 8, border: "1px solid rgba(248,113,113,.12)", marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#fbbf24", marginBottom: 6 }}>🗑 Remove a Specific Account</div>
                  <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 12, lineHeight: 1.6 }}>
                    Deletes ALL trades, cash balance, and adjustments for one investor+broker combo. Other accounts are untouched. Useful if you imported an account with wrong data and want to re-import fresh.
                  </div>
                  {(() => {
                    // List all accounts with data
                    const accts = new Set();
                    buys.forEach(b => accts.add(`${b.inv}::${b.brk}`));
                    sells.forEach(s => accts.add(`${s.inv}::${s.brk}`));
                    adjustments.forEach(a => accts.add(`${a.inv}::${a.brk}`));
                    Object.keys(cash).forEach(k => accts.add(k));
                    const acctList = [...accts].sort().map(k => {
                      const [inv, brk] = k.split("::");
                      const buyCount  = buys.filter(b => b.inv === inv && b.brk === brk).length;
                      const sellCount = sells.filter(s => s.inv === inv && s.brk === brk).length;
                      return { inv, brk, buyCount, sellCount };
                    });
                    if (acctList.length === 0) {
                      return <div style={{ fontSize: 13, color: "#64748b", fontStyle: "italic" }}>No accounts found.</div>;
                    }
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {acctList.map(a => (
                          <div key={`${a.inv}::${a.brk}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#060a10", borderRadius: 6, border: "1px solid #1e293b" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                              <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{a.inv}</span>
                              <span style={{ color: "#94a3b8" }}>·</span>
                              <span style={{ color: "#cbd5e1" }}>{a.brk}</span>
                              <span style={{ color: "#64748b", fontFamily: "'IBM Plex Mono',monospace", fontSize: 12 }}>
                                ({a.buyCount} buys, {a.sellCount} sells)
                              </span>
                            </div>
                            <button
                              onClick={() => removeAccount(a.inv, a.brk)}
                              style={{
                                padding: "5px 12px",
                                background: "rgba(248,113,113,.08)",
                                border: "1px solid rgba(248,113,113,.2)",
                                borderRadius: 5,
                                color: "#f87171",
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Full reset */}
                <div style={{ fontSize: 14, color: "#94a3b8", marginBottom: 8 }}>
                  Or clear <b>everything</b>. <b style={{color:"#fbbf24"}}>Export a backup first!</b>
                </div>
                <button onClick={resetAllData} style={{ padding: "7px 16px", background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.25)", borderRadius: 7, color: "#f87171", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
                  Reset All Data
                </button>
              </Card>
            </div>
          )}

        </div>
      </main>

      {/* ═══ TRADE EDIT MODAL ═══ */}
      {editingTrade && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backdropFilter: "blur(2px)" }}
          onClick={closeTradeEdit}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: "#0a0e18", border: "1px solid #1e293b", borderRadius: 12, padding: 22, width: 520, maxWidth: "92vw", maxHeight: "90vh", overflowY: "auto", WebkitOverflowScrolling: "touch", boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>
                  Edit {editingTrade.type} Trade
                </div>
                <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 3 }}>
                  Changes will trigger FIFO recompute automatically
                </div>
              </div>
              <button onClick={closeTradeEdit} style={{ background: "transparent", border: "none", color: "#94a3b8", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <FF l="Stock Ticker" v={editingTrade.data.stk} onC={v => setEditingTrade(p => ({ ...p, data: { ...p.data, stk: v } }))} ph="BBRI" />
              <FF l="Date"         v={editingTrade.data.date} onC={v => setEditingTrade(p => ({ ...p, data: { ...p.data, date: v } }))} type="date" />
              <FF l="Market"       v={editingTrade.data.mkt}  onC={v => setEditingTrade(p => ({ ...p, data: { ...p.data, mkt: v } }))} opts={["IDX","US"]} />
              <FF l="Investor"     v={editingTrade.data.inv}  onC={v => setEditingTrade(p => ({ ...p, data: { ...p.data, inv: v } }))} opts={INVESTORS} />
              <FF l="Broker"       v={editingTrade.data.brk}  onC={v => setEditingTrade(p => ({ ...p, data: { ...p.data, brk: v } }))} opts={editingTrade.data.mkt === "IDX" ? IDX_BROKERS : US_BROKERS} />
              <FF l={editingTrade.data.mkt === "IDX" ? "Qty (lots)" : "Qty (shares)"} v={editingTrade.data.qty} onC={v => setEditingTrade(p => ({ ...p, data: { ...p.data, qty: v } }))} type="number" />
              <FF l="Price per share" v={editingTrade.data.px} onC={v => setEditingTrade(p => ({ ...p, data: { ...p.data, px: v } }))} type="number" />
              {(() => {
                // Fee can be edited as absolute (Rp/USD) OR as a percentage of gross.
                // Percentage view is useful because IDX fees are conventionally quoted as %
                // (Buy ~0.15%, Sell ~0.25% incl. tax, plus stamp).
                const qty = +editingTrade.data.qty || 0;
                const px  = +editingTrade.data.px  || 0;
                const fee = +(editingTrade.data.fee || 0);
                const shares = editingTrade.data.mkt === "IDX" ? qty * 100 : qty;
                const gross = shares * px;
                const feePctNum = gross > 0 ? (fee / gross) * 100 : 0;
                const setFeeAmount = (v) => setEditingTrade(p => ({ ...p, data: { ...p.data, fee: v } }));
                const setFeePct = (pctStr) => {
                  const pct = parseFloat(pctStr);
                  if (isNaN(pct) || gross <= 0) return;
                  // Round to nearest integer Rupiah for IDX, 2 decimals for USD
                  const newFee = editingTrade.data.mkt === "IDX"
                    ? Math.round(gross * pct / 100)
                    : Math.round(gross * pct / 100 * 100) / 100;
                  setFeeAmount(newFee);
                };
                // Hint chips: typical fee % per market+side
                const isIDX = editingTrade.data.mkt === "IDX";
                const isBuy = editingTrade.type === "BUY";
                const brkRate = brokerFeeRate(brokerFees, editingTrade.data.brk, editingTrade.data.mkt, editingTrade.type);
                const presets = isIDX
                  ? [...new Set([brkRate, ...(isBuy ? [0.15, 0.20] : [0.25, 0.30])])].map(v => ({ lbl: `${v}%`, v }))
                  : [...new Set([brkRate, 1, 1.50])].map(abs => ({ lbl: `$${abs}`, v: null, abs }));
                return (
                  <div>
                    <div style={{ fontSize: 14, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 500, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span>Fee</span>
                      {gross > 0 && (
                        <span style={{ fontSize: 11, color: "#94a3b8", textTransform: "none", letterSpacing: 0, fontFamily: "'IBM Plex Mono',monospace" }}>
                          = <span style={{ color: feePctNum > 0.5 ? "#fbbf24" : "#cbd5e1", fontWeight: 600 }}>{feePctNum.toFixed(3)}%</span> of gross
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
                      <input
                        type="number"
                        step="any"
                        value={fee}
                        onChange={e => setFeeAmount(e.target.value)}
                        placeholder="0"
                        style={{ flex: 1, padding: "8px 10px", background: "#06090f", color: "#e2e8f0", border: "1px solid #1e293b", borderRadius: 6, fontSize: 15, fontFamily: "'IBM Plex Mono',monospace" }}
                        title="Fee in absolute amount"
                      />
                      <PercentInput
                        feePctNum={feePctNum}
                        gross={gross}
                        onCommit={setFeePct}
                      />
                      <span style={{ alignSelf: "center", fontSize: 13, color: "#64748b", fontFamily: "'IBM Plex Mono',monospace" }}>%</span>
                    </div>
                    {/* Quick presets */}
                    {gross > 0 && (
                      <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
                        {presets.map(p => (
                          <button
                            key={p.lbl}
                            onClick={() => p.abs != null ? setFeeAmount(p.abs) : setFeePct(p.v)}
                            style={{ padding: "3px 9px", background: "rgba(96,165,250,.05)", border: "1px solid rgba(96,165,250,.2)", borderRadius: 4, color: "#60a5fa", cursor: "pointer", fontSize: 11, fontFamily: "'IBM Plex Mono',monospace" }}
                            title={p.abs != null ? `Set fee to ${p.lbl}` : `Apply ${p.lbl} of gross`}
                          >
                            {p.lbl}
                          </button>
                        ))}
                        {isIDX && isBuy && (
                          <button
                            onClick={() => setFeeAmount(fee + 10000)}
                            style={{ padding: "3px 9px", background: "rgba(251,191,36,.06)", border: "1px solid rgba(251,191,36,.25)", borderRadius: 4, color: "#fbbf24", cursor: "pointer", fontSize: 11, fontFamily: "'IBM Plex Mono',monospace" }}
                            title="Add Rp 10,000 stamp duty (Bea Materai) to current fee"
                          >
                            +Rp10k stamp
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Preview */}
            {(() => {
              const qty = +editingTrade.data.qty || 0;
              const px  = +editingTrade.data.px || 0;
              const fee = +(editingTrade.data.fee || 0);
              const shares = editingTrade.data.mkt === "IDX" ? qty * 100 : qty;
              const gross = shares * px;
              const net   = editingTrade.type === "BUY" ? gross + fee : gross - fee;
              return (
                <div style={{ padding: 12, background: "#060a10", border: "1px solid #1e293b", borderRadius: 7, fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", marginBottom: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                    <div><span style={{color:"#94a3b8"}}>Shares: </span><span style={{color:"#cbd5e1"}}>{shares.toLocaleString("id-ID")}</span></div>
                    <div><span style={{color:"#94a3b8"}}>Gross: </span><span style={{color:"#cbd5e1"}}>{money(gross, editingTrade.data.mkt)}</span></div>
                    <div><span style={{color:"#94a3b8"}}>Net: </span><span style={{color: editingTrade.type === "BUY" ? "#f87171" : "#34d399", fontWeight: 600}}>{money(net, editingTrade.data.mkt)}</span></div>
                  </div>
                </div>
              );
            })()}

            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <button
                onClick={() => { deleteTrade(editingTrade.type, editingTrade.id); closeTradeEdit(); }}
                style={{ padding: "9px 16px", background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.25)", borderRadius: 7, color: "#f87171", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
              >
                🗑 Delete Trade
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={closeTradeEdit}
                  style={{ padding: "9px 18px", background: "transparent", border: "1px solid #1e293b", borderRadius: 7, color: "#94a3b8", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveTradeEdit}
                  style={{ padding: "9px 20px", background: "linear-gradient(135deg,#1d4ed8,#1e40af)", border: "1px solid rgba(59,130,246,.4)", borderRadius: 7, color: "#bfdbfe", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  UI HELPERS
// ════════════════════════════════════════════════════════════════
function Card({ children, style }) {
  return <div style={{ background: "#0a0e18", border: "1px solid #1e293b", borderRadius: 12, ...style }}>{children}</div>;
}
function SH({ children, style }) {
  return <div style={{ fontSize: 15, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".7px", marginBottom: 12, ...style }}>{children}</div>;
}
function Btn({ onClick, loading, label, small }) {
  return (
    <button onClick={onClick} disabled={loading} style={{ padding: small ? "6px 14px" : "9px 20px", background: "linear-gradient(135deg,#1d4ed8,#1e40af)", border: "1px solid rgba(59,130,246,.4)", borderRadius: 7, color: loading ? "#94a3b8" : "#bfdbfe", fontWeight: 600, fontSize: small ? 12 : 13.5, cursor: loading ? "default" : "pointer", whiteSpace: "nowrap" }}>
      {loading ? "…" : label}
    </button>
  );
}
function Empty({ children }) {
  return <div style={{ textAlign: "center", padding: 32, fontSize: 15, color: "#94a3b8" }}>{children}</div>;
}
function Chip({ mkt }) {
  return <span className="chip" style={{ background: mkt === "IDX" ? "#0f2040" : "#1a1600", color: mkt === "IDX" ? "#93c5fd" : "#fcd34d", fontWeight: 600 }}>{mkt}</span>;
}
function THead({ cols }) {
  return (
    <thead>
      <tr style={{ borderBottom: "1px solid #1e293b" }}>
        {cols.map(h => (
          <th key={h} style={{ padding: "8px 10px", textAlign: ["Stock","Inv","Investor","Broker","Date","Mkt","FIFO Buy","Period"].some(x=>h.startsWith(x)) ? "left" : "right", color: "#94a3b8", fontWeight: 600, fontSize: 14, textTransform: "uppercase", letterSpacing: ".5px", whiteSpace: "nowrap" }}>{h}</th>
        ))}
      </tr>
    </thead>
  );
}

// SortTh — clickable table header that toggles sort direction
function SortTh({ label, col, align, sortKey, sortDir, onSort }) {
  const active = sortKey === col;
  const arrow = active ? (sortDir === "desc" ? "↓" : "↑") : "↕";
  return (
    <th
      onClick={() => onSort(col)}
      style={{
        padding: "8px 10px",
        textAlign: align || "left",
        color: active ? "#60a5fa" : "#94a3b8",
        fontWeight: 600,
        fontSize: 14,
        textTransform: "uppercase",
        letterSpacing: ".5px",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
      }}
      title={`Sort by ${label}`}
    >
      {label}
      <span style={{ marginLeft: 4, fontSize: 11, opacity: active ? 1 : 0.4, color: active ? "#60a5fa" : "#64748b" }}>
        {arrow}
      </span>
    </th>
  );
}
function TD({ children, mono, bold, right, sm, xs, color }) {
  return (
    <td style={{ padding: "8px 10px", textAlign: right ? "right" : "left", fontFamily: mono ? "'IBM Plex Mono',monospace" : undefined, fontWeight: bold ? 600 : 400, fontSize: xs ? 12 : sm ? 12.5 : 13, color: color || (bold ? "#f1f5f9" : "#cbd5e1"), whiteSpace: "nowrap" }}>
      {children}
    </td>
  );
}
function CRow({ l, v, sub, color, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
      <span style={{ fontSize: 15, color: sub ? "#94a3b8" : "#cbd5e1" }}>{l}</span>
      <span style={{ fontSize: 15, fontWeight: bold ? 600 : 500, color: color || "#e2e8f0", fontFamily: "'IBM Plex Mono',monospace" }}>{v}</span>
    </div>
  );
}
function FF({ l, v, onC, type = "text", ph, opts }) {
  const s = { width: "100%", background: "#060a10", border: "1px solid #1e293b", borderRadius: 7, padding: "9px 11px", color: "#e2e8f0", fontSize: 15, transition: "border .15s" };
  // Normalize opts: accept either ["a","b"] OR [["a","Label A"],["b","Label B"]].
  // Tuple form allows pretty display labels while keeping raw values for state.
  const normalized = opts
    ? opts.map(o => Array.isArray(o) ? { value: o[0], label: o[1] } : { value: o, label: o })
    : null;
  // Dedupe by value
  const seen = new Set();
  const uniqueOpts = normalized ? normalized.filter(o => seen.has(o.value) ? false : (seen.add(o.value), true)) : null;
  // Date-specific: dark color scheme so calendar icon is visible
  if (type === "date") {
    s.colorScheme = "dark";
    s.cursor = "pointer";
  }
  return (
    <div>
      <label style={{ display: "block", fontSize: 13, color: "#94a3b8", marginBottom: 5, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".6px" }}>{l}</label>
      {uniqueOpts
        ? <select value={v} onChange={e => onC(e.target.value)} style={{ ...s, cursor: "pointer" }}>{uniqueOpts.map((o, i) => <option key={`${o.value}_${i}`} value={o.value}>{o.label}</option>)}</select>
        : <input type={type} value={v} placeholder={ph} onChange={e => onC(e.target.value)} style={s} />}
    </div>
  );
}

// PercentInput — controlled-but-editable percent field for fee editing.
// Maintains an internal text buffer while focused so the user can type freely
// (including partial values like "0.", "0.1", "0."), only committing the parsed
// number to the parent on blur or Enter. When NOT focused, mirrors the upstream
// numeric value formatted to 3 decimals.
function PercentInput({ feePctNum, gross, onCommit }) {
  const [buf, setBuf] = useState("");
  const [focused, setFocused] = useState(false);
  // When upstream value changes (e.g. user edited the absolute fee), reflect it
  // — but only while NOT focused, so we don't fight the user's typing.
  useEffect(() => {
    if (!focused) setBuf(feePctNum.toFixed(3));
  }, [feePctNum, focused]);
  const commit = () => {
    setFocused(false);
    if (buf.trim() === "" || isNaN(parseFloat(buf))) {
      // Revert to upstream value
      setBuf(feePctNum.toFixed(3));
      return;
    }
    onCommit(buf);
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={buf}
      onFocus={(e) => { setFocused(true); setBuf(feePctNum.toFixed(3)); e.target.select(); }}
      onChange={e => setBuf(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === "Enter") { commit(); e.target.blur(); } }}
      disabled={gross <= 0}
      placeholder="%"
      style={{ width: 80, padding: "8px 10px", background: gross > 0 ? "#06090f" : "#0a0e15", color: gross > 0 ? "#e2e8f0" : "#475569", border: "1px solid #1e293b", borderRadius: 6, fontSize: 15, fontFamily: "'IBM Plex Mono',monospace", textAlign: "right" }}
      title={gross > 0 ? "Fee as % of gross — type a number then Tab/Enter to recompute the absolute fee" : "Enter qty and price first"}
    />
  );
}

// KPIBox — compact stat card with label, big value, optional hint tooltip.
// Used in the Performance tab summary strip.
function KPIBox({ label, value, color = "#e2e8f0", hint = "" }) {
  return (
    <div title={hint} style={{ padding: "10px 12px", background: "#060a10", border: "1px solid #1e293b", borderRadius: 7 }}>
      <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color, fontFamily: "'IBM Plex Mono',monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: "#64748b", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{hint}</div>}
    </div>
  );
}

function PasteImport({ onImport }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef();

  // Normalize period strings into YYYY-MM:
  //   "2026-03" / "2026/03" / "Mar-26" / "Mar 2026" / "March 2026" → "2026-03"
  //   Also handles Excel date serial numbers (number 1 → 1900-01-01)
  const parsePeriod = (s) => {
    if (s == null || s === "") return null;
    // Excel date serial number (raw:true gives a Date object or number)
    if (s instanceof Date && !isNaN(s)) {
      return `${s.getUTCFullYear()}-${String(s.getUTCMonth() + 1).padStart(2, "0")}`;
    }
    if (typeof s === "number") {
      // Excel epoch is 1900-01-00 (serial 0). Convert serial → JS Date.
      const d = new Date(Date.UTC(1899, 11, 30) + s * 86400000);
      if (!isNaN(d)) return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      return null;
    }
    const t = String(s).trim();
    let m = t.match(/^(\d{4})[-\/.](\d{1,2})/);
    if (m) return `${m[1]}-${String(+m[2]).padStart(2, "0")}`;
    const monthNames = { jan:1, feb:2, mar:3, apr:4, may:5, jun:6, jul:7, aug:8, sep:9, oct:10, nov:11, dec:12,
                         january:1, february:2, march:3, april:4, june:6, july:7, august:8, september:9, october:10, november:11, december:12 };
    m = t.match(/^([a-z]+)[\s\-\/]+(\d{2,4})$/i);
    if (m) {
      const mo = monthNames[m[1].toLowerCase()];
      let yr = +m[2];
      if (yr < 100) yr += 2000;
      if (mo) return `${yr}-${String(mo).padStart(2, "0")}`;
    }
    m = t.match(/^(\d{1,2})[\s\-\/]+([a-z]+)[\s\-\/]+(\d{2,4})$/i);
    if (m) {
      const mo = monthNames[m[2].toLowerCase()];
      let yr = +m[3];
      if (yr < 100) yr += 2000;
      if (mo) return `${yr}-${String(mo).padStart(2, "0")}`;
    }
    return null;
  };

  // Parse number with commas / parentheses / "(123,456)" style negatives / Indonesian "Rp" prefix
  const parseNum = (s) => {
    if (s == null || s === "") return null;
    if (typeof s === "number") return isNaN(s) ? null : s;
    let t = String(s).trim().replace(/Rp\s*/gi, "").replace(/[,\s]/g, "");
    if (t === "" || t === "-") return null;
    if (/^\(.*\)$/.test(t)) t = "-" + t.slice(1, -1);
    const n = +t;
    return isNaN(n) ? null : n;
  };

  // Shared parse logic — takes a 2D array (rows × cols) and produces entries.
  const parseRows = (rows) => {
    if (!rows || rows.length < 2) {
      setError("Need at least header row + one data row");
      return null;
    }
    const headers = rows[0].map(h => String(h ?? "").trim());
    if (headers.length < 3) {
      setError("Need at least 3 columns (Period, IHSG, plus a broker)");
      return null;
    }

    let periodCol = 0;
    let ihsgCol = -1, sp500Col = -1;
    const brokers = [];
    // Helper: is this header a fund-flow column rather than a broker balance column?
    // Excel exports often duplicate "Fund Add/Sub" across multiple brokers without
    // numbering, so we must treat ANY column whose header reads like a fund-flow
    // marker as a fund column — never a broker name.
    const looksLikeFundCol = (h) => {
      const s = (h || "").toLowerCase();
      return s.includes("fund") || s.includes("add") || s.includes("sub") || s.includes("δ") || s === "delta";
    };
    let i = 0;
    while (i < headers.length) {
      const h = headers[i].toLowerCase();
      if (i === 0) { i++; continue; }
      if (h.includes("ihsg")) { ihsgCol = i; i++; continue; }
      if (h.includes("s&p") || h.includes("sp500") || h.includes("sp 500")) { sp500Col = i; i++; continue; }
      if (h === "" || h === "-" || h.includes("gain") || h.includes("%")) { i++; continue; }
      // Skip fund-flow columns — they belong to a previous broker, not a new one.
      if (looksLikeFundCol(h)) { i++; continue; }
      const brName = headers[i].trim();
      let fdCol = -1;
      for (let j = i + 1; j < Math.min(headers.length, i + 4); j++) {
        if (looksLikeFundCol(headers[j])) { fdCol = j; break; }
      }
      brokers.push({ name: brName, balCol: i, fdCol });
      i++;
    }

    if (ihsgCol < 0 && brokers.length === 0) {
      setError("Couldn't identify any data columns. Header row should mention IHSG and broker names.");
      return null;
    }

    // Normalize broker names to match the canonical IDX/US broker list — e.g.
    // Excel header "Mirae" → "Mirae Asset" so monthly data joins cleanly to
    // trades/cash already keyed by the canonical name.
    const ALL_KNOWN_BROKERS = ["Stockbit","Mirae Asset","Semesta","Semesta Margin","Semesta Regular","OSK","AM Capital","BNI Sekuritas","Indo Premier","Mandiri","Henan Putihrai","MNC Sekuritas","Interactive Brokers","Schwab","TD Ameritrade","Fidelity","Robinhood"];
    const normalizeBroker = (name) => {
      const n = name.trim().toLowerCase();
      // Exact (case-insensitive) match wins
      const exact = ALL_KNOWN_BROKERS.find(b => b.toLowerCase() === n);
      if (exact) return exact;
      // Substring match (e.g. "Mirae" matches "Mirae Asset"); pick the longest known broker
      // whose lowercase form starts with or contains the user's header.
      const candidates = ALL_KNOWN_BROKERS.filter(b => b.toLowerCase().includes(n) || n.includes(b.toLowerCase()));
      if (candidates.length > 0) return candidates.sort((a, b) => b.length - a.length)[0];
      return name.trim();  // fallback: keep as-is
    };
    brokers.forEach(b => { b.name = normalizeBroker(b.name); });

    const entries = [];
    let badRows = 0;
    for (let li = 1; li < rows.length; li++) {
      const cells = rows[li];
      if (!cells || cells.length === 0) continue;
      const period = parsePeriod(cells[periodCol]);
      if (!period) { badRows++; continue; }
      const ihsg = ihsgCol >= 0 ? parseNum(cells[ihsgCol]) : null;
      const sp500 = sp500Col >= 0 ? parseNum(cells[sp500Col]) : null;
      brokers.forEach(b => {
        const bal = parseNum(cells[b.balCol]);
        const fd  = b.fdCol >= 0 ? parseNum(cells[b.fdCol]) : null;
        if (bal != null) {
          entries.push({ period, brk: b.name, bal, fd: fd || 0, ihsg, sp500 });
        }
      });
    }

    // Sort entries DESCENDING by period (latest first), then by broker name
    entries.sort((a, b) => {
      if (a.period !== b.period) return a.period < b.period ? 1 : -1;
      return a.brk < b.brk ? -1 : 1;
    });

    return { entries, badRows, brokers: brokers.map(b => b.name), ihsgFound: ihsgCol >= 0, sp500Found: sp500Col >= 0 };
  };

  const handleParseText = () => {
    setError("");
    const lines = text.split(/\r?\n/).map(l => l).filter(l => l.trim());
    const delim = lines[0]?.includes("\t") ? "\t" : ",";
    const rows = lines.map(l => l.split(delim).map(c => c.trim()));
    const result = parseRows(rows);
    setPreview(result);
  };

  const handleFileUpload = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      // Pick first sheet, or let user pick if multiple? For now: use first.
      const sheetName = wb.SheetNames[0];
      const sheet = wb.Sheets[sheetName];
      // Get as 2D array of values (header: 1)
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null });
      const result = parseRows(rows);
      setPreview(result);
      setText("");  // clear paste box if file used
    } catch (err) {
      setError(`Failed to read file: ${err.message.slice(0, 100)}`);
    } finally {
      e.target.value = "";  // allow re-uploading same file
    }
  };

  const handleImport = () => {
    if (!preview || preview.entries.length === 0) return;
    onImport(preview.entries);
    setText("");
    setPreview(null);
  };

  return (
    <div>
      {/* File upload button */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.xlsm,.csv,.tsv"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{ padding: "8px 16px", background: "rgba(167,139,250,.1)", border: "1px solid rgba(167,139,250,.3)", borderRadius: 6, color: "#a78bfa", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
        >
          📁 Import from Excel
        </button>
        <span style={{ fontSize: 12, color: "#64748b" }}>or paste below ↓</span>
      </div>

      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={`Period\tIHSG\tSemesta\tFund Δ\tMirae\tFund Δ\n2026-03\t7048.22\t980169572\t66500000\t321205306\t-63000000\n...`}
        style={{ width: "100%", minHeight: 100, padding: "8px 10px", background: "#06090f", color: "#e2e8f0", border: "1px solid #1e293b", borderRadius: 6, fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", resize: "vertical" }}
      />
      <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button
          onClick={handleParseText}
          disabled={!text.trim()}
          style={{ padding: "6px 14px", background: text.trim() ? "rgba(96,165,250,.08)" : "transparent", border: `1px solid ${text.trim() ? "rgba(96,165,250,.3)" : "#1e293b"}`, borderRadius: 5, color: text.trim() ? "#60a5fa" : "#475569", cursor: text.trim() ? "pointer" : "default", fontSize: 12, fontWeight: 600 }}
        >
          Parse pasted text →
        </button>
        {error && <span style={{ fontSize: 12, color: "#f87171" }}>⚠ {error}</span>}
      </div>

      {preview && (
        <div style={{ marginTop: 12, padding: "10px 12px", background: "rgba(52,211,153,.05)", border: "1px solid rgba(52,211,153,.2)", borderRadius: 6, fontSize: 12 }}>
          <div style={{ color: "#cbd5e1", marginBottom: 6 }}>
            <b>Preview:</b> {preview.entries.length} entries from {preview.brokers.length} broker(s) ({preview.brokers.join(", ")})
            {preview.ihsgFound && " · IHSG ✓"}
            {preview.sp500Found && " · S&P500 ✓"}
            {preview.badRows > 0 && ` · ${preview.badRows} bad row(s) skipped`}
          </div>
          {preview.entries.length > 0 && (
            <details>
              <summary style={{ cursor: "pointer", color: "#94a3b8" }}>Show first 10 entries (latest first)</summary>
              <pre style={{ margin: "4px 0", fontSize: 11, color: "#94a3b8", overflow: "auto", maxHeight: 200 }}>
                {preview.entries.slice(0, 10).map(e => `${e.period} ${e.brk}: bal=${e.bal.toLocaleString()} fd=${(e.fd || 0).toLocaleString()}${e.ihsg ? ` ihsg=${e.ihsg}` : ""}`).join("\n")}
              </pre>
            </details>
          )}
          <button
            onClick={handleImport}
            style={{ marginTop: 8, padding: "6px 14px", background: "rgba(52,211,153,.1)", border: "1px solid rgba(52,211,153,.3)", borderRadius: 5, color: "#34d399", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
          >
            ✓ Import {preview.entries.length} entries
          </button>
        </div>
      )}
    </div>
  );
}

function PerfChart({ rows, brokers }) {
  const relevant = rows.filter(r => r.ihsgG != null);
  if (!relevant.length) return <Empty>No performance data yet.</Empty>;
  const all = relevant.flatMap(r => [r.ihsgG, r.sp500G, ...Object.values(r.acctG)].filter(v => v != null));
  const mn = Math.min(...all, -3), mx = Math.max(...all, 3), range = mx - mn || 6;
  const H = 140, W = Math.max(560, relevant.length * 64);
  const bw = W / relevant.length;
  const y0 = H * mx / range;
  const yv = v => v != null ? H * (mx - v) / range : null;
  const COLORS = { IHSG: "#34d399", "S&P500": "#f6ad55", Semesta: "#60a5fa", "Semesta Regular": "#818cf8", "Mirae Asset": "#a78bfa", Stockbit: "#f472b6" };
  const series = [{ k: "IHSG", f: r => r.ihsgG }, { k: "S&P500", f: r => r.sp500G }, ...brokers.map(b => ({ k: b, f: r => r.acctG?.[b] }))];
  const BW = 11, GAP = 2, totalW = series.length * (BW + GAP) - GAP;

  return (
    <div style={{ overflowX: "auto" }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H + 52}`} style={{ minWidth: 400 }}>
        <line x1={0} y1={y0} x2={W} y2={y0} stroke="#0f1825" strokeDasharray="3,3" />
        <text x={4} y={y0 - 3} fontSize="12" fill="#64748b" fontFamily="'IBM Plex Mono',monospace">0%</text>
        {relevant.map((r, i) => {
          const cx = i * bw + bw / 2 - totalW / 2;
          return (
            <g key={r.period}>
              {series.map((s, si) => {
                const v = s.f(r); if (v == null) return null;
                const yy = yv(v), x = cx + si * (BW + GAP);
                return <rect key={s.k} x={x} y={Math.min(yy, y0)} width={BW} height={Math.abs(yy - y0)} fill={COLORS[s.k] || "#6b7280"} opacity={0.8} rx={2} />;
              })}
              <text x={i * bw + bw / 2} y={H + 13} textAnchor="middle" fontSize="8.5" fill="#6b7280" fontFamily="'IBM Plex Mono',monospace">{r.period?.slice(5)}/{r.period?.slice(2, 4)}</text>
            </g>
          );
        })}
        {series.map((s, i) => (
          <g key={s.k} transform={`translate(${10 + i * 76},${H + 26})`}>
            <rect width={9} height={9} fill={COLORS[s.k] || "#6b7280"} rx={2} />
            <text x={13} y={8} fontSize="8.5" fill="#6b7280">{s.k}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function AIMarkdown({ text }) {
  return (
    <div style={{ lineHeight: 1.8, fontSize: 15, color: "#9ca3af" }}>
      {text.split("\n").map((line, i) => {
        if (line.startsWith("## ")) return <div key={i} style={{ fontWeight: 600, color: "#e2e8f0", fontSize: 16, marginTop: 18, marginBottom: 6, borderBottom: "1px solid #1e293b", paddingBottom: 4 }}>{line.slice(3)}</div>;
        if (line.startsWith("# "))  return <div key={i} style={{ fontWeight: 600, color: "#60a5fa", fontSize: 18, marginTop: 14, marginBottom: 8 }}>{line.slice(2)}</div>;
        if (line.startsWith("**") && line.endsWith("**")) return <div key={i} style={{ fontWeight: 600, color: "#e2e8f0", marginTop: 10, marginBottom: 4 }}>{line.slice(2, -2)}</div>;
        if (line.match(/^\*\*(HOLD|ADD|REDUCE|SELL|BUY)\*\*/)) {
          const action = line.match(/HOLD|ADD|REDUCE|SELL|BUY/)?.[0];
          const colors = { HOLD: "#fbbf24", ADD: "#34d399", REDUCE: "#f97316", SELL: "#f87171", BUY: "#34d399" };
          return <div key={i} style={{ background: "#060a10", border: `1px solid ${colors[action] || "#0f1825"}20`, borderLeft: `3px solid ${colors[action] || "#6b7280"}`, borderRadius: "0 6px 6px 0", padding: "6px 12px", marginBottom: 6, fontSize: 14, color: "#d1d5db" }} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(HOLD|ADD|REDUCE|SELL|BUY)\*\*/g, `<span style="color:${colors[action]};font-weight:700">$1</span>`).replace(/\*\*/g, "") }} />;
        }
        if (line.match(/^[-•] /)) return <div key={i} style={{ paddingLeft: 16, color: "#6b7280", marginBottom: 3 }}>· {line.slice(2)}</div>;
        if (line.match(/^\d+\. /)) return <div key={i} style={{ paddingLeft: 16, color: "#9ca3af", marginBottom: 4, fontWeight: 500 }}>{line}</div>;
        if (line === "") return <div key={i} style={{ height: 4 }} />;
        return <div key={i} style={{ marginBottom: 2, color: "#6b7280" }}>{line}</div>;
      })}
    </div>
  );
}
